import {
  answerFrom, compose, decodeRows, LISTED_ROWS, makeCode, oversizedNote, shapesOf, summaryOverflow, tidyOversized, periodRule,
  splitResult,
} from './compute';
import {
  candidates, describe as describeScreen, localToday, makePlan, namedScreen, namesScreen, planScreens, screenTopic,
  skillsBesideNamedScreen, sweepLimit,
} from './plan';
import type { Answer, Column } from './compute';
import type { SequenceStep, Chat, ChatStream, QueryPlan, Retrieval, Shortlist } from './plan';
import type { AgentField, AgentScreen, CodeLists, Concepts } from './screenIndex';
import { readableSourceLines, readableSources } from './sources';
import { codeListFor } from './screenIndex';
import {
  BlockLedger, SAVE_ATTEMPTS, choose, labelKey, namedLabels, plainLabel, settleBlocked, signatureOf,
} from './blocked';
import {
  fillMasked, maskSensitive, unmaskFields, unmaskRows,
} from './sensitive';
import { fillSpoken, fillSpokenRows, spokenValue } from './spoken';
import { correctNotices } from './mislabel';
import { plainSpeech } from './plainSpeech';
import { reviewResult } from './review';
import { uploadTrouble } from './upload';
import { describeFacts, extractFacts, splitFacts } from './extract';
import type { Fact } from './extract';
import { unlistedSpoken } from './unlisted';
import type { Unlisted } from './unlisted';
import type { UploadedFile } from './upload';
import { requestedFormats } from './files';
import type { FileFormat, ReportDocument, ResultAxis, ResultSnapshot, TableData } from './files';
import { fallbackReport, makeReport } from './report';
import type { ReportExtra } from './report';
import { reportCharts, withoutTableTwins } from './charts';
import { axisNotes, chartText, monthsIn, uncovered, writeBrief } from './brief';
import type { Brief, BriefKind } from './brief';
import {
  asksRemaining, duplicateAccounts, isTotalRow, leaveBasis, leaveLaw, leaveNotes, listReport, personNames, withoutNames,
} from './listTables';
import { NOTICE_SENDER, NOTICE_TO, noticeItems, sectionTitles } from './reportHtml';
import {
  actionDays, basisLabel, carryActions, carryLines, carryOver, closingLines, confirmFirst, deadlineFacts, lateChartLine, monthColumn, monthKey,
  nextWeekEnd, overdueSpan,
  siteCounts, statusColumn, titleWithYear,
  withMemoSummary, currentCharts, dateErrorNote, dueNote, dueSplit, exceptionRows, monthNote, monthStates, overdueFacts,
  memoFindings, memoLines, monthScope, notRepeating, overdueFocus, populationTable, refTable, reportFacts, statusFromChart, statusRuleOf, statusTable, tableMonths, withDates, withIdleActions,
  withBothSpan,
  withMonthLabels,
} from './reportData';
import { selectSkillsForTask, selectSkillsForTurn, skillClarification, skillRowsAt } from './skills';
import type { AgentSkill, EditWindow } from './skills';
import { verifyCompletion } from './completion';
import { objectParticle, subjectParticle, topicParticle, withParticle } from './korean';
import { collectQueued, queuedNote } from './queued';
import type { QueuedJob } from './queued';
import { looksLikeFailure, userMessage } from './userMessage';
import { carriedTask, periodish, taskQuestion, updateTaskSpec } from './task';
import type { DuplicateAction, TaskSpec } from './task';
import { buildCapabilityTools, findCapabilityTool } from './capabilities';
import { Budget } from './budget';
import { verifyResult } from './verify';
import { decideRecovery, recoveryNote, RecoveryStoppedError } from './recovery';
import { asksForDataGap, DATA_GAP_NOTE } from './dataGapAsk';
import { rememberRetrievals } from './recall';
import { analyzeAxes, applyExpertCodes, frameFor, glossaryFor, termIndex, termRoutes, writeReport } from './experts';
import type { Expert } from './experts';
import { ISSUE_ANSWER, issueReport } from './issues';
import { isIssues, readingFor, readingForDocument, withReading } from './reading';
import type { DataReading } from './reading';
import { deriveCriterion, derivationFact, derivationNote, vaguePeriodFact } from './reasoning';
// 앱의 TypeScript(4.2)는 `import { type X }` 를 못 읽는다 — 따로 적는다.
import type { Derivation } from './reasoning';
import { explain } from './procedures';
import type { Procedures } from './procedures';
import {
  classifyRetrieved, ExecutionValidationError, makeExperience, validateRetrieved,
} from './experience';
import type { RetrievalOutcome } from './experience';
import type { UiSnapshot } from './experience';

export type { Answer } from './compute';
export type { AgentScreen, CodeList, CodeLists, Concepts } from './screenIndex';
export { codeListFor } from './screenIndex';
export { Sandbox, createSandbox, assertWorkerCsp, WORKER_CSP, WORKER_URL } from './sandbox';
export type { Chat, ChatStream, Shortlist } from './plan';
export type { FileFormat, TableData } from './files';
export type { UploadedFile } from './upload';
export type { ReportDocument, ResultSnapshot } from './files';
export type { AgentSkill } from './skills';
export type { Expert } from './experts';
export type { ArtifactSpec, TaskIntent, TaskSpec } from './task';
export { parseTaskSpec, taskNeedsTable, taskQuestion, updateTaskSpec } from './task';
export { IncompleteTaskError, verifyCompletion } from './completion';
export type { CapabilityField, CapabilityTarget, CapabilityTool } from './capabilities';
export { buildCapabilityTools, findCapabilityTool } from './capabilities';
export { decideRecovery, RecoveryStoppedError } from './recovery';
export { userMessage } from './userMessage';
export type { Procedure, Procedures } from './procedures';
export type { ExperienceCandidate, UiSnapshot, ValidationReport } from './experience';

/**
 * 계획이 되묻기로 끝났다. 실패가 아니라 사용자 차례라는 뜻이다.
 *
 * **하던 작업을 같이 들고 간다.** 앱은 끝난 턴에만 작업을 저장했는데, 되묻기는 끝난
 * 턴이 아니라서 그 자리에서 작업이 사라졌다 — 사용자가 선택지를 누른 다음 턴은
 * `워드로 만들어줘` 를 잊은 채 시작하고, 답은 나왔는데 파일 버튼이 없다(R9).
 */
export class AskUser extends Error {
  task?: TaskSpec;

  constructor(readonly question: string, readonly options?: string[]) {
    super(question);
    this.name = 'AskUser';
  }
}

type Retrieved = {
  label: string;
  rows: Record<string, unknown>[];
  totals?: Record<string, unknown>;
  notices?: string[];
  snapshot?: UiSnapshot;
  answered?: boolean;
  /** 펼쳐 읽기 결과. `reason` 이 있으면 다 못 읽은 것이다. */
  swept?: {
    masterRows: number; read: number; missed?: number; unchanged?: number; reason?: string;
    /** 좁힌 대상 중 위 목록에 없던 수 — 퇴사자 등. */
    unmatched?: number;
  };
  /** 펼쳐 읽은 범위 안 위 표 행. 상세가 빈 행도 있다. */
  masterRows?: Record<string, unknown>[];
  /** 조회 때 화면 조건 칸에 걸려 있던 값(드라이버가 채운다). 계획이 비워 둔 선택 칸의 화면 기본값을 계산이 알게 한다. */
  applied?: Record<string, string>;
};

export type SessionLike = {
  /** 미결함 문서 결재(드라이버 `approval.ts`). 구버전 드라이버에는 없다. */
  approve?(request: {
    appnums: string[];
    action: '승인' | '반려' | '전결';
    remark?: string;
    auto: boolean;
  }): Promise<{ results: { appnum: string; done: boolean; notice: string }[] }>;
  retrieve(request: {
    label: string;
    routePath: string;
    rowsStateKey: string;
    conditions: Record<string, string>;
    scalarKeys?: string[];
    selectionStateKeys?: string[];
    force?: boolean;
  }): Promise<Retrieved>;
  /** 위 표 행마다 상세를 펼쳐 읽는다(드라이버 `sweepDetail`). 구버전 드라이버에는 없다. */
  sweepDetail?(request: {
    label: string;
    routePath: string;
    masterStateKey: string;
    detailStateKey: string;
    conditions: Record<string, string>;
    range?: { key: string; from?: string; to?: string };
    limit?: number;
    tab?: string;
    only?: { key: string; values: string[] };
  }): Promise<Retrieved>;
  switchBusinessSite?(code: string): Promise<void>;
  prepare?(request: {
    routePath: string; fields: Record<string, string>; continueDraft?: boolean;
    /** 목록에서 고르는 칸의 고를 수 있는 값. 드라이버가 타이프 전에 거른다. */
    allowed?: Record<string, string[]>;
    /** 코드 칸과 짝이 되는 이름 칸. 없으면 드라이버가 접미사로 짐작한다. */
    pairs?: Record<string, string>;
    reuse?: {
    rowsStateKey: string; match: Record<string, string>; fields: string[];
  }; rows?: { stateKey: string; items: Record<string, string>[]; tab?: string; addLabel?: string };
    /** 이미 있는 문서를 고친다. 신규를 누르지 않는다. */
    edit?: {
      rowsStateKey: string; match: Record<string, string>; cells?: Record<string, string>;
      conditions?: Record<string, string>; pick?: string;
      /** 칸을 확정에서 바꾼다. 고르는 순간 반영되는 칸이다(점검일자). */
      deferCells?: boolean;
      /** 창에서 고르기까지 하고 창 단추는 확정이 누른다(입금처리). */
      window?: EditWindow;
      cellsStateKey?: string; cellsMatch?: Record<string, string>;
      /** 고른 문서 안의 표에서 한 줄 더 — 보수현장의 호기(`units`). */
      then?: { stateKey: string; match: Record<string, string> };
    } }): Promise<{
    routePath: string; fields: Record<string, string>;
    /** 확정으로 미룬 칸. 준비는 그 행까지 짚었다. `row` 는 사람에게 보일 행 이름이다. */
    deferred?: {
      row: string; cells: Record<string, string>;
      /** 창에서 골라 두고 단추를 미룬 일. `picked` 는 창에서 고른 줄의 이름이다. */
      window?: { action: string; button: string; picked: string };
    };
  }>;
  /**
   * 그 칸이 고를 수 있는 것들. 미리 담을 수 없는 목록(현장마다 다른 호기, 앞 칸에
   * 매달린 고장부위상세)을 저장이 막힌 자리에서 화면에 직접 물어 온다. 팝업이 안 열리면
   * `[]` 대신 `{options:[], reason}` 으로 **왜** 비었는지 실어 온다.
   */
  options?(
    request: { routePath: string; field: string },
  ): Promise<string[] | { options: string[]; reason?: string }>;
  /**
   * 탭을 열고 **그 탭 자료가 도착할 때까지 기다린다.**
   *
   * 기다리는 것이 이 함수의 절반이다. 열자마자 채우면 뒤늦게 온 탭 자료가 방금 넣은
   * 값을 덮는데, 드라이버는 성공이라 답한다 — 실측으로 봤다.
   */
  openTab?(request: { routePath: string; tab: string }): Promise<void>;
  /**
   * 화면을 보여 주기만 한다. 조회하지 않는다.
   *
   * 우리가 조용히 다른 화면에 다녀왔을 때 제자리로 돌려놓는 데 쓴다.
   */
  show?(routePath: string): Promise<void>;
  /**
   * 파일을 문서에 붙인다. 화면을 건드리지 않고 앱이 가진 두 번의 호출을 쓴다.
   *
   * 붙인 수를 돌려준다. 붙일 길이 없으면 0 이다 — 던지지 않는다.
   */
  attach?(request: { routePath: string }): Promise<{ attached: number; notices: string[] }>;
  /** 메일·문자·카카오톡을 보낸다. Auto 가 켜졌을 때만 부른다. */
  send?(request: {
    routePath: string; channel: 'email' | 'sms' | 'kakao' | 'fax'; auto: boolean;
    to?: string; subject?: string; body?: string; fields?: Record<string, string>;
  }): Promise<{ notices: string[] }>;
  /** 화면 고유 버튼으로 문서를 잇는다. Auto 가 켜졌을 때만 부른다. */
  operate?(request: {
    routePath: string; button: string; auto: boolean; confirm?: string;
    fields?: Record<string, string>; selectKey?: string;
    /** 표 안에 달린 버튼일 때. 그 행을 값으로 짚고 칸을 헤더 id 로 짚는다. */
    row?: {
      stateKey: string; match: Record<string, string>; column: string; nth?: number;
      conditions?: Record<string, string>;
    };
    /** 누르기 전에 조회해 둘 화면 목록. check 면 그 행을 다 고른다. */
    list?: {
      stateKey: string; conditions?: Record<string, string>; check?: boolean;
      /** 체크 칸 이름. 기본 `chk` — 전자세금계산서발행은 `selchk` 다. */
      checkKey?: string;
      /** 이 값들로 시작하는 줄만 체크한다(거래처 이름 등). 없으면 목록 전부. */
      match?: Record<string, string>;
    };
    /** 누르기 전에 고를 목록 행 — 문서 단추가 그 문서를 서버에서 다시 읽게. */
    focus?: { stateKey: string; match: Record<string, string>; conditions?: Record<string, string>; then?: { stateKey: string; match: Record<string, string> } };
  }): Promise<{ notices: string[]; queued?: QueuedJob[] }>;
  /**
   * 계획이 고를 것 같은 화면을 미리 연다. 기다리지 않는다 — 드라이버가 다음 일 전에 다 열리기를 기다린다.
   * 계획(8초 남짓) 동안 화면 열기와 그 화면의 자체 조회를 숨긴다(2026-10-14).
   */
  warm?(routePath: string): void;
  /** 저장·삭제. Auto 가 켜졌을 때만 부른다. */
  commit?(request: { routePath: string; auto: boolean; action?: 'save' | 'delete' }): Promise<{
    notices: string[];
    /** 화면 상태로 확인한 결과. 알림 문구가 아니라 미저장 행이 사라졌는지로 판단한다. */
    saved: boolean;
    /** 서버에 맡겨진 일. 저장이 대기열로 넘어가면 새 행은 나중에 생긴다. */
    queued?: QueuedJob[];
    /** 사람이 안 준 코드를 드라이버가 정해 넣었다(설치현장 현장코드). 답에 그 값을 말한다. */
    assigned?: { label: string; value: string };
  }>;
};

export type SandboxLike = {
  run(code: string, data: unknown): Promise<unknown>;
  /** 파이썬을 미리 띄운다. 부팅 시간이 계산 예산을 잡아먹지 않게 한다. */
  warmup?(timeoutMs?: number): Promise<unknown>;
};

export type Deps = {
  screens: AgentScreen[];
  /**
   * 코드 칸이 고르는 목록. 없으면 예전처럼 돈다 — 지어낸 값을 못 거를 뿐이다.
   * 없어도 도는 것이 중요하다. 이 파일 하나를 못 받았다고 질문 전체를 죽일 이유가 없다.
   */
  codeLists?: CodeLists;
  /** 사람이 말한 낱말 → 열의 뜻. 없으면 예전처럼 낱말 표만 쓴다. */
  concepts?: Concepts;
  /** 업무 분야 전문가 — 용어·코드 뜻·분석 틀(`kb/experts.json`). 없으면 예전처럼 돈다. */
  experts?: Expert[];
  session: SessionLike;
  sandbox: SandboxLike;
  chat: Chat;
  /** 지식베이스 검색. 없으면 화면 이름 겹침으로 후보를 좁힌다. */
  shortlist?: Shortlist;
  /** 지금 열려 있는 화면과 탭들. 없으면 전처럼 처음부터 계획한다. */
  context?: {
    /**
     * **이미 정한 계획.** 묶음(batch)을 확인받은 뒤 건마다 이 계획으로 한 턴을 돈다 — 모델에 다시 묻지
     * 않는다. 사람이 본 목록 그대로 해야 하고, 백 건을 백 번 계획하면 한도가 바닥난다.
     */
    presetPlan?: QueryPlan;
    currentRoute?: string;
    openTabs?: { path: string; name: string }[];
    history?: {
      question: string; answer: string; routes?: string[]; table?: TableData;
      feedback?: 'positive' | 'negative';
      result?: ResultSnapshot;
      report?: ReportDocument;
      task?: TaskSpec;
      /**
       * 그 턴이 **되묻기**였는가. 되묻기의 본문은 답이 아니라 질문이다.
       *
       * 되묻기도 작업을 남기게 한 뒤로, 파일 재사용이 그 질문 문장을 최근 답으로 집어
       * `현재 인원 현황도 사업장별로 집계해야 하나요?` 를 본문으로 한 워드를 만들었다.
       */
      asked?: boolean;
    }[];
    /** v2가 갱신할 현재 업무 상태. 원문 대화와 별도로 보존한다. */
    task?: TaskSpec;
    /** 로그인한 사람. divinm(부서명)은 공지의 발신·문의처와 보고서 조치의 담당이 된다(사용자 확인, 2026-10-27). */
    me?: { perid: string; pernm: string; spjangcd?: string; spjangnm?: string; divinm?: string };
    businessSites?: { code: string; name: string; current: boolean }[];
    /** 선택지에 없는 문맥(전사 `ZZ` 등)일 수 있는 현재 사업장 코드. 순회 뒤 돌아갈 곳이다. */
    businessSiteContext?: string;
    experiences?: {
      question: string;
      recipe: {
        route: string; rowsStateKey: string; conditionShape: Record<string, string>; requiredColumns: string[];
      }[];
    }[];
    /**
     * 사용자가 패널에서 켠 자율 모드.
     *
     * 켜져 있으면 초안에서 멈추지 않고 저장까지 한다. 이 값은 **사용자가 체크한 것**만
     * 담는다 — 모델이 정하지 않고, 프롬프트로도 바뀌지 않는다.
     */
    auto?: boolean;
    /**
     * 사람이 말한 이름 → 화면이 확정한 이름. 앞선 대화들에서 배운 것이다.
     *
     * 한 대화 안에서만 기억하면 새 대화마다 같은 현장을 다시 고르게 된다.
     */
    aliases?: Record<string, string>;
    /**
     * 사람이 **이번 턴에** 올린 파일. 이력서를 읽어 인사등록을 채우는 것 같은 일에 쓴다.
     *
     * 대화 기록에 쌓지 않는다 — 그림 한 장이 수천 토큰이라, 남겨 두면 그 뒤의 모든
     * 턴이 같은 그림 값을 다시 낸다. 올린 턴에서만 실린다.
     */
    files?: UploadedFile[];
    /** 올린 문서에서 또렷하게 읽은 값. 계획 프롬프트에 그대로 실린다. */
    facts?: { label: string; value: string }[];
  };
  skills?: AgentSkill[];
  procedures?: Procedures;
  /** 이 질문에 쓸 수 있는 예산. 없으면 기본값으로 새로 만든다. */
  budget?: Budget;
  /** 답변만 흘려 받는다. 없으면 `chat` 으로 한 번에 받는다. */
  chatStream?: ChatStream;
  /** 있으면 문자열 라우팅 대신 구조화 TaskSpec을 먼저 만든다. */
  taskChat?: Chat;
  /**
   * 어디까지 왔는지 알린다.
   *
   * 한 질문에 화면을 열고 조회를 여러 번 하고 파이썬을 돌린다. 그 시간이 대부분이라
   * 마지막 문장만 흘려서는 보는 쪽에서 멎은 것과 구분되지 않는다.
   */
  onProgress?: (step: AgentStep) => void;
  /**
   * 첫 질문의 계획을 과제와 함께 미리 시작한다(`speculatePlan`). 앱이 켠다 — 시험은 계획 호출 수와 첫 계획
   * 프롬프트를 보므로 기본은 끈다.
   */
  speculate?: boolean;
};

export type AgentStep =
  /** 무엇을 하라는 말인지 읽는 중(과제 단계). */
  | { kind: 'understanding' }
  /** 할 일을 짜는 중. `screen` 은 미리 열어 둔 화면 — 계획이 다른 화면을 고를 수도 있다. */
  | { kind: 'planning'; screen?: string }
  | { kind: 'retrieving'; label: string; done: number; total: number }
  | { kind: 'preparing'; screen: string }
  | { kind: 'computing' }
  /** 계산이 업무 규칙대로 셌는지 검토하는 중. */
  | { kind: 'checking' }
  /** 직전 계산이 받아들여지지 않아 다시 계산하는 중. `reason` 은 사람 말이다(파이썬 오류는 「계산 오류」). */
  | { kind: 'retrying'; attempt: number; reason: string }
  | { kind: 'answering'; text: string };

/**
 * 드라이버가 실제 선택지를 들고 던진 실패인가.
 *
 * 런타임은 드라이버를 임포트하지 않는다(세션은 구조적 타입이다). 모양으로 가린다.
 */
/**
 * 고를 것을 못 찾았는데 **내밀 후보도 없는** 실패인가.
 *
 * 이 자리에서 다시 계획하게 두면 모델이 없는 품목을 지어낸다. 사람에게 묻는 편이
 * 언제나 낫다 — 사람은 화면을 보고 있다.
 */
function blindOption(error: unknown): { field?: string; wanted: string } | undefined {
  const candidate = error as { name?: string; field?: unknown; wanted?: unknown; options?: unknown };
  if (candidate?.name !== 'OptionNotFound') return undefined;
  if (Array.isArray(candidate.options) && candidate.options.length) return undefined;
  const wanted = typeof candidate.wanted === 'string' ? candidate.wanted.trim() : '';
  if (!wanted) return undefined;
  return { field: typeof candidate.field === 'string' ? candidate.field : undefined, wanted };
}

function optionCodesOf(candidate: { optionCodes?: unknown }): { label: string; code: string }[] | undefined {
  if (!Array.isArray(candidate.optionCodes)) return undefined;
  const pairs = candidate.optionCodes.filter((pair): pair is { label: string; code: string } => (
    Boolean(pair) && typeof (pair as { label?: unknown }).label === 'string'
      && typeof (pair as { code?: unknown }).code === 'string'
  ));
  return pairs.length ? pairs : undefined;
}

function optionNotFound(
  error: unknown,
): {
    field?: string; wanted?: string; options: string[]; reason?: string;
    optionCodes?: { label: string; code: string }[];
  } | undefined {
  const candidate = error as {
    name?: string; field?: unknown; wanted?: unknown; options?: unknown; reason?: unknown;
    optionCodes?: unknown;
  };
  if (candidate?.name !== 'OptionNotFound') return undefined;
  const reason = typeof candidate.reason === 'string' && candidate.reason.trim()
    ? candidate.reason.trim() : undefined;
  const optionCodes = optionCodesOf(candidate);
  if (!Array.isArray(candidate.options) || !candidate.options.length) {
    // **후보가 없는데 이유가 있다.** 드라이버가 「팝업이 안 열렸다」를 이렇게 알린다 —
    // 그것은 「그 값이 없다」가 아니라 「지금 못 읽었다」다. 여기서 안 받으면 사람에게
    // 드라이버 원문이 그대로 나간다.
    if (!reason) return undefined;
    return {
      field: typeof candidate.field === 'string' ? candidate.field : undefined,
      wanted: typeof candidate.wanted === 'string' ? candidate.wanted : undefined,
      options: [],
      reason,
    };
  }
  return {
    ...(reason ? { reason } : {}),
    ...(optionCodes ? { optionCodes } : {}),
    field: typeof candidate.field === 'string' ? candidate.field : undefined,
    wanted: typeof candidate.wanted === 'string' ? candidate.wanted : undefined,
    options: candidate.options.filter((option): option is string => typeof option === 'string'),
  };
}

/** 상세행에서 그 칸의 값. 되묻기는 헤더 칸에서도 표의 칸에서도 나온다. */
function rowValue(
  rows: { items: Record<string, string>[] } | undefined,
  column: string,
): string | undefined {
  for (const item of rows?.items ?? []) {
    const value = item[column];
    if (value && String(value).trim()) return value;
  }
  return undefined;
}

/**
 * 사용자가 누른 선택지. 그 버튼의 글자가 그대로 이번 턴의 발화다.
 *
 * 표에 실리는 값(자재 품목)은 계획이 다시 내지 않는다. 발화가 유일한 답인데, 아무
 * 문장이나 답으로 읽으면 "취소해줘" 를 자재 이름으로 배운다. **우리가 내민 목록에
 * 있는 글자일 때만** 답으로 읽는다.
 */
function chosenOption(
  question: string,
  pending: { wanted: string; options?: string[] },
): string | undefined {
  const said = question.trim();
  if (!said || said === pending.wanted) return undefined;
  return (pending.options ?? []).find((option) => option.trim() === said);
}

/**
 * 이어받아도 되는 초안. **표(`continuable`)는 초안을 만든 자리에서만 단다.**
 *
 * 초안 자체는 무엇이 자기를 만들었는지 못 말한다 — `{route, fields, rows}` 뿐이라
 * 고치기(edit)에서 나온 것도, 걸음(sequence) 한 칸에서 나온 것도 똑같이 생겼다.
 */
type CarriedDraft = NonNullable<TaskSpec['draft']> & { continuable?: boolean };

/**
 * 되묻기에 **버튼으로** 답한 턴인가. 그렇다면 계획을 모델에게 다시 묻지 않는다.
 *
 * 답 턴에 모델이 계획을 다시 내면 이어받은 초안이 흔들린다 — 수량을 빠뜨리고
 * (`{pcode:"WIRE ROPE"}` 로 `qty:"2"` 유실), 화면을 바꾸고(지급등록→가지급등록),
 * 같은 질문을 되돌려 보냈다(실측 2026-09-06~07). 답한 값 하나만 꽂으면 되는 자리다.
 *
 * **평범한 prepare 초안에서만** 이 길로 온다(`continuable`). 고치기·달력·걸음에서 나온
 * 초안을 홑 prepare 로 이으면 더 크게 부순다 — 고칠 문서를 못 짚어 **새 문서가 하나 더
 * 생기고**(입금입력·고장완료), 달력은 날짜를 안 열어 쓸 자리가 없고, 걸음은 남은
 * 걸음(가족·자격증)을 통째로 잃는다.
 *
 * **정확히 일치할 때만** 이 길로 온다(`chosenOption`). 자유 발화나 새 지시는
 * 지금처럼 모델이 계획한다 — 사용자가 누른 그 값만 꽂는다.
 */
function continuationPlan(
  question: string,
  task: TaskSpec | undefined,
): Extract<QueryPlan, { kind: 'prepare' }> | undefined {
  const pending = task?.pending;
  const draft = task?.draft as CarriedDraft | undefined;
  if (!pending || !draft?.route) return undefined;
  if (!draft.continuable) return undefined;
  if (!chosenOption(question, pending)) return undefined;
  return {
    kind: 'prepare',
    route: draft.route,
    reason: '되묻기에 답한 턴이다. 이어받은 초안에 답한 값만 채운다.',
    fields: { ...draft.fields },
    ...(draft.rows ? { rows: draft.rows } : {}),
  };
}

/** 배운 이름으로 상세행의 값을 갈아 끼운다. */
/**
 * 두 이름이 **같은 것을 가리키는가.** 별칭으로 기억해도 되는지의 기준이다.
 *
 * 사람은 짧게 부르고 화면은 길게 적는다(`덕봉빌딩` → `덕봉빌딩(두암정철)`,
 * `WIRE ROPE` → `WIRE ROPE 일대일, 4본`). 한쪽이 다른 쪽을 담고 있으면 같은 것이다.
 * 담고 있지 않으면 그 턴에 다른 물건을 고른 것뿐이라 기억하지 않는다.
 */
function namesTheSame(said: string, chosen: string): boolean {
  const clean = (value: string) => value.replace(/[^0-9A-Za-z가-힣]/g, '').toLocaleLowerCase();
  const [one, other] = [clean(said), clean(chosen)];
  if (!one || !other) return false;
  if (one === other) return true;
  const [shorter, longer] = one.length <= other.length ? [one, other] : [other, one];
  return shorter.length >= 2 && longer.includes(shorter);
}

/**
 * 쓸 수 있는 이름 짝만 남긴다.
 *
 * 별칭은 **회사가 공유하는 저장소**에 쌓인다. 한 사람이 잘못 배운 짝
 * (`도어센서 → WIRE ROPE 8mm X 2R`)은 지울 길이 없는 채로 모두에게 적용된다 —
 * 그래서 쓰기 전에 한 번 더 거른다. 배울 때 거르는 것만으로는 이미 쌓인 것을 못 막는다.
 */
function usableAliases(aliases: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(aliases)
    .filter(([said, chosen]) => namesTheSame(said, chosen)));
}

function applyAliases(
  items: Record<string, string>[],
  aliases: Record<string, string>,
): Record<string, string>[] {
  if (!Object.keys(aliases).length) return items;
  return items.map((item) => Object.fromEntries(Object.entries(item).map(([key, value]) => (
    [key, aliases[value] ?? value]
  ))));
}

/** 직전 턴이 우리가 던진 선택지 되묻기였는가. 그 답은 새 행이 아니라 그 초안의 값이다. */
function answeredOurQuestion(context: Deps['context']): boolean {
  // elmanpro 의 tsconfig 는 `Array.prototype.at` 이 없는 lib 을 쓴다. 색인으로 센다.
  const turns = context?.history ?? [];
  const last = turns.length ? turns[turns.length - 1]?.answer ?? '' : '';
  // 우리가 던지는 되묻기는 두 가지다. 둘 다 "물었고 답을 받았다" 는 뜻이라,
  // 다음 턴은 새 문서가 아니라 방금 만든 초안을 이어 채워야 한다.
  return /만 골라 주세요\.$/.test(last.trim())
    // 스펙 §5.3 뒤로 되묻기는 단추다. 그 말은 `어느 것입니까?` 로 끝난다 — 이것을
    // 안 세면 답한 턴이 새 문서를 하나 더 만든다.
    || /어느 것입니까\?$/.test(last.trim())
    || /^다음 값을 알려 주세요:/.test(last.trim());
}

/**
 * 직전 턴이 실패했고 이번 발화가 그것을 다시 해달라는 말인가.
 *
 * `수금 상황은 어때?` 가 실패한 뒤 `확인해줘` 라고 하면 사람은 그 수금 질문을
 * 가리킨 것이다. 그런데 실패한 턴은 작업 상태를 남기지 않아서, 그 앞의 성공한
 * 질문(매출 성장세)이 다시 실행됐다. 가리킬 대상을 여기서 정해 준다.
 */
function retryOfFailedTurn(question: string, context: Deps['context']): string | null {
  const asked = question.trim();
  if (asked.length > 12) return null;
  if (!/(확인|다시|왜|안\s*되|해줘|해봐|되나)/.test(asked)) return null;
  const turns = context?.history ?? [];
  const last = turns.length ? turns[turns.length - 1] : undefined;
  if (!last || !looksLikeFailure(last.answer)) return null;
  return last.question;
}

/**
 * 직전 턴에서 화면이 신규를 거부했는가. 거부했다면 그 안내를 계획에 물려준다.
 *
 * `금호빌딩 도어센서 교체해야 해서 자재청구서 올려줘` 가 네 턴을 같은 문장으로 썼다 —
 * `견적없는 자재청구는 신규 할 수 없습니다! 견적서에서 자재청구서 버튼을 이용해주세요`.
 * 화면은 처음부터 어디로 가야 하는지 말했는데, 그 말이 다음 턴의 계획에 닿지 않아
 * 같은 화면을 세 번 더 열었다.
 */
export function refusalNote(context: Deps['context']): string {
  const last = [...(context?.history ?? [])].reverse()
    .find((turn) => /새로 작성을 시작할 수 없습니다/.test(turn.answer ?? ''));
  if (!last) return '';
  const guide = /화면 안내:\s*([^\n]+)/.exec(last.answer)?.[1]?.trim();
  const screen = /조회한 화면:\s*([^\n(]+)/.exec(last.answer)?.[1]?.trim();
  if (!guide) return '';
  return `

직전 턴에서 ${screen ? `${screen} 화면이` : '그 화면이'} 신규 작성을 거부했다: ${guide}`
    + ' 같은 화면에서 신규를 다시 시도하지 마라. 안내가 다른 화면이나 다른 절차를 가리키면'
    + ' 그쪽으로 계획하고, 무엇을 쓸지 정할 수 없으면 사용자에게 물어라.';
}

/**
 * 조회가 헛나온 뒤 계획에 덧붙이는 말. altrdp 의 반성 프롬프트와 같은 자리다.
 *
 * **어느 화면을 열어 봤는지 이름으로 적는다.** 예전에는 `a: 조회는 끝났지만 결과 행이
 * 없었다` 처럼 **라벨만** 적었다 — 모델은 자기가 방금 어느 화면을 열었는지 이 쪽지에서
 * 알 수 없었고, 세 판을 돌면서 같은 화면을 다시 골랐다(점검계획·당직계획, 실측 2026-09-14).
 * 「무엇을 이미 해 봤나」를 모르면 반성이 안 된다.
 */
export function retryNote(
  outcomes: RetrievalOutcome[],
  plan?: { retrievals: { label: string; route: string }[] },
  screens?: AgentScreen[],
): string {
  // **라벨을 같은 규칙으로 씻어서 맞춘다.** `classifyRetrieved` 는 `a__retry1` 에서
  // 꼬리를 떼고 `a` 로 돌려준다 — 계획 쪽 라벨 그대로 지도를 만들면 **재시도 판에서만**
  // 못 찾는다. 하필 쪽지가 필요한 판이 그 판이라, 첫 판에서만 되고 정작 반성해야 할
  // 때 경로가 빠졌다(실측 2026-09-14).
  const clean = (label: string) => label.replace(/__(?:retry|settled)\d+$/, '');
  const routeOf = new Map((plan?.retrievals ?? []).map((one) => [clean(one.label), one.route]));
  const nameOf = (route: string) => screens?.find((one) => one.route === route)?.name ?? '';
  const evidence = outcomes.map((outcome) => {
    const route = routeOf.get(outcome.label);
    const where = route ? `${route}${nameOf(route) ? `(${nameOf(route)})` : ''} ` : '';
    return `${outcome.label}: ${where}${outcome.message}`;
  }).join(' / ');
  // 전부 비었으면 조건만 만지는 것으로는 못 벗어난다. 이미 연 화면을 이름으로 짚어 준다.
  const allEmptyHere = outcomes.length > 0
    && outcomes.every((outcome) => outcome.kind === 'legitimate-empty');
  const tried = Array.from(new Set(outcomes
    .map((outcome) => routeOf.get(outcome.label))
    .filter((route): route is string => Boolean(route))
    .map((route) => `${route}${nameOf(route) ? `(${nameOf(route)})` : ''}`)));
  const already = allEmptyHere && tried.length
    ? ` 이미 ${tried.join(', ')} 을(를) 열어 조회했고 행이 없었다.`
      + ' 질문이 가리키는 업무에 더 맞는 다른 화면이 목록에 있으면 그것을 골라라.'
    : '';
  return `\n\n앞선 조회 진단: ${evidence}.${already} `
    + '원인을 교정한 새 계획을 세워라. 결과 행이 없으면 요청 기간·분류는 유지한 채 조건의 실제 선택값과 결과 표를 먼저 교정하라. '
    + '같은 화면이 데이터 계약에 맞으면 다시 써도 된다. 표나 필수 컬럼이 구조적으로 없을 때만 대체 화면을 골라라.';
}

/**
 * 「조회한 화면」에는 답에 쓴 달만 적는다(20차 검토: 「요새」를 석 달로 읽어 8월까지 조회했는데 계산과 글은 9·10월만 썼고,
 * 답이 근거로 삼은 자료와 밝힌 자료가 어긋났다). 모집단의 대상월에 없는 달을 조건으로 건 조회는 뺀다. 모집단이 없거나
 * 달 칸이 없으면 그대로 둔다.
 */
export function usedRetrievals<T extends { conditions: Record<string, string> }>(
  retrievals: T[],
  population: { columns: string[]; rows: unknown[][] } | undefined,
): T[] {
  const at = population ? population.columns.indexOf('대상월') : -1;
  if (!population || at < 0) return retrievals;
  const months = new Set(population.rows.map((row) => String(row[at] ?? '').trim()));
  const kept = retrievals.filter((retrieval) => !Object.values(retrieval.conditions)
    .some((value) => /^20\d{2}(?:0[1-9]|1[0-2])$/.test(String(value ?? '').trim()) && !months.has(String(value).trim())));
  return kept.length ? kept : retrievals;
}

/** 선택지 답변만으로도 원래 요청·파일 형식·필터를 잃지 않게 계산 질문을 복원한다. */
function effectiveQuestion(question: string, context: Deps['context']): string {
  const history = (context?.history ?? []).slice(-4);
  if (!history.length) return question;
  return [
    ...history.flatMap((turn) => [`사용자: ${turn.question}`,
      `도우미: ${duplicatesTalk(question).asks ? turn.answer ?? '' : withoutOwnLines(turn.answer ?? '')}`]),
    `사용자: ${question}`,
  ].join('\n');
}

/**
 * 공지를 만들어 달라는 말(「공지판에 붙이게」, 「공지 서류 만들어줘」). 공지를 묻는 말(「이번 주 공지 뭐 있어?」, 「최근 공지사항
 * 뽑아줘」, 「공지 작성 권한 누가 있어?」)과 「안내」 단독은 아니다(독립 검토). 과제 단계가 분량을 안 적은 판은 이 말을 문서 요청으로
 * 못 알아봐 계획·조회·계산을 다시 했고 문서 없이 끝났다(실측 2026-10-26 15:05).
 */
const NOTICE_REQUEST = /(?:공지(?!사항)|공고문|안내문|게시용)(?:판|용|문|서류|문서)?[^.?!]{0,20}?(?:만들어|작성해|써\s*줘|써\s*주|붙이게|붙일)/;

/**
 * 공지 요청인가. 공지 게시판 화면의 이름이 든 말(「공지사항 화면에 … 작성해줘」)은 그 화면의 일이다. 다른 화면 이름(「휴가현황 공지
 * 만들어줘」)은 막지 않는다(독립 재검토).
 */
function noticeRequested(question: string, screens: AgentScreen[]): boolean {
  const named = namedScreen(question, screens);
  return NOTICE_REQUEST.test(question) && !(named && /공지|안내문|게시/.test(named.name));
}

/** 조회·계산을 끝낸 직후 형식만 바꾸라는 요청인가. */
function isArtifactFollowup(question: string, artifactSkill?: AgentSkill): boolean {
  if (!requestedFormats(question).length && !artifactSkill) return false;
  const refersBack = /(그럼|그러면|그걸|그거|이걸|이거|이 내용|위 내용|방금|앞의|사장님께|아니|보고자료|이쁘게|예쁘게)/.test(question);
  const formatOnly = /^\s*(?:그럼\s*)?(?:pdf|docx?|word|워드)(?:\s*로)?(?:\s*도)?(?:\s*(?:줘|만들어\s*줘|받아\s*줘))?\s*$/i
    .test(question);
  const reportOnly = /^(?:사장님께\s*)?(?:보고서|문서)(?:로)?\s*(?:만들어\s*줘)?\s*(?:pdf|docx?|word|워드)(?:로)?\s*$/i
    .test(question.trim());
  return refersBack || formatOnly || reportOnly || (Boolean(artifactSkill) && /(?:현황\s*)?보고서/.test(question));
}

/**
 * 이 대화에서 요청한 파일 형식.
 *
 * 실패한 턴은 작업(TaskSpec)을 남기지 않는다 — 앱은 답이 끝난 턴만 저장한다. 그래서
 * `워드로 만들어줘` 로 시작해 몇 턴이 실패하면 형식이 사라지고, 뒤늦게 성공한 답에도
 * 파일 버튼이 붙지 않았다. 그 턴의 발화 하나가 아니라 **대화에 남은 요청**에서 찾는다.
 */
export function threadFormats(question: string, context: Deps['context']): FileFormat[] {
  const asked = requestedFormats(question);
  if (asked.length) return asked;
  const history = context?.history ?? [];
  // **같은 일이 이어지는 동안만** 거슬러 본다. 끝이 없으면 어제의 「엑셀로」가 오늘
  // 출장보고서를 엑셀로 만든다 — 잣대 5판이 3판부터 질문과 무관하게 같은 보고서를
  // 냈고(2026-09-03), 그건 잣대만의 문제가 아니라 실제 사용자가 겪는 결함이다.
  for (let index = history.length - 1; index >= 0; index -= 1) {
    const turn = history[index];
    if (!turn) break;
    // 멈추는 자리는 **다른 일**이다. 실패한 턴은 작업을 안 남기는데(앱은 답이 끝난
    // 턴만 저장한다) 그것까지 다른 일로 세면 옛 결함이 돌아온다 — 「워드로 만들어줘」
    // 뒤에 몇 턴이 실패하면 형식이 사라져 뒤늦은 성공 답에 파일 버튼이 안 붙었다.
    // 작업이 남은 턴이 지금 일과 다르면 거기서 끊는다. 지금 일이 없으면 같음을
    // 증명할 수 없으므로 그것도 끊는다 — 새 일의 첫 턴은 물려받지 않는다(§5.5).
    if (turn.task && !sameTaskCore(turn.task, context?.task)) break;
    const found = requestedFormats(turn.question ?? '');
    if (found.length) return found;
  }
  return [];
}

function sameTaskCore(left: TaskSpec | undefined, right: TaskSpec | undefined): boolean {
  if (!left || !right) return false;
  return left.capability === right.capability
    && JSON.stringify(left.filters) === JSON.stringify(right.filters)
    && left.entity === right.entity
    && left.metric === right.metric;
}

/** 날짜 하나(「2026-09-01」, 「20260901」, 「2026-09」, 「202609」). */
const DATE_TEXT = '(?:19|20)\\d{2}[-./]?(?:0[1-9]|1[0-2])(?:[-./]?(?:0[1-9]|[12]\\d|3[01]))?';
/**
 * 값 전체가 날짜나 날짜 범위인가. 과제 조건의 기간 열쇠 이름은 판마다 달라(기간, startDate·endDate, start_date·end_date) 값으로
 * 가린다. 날짜 꼴이 든 번호(「202610-0001」)는 기간이 아니다(독립 검토).
 */
const TASK_PERIOD = new RegExp(`^\\s*${DATE_TEXT}\\s*(?:[~∼]\\s*${DATE_TEXT}\\s*)?$`);

/** YYYYMM 다음 달. */
function nextMonth(key: number): number {
  return key % 100 === 12 ? (Math.floor(key / 100) + 1) * 100 + 1 : key + 1;
}

/**
 * 과제 조건의 기간이 덮는 달(YYYYMM)과, 그 기간이 달 단위로 맞는지. 날짜까지 적혔으면 시작이 1일이고 끝이 그 달 말일이거나
 * 오늘 이후여야 달 단위다. 같은 달 안에서 좁힌 기간(9/15~10/15)은 아니다(독립 검토).
 */
function periodMonths(filters: Record<string, string> | undefined, today: string): { months: string[]; whole: boolean } {
  const dates = Object.values(filters ?? {}).filter((value) => TASK_PERIOD.test(String(value ?? '')))
    .flatMap((value) => String(value).match(new RegExp(DATE_TEXT, 'g')) ?? [])
    .map((text) => text.replace(/[-./]/g, '')).sort();
  if (!dates.length) return { months: [], whole: false };
  const first = dates[0]!;
  const last = dates[dates.length - 1]!;
  const lastDay = (text: string) => new Date(Number(text.slice(0, 4)), Number(text.slice(4, 6)), 0).getDate();
  const whole = (first.length < 8 || first.slice(6) === '01')
    && (last.length < 8 || Number(last.slice(6)) === lastDay(last) || last >= today.replace(/-/g, ''));
  const months: string[] = [];
  for (let key = Number(first.slice(0, 6)); key <= Number(last.slice(0, 6)) && months.length < 36; key = nextMonth(key)) {
    months.push(String(key));
  }
  return { months, whole };
}

/** 앞 결과가 담은 달: 모집단(없으면 계산 표)의 달 칸. */
function coveredMonths(result: { population?: TableData; table?: TableData } | undefined): Set<string> {
  const table = result?.population ?? result?.table;
  const at = table ? monthColumn(table) : -1;
  return new Set(table && at >= 0 ? table.rows.map((row) => monthKey(String(row[at] ?? ''))).filter(Boolean) : []);
}

/** 이번 발화가 문서를 청한 말. 「문서」 하나로는 「결재 문서」에도 걸려 「문서로」만 받는다(재검토). */
const DOCUMENT_WORDS = /보고서|문서로|한\s*(?:장|페이지)|그래프|차트|pdf|발표\s*자료|슬라이드|ppt|워드|엑셀|파일로|공지(?!사항)|서류로|안내문/i;

/**
 * 문서를 청한 후속 턴이 앞 결과로 만들어도 되는 같은 일인가. 실측(2026-10-26 06:37): 1턴 과제 조건은 비었고({}) 2턴 과제는
 * 「기간 2026-09-01~2026-10-26」을 적어 같은 일로 보지 않았고, 앞 결과 대신 계획과 계산을 다시 했다(AI 14번, 보통 2번).
 * 능력, 대상, 지표, 갈래, 연산이 같고, 기간이 아닌 조건이 같으며, 새 과제의 기간이 달 단위로 앞 결과의 달과 같으면 같은 일이다.
 * 좁힌 기간(「9월 것만」)과 문서 말이 든 새 분석(「담당자별 미실시 건수 엑셀로」)은 새 일이다(재검토). 실측 여덟 판의 2턴은 지표,
 * 갈래, 연산이 1턴과 같았고 06:37 판만 기간 조건이 달랐다.
 */
function sameWork(
  next: TaskSpec | undefined, prior: TaskSpec | undefined, covered: Set<string>, today: string,
  applied: AppliedCondition[] = [], words: Set<string> = new Set(), plain = false, said = '', stated: unknown = undefined,
  /** 문서만 청한 말이고 내용 낱말이 모두 사용자 자신의 앞 질문에 있다(앞 답 글은 빼고 본다). */
  ownPlain = false,
): boolean {
  if (sameTaskCore(next, prior)) return true;
  // 문서만 청한 말이면 과제의 업무 이름이 흔들려도 같은 일이다. 「보고용으로도 ppt 줘」에 과제 단계가 업무를 중대고장 보고서로 바꿔
  // 다시 조회했다(실측 2026-10-28 08:30). 이때 내용 낱말은 사용자가 앞서 한 말에만 있어야 한다. 앞 답 글에 나온 말(「고장 3건」)로
  // 「고장 건 보고서로」가 옛 결과로 만들어질 수 있었다(독립 재검토).
  if (!next || !prior || (next.capability !== prior.capability && !ownPlain)) return false;
  // 문서만 청한 말이라도 과제가 앞 턴에 없던 대상·지표(「올해 입사자」, 「대체잔여」)를 적었으면 새 일이다. 대상 글만 바뀌고 조건이
  // 그대로라 앞 결과 전원으로 공지가 나갔다(독립 재검토). 문서·목적·단위 말을 뺀 낱말이 앞 턴 글(said)에 있어야 한다.
  const known = (text: string | undefined) => !text || saidAll(contentWords(text), said);
  if (plain && ((next.entity !== prior.entity && !known(next.entity)) || (next.metric !== prior.metric && !known(next.metric)))) {
    return false;
  }
  // 질문이 문서만 청했으면(plain) 과제의 대상·지표·갈래·처리가 흔들려도 아래 조건 검사로만 가린다. 과제 단계가 대상을 「잔여 연차
  // 소진 촉진 공지 및 보고서」로, 갈래를 빈 목록으로 적어 같은 일에서 떨어졌고, 계획이 「ERP 공지사항에 게시할까요」라고 되물었다
  // (실측 2026-10-26 17:38). 질문에 묶거나 좁히는 말이 있으면 예전처럼 과제가 같아야 한다(「담당자별 미실시 건수 엑셀로」).
  if (!plain) {
    if (next.entity !== prior.entity) return false;
    const listed = (items: string[] | undefined) => JSON.stringify([...(items ?? [])].sort());
    // 앞 처리는 그대로 두고 문서를 만드는 처리(「연말 연차 촉진 안내 공지 서류 작성」)만 더한 것은 같은 일이다.
    const added = (next.operations ?? []).filter((one) => (prior.operations ?? []).indexOf(one) < 0);
    const operations = added.every((one) => documentOnly(one, words))
      ? listed((next.operations ?? []).filter((one) => added.indexOf(one) < 0)) : listed(next.operations);
    if ((next.metric || '') !== (prior.metric || '') || listed(next.dimensions) !== listed(prior.dimensions)
      || operations !== listed(prior.operations)) return false;
  } else if ((next.operations ?? []).some((one) => (prior.operations ?? []).indexOf(one) < 0 && !documentOnly(one, words))) {
    // 문서만 청한 말이어도 과제가 좁히는 처리(「10일 이상만 추려」)를 더 적었으면 새 일이다.
    return false;
  }
  // 기간이 아닌 조건: 앞 조건은 모두 같은 값으로 남아야 하고, 더한 조건은 앞 조회에 이미 걸린 값이어야 한다. 1턴이 「년도 2026,
  // 재직자」로 조회했는데 2턴 과제가 그 둘을 조건으로 적어 다시 조회했다(실측 2026-10-26 18:25, 국가직 팀장 검토).
  const rest = (filters: Record<string, string> | undefined) => Object.entries(filters ?? {})
    .filter(([, value]) => !TASK_PERIOD.test(String(value ?? '')));
  const before = rest(prior.filters);
  const after = rest(next.filters);
  // 값의 꼴이 턴마다 달라도(「0일 초과」, 「0 초과」) 같은 조건이다. 조건 이름도 띄어쓰기와 끝말 「일수」만 다르면 같은 칸이다(「연차
  // 잔여」와 「연차잔여일수」, 실측 2026-10-27 07:45 재현). 「금액」, 「건수」는 떼지 않는다. 「잔여금액」과 「잔여건수」는 다른 조건이다.
  const form = (value: unknown) => String(value ?? '').replace(/\s+/g, '').replace(/(\d)(?:일|원|건|개|명)(?=초과|이상|이하|미만)/, '$1');
  const keyForm = (key: string) => key.replace(/\s+/g, '').replace(/일수$/, '');
  // 앞 조건과 꼴이 같은 새 조건은 모두 앞 값과 같아야 한다. 하나만 보면 「연차잔여일수 5 이상」을 더해도 같은 일이 됐다(독립 재검토).
  const matching = (key: string) => after.filter(([other]) => keyForm(other) === keyForm(key));
  if (before.some(([key, value]) => !matching(key).length || matching(key).some(([, one]) => form(one) !== form(value)))) return false;
  if (after.some(([key, value]) => !before.some(([other]) => keyForm(other) === keyForm(key))
    && !appliedValue(key, String(value ?? ''), applied) && !summaryValue(key, String(value ?? ''), stated, applied))) return false;
  const asked = periodMonths(next.filters, today);
  // 기간 조건이 양쪽에 다 없으면 기간은 같다. 한쪽에만 있으면 아래처럼 앞 결과가 담은 달과 견준다.
  if (!asked.months.length && !periodMonths(prior.filters, today).months.length) return true;
  // 앞 과제와 같은 달을 말했으면 같은 기간이다. 앞 결과에 달 칸이 없으면(한 달 조회) 담은 달을 셀 수 없어, 업무 이름만 흔들린 「보고용으로도
  // ppt 줘」가 다시 조회했다(실측 2026-10-28 08:30).
  const before_ = periodMonths(prior.filters, today);
  if (asked.whole && before_.whole && asked.months.length === before_.months.length
    && asked.months.every((month) => before_.months.includes(month))) return true;
  return asked.whole && asked.months.length === covered.size && asked.months.every((month) => covered.has(month));
}

/**
 * 문서를 만드는 말만 든 처리. 묶거나 세는 말(「부서별 합계」), 좁히는 말(「10일 이상만 추려」), 숫자, 앞 표의 값(부서 이름)이
 * 들면 새 일이다. 「가부서 공지 서류 작성」, 「2026년 입사자 대상 공지 작성」이 전원 공지로 나갔다(독립 재검토).
 */
function documentOnly(operation: string, words: Set<string>): boolean {
  return /공지|보고서|서류|문서|안내문|게시|발표\s*자료|ppt|pdf|엑셀|워드|한\s*(?:장|페이지)/i.test(operation)
    && !/별(?![가-힣])|건수|합계|비율|평균|비교|추이|순위|상위|하위|누계|집계|분류/.test(operation)
    && !/만(?![가-힣])|이상|이하|초과|미만|제외|빼고|추려|추린|골라|\d/.test(operation)
    && !Array.from(words).some((word) => operation.indexOf(word) >= 0);
}

/**
 * 문서만 청한 질문인가(「사내 공지판에 붙이게 한페이지로 공지 서류 만들어줘」). 묶거나 세거나 좁히는 말, 숫자, 앞 표의 값(부서 이름,
 * 사람 이름)이 없고, 남은 낱말이 앞 턴 글(said)에 다 있어야 한다. 「올해 입사자 공지 만들어줘」, 「퇴직자 대상 공지로」는 새 대상을
 * 말한 것이다(독립 재검토). 분량 말(1페이지, A4 1장)과 앞 조회의 해(2026년)는 숫자여도 문서 말이다.
 */
function documentOnlyQuestion(question: string, words: Set<string>, said: string, years: string[] = []): boolean {
  const plain = years.reduce((text, year) => text.split(`${year}년`).join(' ').split(year).join(' '),
    question.replace(/a4|\d+\s*(?:장|페이지|쪽|매)/gi, ' '));
  // 「많은」, 「적은」, 「높은」, 「낮은」도 좁히는 말이다(「잔여 많은 직원 공지」).
  // 「현장별로」, 「시각별로」도 묶는 말이다(독립 재검토: 「별」 뒤에 「로」가 붙으면 놓쳤다).
  return !/별(?![가-힣])|[가-힣]별로|건수|합계|비율|평균|비교|추이|순위|상위|하위|누계|집계|분류|만(?![가-힣])|이상|이하|초과|미만|제외|빼고|추려|골라|많은|많이|적은|높은|낮은|\d/
    .test(plain) && !Array.from(words).some((word) => question.indexOf(word) >= 0)
    && saidAll(contentWords(plain), said);
}

/**
 * 문서·목적·단위·말투 낱말(같은 일을 가를 때 보지 않는다). 두 글자 이상은 앞부분이 맞으면 그 낱말이다. 한 글자 머리(주, 해, 판)로
 * 맞추면 「주임」, 「해외사업부」, 「판매팀」이 지워져 대상이 바뀐 공지가 전원 공지로 나갔다(독립 재검토). 한 글자 말은 낱말 전체만 본다.
 */
// 발표자료 꼴과 말투(키노트처럼, 임팩트 있게, 깔끔하게)도 문서 말이다. 「발표용으로 키노트처럼 임팩트 있게 ppt」가 새 내용으로 읽혀
// 다시 조회했고 문서 없이 끝났다(실측 2026-10-28 08:30). 부서나 거래처 이름이 될 수 있는 말(마케팅, 애플)은 넣지 않는다.
const PLAIN_WORD = /^(?:공지|게시|보고|서류|문서|안내|자료|발표|슬라이드|ppt|pdf|엑셀|워드|한장|한페이지|페이지|만들|작성|써줘|써주|뽑아|정리|붙이|붙일|올리|올릴|사내|전직원|직원|전체|대상|이걸|그걸|이거|그거|내용|바탕|기반|사용|소진|촉진|일수|금액|건수|수량|그래프|차트|그림|넣어|같이|함께|해줘|해주|해줄|주세|주시|주실|줄래|부탁|하나|양식|보유|으로|에게|키노트|임팩트|이쁘|예쁘|깔끔|멋지|멋있|세련|화려|심플|간결|프레젠|보기|크게|강렬|느낌)/i;
// 「있게」(임팩트 있게)는 낱말 전체일 때만 문서 말이다. 「있는」(잔여 연차 있는 직원), 「시각」, 「스타일」, 「버전」은 대상이나 갈래가
// 될 수 있어 넣지 않는다(독립 재검토).
const PLAIN_WHOLE = /^(?:해|줘|주|좀|및|등|용|판|장|써|쓰|넣|뽑|있게)$/;

/**
 * 글의 낱말(조사와 말끝을 한 번 떼고, 두 글자 이상, 문서·목적·단위 말은 뺀다). 단위 말(일수, 금액, 건수)은 끝에서 뗀다. 「연차잔여일수」는
 * 「연차잔여」다.
 */
function contentWords(text: string): string[] {
  // 말끝을 떼기 전 낱말로도 본다. 띄어 쓴 「주세요」가 「주세」로 떼어져 문서 말로 보이지 않았다(독립 재검토: 「만들어 주세요」).
  const plain = (word: string) => PLAIN_WORD.test(word) || PLAIN_WHOLE.test(word);
  return text.split(/[^가-힣a-zA-Z0-9]+/).filter((raw) => !plain(raw))
    .map((word) => word.replace(/(?:으로|에서|에게|이라|짜리|들|로|를|을|이|가|은|는|도|만|에|의|과|와|게|건|요)$/, '')
      .replace(/(?:일수|금액|건수)$/, ''))
    .filter((word) => word.length >= 2 && !plain(word));
}

/** 띄어쓰기 없이 견준다. 「잔여연차」와 「잔여 연차」는 같은 말이다(독립 재검토). */
function saidAll(words: string[], said: string): boolean {
  const flat = said.replace(/\s+/g, '');
  return words.every((word) => flat.indexOf(word.replace(/\s+/g, '')) >= 0);
}

/**
 * 공지 대상 줄의 인원과 합계를 지운다. 확인 중인 계정이 섞여 숫자가 바뀔 수 있다(국가직 팀장 재검토). 괄호는 인원이나 합계가 든 것만,
 * 괄호 밖은 「N명」만, 「합계」 절은 통째로 지운다. 「5일 이상」, 「12월 31일」까지 지워 문장이 깨졌다(독립 재검토).
 */
function withoutCounts(line: string): string {
  if (!/^대상\s*[:：]/.test(line)) return line;
  // 「13명 중」, 「총 13명」 꼴도 인원이다(독립 재검토: 1턴 인원이 공지 2항에 그대로 실렸다). 「직원 13명 중」은 「직원 중」, 꾸밈말
  // 뒤의 「13명 중」은 「직원 중」이다. 지우고 나면 빈 줄이면 그대로 둔다(독립 재검토: 「…있는 중」, 「대상:입니다.」).
  const done = line.replace(/(?:이며|이고|으로|로)?,\s*[^,]*합계[^,]*$/, '입니다.')
    .replace(/\s*\([^()]*(?:\d[\d,]*\s*명|합계)[^()]*\)/g, '')
    .replace(/(직원|재직자|임직원|대상자|사원|인원)\s*\d[\d,]*\s*명(?=\s*중\s)/g, '$1')
    .replace(/\s\d[\d,]*\s*명(?=\s*중\s)/g, ' 직원')
    .replace(/\d[\d,]*\s*명의\s+/g, '')
    .replace(/\s*(?:총|모두|전체)?\s*\d[\d,]*\s*명(?=\s*(?:입니다|이며|이고|,|\.|$))/g, '');
  // 지우고 나서 빈 줄이면 수 없는 문장이다(독립 재검토: 인원이 든 원래 줄로 돌아갔다).
  return /^대상\s*[:：]\s*(?:입니다)?\.?$/.test(done) ? '대상: 해당 직원입니다.' : done;
}

/**
 * 공지의 신청 방법 항목(「신청 방법: …」, 「연차휴가 신청 방법: …」, 「신청: …」)과, 사용자가 그것을 청한 말(「신청하는 방법도」). 연차
 * 공지에는 신청 경로를 넣지 않는다(사용자 확인, 2026-10-27).
 */
const APPLY_LINE = /^(?:(?:연차\s*휴가|연차|휴가)\s*)?신청(?:\s*(?:방법|절차|경로))?\s*[:：]/;
const APPLY_ASKED = /신청\S*\s*(?:방법|절차|경로)|어떻게\s*신청/;
/** 공지의 사용 기한 항목(「사용 기한: …」, 「연차휴가 사용 기한: …」, 「잔여 연차 사용 기한: …」). */
const DUE_LINE = /^(?:잔여\s*)?(?:연차\s*휴가|연차|휴가)?\s*(?:사용|소진)\s*(?:권장\s*)?기한\s*[:：]\s*/;

/**
 * 생성 기준이 입사일인 연차 공지의 사용 기한을 권장 기한으로 바꾼다. 사용 기간이 직원마다 달라 연말보다 먼저 끝나는 직원이 있다.
 * 말끝이 없는 날짜(「'26.12.31.까지」)에는 요청 말을 붙이고, 본인 사용 기간을 말한 글이 없으면 그 줄을 붙인다(독립 검토).
 */
function recommendedDue(line: string): string {
  const found = DUE_LINE.exec(line);
  if (!found) return line;
  const rest = line.slice(found[0].length).trim();
  // 날짜 없는 빈 기한 줄은 뺀다(독립 재검토).
  if (!rest) return '';
  const body = /까지\.?$/.test(rest) ? rest.replace(/까지\.?$/, '까지 사용하여 주시기 바랍니다.') : rest.replace(/([^.])$/, '$1.');
  const own = /입사일|본인\S*\s*사용\s*기간|개인별|직원마다|사람마다/.test(body) ? ''
    : ' 연차는 입사일 기준으로 발생하므로 본인 사용 기간이 이보다 먼저 끝나는 경우에는 그 전에 사용하여 주시기 바랍니다.';
  return `사용 권장 기한: ${body}${own}`;
}

/** 회사가 권하는 기한을 말하는 줄(목적, 연말, 올해 말일)과, 직원 본인의 사용 기한을 말하는 줄. */
const COMPANY_DUE = /^목적\s*[:：]|연말|'\d{2}\.12\.31\./;
const OWN_DUE = /본인|개인|입사일|직원마다|사람마다|지난|지나|넘/;

/**
 * 연차 공지의 줄을 사용자 결정대로 고친다(2026-10-27). 신청 방법 줄은 사용자가 청했을 때만 남긴다. 생성 기준이 입사일이면 사용 기한
 * 항목을 권장 기한으로 바꾸고, 회사가 권하는 기한을 말한 줄의 「사용 기한」도 맞춘다(국가직 팀장 검토: 목적의 「사용 기한」과 항목의
 * 「사용 권장 기한」이 엇갈렸다). 직원 본인의 사용 기한을 말한 줄은 그대로다(독립 재검토: 「사용 권장 기한이 지난 연차는 사용할 수
 * 없습니다」로 뜻이 뒤집혔다). 임원이 대상에 들면 대상 줄의 「직원」을 「임직원」으로 쓴다. 합성어(정규직원)와 이미 임원을 말한 줄은
 * 그대로다(독립 재검토).
 */
function noticeLeaveLines(lines: string[], options: {
  asked: string; hired: boolean; executives: boolean; yearEnd?: string;
  /** 이번 질문. 숫자나 기간 말이 하나라도 있으면 사용자가 기한을 정한 것으로 본다. */
  question?: string;
}): string[] {
  const kept = lines.filter((line) => !APPLY_LINE.test(line) || APPLY_ASKED.test(options.asked));
  // 사용자가 연말 촉진을 말했고 기한을 정하지 않았으면 사용 기한은 올해 말일이다(실측 08:40: 「곧 연말인데」에 모델이 '26.11.30.을
  // 골랐다). 이번 질문에 숫자나 기간 말(보름, 2주, 다음 달, 내년, 명절)이 하나라도 있으면, 앞 질문에 기한 말과 함께 나온 날짜나 기간
  // 말이 있으면 사용자가 정한 날이다(독립 재검토: 「11월 말일까지」, 「11/30까지」, 「보름 안에」를 덮었다). 기한 줄의 「까지」 앞 날짜
  // 하나만 바꾸고(날짜가 둘인 범위 줄은 그대로), 같은 옛 날짜가 든 목적 줄도 맞춘다.
  const intent = /연말|연내|올해\s*안|촉진|소진/.test(options.asked);
  const now = /\d|보름|열흘|며칠|주\s*(?:안|내|뒤|후)|주일|달|개월|분기|반기|내년|여름|하계|겨울|동계|추석|설\s*(?:연휴|전)|명절|크리스마스|성탄/
    .test(options.question ?? '');
  const own = now || /(?:\d{1,2}\s*월(?:\s*\d{1,2}\s*일)?|\d{1,2}\s*일|\d{1,2}\s*[./]\s*\d{1,2}\.?|'\d{2}\.\d{1,2}\.\d{1,2}\.)\s*(?:말일|말|초|중순|하순)?\s*(?:까지|안에|안으로|이내|내로|중에|전에|이전|기한)/
    .test(options.asked) || /이번\s*달|다음\s*달|이달|금월|월말|분기|상반기|하반기|이번\s*주|다음\s*주|주\s*(?:안|내|뒤)|달\s*(?:안|내)|개월|내년|여름|하계|추석|설\s*(?:연휴|전)|크리스마스|성탄/
    .test(options.asked);
  const dueLine = kept.find((line) => DUE_LINE.test(line));
  const dates = dueLine?.match(/'\d{2}\.\d{1,2}\.\d{1,2}\./g) ?? [];
  const old = options.yearEnd && intent && !own && dates.length === 1 && dueLine?.includes(`${dates[0]}까지`) ? dates[0] : undefined;
  return kept
    .map((line) => (old && options.yearEnd && (DUE_LINE.test(line) || /^목적\s*[:：]/.test(line)) ? line.split(old).join(options.yearEnd) : line))
    .map((line) => (options.hired ? recommendedDue(line) : line)).filter(Boolean)
    // 목적 줄의 「지나기 전에」, 「넘기지 않도록」은 회사 기한 이야기다(독립 재검토: 1항과 4항이 다시 엇갈렸다).
    .map((line) => (options.hired && !DUE_LINE.test(line) && COMPANY_DUE.test(line)
      && !(/^목적\s*[:：]/.test(line) ? /본인|개인|입사일|직원마다|사람마다/ : OWN_DUE).test(line)
      ? line.replace(/사용\s*기한/g, '사용 권장 기한') : line))
    .map((line) => (options.executives && /^대상\s*[:：]/.test(line) && !/임원/.test(line)
      ? line.replace(/(?<![가-힣])직원/g, '임직원') : line));
}

/**
 * 코드가 쓴 제61조 조치(서면 촉구, 사용 시기 서면 지정 통보)와 같은 일을 적은 모델 조치. 사용 시기 지정의 통보·요청과 촉진 통보는
 * 그 자체로 같은 일이고, 그 밖에는 직원 개인에게 알리거나 촉구하는 줄이다(「공지 미확인 직원 개별 서면 촉구」, 「직원별 잔여 일수
 * 통지」). 계획 제출을 챙기는 줄(「미제출자 제출 촉구」), 현황 점검, 검토 의뢰, 결과 통보, 부서장·노무사에게 하는 요청은 다른 일이다
 * (독립 검토: 낱말 하나로 지웠다). 끝 괄호는 담당 자리표시가 든 겹괄호(「(…, (담당 부서))」)까지 뗀다(독립 재검토).
 */
function lawDuplicate(line: string): boolean {
  // 끝 괄호와 「및 회신 접수」 같은 정해 둔 꼬리만 떼고 본다. 「및」 뒤를 통째로 떼어 「직원 및 임원 서면 촉구」를 놓쳤다(독립 재검토).
  const head = line.replace(/\s*\((?:[^()]|\([^()]*\))*\)\s*$/, '').replace(/\s+및\s+(?:회신|결과|답신)\s*(?:접수|확인|취합|정리)$/, '').trim();
  // 줄 끝이 서면 촉구(촉구서 발송)이고 직원 쪽 말이 있으면 받는 사람 말이 있어도 같은 일이다(「부서장 경유 직원별 서면 촉구」).
  // 부서장에게 하는 촉구와 촉구 준비(양식, 명단)는 다른 일이다(독립 재검토).
  const urged = /서면\s*촉구(?:\s*(?:실시|시행))?$|촉구서\s*(?:발송|송부|전달)?$/.test(head);
  const recipient = /부서장|노무|자문|협조|방침|전\s*(?:임)?직원/.test(head);
  // 받는 사람이 직원 개인이 아니면(부서장, 노무사, 전 직원 안내) 다른 일이다. 이것을 먼저 본다(독립 재검토: 판정 차례 때문에 지웠다).
  if (recipient && !(urged && /직원|미사용|개별|개인/.test(head))) return false;
  if (/사용\s*시기[^,]*지정\s*(?:통보|통지|요청(?:서\s*발송)?)$/.test(head)) return true;
  if (/제출|계획|현황|점검|취합|보고|검토|의뢰|결과/.test(head)) return false;
  if (urged || /촉진\s*(?:통보|통지)$/.test(head)) return true;
  return /(?:직원별|개인별|개별|미사용자|미사용\s*직원|직원\s*대상|직원에게)[^,]*(?:통지|통보|안내|알림|발송|촉구|요청)$/.test(head);
}

/** 조치 줄의 기한(「'26.10.31.까지」, 범위면 끝 날). 없으면 undefined. */
function actionDate(line: string): number | undefined {
  const found = Array.from(line.matchAll(/'(\d{2})\.(\d{1,2})\.(\d{1,2})\./g));
  const last = found[found.length - 1];
  return last ? 20000000 + Number(last[1]) * 10000 + Number(last[2]) * 100 + Number(last[3]) : undefined;
}

/**
 * 날짜가 붙은 조치(dated)를 날짜 차례 자리에 넣는다. 먼저 날짜가 붙은 줄끼리만 날짜 차례로 맞추고(날짜 없는 줄은 제자리), 넣을 줄은
 * 그보다 늦은 기한의 첫 줄 앞에 둔다. 넣을 줄이 없으면 그대로다. 모델이 쓴 차례에 기대어 끼우면 날짜가 뒤섞였고, 함께 정렬하면 날짜
 * 없는 줄이 맨 뒤로 갔다(독립 재검토).
 */
function byDate(lines: string[], dated: string[]): string[] {
  if (!dated.length) return lines;
  const queue = lines.map((line, at) => ({ line, at, when: actionDate(line) })).filter((one) => one.when !== undefined)
    .sort((left, right) => (left.when === right.when ? left.at - right.at : (left.when ?? 0) - (right.when ?? 0)));
  let next = 0;
  const ordered = lines.map((line) => (actionDate(line) === undefined ? line : queue[next++]?.line ?? line));
  return dated.reduce((done, line) => {
    const when = actionDate(line) ?? Infinity;
    const at = done.findIndex((one) => (actionDate(one) ?? -Infinity) > when);
    return at < 0 ? [...done, line] : [...done.slice(0, at), line, ...done.slice(at)];
  }, ordered);
}

/**
 * 담당이 빠진 조치 기한(「('26.11.6.까지)」, 「('26.10.30.~'26.11.6.까지)」)에 담당을 붙인다(국가직 팀장 검토: 둘째 조치의 담당이 비었다).
 * 괄호 안이 날짜뿐일 때만이다. 「(부서장 '26.11.6.까지)」에 또 붙였다(독립 재검토).
 */
function ownedAction(line: string, owner: string): string {
  return line.replace(/\(((?:~\s*)?'\d{2}\.\d{1,2}\.\d{1,2}\.(?:\s*~\s*'\d{2}\.\d{1,2}\.\d{1,2}\.)?까지)\)(\.?)$/, `($1, ${owner})$2`);
}

/**
 * 엑셀로 받는 명단에 대상 여부 칸을 더한다. 뺀 계정을 숨기지 않고 밝힌다(국가직 팀장 검토: 엑셀이 74행이라 65명과 엇갈렸다). 합계 줄은
 * 비운다(독립 재검토).
 */
function markExcluded(table: TableData, names: string[], nameColumn: string): TableData | undefined {
  const at = table.columns.indexOf(nameColumn);
  if (at < 0) return undefined;
  const plain = (cell: unknown) => String(cell ?? '').replace(/\s+/g, ' ').trim();
  return {
    ...table, columns: [...table.columns, '대상 여부'],
    rows: table.rows.map((row) => [...row,
      // 이름이 비고 다른 칸(사원코드)이 「합계」인 줄도 합계 줄이다(독립 검토: 「대상」으로 찍혔다).
      isTotalRow(row[at]) || (!plain(row[at]) && row.some(isTotalRow)) ? ''
        : names.indexOf(plain(row[at])) >= 0 ? '제외(중복 등록)' : '대상']),
  };
}

/** 표 차례나 묶음을 말한 질문(「적은순」, 「사번순」, 「부서별로」, 「하위 10명」). 1턴 표를 다시 늘어놓지 않는다(독립 검토). */
const ORDER_ASKED = /순(?:으로|서|위)?(?![가-힣])|정렬|차례|별로|별(?![가-힣])|하위|적[은게]|낮[은게]|덜|오름|내림/;

/**
 * 연차 촉진처럼 남은 양을 물은 1턴 명단은 답이 보는 표 앞부분을 주 수치(잔여) 많은 순으로 놓고 그 기준을 밝히게 한다. 보인 대표
 * 직원이 표 앞 행이라 고른 기준이 없었다(국가직 팀장 검토). 계산이 이미 그 순으로 냈으면 차례는 그대로이고 기준만 알린다. 사용자가
 * 차례나 묶음을 말했으면 손대지 않는다. 엑셀 표와 결과 기록은 계산 그대로다.
 */
function rankedView(value: { summary: unknown; table?: TableData }, asked: string, question: string): {
  view: { summary: unknown; table: TableData }; facts: string[];
} | undefined {
  const { table } = value;
  if (!table || !asksRemaining(asked) || ORDER_ASKED.test(question) || !table.columns.some((column) => /연차|휴가/.test(column))
    || statusColumn(table)) return undefined;
  const list = listReport(table, { asked, said: JSON.stringify(value.summary ?? null) });
  if (!list?.leave || !list.remaining || list.ranked.length < 2) return undefined;
  const ranked = new Set(list.ranked);
  return {
    view: { summary: value.summary, table: { ...table, rows: [...list.ranked, ...table.rows.filter((row) => !ranked.has(row))] } },
    facts: [`표 앞부분은 ${subjectParticle(list.mainLabel)} 많은 순이다. 대표 직원을 적으면 그 기준(${list.mainLabel} 많은 순)을 밝혀라.`],
  };
}

/** 제목·목록·표 줄(「## …」, 「**…**」, 「- …」, 「1. …」, 「| … |」). 코드가 쓴 줄을 그 사이에 넣지 않는다. */
const NOT_LEAD = /^\s*(?:#|[-*•]\s|\d+[.)]\s|\||\*\*[^*]+\*\*\s*$)/;
/** 센 수가 든 줄(「65명」, 「587.5일」). 「2026년 기준입니다」 같은 기간 줄보다 먼저 고른다. */
const COUNTED = /\d[\d,.]*\s*(?:명|일|건|개|곳|원|%)/;

/**
 * 답의 첫 수치 문장 뒤에 코드가 쓴 줄을 문단으로 넣는다. 제목·목록·표 줄은 건너뛰고, 숫자가 든 문장이 이어지면 그 끝에 넣는다. 수치
 * 문장이 없으면 제목이 아닌 첫 덩어리(목록, 표째) 뒤다. 표와 번호 목록을 가르지 않는다(독립 재검토). 모델이 이미 같은 말(두 번 등록)을
 * 썼으면 넣지 않는다.
 */
function afterLead(text: string, line: string): string {
  if (!text.trim()) return line;
  if (text.includes('두 번 등록')) return text;
  const lines = text.split('\n');
  const prose = (one = '') => !!one.trim() && !NOT_LEAD.test(one);
  let end = [COUNTED, /\d/].map((pattern) => lines.findIndex((one) => prose(one) && pattern.test(one))).find((at) => at >= 0) ?? -1;
  if (end >= 0) {
    while (end + 1 < lines.length && prose(lines[end + 1]) && /\d/.test(lines[end + 1] ?? '')) end += 1;
  } else {
    end = lines.findIndex((one) => one.trim() && !/^\s*(?:#|\*\*[^*]+\*\*\s*$)/.test(one));
    if (end < 0) return `${text}\n\n${line}`;
    while (end + 1 < lines.length && (lines[end + 1] ?? '').trim()) end += 1;
  }
  const rest = lines.slice(end + 1);
  return [...lines.slice(0, end + 1), '', line, ...(rest[0]?.trim() ? [''] : []), ...rest].join('\n');
}

/**
 * 우리가 답에 넣은 뺀 계정 문장(`duplicateAccounts`)과 넣은 계정 문장(`includedNote`). 다음 턴의 과제, 계산 질문, 문서 근거에는 싣지
 * 않는다(독립 검토). 과제가 그것을 조건으로 적으면 2턴 공지가 같은 일로 보이지 않았다.
 */
const OWN_LINE = /두 번 등록한 것이라 대상에서 뺐습니다|계정\([^)]*\)도 넣어서 셌습니다/;

function withoutOwnLines(answer: string): string {
  return answer.split('\n').filter((line) => !OWN_LINE.test(line)).join('\n');
}

/** 숫자로 쓴 수나 한글 수(「두 개」, 「9개」). 뺀 계정을 가리키는 말의 수 자리다. */
const COUNT = '(?:[\\d한두세네다섯여섯일곱여덟아홉열]+\\s*(?:개|명)?\\s*)?';
/**
 * 이름에 숫자가 붙은 계정(중복 등록)을 두고 한 말. 꾸미는 꼴(「숫자 붙은」, 「번호 달린」)만 본다. 「전화번호 있는」, 「번호 붙여서」는
 * 아니다(독립 재검토).
 */
const ABOUT_DUPLICATES = new RegExp('숫자\\s*(?:가|이)?\\s*(?:붙은|붙어\\s*있는|들어간|들어\\s*있는|있는|달린)|번호\\s*(?:가|이)?\\s*(?:붙은|달린)'
  + '|\\d\\s*(?:가|이)?\\s*붙은|중복|두\\s*번\\s*등록|이중\\s*등록|관리용[^.?!]{0,10}계정');
/**
 * 우리가 뺀 계정을 가리키는 말: 「뺀 (두) 계정」, 「그 계정들」, 「그·앞에서·아까 뺀 계정·사람·직원」. 「임원 제외한 직원」, 「아까 뺀
 * 임원」, 「그 제외한 부서」는 아니다(독립 재검토).
 */
const OUR_DROPPED = new RegExp(`(?:뺀|제외(?:한|된|했던)|빠진)\\s*${COUNT}계정|그\\s*${COUNT}계정`
  + `|(?:그|앞에서|아까)\\s*(?:뺀|제외한|제외된|빠진)\\s*${COUNT}(?:계정|사람|직원)`);
/**
 * 빼라는 말: 대상에 붙은 빼기 말(「계정 뺀」, 「계정 지우고」, 「사람은 제외 후」, 「중복 빼고」)이나 「중복 없게」, 「빼고」, 「제거」 같은
 * 말. 「뺀 계정」(가리키는 말)은 아니다. 어순과 활용이 많아 대상에 붙은 꼴로 본다(독립 재검토).
 */
const DROP_WORDS = new RegExp('(?:계정|사람|직원|중복|이름|것)들?\\s*(?:은|는|을|를|도)?\\s*(?:빼|뺀|제외|제거|지우|지워|삭제|거르|거른|걸러|정리)'
  + '|(?:계정|사람|직원|이름)들?\\s*(?:이|가)?\\s*(?:빠진|없는)|중복\\s*(?:없|안\\s*되)|세지\\s*(?:말|마)'
  + '|빼고|빼서|빼\\s*(?:줘|주)|빼면|빼야|빼놓|제외하고|제외해|제외시|제외\\s*후|제거|지우고|삭제하고|거르|거른|걸러|말고|없이');
/** 빼지 말라는 말(「빼지 말고」, 「제외하지 마」). 빼기 말이 들어 있어도 넣으라는 뜻이다(독립 재검토). */
const NOT_DROPPED = /(?:빼|제외하|지우|삭제하)지\s*(?:말|마)/;
/**
 * 넣어 달라는 말(「포함해서」, 「다시 넣어줘」, 「이랑 같이」, 「빼지 말고」, 「계정까지 보여줘」). 계정을 두고 한 말에서만 본다.
 * 「연말까지」의 「까지」는 아니다. 가장 흔한 넣기 말투를 놓쳐 뺀 결과로 공지가 나갔다(독립 재검토).
 */
const KEEP_WORDS = /포함|넣|같이|반영|그대로\s*두|(?:빼|제외하)지\s*(?:말|마)|(?:계정|사람|직원|이름)들?\s*(?:도|까지)\s*(?:(?:다|모두|전부)\s*)?보여/;

/**
 * 뺀 계정을 두고 한 말인가. asks: 그 계정을 묻거나 넣어 달라는 말이라 계산 전에 빼지 않고, 앞 답의 뺀 계정 문장을 과제에도 준다.
 * keep: 넣어 달라는 말이라 다음 턴 문서도 빼지 않는다. 빼라는 말(「중복 빼고」, 「그 계정들 빼고」)과 다른 사람을 뺀다는 말(「임원
 * 제외한 직원」)은 아니다(독립 재검토: 흔한 말투가 빼기를 껐다). 지금 질문만 본다. 과제 목표에 남은 말로 뒤 턴까지 빼기를 끄지 않는다.
 * 말로 가리는 일이라 다 잡지는 못한다. 놓치면 그 턴만 규칙대로 빼고 답에 뺀 계정 이름을 밝힌다.
 */
function duplicatesTalk(question: string): { asks: boolean; keep: boolean; action: 'none' | 'ask' | 'include' | 'exclude' } {
  const about = OUR_DROPPED.test(question) || ABOUT_DUPLICATES.test(question);
  const dropping = DROP_WORDS.test(question) && !NOT_DROPPED.test(question);
  const action = !about ? 'none' : dropping ? 'exclude' : KEEP_WORDS.test(question) ? 'include' : 'ask';
  return { asks: action === 'ask' || action === 'include', keep: action === 'include', action };
}

/**
 * 근거 구절에 그 계정을 가리키는 단서가 있는가. 숫자만으로는 아니다(「잔여 10일 이상인 직원도 포함해서」, 독립 검토). 가리키는 말(「그
 * 사람들」, 「아까 빠진 두 명」)과 계정 이름은 직전 답에서 계정을 뺐을 때만 단서다(`DROPPED_CUE`, `droppedNames`).
 */
const DUPLICATE_CUE = /계정|숫자|중복|두\s*번|이중|관리용|번호\s*(?:가|이)?\s*(?:붙은|달린)|\d\s*(?:가|이)?\s*붙/;
const DROPPED_CUE = new RegExp(`뺀|제외(?:한|된|했던)|빠진|빠졌|그\\s*${COUNT}(?:사람|직원|명|분)|그들|그\\s*(?:둘|셋|넷)`);

/**
 * 직전 답(되묻기 말고)에서 우리가 뺀 계정 이름. `duplicateAccounts`가 쓴 「…대상에서 뺐습니다. 뺀 계정은 …입니다.」에서만 읽는다.
 * 모델이 쓴 「뺀 계정은 없습니다」에서 이름을 뽑았다(독립 재검토).
 */
function droppedNames(history: { answer?: string; asked?: boolean }[] | undefined): string[] {
  const last = [...(history ?? [])].reverse().find((turn) => !turn.asked && turn.answer?.trim());
  const named = (last?.answer ?? '').match(/대상에서 뺐습니다\.(?: [^.]*대상이 없습니다\.)? 뺀 계정은 [^.]+?입니다\./g) ?? [];
  return Array.from(new Set(named.flatMap((one) => one.replace(/^.*뺀 계정은 /, '').replace(/(?: 외 [\d,]+개 계정)?입니다\.$/, '').split(', '))
    .map((one) => one.trim()).filter(Boolean)));
}

/**
 * 이번 턴의 판정. keep은 넣기 상태를 다음 턴까지 이을 만큼 분명한가, doubted는 두 신호가 어긋났을 때 내밀 반대 선택(넣기 또는 빼기)이다.
 */
type DuplicateDecision = { action: 'none' | 'ask' | 'include' | 'exclude'; keep?: boolean; doubted?: 'include' | 'exclude' };

/**
 * 정규식(`duplicatesTalk`)과 과제 단계(`TaskSpec.duplicates`)를 맞대 이번 턴에 이름에 숫자가 붙은 계정을 어떻게 다룰지 정한다(조사
 * 보고서, 2026-10-27). 과제 단계의 구절은 발화에 그대로 있어야 받는다(띄어쓰기와 문장부호는 보지 않는다). 넣기와 묻기는 구절에 그
 * 계정을 가리키는 단서도 있어야 한다(근거 없이 「넣기」를 고르는 슬롯 환각을 막는다). 빼라는 쪽은 기본 규칙과 같은 방향이라 단서를
 * 따지지 않는다(「다시 빼고」). 구절이 빈 빼기는 받지 않는다. 계정 얘기가 없는 턴에서 넣기 상태를 끄지 않게(독립 검토).
 * 검사를 지난 과제 단계가 주 판정이다. 구현을 안 본 쪽이 쓴 78문장에서 과제 단계가 92.3%와 93.6%(두 판), 정규식이 83.3%였다.
 * 정규식이 다르면 반대 선택을 단추로 내민다(묻기와 넣기는 같은 쪽으로 본다). 과제 단계가 스스로 판단을 못 했으면(unclear) 기본대로
 * 뺀다. 구절 검사를 못 지났으면 정규식을 따른다. 넣기 상태는 두 신호가 모두 넣기일 때만 다음 턴까지 잇는다. 어긋난 넣기가 다음 턴에
 * 넣었다는 말도 없이 남았다(독립 검토). 과제 단계 값이 없으면 정규식대로다.
 */
function duplicateDecision(question: string, task: TaskSpec | undefined, dropped: string[] = []): DuplicateDecision {
  const { regex, llm, raw } = duplicateSignals(question, task, dropped);
  const shown = (action?: string) => action === 'include' || action === 'ask';
  if (!llm || llm === regex) return { action: regex, ...(regex === 'include' ? { keep: true } : {}) };
  if (llm === 'unclear' && raw === 'unclear') {
    return { action: regex === 'exclude' ? 'exclude' : 'none', ...(shown(regex) ? { doubted: 'include' } : {}) };
  }
  if (llm === 'unclear') {
    // 과제 단계가 낸 값도 같은 쪽이면(구절만 다듬어 옮김) 두 신호가 맞은 것으로 본다. 갈리면 반대 선택을 내밀고 넣기를 잇지 않는다.
    const agrees = shown(regex) === shown(raw) && (regex !== 'include' || raw === 'include');
    return {
      action: regex, ...(regex === 'include' && agrees ? { keep: true } : {}),
      ...(agrees ? {} : { doubted: shown(regex) ? 'exclude' : 'include' }),
    };
  }
  if (shown(llm)) return { action: llm, ...(shown(regex) ? {} : { doubted: 'exclude' }) };
  return { action: llm === 'exclude' || regex === 'exclude' ? 'exclude' : 'none', ...(shown(regex) ? { doubted: 'include' } : {}) };
}

/**
 * 두 신호: 정규식 판정, 검사를 거친 과제 단계 판정(구절이 없거나 어긋나면 unclear), 과제 단계가 낸 그대로의 값. dropped는 직전 답에서
 * 뺀 계정 이름이다. 있으면 가리키는 말과 그 이름도 단서로 받는다.
 */
function duplicateSignals(question: string, task: TaskSpec | undefined, dropped: string[] = []): {
  regex: 'none' | 'ask' | 'include' | 'exclude'; llm?: DuplicateAction; raw?: DuplicateAction;
} {
  const regex = duplicatesTalk(question).action;
  const said = task?.duplicates;
  if (!said) return { regex };
  const flat = (text: string) => text.replace(/[\s.,!?·、「」『』"'“”‘’()]+/g, '');
  const quoted = !!flat(said.quote) && flat(question).includes(flat(said.quote));
  const cue = DUPLICATE_CUE.test(said.quote)
    || (dropped.length > 0 && (DROPPED_CUE.test(said.quote) || dropped.some((name) => flat(said.quote).includes(flat(name)))));
  const llm = said.action === 'none' || said.action === 'unclear' ? said.action
    : said.action === 'exclude' ? (quoted ? 'exclude' : 'unclear') : (quoted && cue ? said.action : 'unclear');
  return { regex, llm, raw: said.action };
}

/** 신호가 어긋났을 때 답 아래에 내미는 반대 선택. 누르면 이 글이 다음 질문이 되고, 정규식과 과제 단계가 같이 알아듣는다. */
const INCLUDE_AGAIN = '숫자 붙은 계정까지 넣어서 다시 보여줘';
const EXCLUDE_AGAIN = '숫자 붙은 계정 빼고 다시 보여줘';

/** 남에게 나갈 문서를 만드는 턴인가(공지, 보고서, 발표자료, 워드, PDF). 엑셀 명단은 아니다. */
function documentTurn(question: string, screens: AgentScreen[]): boolean {
  return noticeRequested(question, screens) || /보고서|문서|서류|공지|발표|ppt|pdf|워드|한\s*(?:장|페이지)/i.test(question)
    || requestedFormats(question).some((format) => format !== 'xlsx');
}

/**
 * 되묻기 단추에 넣는 문서 종류. 단추 글만으로 다음 턴이 문서 요청으로 읽혀야 한다. 「넣어서 만들어줘」에는 채팅 답만 나왔다(독립 검토).
 * 앞 문서가 공지면 공지를 잇는다. 공지 뒤 「pdf로도」의 단추가 「pdf 보고서」라 보고서가 됐다(독립 재검토).
 */
function documentKind(question: string, screens: AgentScreen[], genre?: string): string {
  if (genre === 'notice' || noticeRequested(question, screens) || /공지(?!사항)|안내문/.test(question)) return '공지 서류';
  const format = requestedFormats(question).find((one) => one !== 'xlsx');
  if (format === 'pptx' || /발표|슬라이드/.test(question)) return 'ppt 보고서';
  if (format === 'pdf') return 'pdf 보고서';
  if (format === 'docx') return '워드 보고서';
  return '보고서';
}

/** 판정이 어긋난 채 남에게 나갈 문서를 만들려 할 때 먼저 묻는다. 두 단추는 다음 턴에 정규식과 과제 단계가 같이 알아듣는 말이다. */
function askDuplicates(count: number | undefined, task: TaskSpec | undefined, kind: string): AskUser {
  return asking(`이름에 숫자가 붙은 계정${count ? ` ${count}개를` : '을'} 넣을까요, 뺄까요?`,
    [`숫자 붙은 계정 넣어서 ${kind} 만들어줘`, `숫자 붙은 계정 빼고 ${kind} 만들어줘`], task);
}

/**
 * 계산에 남긴 이름에 숫자가 붙은 계정을 밝히는 문장. 넣으라고 했거나 넣기 상태를 이은 턴에 쓴다. 조용히 넣는 일이 없게 한다(독립 검토).
 * 연차 조회가 둘이면 문장마다 조회 이름을 밝힌다.
 */
function includedNote(included: { label: string; names: string[] }[]): string {
  return included.map((one) => {
    const named = `${one.names.slice(0, 10).join(', ')}${one.names.length > 10 ? ` 외 ${one.names.length - 10}개 계정` : ''}`;
    return `${included.length > 1 ? `${one.label} 자료: ` : ''}이름에 숫자가 붙은 ${one.names.length}개 계정(${named})도 넣어서 셌습니다.`;
  }).join(' ');
}

/**
 * 연차 공지 작성이 실패했을 때의 기본 항목. 숫자 없이 목적, 대상, 올해 말일 사용 기한만 쓴다. 작성이 실패한 공지가 「1. 문의처」
 * 한 줄이거나 목록 표의 인원·합계 줄이었다(독립 재검토). 권장 기한, 임직원은 공지 줄 규칙이 바꾼다.
 */
function leaveNoticeFallback(yearEnd: string): Brief {
  return {
    title: '연차 사용 안내', summary: ['목적: 남은 연차의 사용 기한을 안내드립니다.', '대상: 연차가 남아 있는 직원입니다.'], checks: [],
    actions: [`사용 기한: ${yearEnd}까지 사용하여 주시기 바랍니다.`],
  };
}

/** 앞 결과 표의 글자 값(부서 이름, 사람 이름). 처리 글에 이것이 들면 그 값으로 좁힌 새 일이다. */
function tableWords(table: TableData | undefined): Set<string> {
  const found = new Set<string>();
  (table?.rows ?? []).slice(0, 5000).forEach((row) => row.forEach((cell) => {
    const value = typeof cell === 'string' ? cell.trim() : '';
    if (value.length >= 2 && !/^[\d.,\s-]+$/.test(value)) found.add(value);
  }));
  return found;
}

/** 앞 결과에 걸린 조회 조건 하나: 칸(searchQuery), 그 화면 칸 이름(년도), 값(2026). */
type AppliedCondition = { key: string; label: string; value: string };

/**
 * 앞 결과의 조회 조건(「/w_pb209 (searchQuery=2026, rtclafi=재직자)」)을 화면 칸 이름과 함께 읽는다. 사람 말 줄(「연차생성 (년도 2026,
 * 재직구분 재직자)」)도 읽는다. 콤보 코드 값(rtclafi=001)은 화면 글자(재직자)로 읽는다. 과제는 글자로 적는다(독립 재검토).
 */
function appliedConditions(sources: string[] | undefined, screens: AgentScreen[]): AppliedCondition[] {
  return (sources ?? []).flatMap((source) => {
    const found = /^(.*?)\s*\(([^()]*)\)\s*$/.exec(source);
    if (!found) return [];
    const screen = screens.find((one) => one.route === (found[1] ?? '').trim());
    return (found[2] ?? '').split(/,\s*/).flatMap((part) => {
      const at = part.indexOf('=');
      const key = (at > 0 ? part.slice(0, at) : part.replace(/\s+\S+$/, '')).trim();
      const raw = (at > 0 ? part.slice(at + 1) : /\s(\S+)$/.exec(part)?.[1] ?? '').trim();
      const field = screen?.fields.find((one) => one.key === key);
      const value = field?.optionCodes?.find((one) => one.value === raw)?.label ?? raw;
      return key && value ? [{ key, label: plainLabel(field?.label ?? key), value }] : [];
    });
  });
}

/** 해를 말하는 칸 이름(년도, 연도, 조회년도). 「입사년도」는 다른 칸이다. */
const YEAR_FIELD = /^(?:조회|기준)?(?:년도|연도)$/;

/**
 * 과제가 더한 조건이 앞 조회에 이미 걸린 것인가. 값과 칸이 함께 맞아야 한다. 값만 보면 「입사년도 2026」이 조회 년도 2026과 같아
 * 올해 입사자 공지가 전원 공지로 나갔다(독립 재검토). 한두 자리 코드 값(「1」)은 우연히 같을 수 있어 받지 않는다.
 */
function appliedValue(name: string, value: string, applied: AppliedCondition[]): boolean {
  const plain = (text: string) => text.replace(/\s+/g, '');
  const wanted = plain(name);
  const sameField = (one: AppliedCondition) => wanted === plain(one.label) || wanted === plain(one.key)
    || (YEAR_FIELD.test(wanted) && YEAR_FIELD.test(plain(one.label)));
  // 「2026년」과 조회 값 「2026」은 같은 해다(실측 17:38 과제가 「연도: 2026년」으로 적었다).
  const year = (text: string) => text.trim().replace(/^((?:19|20)\d{2})\s*년$/, '$1');
  const given = year(value);
  return !!given && !/^\d{1,2}$/.test(given) && applied.some((one) => year(one.value) === given && sameField(one));
}

/** 계산 요약에서 조건이 아니라 결과 값을 담는 칸(최다잔여부서, 부서수, 합계, 출처). 더한 조건의 근거로 보지 않는다. */
const RESULT_KEY = /최다|최소|최대|상위|하위|평균|합계|총|수$|건수|인원|비율|명단|출처/;

/**
 * 과제가 더한 조건이 앞 결과의 계산 요약이 이미 밝힌 값인가(요약 「생성기준: 입사일」과 조건 「연차생성기준: 입사일」). 화면 기본값으로
 * 조회해 조회 조건에는 없지만 앞 결과가 그 값이었다. 실측 07:45: 2턴 과제가 1턴 답의 「입사일 기준」을 조건으로 적어 다시 계획했다
 * (AI 없이 재현). 조건 이름은 요약 칸 이름과 같거나 그것으로 끝나야 하고(두 글자 이상), 값은 꼴까지 같아야 한다. 결과 칸, 한두 자리 값,
 * 해 값(조회 년도 칸이 아니면)은 받지 않는다. 양쪽 포함으로 보다가 「부서 가부서」를 요약의 「최다잔여부서 가부서」로, 「입사년도
 * 2026」을 「년도 2026」으로 받아 좁힌 공지가 전원 공지로 나갔다(독립 재검토).
 */
function summaryValue(name: string, value: string, stated: unknown, applied: AppliedCondition[] = []): boolean {
  if (!stated || typeof stated !== 'object' || Array.isArray(stated)) return false;
  const plain = (text: string) => text.replace(/\s+/g, '');
  const wanted = plain(name);
  const given = plain(value);
  if (!given || /^\d{1,2}$/.test(given) || (/^(?:19|20)\d{2}(?:년)?$/.test(given) && !YEAR_FIELD.test(wanted))) return false;
  // 요약 칸은 조건을 말하는 칸(「…기준」, 「…조건」, 「…구분」)이거나 앞 조회의 조건 칸일 때만 근거다. 결과를 그냥 「부서」로 적은 요약에
  // 기대면 좁힌 공지가 전원 공지로 나갈 수 있었다(독립 재검토).
  const condition = (have: string) => /(?:기준|조건|구분)$/.test(have)
    || applied.some((one) => have === plain(one.label) || have === plain(one.key));
  return Object.entries(stated as Record<string, unknown>).some(([key, one]) => {
    const have = plain(key);
    return have.length >= 2 && !RESULT_KEY.test(have) && condition(have) && (wanted === have || wanted.endsWith(have))
      && (typeof one === 'string' || typeof one === 'number') && plain(String(one)) === given;
  });
}

/** 조회 조건의 연도 값이 하나뿐이면 그 해(「searchQuery=2026」). 기간 조건(2026-09-01~…)은 연도 값이 아니다. */
function conditionYear(applied: AppliedCondition[]): string | undefined {
  const years = Array.from(new Set(applied.map((one) => one.value))).filter((value) => /^(?:19|20)\d{2}$/.test(value));
  return years.length === 1 ? years[0] : undefined;
}

/** 「지난달 것까지」, 「지난달도」: 이번 달과 지난달을 말한 말. 「지난달까지 누계」의 「까지」는 끝 경계라 받지 않는다(독립 검토). */
// 「지지난달 것도」, 「전전월도」 안에서는 맞지 않게 앞 글자를 막는다(재검토: 사용자가 청한 8월을 뺐다).
const LAST_MONTH_TOO = /(?<![지전])(?:지난\s*달|전월)\s*(?:(?:것|거|꺼)\s*(?:까지|도)|도)/;
/** 다른 기간 말. 있으면 조회 달을 줄이지 않는다(「작년 동월 대비」의 비교 달을 지우면 안 된다). */
const OTHER_PERIOD = /작년|전년|지난\s*해|올해|금년|동월|같은\s*달|분기|상반기|하반기|반년|석\s*달|세\s*달|누계|누적|연간|연초|부터|\d+\s*(?:개월|달|년)|\d{1,2}\s*월/;
/** 달 칸 이름(planmon, yearmon …). */
const MONTH_FIELD = /mon$|month|ym$/i;

/** 이번 달과 지난달(YYYYMM 수). */
function currentAndLast(today: string): { current: number; last: number } {
  const [year = 0, month = 0] = today.split('-').map(Number);
  const current = year * 100 + month;
  return { current, last: month === 1 ? (year - 1) * 100 + 12 : current - 1 };
}

/**
 * 질문과 과제가 말한 달(YYYYMM). 「지난달 것까지」는 이번 달과 지난달, 과제 기간은 그 달들이다. 보고서 모집단은 이 달의 조회를
 * 버리지 않는다(실측 2026-10-26 10:00: 계산 표가 10월만 담은 집계라 모집단이 9월 조회를 버렸다).
 */
export function askedMonths(question: string, filters: Record<string, string> | undefined, today: string): string[] {
  const { current, last } = currentAndLast(today);
  const said = LAST_MONTH_TOO.test(question) ? [String(last), String(current)] : [];
  return Array.from(new Set([...said, ...periodMonths(filters, today).months]));
}

/**
 * 질문이 「지난달 것까지」처럼 이번 달과 지난달만 말했으면 그보다 이른 달을 조건으로 건 조회는 뺀다. 실측(2026-10-26 06:37·07:17·07:33):
 * 계획이 「요새」를 석 달로 읽어 8월까지 조회했고, 06:37 판은 그 때문에 2턴이 계산을 다시 했다. 좁게만 줄인다(독립 검토).
 * - 이번 질문과 최근 앞 질문에 다른 기간 말이 없고, 과제 기간이 지난달보다 이르지 않을 때.
 * - 이름이 달 칸인 조건 하나만 다른 같은 화면·같은 조건 묶음이고, 이번 달로 끝나는 연속 달일 때. 범위 조회는 손대지 않는다.
 * 다 빠지면 그대로 둔다.
 */
export function withinAskedMonths<T extends { route?: string; conditions: Record<string, string> }>(
  retrievals: T[],
  question: string,
  today: string,
  context: { history?: string[]; taskMonths?: string[] } = {},
): T[] {
  if (!LAST_MONTH_TOO.test(question)) return retrievals;
  if ([question.replace(LAST_MONTH_TOO, ' '), ...(context.history ?? [])].some((text) => OTHER_PERIOD.test(text))) return retrievals;
  const { current, last } = currentAndLast(today);
  if ((context.taskMonths ?? []).some((key) => Number(key) < last)) return retrievals;
  const monthOf = (one: T) => {
    const keys = Object.keys(one.conditions).filter((key) => MONTH_FIELD.test(key) && monthKey(String(one.conditions[key] ?? '')));
    return keys.length === 1 ? { key: keys[0]!, month: Number(monthKey(String(one.conditions[keys[0]!] ?? ''))) } : undefined;
  };
  const groupOf = (one: T, key: string) => `${one.route ?? ''}\u0001${key}\u0001${JSON.stringify(Object.entries(one.conditions)
    .filter(([name]) => name !== key).sort(([left], [right]) => left.localeCompare(right)))}`;
  const groups = new Map<string, number[]>();
  retrievals.forEach((one) => {
    const found = monthOf(one);
    if (found) groups.set(groupOf(one, found.key), [...(groups.get(groupOf(one, found.key)) ?? []), found.month]);
  });
  const trimmable = new Set(Array.from(groups.entries()).filter(([, list]) => {
    const sorted = [...list].sort((left, right) => left - right);
    return sorted.length >= 2 && sorted[sorted.length - 1] === current
      && sorted.every((value, at) => at === 0 || nextMonth(sorted[at - 1]!) === value);
  }).map(([key]) => key));
  const kept = retrievals.filter((one) => {
    const found = monthOf(one);
    return !found || !trimmable.has(groupOf(one, found.key)) || found.month >= last;
  });
  return kept.length ? kept : retrievals;
}

/** 공지를 말한 말. 「공지사항」(게시판 화면 이름)과 「입찰공고」는 공지 문서가 아니다(독립 검토). 「공고문」은 공지다. */
const NOTICE_GENRE = /공지(?!사항)|공고문|게시(?:판|용)|안내문/;
/** 보고를 말한 말. 공지 뒤의 「ppt로 보고서도」는 보고서로 다시 쓴다. */
const REPORT_GENRE = /보고서|보고\s*자료|보고용/;

/**
 * 문서 종류. 공지 말이면 공지, 보고 말이면 보고, 둘 다 없으면 앞 문서를 잇는다.
 * 공지 뒤의 「워드로도 줘」가 공지를 버리고 실명 상위 명단이 붙은 보고서로 다시 쓰였다(독립 검토).
 */
function documentGenre(question: string, inherited?: 'notice'): 'notice' | undefined {
  if (NOTICE_GENRE.test(question)) return 'notice';
  return REPORT_GENRE.test(question) ? undefined : inherited;
}

/** 그래프를 빼라는 말(「그래프 없이」, 「그래프는 빼고」, 「차트는 넣지 말고」, 「그래프 없는 버전」, 「그래프 넣지 마」, 「안 넣어도 돼」). */
const NO_CHARTS = /(?:그래프|차트|그림)\s*(?:은|는|도|을|를)?\s*(?:없이|없는|빼|제외|말고|넣지\s*(?:마|말)|안\s*넣|필요\s*없)/;

function chartsRefused(question: string): boolean {
  return NO_CHARTS.test(question);
}

/**
 * 문서의 새 주제(「잔여 연차 소진 촉진건 으로」, 「연차 촉진 계획으로」). 형식만 바꾸는 말과 달리 앞 문서를 다시 써야 한다(실측
 * 2026-10-26: 앞 공지를 그대로 발표자료로 냈다). 「몇 건으로」, 「전체 건으로」처럼 수나 범위를 말한 것은 아니다. 「제목은 그대로」를
 * 새 주제로 읽어 제목을 바꿀 뻔했다(독립 검토).
 */
function newSubject(question: string): boolean {
  const named = /([가-힣]{2,})\s?건\s*(?:으로|로)/.exec(question)?.[1];
  if (named && !/^(?:전체|모든|해당|이번|나머지)$/.test(named)) return true;
  return /[가-힣]{2,}\s?(?:계획|방안|대책)(?:\s*\(안\))?\s*(?:으로|로)\s/.test(question);
}

/**
 * 과제 단계가 문서 분량(layout)을 정했으면 그래프를 고르고 결론 제목·요약을 쓰게 한다.
 * 정하지 않았으면 아무것도 하지 않는다 — 지금처럼 표 전부, 작성 호출 없음.
 * asked는 앞 턴까지 사용자가 한 말(질문·과제 목표)이다. 목록 표의 주 수치를 고를 때 쓴다. genre는 앞 문서의 종류(공지면 이어받는다),
 * priorLayout은 앞 턴 과제의 분량이다.
 */
async function reportExtra(
  question: string,
  task: TaskSpec | undefined,
  source: {
    summary?: unknown; table?: TableData; answer: string; axes?: ResultAxis[]; population?: TableData; asked?: string;
    genre?: 'notice'; priorLayout?: TaskSpec['layout'];
    /** 결과의 조회 조건(화면 칸 이름과 글자 값). 점검이 아닌 문서 제목에 붙일 자료의 해와 연차 생성 기준을 여기서 찾는다. */
    applied?: AppliedCondition[];
    /** 앞 턴이 공지였으면 그 제목. 보고서 조치의 첫 줄(공지 게시)에 쓴다. */
    noticeTitle?: string;
    /** 앞 턴 공지의 요청 항목(「사용 계획 제출: '26.10.30.까지 …」). 직원 기한을 근거로 준다. */
    noticeActions?: string[];
    /** 로그인한 사람의 부서. 공지의 발신·문의처와 보고서 조치의 담당이다. 비면 자리표시(「(담당 부서)」)다. */
    unit?: string;
    /** 앞 턴까지 사용자가 한 질문만(과제 목표는 뺀다). 공지의 신청 방법을 청했는지 본다. */
    questions?: string;
    /** 계산 전에 뺀 계정의 줄(결과 기록의 `excluded`). 보고서 요약 끝에 싣는다. */
    excluded?: string[];
    /** 계획이 비워 둔 선택 칸의 화면 값(결과 기록의 `screenValues`). 연차 생성 기준을 여기서도 찾는다. */
    screenValues?: string[];
    /** 1턴이 사용자 말대로 이름에 숫자가 붙은 계정을 남겼다(결과 기록의 `keepNumbered`). 목록 표에서도 빼지 않는다. */
    keepNumbered?: boolean;
    /** 조회한 표의 자료 읽기(결과 기록의 `reading`). 열린 문제면 그 이야기로 쓴다. */
    reading?: DataReading;
    /** 결과의 출처 글(「자체점검중대결함현황 (기간 202610)」). 읽기가 없을 때 모델 읽기에 화면 이름으로 준다. */
    sources?: string[];
    /** 화면 목록. 모델 읽기에 화면 설명을 준다. */
    screens?: AgentScreen[];
    /** 분야 전문가. 분석 틀이 있는 화면은 틀의 읽기를 쓰고 모델에게 묻지 않는다. */
    experts?: Expert[];
  },
  chat: Chat,
): Promise<ReportExtra | undefined> {
  const slides = requestedFormats(question).indexOf('pptx') >= 0 || task?.artifact?.format === 'pptx';
  const genre = documentGenre(question, source.genre);
  // 공지는 과제 단계가 분량을 안 적어도 한 장(표는 코드가 센 것만)으로 만든다(실측 15:05).
  const asked = task?.layout ?? (slides ? { detail: 'top' as const, charts: true }
    : genre ? { detail: 'top' as const, charts: false } : undefined);
  // 발표자료는 그래프가 본문이다. 앞 턴(공지)에서 이어진 「그래프 없음」은 뒤집는다. 표지와 요약 두 장으로 나갔다(실측
  // 2026-10-26). 그래프를 빼라고 했거나 과제 단계가 이번 턴에 그래프 없음으로 바꿨으면 따른다(독립 검토).
  const inherited = !!task?.layout && JSON.stringify(task.layout) === JSON.stringify(source.priorLayout ?? null);
  const layout = asked && slides && !asked.charts && !chartsRefused(question) && inherited ? { ...asked, charts: true } : asked;
  if (!layout) return undefined;
  const today = new Date(`${localToday()}T00:00:00`);
  // 두 달 이상을 조회한 표 — 경과 구분과 그림은 진행 중인 기준월로, 미실시는 호기 묶음으로 본다. 두 달을 합쳐
  // 「11~54일 경과 103건」(실은 65호기)으로 쓰고 동역을 60으로 그려 반려됐다(국가직 팀장 10차 검토).
  // 코드로 세는 부분(〈표 1〉·경과·묶음·그림·판정)은 모집단으로 센다. 계산 표가 행을 덜 담았으면 조회한 행 전부다
  // (10:11 판: 비고가 있거나 미실시인 1,828행을 합계로 실었다).
  // 자료 읽기를 붙인다. 열린 문제(결함, 고장)로 읽힌 표는 상태 칸 짐작이 꺼져 경과·묶음·〈표 1〉·〈표 2〉·메모·서명 줄이 나오지
  // 않고, 긴급도·처리 상태·쏠림·반복을 코드가 센다(`issues.ts`). 중대결함 104건이 「예정일 경과 102건 점검 완료」로 나갔다(2026-10-27).
  // 결과 기록에 읽기가 없으면(분석 틀이 없는 화면) 지금 짐작이 상태 칸을 찾은 표만 모델에게 묻는다. 문서를 만드는 턴에 한 번이다.
  const plainTable = source.population ?? source.table;
  const reading = source.reading ?? await readingForDocument({
    table: plainTable, screen: source.sources?.[0]?.replace(/\s*\(.*$/, ''), screens: source.screens ?? [], question, chat,
    sources: source.sources, experts: source.experts,
  });
  const table = withReading(plainTable, reading);
  const issues = table && isIssues(table) ? issueReport(table, today) : undefined;
  const carry = table ? carryOver(table, today) : undefined;
  const current = table && carry ? refTable(table, today) : table;
  // 채팅 답 끝의 「이 값은 화면 자료와 맞지 않을 수 있습니다」는 문서 근거가 아니다. 요약이 제 숫자를 의심했다(2026-10-23).
  // 공지에는 사람 이름을 싣지 않는다. 1턴 답과 계산 요약의 이름을 ○○로 지운 글만 공지 작성에 주고, 작성이 실패했을 때 요약
  // 자리에 오르는 글도 그것이다. 공지 지시문에 1턴 답의 실명이 실렸고, 작성이 실패하면 그 답이 공지 본문이 됐다(독립 검토).
  const people = genre && table ? personNames(table) : [];
  const unnamed = (text: string) => (people.length ? withoutNames(text, people) : text);
  // 1턴 답에 코드가 넣은 뺀 계정 문장도 근거가 아니다. 문서는 같은 줄을 코드가 싣는다.
  // 열린 문제 문서는 직전 답에서 할 일 이야기(예정일 경과, 미실시)가 든 줄도 거른다. 2턴 답의 「예정일이 지난 건 102건」이
  // 근거로 넘어갔다(독립 검토).
  const answer = unnamed(withoutOwnLines(source.answer).split('\n')
    .filter((line) => !/맞지 않을 수 있습니다|확인하지 못했습니다|계산에서 빠졌습니다/.test(line))
    // 「발견 후 21일 경과」 같은 줄은 남긴다(경과만으로는 거르지 않는다, 독립 검토).
    .filter((line) => !issues || !/예정일|미실시|(?<!재)점검\s*완료/.test(line)).join('\n'));
  const summary: unknown = people.length ? JSON.parse(unnamed(JSON.stringify(source.summary ?? null))) : source.summary;
  // 상태 칸(완료·미실시)이 없는 목록(연차 명단)은 부서별 현황·상위 명단·그림을 코드가 센다. 〈표 1〉·경과는 상태 칸이 있어야
  // 세므로 그런 표에는 아무 표도 안 실렸다(실측 2026-10-26: 74명 명단이 공지와 발표자료에 한 줄도 없었다).
  const userWords = [source.asked ?? '', question, task?.goal ?? ''].join('\n');
  const list = table && !issues && !statusColumn(table) ? listReport(table, {
    asked: userWords, said: `${answer}\n${JSON.stringify(summary ?? null)}`,
  }, { notice: genre === 'notice', keepNumbered: source.keepNumbered }) : undefined;
  // 완료 점검 메모의 이상 기재(12차 검토: 긴급수리·통화 불능 메모를 「판단 불가」로만 올려 반려). 판정 항목이 아니라 메모다.
  // 메모 줄이 들어가면 그림을 한 단계 줄인다(한 쪽이 넘치면 그림 범주부터, 메모 줄은 남긴다).
  const memoFound = table ? memoFindings(table) : [];
  // 〈표 2〉 묶음이 넷 이상이면 더 붐비는 판이다. 메모 줄을 짧게 쓴다(실측 2026-10-26 08:49: 원자료가 바뀌어 묶음이 넷이 되자
  // 1,174px로 두 장이 됐고, 그림은 끝 동점 때문에 더 줄지 않았다).
  const memo = memoLines(memoFound, today, table ? monthScope(table, today)?.ref : undefined, (carry?.groups.length ?? 0) >= 4);
  // 목록 그림은 주 수치(연차잔여일수)로 그린다. 계산 요약 막대는 인원과 일수를 한 막대에 섞었고, 표 그림은 첫 수 칸(연차생성)을 골랐다.
  // 공지는 목록 표의 부서 그림만 그린다. 표 그림은 범주가 사람 이름일 수 있다(사용 내역의 「사원명별 사용일수」, 독립 재검토).
  const drawn = !layout.charts ? [] : issues ? issues.charts : list ? (list.chart ? [list.chart] : []) : genre ? []
    : currentCharts(reportCharts(source.summary, current, today, !!carry, !!memo), !!carry);
  // 표는 코드가 센다 — 합계 요약표·예외 행·비율. 모델은 그것을 근거로 개조식 문장만 쓴다.
  const due = current ? dueSplit(current, today) : undefined;
  const counts = (table ? statusTable(table) : undefined)
    ?? drawn.map((chart) => statusFromChart(chart, due?.attention, due?.total)).find(Boolean);
  // 〈표 1〉 달 줄에 시점 — 「'26.9월(마감)」·「'26.10월(25일 현재)」(10차 검토).
  const status = counts && carry ? { ...counts, table: monthStates(counts.table, today, carry) } : counts;
  // 쏠림 줄은 〈표 2〉 위에 코드가 쓴다 — 그림 결론 자리에 붙어 그림에 제 결론이 없었다(5차 검토).
  const focus = overdueFocus(due, carry);
  // 서명·미전송 확인 줄은 모집단으로 코드가 쓴다(16차 검토). 쪽이 더 붐비면 〈표 2〉 현장 줄을 뺀다.
  const closing = closingLines(table, today, table ? monthScope(table, today)?.ref : undefined);
  const listed = table ? exceptionRows(table, today, !!memo, !!memo && closing.checks.length > 0) : undefined;
  const exceptions = listed && focus.length ? { ...listed, points: focus } : listed;
  // 〈표 1〉 아래 경과 외 줄 — 요약표의 주의 건수와 같을 때만(다른 표에서 센 것을 붙이지 않는다). 두 달 판은
  // 마감 수치와 진행 중 수치를 견줄 수 없다는 줄(10차 검토).
  const note = carry && due ? monthNote(carry, due, today) : dueNote(due);
  const watched = status?.table.rows.length === 1 ? Number(status.table.rows[0]?.[status.table.columns.length - 2]) : NaN;
  const noted = !!note && !!due && (!!carry || watched === due.total);
  // 원자료의 없는 날짜(20260931)는 ※ 줄로 — ERP 자료를 고칠 일이다(10차 검토).
  const broken = table ? dateErrorNote(table) : undefined;
  // 〈표〉와 값이 같은 그림은 뺀다 — 같은 숫자를 두 번 보였다(3차 검토). 〈표 1〉 아래 줄의 수도 〈표 1〉에 있는 셈이다.
  // 겹치는 그림을 뺀 뒤 둘로 자른다 — 먼저 자르면 현장별 경과·예정 그림이 잘려 나갔다(실제 테넌트, 2026-10-23).
  // 두 달 문서에서 그림만 기준월 자료라 제목에 그 달을 쓴다(12차 검토).
  const charts = withoutTableTwins(drawn, exceptions, status?.table,
    noted && due ? [due.overdue, due.today, due.upcoming, due.today + due.upcoming] : []).slice(0, 2)
    .map((chart) => (carry ? { ...chart, title: `${carry.refName} ${chart.title}` } : chart));
  // 점검(〈표 1〉·경과·묶음·메모가 있는 판)은 지금 글 규칙 그대로, 그 밖의 업무는 일반 보고 규칙, 공지는 공지 규칙으로 쓴다.
  // 점검 규칙의 요약 셋째 줄 「판단 불가」가 연차 문서를 「…판단 불가」로 끝냈다(실측 2026-10-26).
  const kind: BriefKind = genre ? 'notice' : issues ? 'issues' : status || exceptions || carry || due || memo ? 'status' : 'general';
  // 근거에도 서명·미전송을 완료 건 중에서 센 수를 준다(16차 검토). 점검이 아닌 문서는 목록 표의 수와 기한 후보를 준다.
  // 공지는 근거에서도 이름을 지우고, 사람별로 갈 수 있는 갈래 값은 주지 않는다.
  // 앞 턴에 공지를 만들었으면 보고서 조치의 첫 단계가 그 게시다(국가직 팀장 재검토: 조치에서 공지가 빠졌다).
  // 앞 턴 공지의 직원 기한(항목 이름과 날짜). 부서장 취합 기한이 직원 제출 기한과 같은 날이었다(국가직 팀장 재검토, 실측 18:06).
  const noticeDue = kind === 'general' ? (source.noticeActions ?? []).flatMap((line) => {
    const named = /^([^:：]{1,12})\s*[:：]/.exec(line)?.[1]?.trim();
    const date = /'\d{2}\.\d{1,2}\.\d{1,2}\./.exec(line)?.[0];
    return named && date ? [`${named} ${date}`] : [];
  }) : [];
  const facts = [...reportFacts(status, exceptions, due, today, carry), ...(memo?.facts ?? []), ...closing.facts,
    ...(list?.facts ?? []), ...(issues?.facts ?? []), ...(kind === 'status' ? [] : [deadlineFacts(today, { notice: kind === 'notice' })]),
    ...(kind === 'general' && source.genre === 'notice' ? ['앞 턴에 직원 공지 문서를 만들었음'] : []),
    ...(noticeDue.length ? [`앞 턴 공지의 직원 기한: ${noticeDue.join(', ')}`] : [])].map(unnamed);
  // 목록 보고서의 확인 필요 사항은 코드가 쓴다: 숫자가 붙은 이름(연차가 아닌 목록), 연차면 생성 기준(국가직 팀장 재검토: 자료에 있는
  // 사실은 사용자 답을 기다리지 않고 싣는다). 공지에는 싣지 않는다.
  const conditions = (source.applied ?? []).map((one) => `${one.label} ${one.value}`).join(', ');
  const dataYear = conditionYear(source.applied ?? []);
  const leaveContext = `${JSON.stringify(source.summary ?? null)} ${conditions} ${(source.screenValues ?? []).join(', ')}`;
  // 보고서 조치의 첫 줄(앞 턴 공지의 게시와 대상 확정)과 끝 줄(연차 촉진의 결과 취합과 보고)은 코드가 쓴다. 게시를 첫 조치로 쓰라는
  // 지시를 모델이 따르지 않았고, 부서장 일만 있어 누가 모아 보고하는지가 없었다(국가직 팀장 재검토). 담당은 로그인한 사람의 부서이고,
  // 모르면 자리표시다(사용자 확인, 2026-10-27). 결과 보고는 연말 권장 기한 뒤다(국가직 팀장 검토: 같은 날이면 결과를 모을 수 없다).
  const owner = source.unit?.trim() || NOTICE_SENDER;
  const days = actionDays(today);
  const firstAction = kind === 'general' && source.noticeTitle
    ? `${list?.confirm.length ? `대상자 확정(${list.confirm.join(', ')})과 ` : ''}「${source.noticeTitle}」 공지 게시`
      + `(${days.next}까지, ${owner})` : undefined;
  const promoted = kind === 'general' && !!list?.leave && /촉진/.test(userWords);
  const lastAction = promoted ? `부서별 사용 결과 취합과 보고(${days.report}까지, ${owner})` : undefined;
  // 연차 촉진 보고서는 근로기준법 제61조의 서면 촉구와 시기 지정을 조치로 쓰고(사용자 확인, 2026-10-27), 시점이 지난 범위와 1년 미만
  // 근로자 줄을 확인 사항에 싣는다. 회계년도 기준이면 날짜가 붙어 날짜 차례에 들고, 아니면 공지 게시 바로 뒤에 둔다. 입사일 기준이면
  // 다음 주 마지막 근무일까지 촉구 기간이 열리는 직원은 만료일 확인 즉시이고, 만료일 확인에 기한을 단다(국가직 팀장 검토: 서면 촉구가
  // 다른 조치 뒤에 기한 없이 놓였다).
  const basisOfLeave = list?.leave ? leaveBasis(leaveContext) : undefined;
  const law = promoted ? leaveLaw({
    owner, basis: basisOfLeave, today, year: Number(dataYear) || today.getFullYear(), soon: nextWeekEnd(today),
  }) : { actions: [], checks: [] };
  // 회계년도 줄만 기한이 붙는다. 입사일 기준 줄의 날짜(「끝나는 날이 '27.4.17.~'27.5.5.인 직원」)는 기한이 아니다.
  const lawDated = basisOfLeave === 'fiscal' ? law.actions : [];
  const lawActions = basisOfLeave === 'fiscal' ? [] : law.actions;
  const expiry = promoted && basisOfLeave === 'hired' ? `${days.next}까지, ${owner}` : undefined;
  const listChecks = kind === 'general' && list ? [...list.checks, ...(list.leave ? leaveNotes(leaveContext, expiry) : []), ...law.checks]
    : [];
  const axes = genre ? [] : source.axes ?? [];
  // 제목에 써도 되는 달: 사용자가 말한 달(「이번 달」, 「지난달」 포함), 과제 기간의 달, 표에 든 달, 그리고 점검이 아닌 문서는
  // 다음 달부터 올해 끝 달까지(기한 후보의 달, 「11~12월 연차휴가 사용 촉진 안내」). 이번 달만 쓴 「2026년 10월 연차 잔여 현황
  // 안내」는 아니다(실측 14:35). 「이번 달 거래처별 매출 보고서」의 「10월」을 버리지 않는다(독립 검토).
  const { current: thisMonth, last: lastMonth } = currentAndLast(localToday());
  const titleMonths = [...monthsIn(userWords), ...(/이번\s*달|금월|당월/.test(userWords) ? [thisMonth % 100] : []),
    ...(/지난\s*달|전월/.test(userWords) ? [lastMonth % 100] : []),
    ...periodMonths(task?.filters, localToday()).months.map((key) => Number(key.slice(4))), ...(table ? tableMonths(table) : []),
    ...(kind === 'status' ? [] : Array.from({ length: 12 - (thisMonth % 100) }, (_, at) => (thisMonth % 100) + at + 1))];
  // 경과 순 그림의 결론은 코드가 쓴다(14차 검토: 그림을 경과 순으로 바꾼 뒤 결론 줄이 빠졌다). 두 달 판만: 한 달 판은
  // 〈표 2〉 위 쏠림 줄이 같은 말을 한다. 모델 글보다 먼저 만들어 「이미 쓴 줄」로 알린다. 모델이 그 줄을 확인 사항에 되풀이해
  // 두 장이 됐다(실측 2026-10-26 07:03, 1,157px).
  const lateAt = carry ? charts.findIndex((chart) => /예정일 경과/.test(chart.title)) : -1;
  const lateLine = carry && lateAt >= 0 ? lateChartLine(due, carry.refName, charts[lateAt]?.categories) : undefined;
  // 목록 묶음 그림의 결론(상위 3개 묶음의 합과 몫)은 코드가 쓴다. 그림 이름만 그림 장 제목에 올랐고(국가직 팀장 검토), 모델은
  // 순위만 견준 줄(「가부서 45.5일로 나부서 35.5일보다 많음」 꼴)을 썼다(실측 17:08). 모델 글보다 먼저 만들어 이미 쓴 줄로 알린다.
  const listLine = kind === 'general' && list?.chart && list.chartLine && charts.indexOf(list.chart) === 0 ? list.chartLine : undefined;
  // 이름에 숫자가 붙은 계정을 뺀 줄은 보고서 요약 끝에 코드가 싣는다. 1턴이 계산 전에 뺐으면 결과 기록의 줄이고, 표에 그 계정이 남은
  // 결과면 목록 표에서 뺀 줄이다. 표에서 뺐으면 보고서에는 1턴 계산 요약(뺀 계정이 든 인원)을 근거로 주지 않는다. 근거면 「대상 74명」
  // 같은 앞 수가 검사를 지난다. 공지는 준다: 대상 줄의 인원은 지우고 싣는데, 근거가 없으면 1턴 인원을 옮긴 대상 줄이 통째로
  // 버려졌다(독립 검토).
  const dropped = kind === 'general' ? list?.excluded : undefined;
  const excluded = kind === 'general' ? dropped ?? (source.excluded?.length ? source.excluded.join(' ') : undefined) : undefined;
  // 연차 공지는 작성이 실패해도 숫자 없는 기본 항목으로 낸다(독립 재검토).
  const drafted = await writeBrief({
    // 열린 문제 문서는 계산 요약을 근거로 주지 않는다. 1턴 계산이 할 일 이야기(예정일 경과)로 센 값이 들어 있었다(독립 검토).
    question, answer, summary: dropped || issues ? null : summary, charts, chat, facts, axes, kind, titleMonths, forbidden: people,
    stated: [...focus, ...(note ? [note] : []), ...(lateLine ? [lateLine] : []), ...(listLine ? [listLine] : []), ...listChecks,
      ...(excluded ? [excluded] : []), ...(firstAction ? [firstAction] : []), ...law.actions,
      ...(lastAction ? [lastAction] : [])].map(unnamed),
    // 문서가 말해도 되는 달 — 표에 든 달. 1턴 갈래가 여러 달로 셌어도 10월 판에는 10월만(2026-10-25 08:54).
    months: table ? tableMonths(table) : [],
    // 점검 대상·전체 건수는 〈표 1〉의 수로만(10:23: 25일까지 자른 갈래 값 2,009건이 같은 이름으로 실렸다).
    totals: (status?.table.rows ?? []).map((row) => Number(row[1])).filter((value) => Number.isFinite(value) && value > 0),
    // 「○○ 없음 N건」은 그 달 끝낸 건보다 많을 수 없다(10:51: 10월 고객 서명 없음 2,206건, 완료 1,952건).
    done: (() => {
      const at = status?.table.columns.indexOf('완료') ?? -1;
      return at > 0 ? (status?.table.rows ?? []).map((row) => ({ label: String(row[0] ?? ''), count: Number(row[at]) || 0 })) : [];
    })(),
  }) ?? (genre && list?.leave && (list.remaining || /촉진|잔여|소진|남은|남아|미사용/.test(`${source.questions ?? ''}\n${question}`))
    // 남은 연차 공지만이다. 사용 실적 공지가 「남은 연차의 사용 기한」 공지로 바뀌었다(독립 재검토).
    ? leaveNoticeFallback(days.yearEnd) : undefined);
  // 여러 달 판은 묶음별 조치를 코드가 쓴다 — 모델이 두 번(08:29, 09:14) 묶음을 섞었다. 한 달 판은 배정분을 하나도
  // 못 한 담당의 사유 확인만 모델이 빠뜨리면 넣는다(10차 검토). 확인 조치의 기한은 다음 근무일 — 사유 확인과 점검
  // 완료가 같은 월말이었다(9차 검토). 조치가 다 정해진 뒤 같은 일을 적은 확인 사항을 뺀다(09:14).
  // 「최근 예정분」 같은 시점어는 날짜로 바꾼다(9차 검토).
  const actions = withDates(!drafted ? [] : carry?.groups.length ? carryActions(drafted.actions, carry, due, today)
    : due ? withIdleActions(drafted.actions, due, today) : drafted.actions, due);
  // 여러 달 판은 요약·확인 줄의 숫자가 맞는 〈표 1〉 달 이름을 앞에 붙인다(11차 검토: 어느 달 숫자인지 안 보였다).
  // 두 달 모두 미실시 줄의 건수 괄호는 「(두 달 76건)」으로(13차 검토).
  const dated = (lines: string[]) => (carry && status
    ? withMonthLabels(withBothSpan(withDates(lines, due), carry), status.table) : withDates(lines, due));
  // 메모 확인은 코드가 쓴다: 「판단 불가」 줄을 바꾸고, 확인 사항 앞에 메모 줄, 조치 끝에 메모 조치. 모델이 쓴 메모 줄은
  // 뺀다(같은 일을 두 번 쓰지 않게).
  const memoTalk = (line: string) => !!memo && /긴급\s*수리|구출\s*운전|비상\s*통화|메모|비고/.test(line);
  const withMemo = (summary: string[]) => withMemoSummary(summary, memo?.summary);
  // 모델의 서명·미전송 줄: 코드가 쓰면 뺀다. 셀 칸이 없으면 숫자 없는 서명 줄과 미전송 줄을 뺀다(16차 검토: 아직 안 한 점검의
  // 미전송을 전송 누락처럼 썼고, 서명 줄은 「판단할 기준 자료 없음」으로 돌아갔다).
  const closingTalk = (line: string) => (closing.has.sign ? /서명|싸인/.test(line) : /서명|싸인/.test(line) && !/\d/.test(line))
    || (closing.has.send ? /전송/.test(line) : /미전송/.test(line));
  // 점검이 아닌 문서의 제목에는 자료의 해(조회 조건의 연도)를 붙인다. 「잔여 연차 소진 촉진 건」 표지에 해가 없었다(국가직 팀장
  // 검토). 점검 문서는 예전처럼 달로 시작하는 제목에만 붙인다.
  const year = kind === 'status' ? undefined : dataYear;
  // 공지의 앞 항목(목적·대상)에는 인원과 합계를 싣지 않는다. 확인 중인 계정이 섞여 있어 숫자가 바뀔 수 있다(국가직 팀장 재검토:
  // 「대상: 잔여 연차가 있는 직원 74명입니다」). 연차 공지는 본인 잔여 일수를 어디서 보는지 한 줄을 더한다. 휴가신청서를 쓰는
  // 화면에 남은 연차가 나온다(사용자 확인, 2026-10-27).
  const leaveAsk = genre && list?.leave ? ['잔여 일수 확인: 본인 잔여 일수는 ERP 휴가신청서 작성 화면에서 확인하실 수 있습니다.'] : [];
  // 연차 공지의 줄은 사용자 결정대로 고친다(신청 방법 줄, 권장 기한, 임직원). 청했는지는 사용자 질문으로만 본다(과제 목표는 모델이
  // 지은 글이다, 독립 검토).
  const noticeLeave = (lines: string[]) => (genre && list?.leave ? noticeLeaveLines(lines, {
    asked: `${source.questions ?? ''}\n${question}`, question, hired: basisOfLeave === 'hired', executives: list.executives,
    yearEnd: days.yearEnd,
  }) : lines);
  // 모델이 쓴 게시 줄, 제61조 줄(직원별 서면 촉구, 시기 지정 통보), 결과 취합·보고 줄은 코드가 쓴 줄과 겹치니 뺀다. 「공지」,
  // 「결과」라는 말만으로 빼면 서면 촉구와 시기 지정 통보가 지워졌다(독립 재검토). 그 줄은 이제 코드가 쓴다.
  const overlap = (line: string) => (!!firstAction && /게시|공지[^,]*(?:부착|등록|게재)/.test(line))
    || (law.actions.length > 0 && lawDuplicate(line))
    || (!!lastAction && /결과/.test(line) && /취합|보고/.test(line));
  // 연차 촉진 보고서의 조치에 담당이 빠졌으면 붙인다(국가직 팀장 검토: 둘째 조치의 담당이 비었다). 다른 보고서에는 붙이지 않는다(독립
  // 재검토: 자리표시가 알리지 않은 채 붙었다).
  const owned = (line: string) => (promoted ? ownedAction(line, owner) : line);
  // 「올해」, 「내년」 같은 해 말이 있으면 붙이지 않는다(「2026년 올해 연말 …」, 독립 재검토).
  const titled = (title: string) => (year && !/(?:19|20)\d{2}|올해|금년|내년|명년|작년|지난\s*해|전년|이듬해/.test(title) ? `${year}년 ${title}`
    : titleWithYear(title, table ? monthScope(table, today)?.ref : undefined, today));
  // 공지는 코드가 쓰는 내부 확인 줄(메모, 서명·미전송)을 싣지 않는다.
  const brief = drafted ? {
    ...drafted, title: titled(drafted.title),
    summary: genre ? noticeLeave(dated(drafted.summary).map(withoutCounts))
      : [...withMemo(dated(drafted.summary)), ...(excluded ? [excluded] : [])],
    ...(lateLine ? { charts: charts.map((_, at) => (at === lateAt ? lateLine : drafted.charts?.[at] ?? '')) }
      : listLine ? { charts: charts.map((_, at) => (at === 0 ? listLine : drafted.charts?.[at] ?? '')) } : {}),
    // 확인 조치를 다음 근무일로 당기는 것은 점검 문서의 규칙이다(9차 검토). 공지의 「사용 계획 확인」 기한까지 당기지 않는다.
    // 날짜가 붙은 제61조 조치(회계년도)는 날짜 차례 자리에, 날짜 없는 제61조 조치(입사일 기준)는 공지 게시 바로 뒤에 둔다.
    actions: [...(firstAction ? [firstAction] : []), ...lawActions,
      ...byDate(noticeLeave((kind === 'status' ? confirmFirst(actions, today) : actions)
        .filter((line) => !memoTalk(line) && !closingTalk(line) && !overlap(line))).map(owned), lawDated),
      ...(genre ? [] : closing.actions), ...(memo?.action && !genre ? [memo.action] : []),
      ...(lastAction ? [lastAction] : []), ...leaveAsk],
    // ※ 날짜 오류 줄을 되풀이한 확인 사항도 뺀다(10:51). 공지에는 확인 필요 사항이 없다. 직원에게 내부 검토를 알리지 않는다.
    checks: kind === 'notice' ? [] : [...(memo?.checks ?? []), ...closing.checks.map((one) => one.line), ...listChecks,
      ...notRepeating(uncovered(dated(drafted.checks), actions), broken).filter((line) => !memoTalk(line) && !closingTalk(line))],
  } : drafted;
  // 갈래 숫자를 쓴 줄은 그 줄 바로 아래에 센 칸을 밝히고 갈래 값의 비율을 붙인다. 쓴 갈래 값은 보고서 데이터에
  // 남긴다 — 보고서만으로 그 숫자를 대조할 수 있게(7·8차 검토).
  // 코드가 쓴 줄(묶음별 조치, 사유 확인, 메모 줄)은 갈래 숫자가 아니라 각주를 붙이지 않는다. 메모 조치의 「외 6명」이
  // 근거에 없어 그 숫자를 가진 「계획 대비 완료」 각주가 붙었다(실측 11:56).
  const coded = new Set([
    ...(memo ? [memo.summary, ...memo.checks, ...(memo.action ? [memo.action] : [])] : []),
    ...(carry?.groups.length ? carryActions([], carry, due, today) : due ? withIdleActions([], due, today) : []),
    ...(lateLine ? [lateLine] : []),
    ...(listLine ? [listLine] : []),
    ...listChecks,
    ...(excluded ? [excluded] : []),
    ...(firstAction ? [firstAction] : []),
    ...law.actions,
    ...(lastAction ? [lastAction] : []),
    ...leaveAsk,
    ...closing.checks.map((one) => one.line),
    ...closing.actions,
  ]);
  const counted = brief ? axisNotes([...brief.summary, ...brief.checks, ...brief.actions, ...(brief.charts ?? [])]
    .filter((line) => !coded.has(line)), axes,
    [summary, charts.map((chart) => chart.series), facts, chartText(charts)]) : [];
  const shown = (line: string) => counted.find((one) => one.line === line)?.shown ?? line;
  const written = brief && counted.length ? {
    ...brief, summary: brief.summary.map(shown), checks: brief.checks.map(shown), actions: brief.actions.map(shown),
    ...(brief.charts ? { charts: brief.charts.map(shown) } : {}),
  } : brief;
  // ※ 판정 — 상태 코드의 뜻과 경과의 정의(당일 예정분이 경과에 드는지 읽는 사람이 알게, 3차 검토).
  const rule = [table ? statusRuleOf(table) : undefined, exceptions?.rule, issues?.rule].filter(Boolean).join(', ') || undefined;
  // 작성이 실패했을 때 요약 자리에 오르는 글. 공지는 목록 표의 전체 숫자 두 줄만 쓴다. 이름을 지운 답에도 「○○(가부서) 25일」
  // 같은 개인별 수치가 남았다(독립 재검토).
  // 계정을 뺀 보고서는 1턴 답(뺀 계정이 든 수) 대신 목록 표의 수와 뺀 줄을 쓴다. 목록 표로 만든 공지는 인원·합계를 싣지 않는다
  // (국가직 팀장 재검토). 작성이 실패한 사용 실적 공지에 「대상 5명, 연차 사용 합계 36일」이 항목으로 나갔다(독립 재검토).
  const fallback = excluded ? [...(list?.facts.slice(0, 2) ?? []), excluded].join('\n') : !genre ? answer
    : list ? '' : answer.split('\n').filter((line) => !line.includes('○○')).join('\n');
  // 엑셀로 받는 명단(보고서 표)에는 뺀 계정을 남기되 대상 여부를 밝힌다.
  const marked = excluded && list?.excludedNames && source.table ? markExcluded(source.table, list.excludedNames, list.nameColumn) : undefined;
  return {
    layout, charts, brief: written, answer: fallback, basis: basisLabel(new Date()),
    ...(reading ? { reading } : {}),
    ...(genre ? { genre } : {}),
    ...(source.unit?.trim() ? { unit: source.unit.trim() } : {}),
    ...(marked ? { table: marked } : {}),
    // 부서별 그림이 들어가면 부서별 표는 뺀다(같은 숫자를 두 번 보이지 않는다, 3차 검토). 그림 범주에 인원을 붙였다.
    // 목록 표가 없는 공지는 핵심 숫자를 싣지 않는다. 계산 요약의 「최다사용자 ○○」 같은 값이 숫자 줄에 찍혔다(독립 재검토).
    // 공지에는 표를 싣지 않는다. 잔여 보유자만 센 부서 표(생성·사용 열)가 부서 전체 수처럼 읽혔고, 확인 전인 계정의 「사용 0」이
    // 게시판에 붙을 뻔했다(국가직 팀장 재검토). 부서 표는 보고서에 있다.
    // 열린 문제 표(긴급도별, 현장별)는 그림과 숫자가 겹치지 않아 그림이 있어도 싣는다. 공지에는 싣지 않는다.
    ...(issues ? { tables: genre ? [] : issues.tables, figures: issues.figures }
      : list ? { tables: genre ? [] : list.tables.filter((one) => one.role !== 'groups' || !charts.length), figures: list.figures }
        : genre ? { figures: [] } : {}),
    ...(rule ? { statusRule: rule } : {}),
    // 메모 확인 사항 아래에는 「메모(비고) 문구 기준, 판정 항목 아님」(12차 검토). 공지에는 확인 사항이 없다.
    ...(counted.length || (memo && written && !genre) || (closing.checks.length && written && !genre) ? {
      lineNotes: [...counted.map((one) => ({ line: one.shown, note: one.note })),
        ...(memo && written && !genre ? memo.checks.map((line) => ({ line, note: memo.note })) : []),
        ...(written && !genre ? closing.checks : [])],
    } : {}),
    ...(counted.length ? { axes: counted.map((one) => one.axis).filter((axis, index, all) => all.indexOf(axis) === index) } : {}),
    ...(status ? { statusTable: status.table } : {}),
    ...(noted && note ? { statusNote: note } : {}),
    // 공지에는 예외 행(〈표 2〉, 담당 이름이 든다)과 메모 목록을 싣지 않는다. 공지판에 붙이는 문서다.
    ...(exceptions && !genre ? { exceptions } : {}),
    // ※ 줄: 날짜 오류, 전송 칸의 뜻 모를 값(17차 검토).
    ...(broken || closing.notes.length ? { dataNotes: [...(broken ? [broken] : []), ...closing.notes] } : {}),
    // 〈표 1〉·〈표 2〉를 센 모집단을 보고서 데이터에 남긴다(12차 검토: 보고서 파일만으로 검산할 수 없었다).
    ...(source.population ? { population: source.population } : {}),
    // 메모 확인에 센 현장 목록·메모 원문·담당(13차 검토: 보고서만으로 검산하게).
    ...(memoFound.length && !genre ? {
      memos: memoFound.map(({ family, name, site, months, units, owners, memo: text }) => ({ family, name, site, months, units, owners, memo: text })),
    } : {}),
  };
}

function uniqueFormats(formats: FileFormat[]): FileFormat[] {
  return formats.filter((format, index) => formats.indexOf(format) === index);
}

/**
 * 이번 턴에 문서를 청했는가 — 과제 단계가 분량(layout)을 새로 정했거나 문서 말이 있다.
 * 보고서 스킬이 뽑히지 않은 판이 다시 조회하고 파일 없이 끝났다(실제 테넌트, 2026-10-23).
 * 앞 턴에서 이어진 layout 만으로는 아니다 — 같은 일의 다음 질문까지 옛 보고서로 답하게 된다.
 */
function layoutAsked(question: string, task: TaskSpec | undefined, context: Deps['context']): boolean {
  if (!task?.layout) return false;
  return JSON.stringify(task.layout) !== JSON.stringify(context?.task?.layout)
    || /보고서|문서|서류로|공지(?!사항)|안내문|한\s*(?:장|페이지)|그래프|차트|pdf/i.test(question);
}

/**
 * 문서 표를 줄였으면 전체를 받을 엑셀 단추를 붙인다. 답에는 「엑셀로 받기」라고 적는다. 표가 없는 공지는 엑셀을 내밀지 않는다
 * (국가직 팀장 재검토: 문서 스킬의 형식 목록에서 xlsx가 남아 빈 엑셀을 받을 뻔했다).
 */
function withFullTable(formats: FileFormat[], document: ReportDocument): FileFormat[] {
  if (emptyNotice(document)) return [];
  if (document.genre === 'notice' && !document.table) return formats.filter((format) => format !== 'xlsx');
  const trimmed = document.detailRows !== undefined && (document.table?.rows.length ?? 0) > document.detailRows;
  return trimmed && formats.indexOf('xlsx') < 0 ? [...formats, 'xlsx'] : formats;
}

/**
 * 내용 없는 공지(작성이 실패해 항목이 없고 요약도 빈 것). 문의처 한 줄뿐인 공지 파일을 내밀지 않고 쓰지 못했다고 답한다(독립 재검토).
 */
function emptyNotice(document: ReportDocument): boolean {
  return document.genre === 'notice' && !document.points && !document.summary.trim();
}

/**
 * 보고서를 내미는 답 본문. 결론이 있으면 결론부터 쓴다. 「…를 준비했습니다」는 일 보고 말이다. note는 공지 답에만 붙이는 알림이다
 * (계산 전에 뺀 계정, 공지 문서에는 싣지 않는다).
 */
function reportText(document: ReportDocument, note = ''): string {
  const points = document.points;
  if (emptyNotice(document)) return '공지 글을 쓰지 못했습니다. 같은 말로 다시 청해 주세요.';
  // 공지는 문서와 같은 번호 항목으로 보인다. 발신 부서를 모르면(로그인한 사람의 부서가 비었으면) 자리표시라 그것을 알린다.
  if (document.genre === 'notice' && points) {
    return [document.title, NOTICE_TO, noticeItems(document).join('\n'),
      document.unit?.trim() ? '' : `발신과 문의처는 「${NOTICE_SENDER}」로 비워 두었습니다. 워드 파일에서 부서 이름을 넣어 주세요.`,
      note, '아래 단추로 받아 주세요.'].filter(Boolean).join('\n\n');
  }
  if (!points) return `${objectParticle(document.title)} 준비했습니다. 아래 파일 버튼으로 받아 주세요.\n\n${document.summary}`;
  const list = (items: string[]) => items.map((item) => `- ${item}`).join('\n');
  const titles = sectionTitles(document);
  return [
    document.title,
    list(points.summary),
    points.checks.length ? `${titles.checks}\n${list(points.checks)}` : '',
    points.actions.length ? `${titles.actions}\n${list(points.actions)}` : '',
    // 원자료 결함(없는 날짜)은 채팅에서도 알린다 — ERP 에서 고칠 일이다(10차 검토).
    ...(document.dataNotes ?? []).map((note) => `※ ${note}`),
    '아래 단추로 받아 주세요.',
  ].filter(Boolean).join('\n\n');
}

/**
 * 직전 답변의 내용과 표를 그대로 파일로 포장한다. 형식 변경 때문에 ERP를 다시
 * 조회하면 데이터가 바뀌거나 다른 화면이 선택될 수 있으므로 이 갈래는 계획도 세우지 않는다.
 */
async function reuseForArtifact(
  question: string,
  context: Deps['context'],
  chat: Chat,
  screens: AgentScreen[],
  artifactSkill?: AgentSkill,
  task?: TaskSpec,
  /** 이번 턴에 이름에 숫자가 붙은 계정을 어떻게 다룰지(`duplicateDecision`). */
  decision: DuplicateDecision = { action: 'none' },
  /** 분야 전문가. 분석 틀이 있는 화면의 문서는 틀의 자료 읽기를 쓴다. */
  experts: Expert[] = [],
): Promise<Answer | undefined> {
  // 형식이나 분량을 청한 후속 턴이면 같은 일인지 본다(같은 일 판정은 앞 결과가 담은 달을 알아야 해 아래에서 한다).
  // 공지 요청은 분량 쪽으로 받는다. 같은 일 검사가 살아 있어야 「가부서만 공지로」가 전 부서 공지가 되지 않는다(독립 검토).
  const layoutFollowup = Boolean(task?.artifact || layoutAsked(question, task, context) || noticeRequested(question, screens));
  const formatFollowup = isArtifactFollowup(question, artifactSkill);
  if (!layoutFollowup && !formatFollowup) return undefined;
  const history = context?.history ?? [];
  // 사용자가 맞다고 확인한 답 뒤로 형식 변경 요청만 이어졌다면 그 확인값이
  // 보고서의 원본이다. 중간의 잘못된 재조회 답이 더 최근이라는 이유로 덮지 않는다.
  let confirmedIndex = -1;
  for (let index = history.length - 1; index >= 0; index -= 1) {
    if (history[index]?.feedback === 'positive' && history[index]?.answer.trim()) {
      confirmedIndex = index;
      break;
    }
  }
  const confirmed = confirmedIndex >= 0
    && history.slice(confirmedIndex + 1).every((turn) => isArtifactFollowup(turn.question, artifactSkill))
    ? history[confirmedIndex]
    : undefined;
  // 오류 응답은 본문이 비어 이력에 남을 수 있다. 그것 때문에 바로 앞의 정상
  // 계산 결과를 놓치지 않도록 가장 최근의 비어 있지 않은 답을 찾는다.
  let previous: typeof history[number] | undefined = confirmed;
  for (let index = history.length - 1; !previous && index >= 0; index -= 1) {
    // 되묻기는 건너뛴다. 그 본문은 우리가 낸 질문이지 답이 아니다.
    if (history[index]?.asked) continue;
    if (history[index]?.answer.trim()) {
      previous = history[index];
      break;
    }
  }
  if (!previous) return undefined;
  // 1턴이 계산 전에 뺀 계정을 넣어 달라는 문서 요청(「숫자 붙은 계정도 넣어서 공지」)은 뺀 결과로 만들지 않는다. 다시 조회해 넣는다
  // (독립 재검토: 넣지 못했다는 말도 없이 뺀 결과로 공지가 나갔다).
  if (decision.action === 'include' && previous.result?.excluded?.length) return undefined;
  // 기간 조건만 달라진 것을 같은 일로 넘기는 것은 이번 발화가 문서를 청했을 때만이다. 과제의 산출물은 다음 턴에도 남아(task.ts),
  // 「보고서로」 뒤의 새 질문에 옛 보고서를 낼 뻔했다(독립 검토). 그 밖에는 예전처럼 과제가 같을 때만 앞 결과를 쓴다.
  const asksDocument = requestedFormats(question).length > 0 || DOCUMENT_WORDS.test(question);
  const words = tableWords(previous.result?.table ?? previous.table);
  const applied = appliedConditions(previous.result?.sources, screens);
  // 앞 턴 글: 사용자가 한 말과 과제의 목표·대상·지표·요지. 계산 요약과 답 글은 넣지 않는다(「대체잔여일수」 같은 칸 이름이 든다).
  const prior = context?.task;
  const said = [...history.map((turn) => turn.question), prior?.goal, prior?.entity, prior?.metric, prior?.summary]
    .filter(Boolean).join('\n');
  const years = applied.map((one) => one.value).filter((value) => /^(?:19|20)\d{2}$/.test(value));
  // 질문 판정에는 앞 턴 답 글도 넣는다(3턴의 「잔여」는 사용자가 아니라 앞 답이 먼저 말했다). 대상·지표 판정에는 넣지 않는다.
  const same = asksDocument
    ? sameWork(task, prior, coveredMonths(previous.result), localToday(), applied, words,
      documentOnlyQuestion(question, words, `${said}\n${previous.answer}`, years), said, previous.result?.summary,
      documentOnlyQuestion(question, words, history.map((turn) => turn.question).join('\n'), years))
    : sameTaskCore(task, prior);
  if (!formatFollowup && !same) return undefined;
  // 앞 문서가 계정을 남겼는가. 이 칸이 없는 예전 문서는 결과 기록의 넣기 상태로 본다.
  const reportKeep = previous.report?.keepNumbered ?? !!previous.result?.keepNumbered;
  // 판정이 어긋났고 앞 결과나 앞 문서에 그 계정이 걸려 있으면(뺐거나 남겼음) 문서를 만들기 전에 묻는다. 단추는 이번에 만들 문서 종류다.
  if (decision.doubted && (previous.result?.excluded?.length || previous.result?.keepNumbered || previous.result?.numbered?.length
    || (previous.report && reportKeep))) {
    throw askDuplicates(undefined, task, documentKind(question, screens, documentGenre(question, previous.report?.genre)));
  }
  const formats = requestedFormats(question);
  const resolvedFormats = formats.length ? formats : (task?.artifact ? [task.artifact.format] : []);
  // 형식을 말하지 않았으면 과제가 고른 형식 말고 스킬이 내는 형식도 내민다 — 「보고서 한장으로」에
  // 과제 단계가 워드를 골라 PDF 단추가 없었다(실제 테넌트, 2026-10-23).
  const offered = formats.length ? formats
    : uniqueFormats([...resolvedFormats, ...(artifactSkill?.formats ?? ['pdf', 'docx'])]);
  // 엑셀은 구조화 표가 꼭 필요하다. 직전 답변이 요약뿐이면 원천 행을 다시 계산해야 한다.
  if (resolvedFormats.includes('xlsx') && !previous.table) return undefined;
  // 분량을 새로 정했으면 그것으로 다시 만든다. 아니면 형식만 바꿀 때도 직전 회의 보고서의 제목·대상·주기를 보존한다.
  // 형식만 바꾸면(「발표자료로도」) 앞 보고서를 그대로 — 다시 쓰게 하면 표지 제목이 보고서와 달라졌다(2026-10-23).
  // 문서 종류가 바뀌었거나(공지 뒤의 「보고서도」) 새 주제를 말했으면(「잔여 연차 소진 촉진건 으로」) 다시 쓴다. 앞 공지를 그대로
  // 발표자료로 냈다(실측 2026-10-26).
  // 발표자료 꼴(보고용, 마케팅형)만 바뀐 것은 같은 문서다. 내용은 그대로 두고 꼴만 바꿔 낸다(작성 호출 없음).
  const plainLayout = (layout: TaskSpec['layout']) => (layout ? { detail: layout.detail, charts: layout.charts } : null);
  const sameLayout = JSON.stringify(plainLayout(task?.layout)) === JSON.stringify(plainLayout(context?.task?.layout));
  // 앞 문서가 공지면 「워드로도」, 「그래프도 넣어서」는 공지를 잇는다. 「보고서도」라고 해야 보고서로 다시 쓴다.
  const sameGenre = previous.report?.genre === documentGenre(question, previous.report?.genre);
  // 형식만 바꾸는 같은 문서(「워드로도」)인가.
  const sameDocument = !!previous.report?.basis && sameLayout && sameGenre && !newSubject(question);
  // 넣고 빼기: 이번 턴이 넣으라고 하면 넣고, 빼라고 하면 뺀다. 말이 없으면 사용자가 본 앞 문서대로, 앞 문서가 없으면 이어 온 넣기
  // 상태대로다. 넣어 둔 뒤 「다시 빼고 공지로」를 끌 길이 없었고(조사 보고서, 2026-10-27), 넣은 보고서 뒤 「다시 빼고 워드로도」에 앞
  // 보고서가 그대로 나갔고(독립 검토), 넣은 공지 뒤 「그래프도 넣어서 공지 다시」가 말없이 11명이 됐다(독립 재검토 세 번째). 다음 턴까지
  // 잇는 상태는 분명할 때만이다(`duplicateDecision`의 keep).
  const keep = decision.action === 'include' ? true : decision.action === 'exclude' ? false
    : previous.report ? reportKeep : !!previous.result?.keepNumbered;
  const keepNext = decision.keep ? true : decision.action === 'exclude' ? false : !!previous.result?.keepNumbered;
  // 넣고 빼기가 앞 문서와 같을 때만 앞 문서를 그대로 낸다.
  const deck = task?.layout?.style === 'keynote' ? 'keynote' as const : undefined;
  // 과제가 고른 분량과 꼴. 발표자료가 보고용으로 나온 까닭을 이것 없이는 못 가렸다(실측 2026-10-28 08:43).
  // eslint-disable-next-line no-console
  console.debug('[agent] 문서 분량', JSON.stringify(task?.layout ?? null), sameDocument ? '앞 문서' : '새로 씀');
  // 앞 문서를 그대로 쓸 때도 발표자료 꼴은 이번 과제대로다.
  const withDeck = (document: ReportDocument): ReportDocument => {
    const { deck: _deck, ...rest } = document;
    return deck ? { ...rest, deck } : rest;
  };
  const kept = sameDocument && keep === reportKeep && previous.report ? withDeck(previous.report) : undefined;
  const extra = kept ? undefined : await reportExtra(question, task, {
    summary: previous.result?.summary, table: previous.result?.table ?? previous.table, answer: previous.answer,
    // 1턴이 만든 모집단도 넘긴다. 빠뜨려 2턴 보고서가 〈표 1〉·〈표 2〉 없이 나갔다(2026-10-25 10:42).
    axes: previous.result?.axes, population: previous.result?.population, excluded: previous.result?.excluded,
    // 문서 턴에 모델이 읽었으면 앞 문서에 남아 있다. 보고서 뒤 발표자료가 다시 묻지 않는다.
    screenValues: previous.result?.screenValues, keepNumbered: keep, reading: previous.result?.reading ?? previous.report?.reading,
    sources: previous.result?.sources, screens, experts,
    // 앞 턴까지 사용자가 한 말. 목록 표의 주 수치(「연차 촉진 대상자」면 잔여)를 고른다.
    asked: [...history.map((turn) => turn.question), context?.task?.goal ?? ''].join('\n'),
    genre: previous.report?.genre, priorLayout: context?.task?.layout, applied: appliedConditions(previous.result?.sources, screens),
    ...(previous.report?.genre === 'notice'
      ? { noticeTitle: previous.report.title, noticeActions: previous.report.points?.actions ?? [] } : {}),
    unit: context?.me?.divinm, questions: history.map((turn) => turn.question).join('\n'),
  }, chat);
  // 목록 표를 다시 못 만들면(분량 없는 「pdf로」) 앞 문서나 앞 계산 표를 그대로 싣는다. 이번 턴이 넣거나 빼라고 했는데 그 표와 다르면
  // 다시 조회한다(독립 재검토: 계정을 남긴 채팅 답 뒤 「빼고 pdf로」에 13행 pdf가 나갔다). 말이 없으면 사용자가 본 표를 그대로 쓴다.
  // 이름을 물은 답 뒤 「pdf로 줘」를 다시 조회하면 질문에 이름이 없어 그 계정이 빠져 0행이 됐다(독립 재검토 네 번째).
  const contentKeep = previous.report ? reportKeep : !!previous.result?.numbered?.length;
  if (!extra && (decision.action === 'include' || decision.action === 'exclude') && keep !== contentKeep) return undefined;
  const made = kept || (!extra && previous.report && withDeck(previous.report)) || (previous.result
    ? makeReport(previous.result, artifactSkill, extra)
    : fallbackReport(previous.answer, previous.table, question, artifactSkill, extra));
  // 문서가 계정을 남겼는지는 실제로 실은 표대로 적는다. 목록 표를 다시 만들었으면 이번 넣고 빼기, 아니면 앞 표의 상태다.
  const documentKeep = extra ? keep : contentKeep;
  const document = { ...made, sources: readableSourceLines(made.sources, screens), keepNumbered: documentKeep };
  // 공지가 계정을 넣어서 셌으면 채팅 글에 밝힌다(공지 문서에는 싣지 않는다). 조용히 넣는 일이 없게(독립 검토).
  const keptNames = documentKeep && document.genre === 'notice' ? previous.result?.numbered ?? [] : [];
  return {
    text: reportText(document, keptNames.length ? includedNote([{ label: '', names: keptNames }]) : ''),
    code: '',
    sources: [],
    table: document.table,
    formats: withFullTable(offered, document),
    // 넣기 상태가 바뀌었으면 결과 기록에도 남겨 다음 문서가 잇는다.
    result: previous.result && !!previous.result.keepNumbered !== keepNext
      ? { ...previous.result, keepNumbered: keepNext || undefined } : previous.result,
    // 빈 공지에는 보고서를 붙이지 않는다. 붙이면 다시 청해도 앞 턴의 빈 공지를 그대로 꺼냈다(독립 재검토).
    ...(emptyNotice(document) ? {} : { report: document }),
    task: task ? { ...task, keepNumbered: keepNext || undefined } : task,
  };
}

function asksByBusinessSite(plan: Extract<QueryPlan, { kind: 'retrieve' }>): boolean {
  return (plan.requirements?.dimensions ?? []).some((dimension) => /사업장|지점/.test(dimension));
}

function observedBusinessSites(
  retrieved: Record<string, Retrieved>,
  sites: { code: string; name: string }[],
): Set<string> {
  const found = new Set<string>();
  for (const result of Object.values(retrieved)) {
    for (const row of result.rows) {
      const value = row.spjangcd ?? row.spjangnm
        ?? row.__businessSiteCode ?? row.__businessSiteName;
      const text = value === null || value === undefined ? '' : String(value).trim();
      const site = sites.find((candidate) => candidate.code === text || candidate.name === text);
      if (site) found.add(site.code);
    }
  }
  return found;
}

/**
 * 빈 표를 받았을 때 같은 화면의 다른 표를 시도한다.
 *
 * 한 화면이 열 구성이 같은 표를 둘 두는 일이 있다 — 월별입금현황의 현장별과
 * 거래처별이 그렇다. 어느 쪽에 값이 들어오는지는 조회해 보기 전에는 알 수 없고,
 * 계획이 빈 쪽을 집으면 화면 합계에 백팔십억이 있어도 답은 `0원` 이 된다.
 *
 * 화면은 이미 열려 조회까지 끝난 상태라 다른 상태 키를 읽는 것은 값싸다. 그래도
 * 예산에서 세는 것은, 세지 않는 일이 쌓이면 시간이 어디로 갔는지 알 수 없어서다.
 */
async function tryOtherGrids(
  plan: Extract<QueryPlan, { kind: 'retrieve' }>,
  retrieved: Record<string, Retrieved>,
  deps: Deps,
  budget: Budget,
): Promise<void> {
  for (const retrieval of plan.retrievals) {
    if ((retrieved[retrieval.label]?.rows.length ?? 0) > 0) continue;
    // 펼쳐 읽은 상세가 빈 것은 표를 잘못 고른 것이 아니다 — 다른 표는 한 건 조회라 그 기간을 못 본다.
    if (retrieval.sweep) continue;
    const screen = deps.screens.find((candidate) => candidate.route === retrieval.route);
    const wanted = retrieval.requiredColumns ?? [];
    if (!screen || !wanted.length) continue;
    const others = screen.grids.filter((grid) => grid.key !== retrieval.rowsStateKey
      && !/^openChk\d*$/i.test(grid.key)
      && wanted.every((column) => grid.columns.some((have) => have.id === column)));
    for (const grid of others) {
      if (!budget.allows('retrievals')) return;
      budget.spend('retrievals');
      try {
        // eslint-disable-next-line no-await-in-loop
        const result = await deps.session.retrieve({
          // 같은 이름을 다시 쓰면 세션이 막는다. 표마다 다른 이름으로 부른다.
          label: `${retrieval.label}__${grid.key}`,
          routePath: retrieval.route,
          rowsStateKey: grid.key,
          conditions: retrieval.conditions,
          scalarKeys: screen.scalars?.map((scalar) => scalar.key),
          selectionStateKeys: screen.grids.map((one) => one.key)
            .filter((key) => /^openChk\d*$/i.test(key)),
        });
        if (result.rows.length) {
          retrieved[retrieval.label] = { ...result, label: retrieval.label };
          retrieval.rowsStateKey = grid.key;
          break;
        }
      } catch (error) {
        // 이건 **덤으로 해 보는 조회**다. 여기서 던지면 원래 결과까지 잃는다 —
        // 없는 조건 하나로 질문이 끝났다.
        // eslint-disable-next-line no-console
        console.debug('[agent] 다른 표 시도 실패', grid.key, String(error));
      }
    }
  }
}

/**
 * 한 화면 조회가 현재 사업장 한 곳만 돌려주면 우측 상단 사업장을 순회해 원래 계획의
 * 행으로 합친다. 전역 문맥은 반드시 원래 값으로 복구한다.
 */
async function expandBusinessSites(
  plan: Extract<QueryPlan, { kind: 'retrieve' }>,
  retrieved: Record<string, Retrieved>,
  deps: Deps,
  budget: Budget,
): Promise<void> {
  const sites = deps.context?.businessSites ?? [];
  if (!asksByBusinessSite(plan) || sites.length <= 1) return;
  // 화면 자체가 이미 사업장들을 모두 구분해 돌려주면 전역 문맥을 반복할 필요가 없다.
  if (observedBusinessSites(retrieved, sites).size >= sites.length) return;
  if (!deps.session.switchBusinessSite) {
    throw new Error('사업장별 결과가 한 곳뿐인데 우측 상단 사업장 전환 기능을 사용할 수 없다');
  }
  // 사용자가 선택지에 없는 문맥(전사 `ZZ`)에 있을 수 있다. 그때도 순회는 할 수 있다 —
  // 돌아갈 곳만 알면 된다. 그것마저 모를 때만 손대지 않고 멈춘다.
  const contextCode = deps.context?.businessSiteContext;
  const original = sites.find((site) => site.current)
    ?? (contextCode ? { code: contextCode, name: contextCode, current: true } : undefined);
  if (!original) throw new Error('현재 사업장을 확인할 수 없어 전체 사업장을 순회하지 못했다');

  // 사업장 이름이 서로 같은 계정이 있다 — 열한 곳이 모두 `웹이알피` 였다. 이름만 붙이면
  // 순회를 다 돌고도 보고서에 같은 줄이 열한 번 나온다. 겹칠 때만 코드를 덧붙인다.
  const duplicated = new Set(
    sites.map((site) => site.name).filter((name, index, all) => all.indexOf(name) !== index),
  );
  const label = (site: typeof sites[number]): string => (
    duplicated.has(site.name) ? `${site.name}(${site.code})` : site.name
  );

  const combined = new Map<string, Retrieved>();
  const append = (site: typeof sites[number], retrieval: typeof plan.retrievals[number], result: Retrieved) => {
    const previous = combined.get(retrieval.label);
    const rows = result.rows.map((row) => ({
      ...row,
      ...('spjangcd' in row ? { spjangcd: site.code } : {}),
      ...('spjangnm' in row ? { spjangnm: label(site) } : {}),
      __businessSiteCode: site.code,
      __businessSiteName: label(site),
    }));
    combined.set(retrieval.label, {
      ...result,
      label: retrieval.label,
      rows: [...(previous?.rows ?? []), ...rows],
      notices: [...(previous?.notices ?? []), ...(result.notices ?? [])],
    });
  };
  // 순회는 "화면이 지금 사업장의 행만 준다"는 전제 위에 있다. 그 전제가 틀린 화면이
  // 있다 — 월매출매입현황은 문맥과 무관하게 전사 행을 준다. 그런 화면을 열한 곳 돌면
  // 같은 행이 열한 벌 쌓여 연매출이 실제의 열한 배(130억이 1,438억)가 된다.
  // 오류는 하나도 안 나고 문장도 멀쩡해서, 숫자를 대조하기 전에는 아무도 모른다.
  const fingerprint = (result: Retrieved): string => JSON.stringify(result.rows);
  const before = new Map<string, string>(
    plan.retrievals
      .map((retrieval) => [retrieval.label, retrieved[retrieval.label]] as const)
      .filter((pair): pair is [string, Retrieved] => Boolean(pair[1]))
      .map(([label, result]) => [label, fingerprint(result)]),
  );
  let contextBlind = false;
  let activeSite = original.code;
  try {
    for (const site of sites) {
      // 계획 직전에 현재 사업장은 이미 조회했다. 같은 사업장으로 다시 전환하고 같은
      // 조회를 한 번 더 하지 말고 그 결과를 첫 묶음으로 재사용한다.
      if (site.code === original.code) {
        for (const retrieval of plan.retrievals) {
          const initial = retrieved[retrieval.label];
          if (initial) append(site, retrieval, initial);
        }
        continue;
      }
      // 순회는 한 곳이 전환 한 번에 조회 한 번이다. 전환도 화면을 다시 그리므로 싸지
      // 않다 — 예산은 전환 **전에** 본다. 닳으면 거기까지 모은 사업장으로 답한다.
      if (!budget.allows('retrievals')) break;
      // eslint-disable-next-line no-await-in-loop
      await deps.session.switchBusinessSite(site.code);
      activeSite = site.code;
      for (const retrieval of plan.retrievals) {
        if (!budget.allows('retrievals')) break;
        budget.spend('retrievals');
        const screen = deps.screens.find((candidate) => candidate.route === retrieval.route);
        try {
          // eslint-disable-next-line no-await-in-loop
          const result = await deps.session.retrieve({
            label: `${retrieval.label}__business_${site.code.replace(/[^0-9A-Za-z]/g, '_')}`,
            routePath: retrieval.route,
            rowsStateKey: retrieval.rowsStateKey,
            conditions: retrieval.conditions,
            scalarKeys: screen?.scalars?.map((scalar) => scalar.key),
            selectionStateKeys: screen?.grids.map((grid) => grid.key)
              .filter((key) => /^openChk\d*$/i.test(key)),
            force: true,
          });
          // 바꾸기 전과 똑같은 행이 왔다. 이 화면은 사업장 문맥을 안 본다는 뜻이니
          // 여기서 멈추고 처음 조회한 한 벌을 그대로 쓴다. 남은 사업장을 더 도는 것은
          // 시간만 쓰고 같은 행을 또 쌓는 일이다.
          if (before.get(retrieval.label) === fingerprint(result)) {
            contextBlind = true;
            break;
          }
          append(site, retrieval, result);
        } catch (error) {
          // 한 사업장에서 막혔다고 질문을 끝내지 않는다. 열한 곳을 도는 일이고, 그중
          // 하나가 안 된다고 나머지 열 곳의 답까지 버릴 이유가 없다 —
          // `검색조건 spjangcd 이 화면에 없다` 하나로 전체가 죽은 적이 있다.
          // eslint-disable-next-line no-console
          console.debug('[agent] 사업장 건너뜀', site.code, String(error));
        }
      }
      if (contextBlind) break;
    }
  } finally {
    if (activeSite !== original.code) await deps.session.switchBusinessSite(original.code);
  }
  if (contextBlind) {
    // eslint-disable-next-line no-console
    console.debug('[agent] 사업장 순회 취소', '화면이 사업장 문맥과 무관하게 같은 행을 준다');
    return;
  }
  combined.forEach((result, label) => { retrieved[label] = result; });
}

/**
 * 되물을 수 있는 항목인가.
 *
 * `unresolved` 는 **항목 이름**이 들어올 자리다(`부서`, `대상 사업장`). 모델이 거기에
 * 문장을 적어 보내는 턴이 있고, 그것을 그대로 `…을 알려 주세요` 로 만들면
 * `이전에 조회한 결과와 이어서 진행할 후속 작업 내용이 현재 대화에 없어 확인이 필요함을
 * 알려 주세요` 같은 말이 사무직에게 나간다 — 공사 실적(R9)이 세 턴을 그렇게 반복했다.
 *
 * 되물을 것이 없으면 묻지 말고 진행한다. 정말 못 고르면 계획이 구체적으로 되묻는다.
 */
/** 값이 아니라 **판정하는 법**을 묻는 항목 — 「경력직 구분 기준」, 「단골 여부」. */
// 「부가세 포함 여부」·「조회 기준일」 같은 값 항목은 사람에게 묻는다 — 판정하는 법만 고른다(검토, 2026-10-21).
const DEFINITION = /(판정|판단|정의|(?:구분|분류|판별)\s*기준)/;

function askable(item: string): boolean {
  const text = item.trim();
  if (!text || text.length > 20) return false;
  return !/(없|필요|해\s*주|알려|확인|바랍|주세요|입니다)/.test(text);
}

/**
 * **스킬이 못박은 표가 비었거나 칸이 빈 것은 자료 사정이다.** 다시 계획할 일이 아니다.
 *
 * 스킬이 화면 하나와 결과 표를 댄 조회라면 다시 계획해도 같은 표로 돌아온다 — 고쳐
 * 쓰기(`rewriteToSkillRoute`)가 그렇게 만든다. 그런데 회복 규칙은 반대로 당긴다:
 *
 * - `empty-column` → 「표를 잘못 골랐다」(`replan-target`). 「이번 달 점검 계획 보여줘」가
 *   호기명이 빈 1행 때문에 두 번 더 계획하고(각 20k 자) 마지막에 **같은 행으로** 답했다.
 * - `legitimate-empty` → 「다른 화면을 골라라」(`replan-screen`). 「그 중에 덕봉빌딩
 *   것만」이 그 달에 없는 현장이라 비었는데, 모델이 고른 딴 화면을 고쳐 쓰기가 되돌리기를
 *   두 번 했고 그 사이 기간 조건까지 빠졌다(둘 다 2026-09-23 실측).
 *
 * 빈 표의 렌더링 지연 재확인(`retry-same`)은 계획을 안 부르므로 그 뒤에 여기서 본다.
 *
 * **사람이 이름으로 댄 화면**도 못박힌 것으로 본다. 「안전교육 일지 목록 보여줘」가
 * 안전교육일지를 맞게 열었는데 9월 자료가 없자 「다른 화면을 골라라」로 두 번 더
 * 계획했다(2026-09-23). 사람이 그 화면을 말했으면 빈 것이 답이다.
 *
 * 스킬이 화면을 여럿 대면 빈 칸은 「다른 표」의 신호일 수 있으므로 예전대로 둔다 — 화면마다
 * 표를 따로 댄(`rowsByRoute`) 스킬만 빼고.
 */
function emptyOnPinnedTable(
  outcomes: { kind: string }[],
  plan: { retrievals: { route: string; rowsStateKey: string }[] },
  skills: AgentSkill[],
  question: string,
  screens: AgentScreen[],
): boolean {
  const failed = outcomes
    .map((outcome, at) => ({ outcome, retrieval: plan.retrievals[at] }))
    .filter(({ outcome }) => outcome.kind !== 'success');
  return failed.length > 0 && failed.every(({ outcome, retrieval }) => (
    (outcome.kind === 'empty-column' || outcome.kind === 'legitimate-empty')
    && retrieval !== undefined && (skills.some((skill) => (
      skill.kind === 'metric'
      && skill.preferredRoutes?.includes(retrieval.route)
      // 화면마다 표를 따로 댄 스킬(`rowsByRoute`)도 못박은 것이다 — 업무일지 스킬은 두 화면을
      // 아는데, 사람마다 메뉴에 한쪽만 있어 둘은 서로의 대체가 아니다. 이것이 빠져 「이번 달 내
      // 업무일지」가 그 달 자료가 없자 개별업무일지작성 → [총무팀] → 작성으로 두 번 더 계획했다
      // (실제 테넌트, 2026-10-05 — 스킬이 두 화면을 알게 된 날).
      && (skill.preferredRoutes.length === 1 || Boolean(skill.rowsByRoute))
      && skillRowsAt(skill, retrieval.route) === retrieval.rowsStateKey
    )) || namesScreen(question, screens.find((screen) => screen.route === retrieval.route)?.name))
  ));
}

/**
 * **화면 요약 값을 숫자로 정리한다.** 요약은 흔히 숫자가 아니라 화면에 찍는 글이다 —
 * 고장처리현황의 `rececnt_tot` 는 `접수 : 372건` 이다. 계산 프롬프트는 「요약은 totals 로
 * 주어진다, 그것을 써라」고 말하므로 모델은 `int()` 를 하고, 거기서 죽거나(Traceback) 0으로
 * 읽어 「요약 0건 · 처리완료 1,089건」이라 답했다(실제 테넌트, 2026-09-23).
 *
 * **숫자가 딱 하나인 글만** 바꾼다. 기간(`2026-09-01 ~ 2026-09-30`)처럼 숫자가 여럿이거나
 * 숫자가 없는 글은 그대로 둔다 — 뜻을 모르는 것을 숫자로 만들면 그게 더 나쁘다.
 */
function plainTotals(totals: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(totals).map(([key, value]) => {
    if (typeof value !== 'string') return [key, value];
    const numbers = value.match(/-?\d[\d,]*(?:\.\d+)?/g) ?? [];
    if (numbers.length !== 1) return [key, value];
    const parsed = Number(numbers[0]!.replace(/,/g, ''));
    return [key, Number.isFinite(parsed) ? parsed : value];
  }));
}

/** 이름이 실제 현장인지 확인할 때 보는 곳./** 이름이 실제 현장인지 확인할 때 보는 곳. 보수현장현황은 현장을 다 들고 있다. */
const SITE_LIST = { route: '/w_tb_e601w', rowsStateKey: 'Acts' };

/**
 * 화면이 띄운 알림 중 **사람에게 말이 되는 것만** 고른다.
 *
 * 창을 여는 버튼은 그 창의 표를 통째로 알림으로 흘린다 — 견적서에서 자재청구서를
 * 누르니 `선택 품번 품명 규격 단위 동호기 …` 가 두 번 반복돼 답변에 실렸다.
 * 문장이 된 것만 남기고, 그래도 길면 자른다.
 */
const NOTICE_LIMIT = 200;

/**
 * 저장을 누른 뒤 사용자에게 할 말. 드라이버가 **저장된 증거(성공 알림·새 줄)를 못 봤다**고 하면
 * 「저장했습니다」라고 하지 않는다 — 거래처등록이 저장되지 않았는데 그렇게 답했다(2026-10-05).
 * 다시 누르지는 않는다. 진짜 저장된 경우 두 벌이 된다(고장접수 R1).
 */
function savedWord(committed: {
  saved: boolean; unconfirmed?: boolean; assigned?: { label: string; value: string };
}): string {
  // 정해 넣은 코드는 사람이 처음 듣는 값이다. 안 말하면 무슨 번호로 들어갔는지 목록을 뒤져야 한다.
  const assigned = committed.assigned
    ? ` ${topicParticle(committed.assigned.label)} ${committed.assigned.value}입니다.`
    : '';
  return committed.unconfirmed
    ? `저장 단추를 눌렀지만 저장됐는지 화면에서 확인하지 못했습니다. 목록에서 확인해 주세요.${assigned}`
    : `저장했습니다.${assigned}`;
}

/**
 * 저장했다고 말할 때 덧붙일 화면 안내. 화면의 **성공 알림은 뺀다** — 「저장했습니다. 화면 안내:
 * 저장: 서버에 잘 저장했습니다」로 같은 말을 두 번 했다(2026-10-05). 다른 안내(경고 등)는 남긴다.
 */
function noticesAfterSave(notices: string[]): string {
  return readableNotices(notices.filter((notice) => !(
    /잘 저장|저장(되었|되엇|하였|했)|일괄 저장되|처리되었|완료되었|등록되었/.test(notice) && !/오류|실패/.test(notice)
  )));
}

function readableNotices(notices: string[]): string {
  return notices
    .map((notice) => notice.trim())
    // **`하십시요` 와 `하십시오` 를 둘 다 받는다.** 이 ERP 는 두 표기를 섞어 쓴다 —
    // 자격증 탭이 `교육기관을 입력하십시오` 라고 말했는데 이 자리가 그것을 버려,
    // 사람에게는 이유 없는 `멈췄습니다` 만 나갔다.
    .filter((notice) => /니다|되었|완료|실패|오류|없음|하십시[요오]|하세요/.test(notice))
    .map((notice) => (notice.length > NOTICE_LIMIT ? `${notice.slice(0, NOTICE_LIMIT)}…` : notice))
    .join(' / ');
}

/**
 * **버튼을 누른 뒤 할 말** — 총무팀 직원이 읽는다. 드라이버는 물음마다 `단추: 제목: 본문 — 「예」로 답했다` 를 남기고,
 * 고르는 창은 본문과 단추 글자까지 통째로 남긴다. 그대로 실으면 창 문구 세 개가 이어진 긴 덩어리가 나갔다
 * (정보센터연동, 2026-10-14). 물음은 **무엇에 어떻게 답했는지 한 마디**로, 화면이 마지막에 한 말은 본문만 싣는다.
 */
export function operateSaid(notices: string[], button: string, confirm?: string): string {
  const steps: string[] = [];
  const told: string[] = [];
  const strip = (text: string): string => (text.startsWith(`${button}:`) ? text.slice(button.length + 1) : text).trim();
  for (const raw of notices) {
    const notice = strip(raw.trim());
    const answered = /^(.*?) — 「(.+?)」로 답했다$/.exec(notice);
    if (answered) {
      const title = answered[1]!.split(':')[0]!.trim();
      const pressed = answered[2]!;
      steps.push(/엘맨 매니저/.test(title) && /^예/.test(pressed)
        ? '엘맨 매니저를 실행했습니다'
        : `「${title}」에 「${pressed}」로 답했습니다`);
      continue;
    }
    if (confirm && notice.includes(confirm)) {
      steps.push(`「${confirm}」로 진행했습니다`);
      continue;
    }
    // `완료: 연동이 완료되었습니다` 처럼 제목이 본문을 되풀이하면 본문만.
    const parts = notice.split(/:\s*/);
    told.push(parts.length > 1 && parts[0]!.length <= 12 ? parts.slice(1).join(': ') : notice);
  }
  const said = readableNotices(told);
  return [steps.length ? `${steps.join(', ')}.` : '', said ? `화면 안내: ${said}` : ''].filter(Boolean).join(' ');
}

/** `20261002` → `10월 2일`. 올해가 아니면 해를 붙인다. 날짜가 아니면 그대로. */
function spokenDate(value: string, today = new Date()): string {
  const digits = value.replace(/\D/g, '');
  if (!/^\d{8}$/.test(digits)) return value;
  const year = Number(digits.slice(0, 4));
  const day = `${Number(digits.slice(4, 6))}월 ${Number(digits.slice(6, 8))}일`;
  return year === today.getFullYear() ? day : `${year}년 ${day}`;
}

/**
 * 이미 있는 줄의 칸을 고친 것을 사람 말로 — `10월 5일 김철수 줄의 근태를 지각으로, 출근시간을 9시 40분으로`.
 *
 * 일근태등록의 고치기는 머리 칸이 하나도 없다. 그대로 두면 답이 「올려 둔 문서를 열어 입력했습니다.
 * 저장했습니다.」뿐이라 무엇을 바꿨는지 사람이 모른다(2026-10-05). 칸 이름은 스킬이 아는 글자,
 * 없으면 카탈로그의 열 이름이다 — 둘 다 없으면 말하지 않는다(내부 이름을 내보내지 않는다).
 */
function editedCellsSaid(
  edit: { match?: Record<string, string>; cellsMatch?: Record<string, string>; cells?: Record<string, string> },
  screen: AgentScreen | undefined,
  skill: AgentSkill | undefined,
): string {
  const labelOf = (key: string): string | undefined => skill?.editCellLabels?.[key]
    ?? screen?.grids.flatMap((grid) => grid.columns).find((column) => column.id === key)?.text;
  // `0940` → `9시 40분`. 시각 칸만 — 금액·코드를 시각으로 읽지 않는다.
  const voice = (key: string, value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (/time$/i.test(key) && /^\d{4}$/.test(digits)) {
      const [hour, minute] = [Number(digits.slice(0, 2)), Number(digits.slice(2))];
      return minute ? `${hour}시 ${minute}분` : `${hour}시`;
    }
    return spokenDate(value);
  };
  const changes = Object.entries(edit.cells ?? {})
    .filter(([, value]) => String(value ?? '').trim())
    .map(([key, value]) => {
      const label = labelOf(key);
      return label ? `${objectParticle(label)} ${withParticle(voice(key, String(value)), '으로', '로')}` : '';
    })
    .filter(Boolean);
  if (!changes.length) return '';
  const target = [...Object.values(edit.match ?? {}), ...Object.values(edit.cellsMatch ?? {})]
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)
    .map((value) => spokenDate(value))
    .join(' ');
  return `${target ? `${target} 줄의 ` : ''}${changes.join(', ')} 바꿨습니다.`;
}

/**
 * **고르는 순간 반영되는 칸**을 바꾸는 마지막 걸음. 저장과 같은 Auto 게이트다.
 *
 * 점검계획의 점검일자는 달력에서 날을 누르면 곧바로 옮겨진다. 되돌리기 어렵다 — 그래서
 * Auto 가 꺼져 있으면 창을 열어 그 줄까지 짚어 두고 **무엇을 고르면 되는지** 말하고 멈춘다.
 * 사람은 그 자리에서 날 하나만 누르면 된다.
 *
 * 저장 막힘을 되짜는 일반 길(`settleBlocked`)로 보내지 않는다. 그 길은 초안을 다시 채우고
 * 저장을 다시 누르는데, 이 칸은 누르는 것이 곧 반영이라 두 번 누르면 두 번 옮긴다.
 */
async function finishDeferred(
  deferred: {
    row: string; cells: Record<string, string>; name?: string; before?: Record<string, string>;
    window?: { action: string; button: string; picked: string };
  },
  route: string,
  where: string,
  deps: Deps,
  task: TaskSpec | undefined,
): Promise<Answer> {
  if (deferred.window) return finishWindow({ ...deferred, window: deferred.window }, route, where, deps, task);
  const skill = (deps.skills ?? []).find((one) => one.deferCells && one.preferredRoutes?.includes(route));
  const screen = deps.screens.find((one) => one.route === route);
  // 칸 이름은 사람이 창에서 보는 글자로 — 스킬이 알면 그것, 아니면 카탈로그의 열 이름.
  const labelOf = (key: string): string => skill?.editCellLabels?.[key]
    ?? screen?.grids.flatMap((grid) => grid.columns).find((column) => column.id === key)?.text
    ?? '그 칸';
  // `2일로` — ㄹ 받침 뒤에는 `으로` 가 아니라 `로` 다. 날짜는 늘 `일` 로 끝난다.
  const toward = (value: string): string => {
    const said = spokenDate(value);
    return /[일월]$/.test(said) ? `${said}로` : withParticle(said, '으로', '로');
  };
  const who = deferred.name ?? deferred.row;
  const moves = Object.entries(deferred.cells).map(([key, value]) => {
    const was = String(deferred.before?.[key] ?? '').trim();
    return `${objectParticle(labelOf(key))} ${was ? `${spokenDate(was)}에서 ` : ''}${toward(value)}`;
  }).join(', ');
  const picks = Object.entries(deferred.cells)
    .map(([key, value]) => `${objectParticle(labelOf(key))} ${toward(value)}`)
    .join(', ');
  const base = { code: '', sources: [where], task };
  if (!deps.context?.auto || !deps.session.commit) {
    return {
      ...base,
      text: `${where}에서 ${deferred.row} 줄을 열어 짚어 두었습니다. ${picks} 고르면 바로 반영됩니다.`
        + ' 되돌리기 어려운 변경이라 Auto 가 꺼져 있을 때는 바꾸지 않았습니다.',
    };
  }
  try {
    const committed = await deps.session.commit({ routePath: route, auto: true, action: 'save' });
    // 못 옮긴 까닭은 화면 문구뿐이다. 문장 꼴이 아니어도 버리지 않는다 — 짧게 자를 뿐이다.
    const said = readableNotices(committed.notices) || committed.notices.join(' / ').slice(0, NOTICE_LIMIT);
    if (committed.saved) return { ...base, text: `${where}에서 ${who}의 ${moves} 옮겼습니다.` };
    return {
      ...base,
      text: `${where}에서 ${who}의 ${picks} 옮기지 못했습니다.${said ? ` 화면 안내: ${said}` : ''}`
        + ' 화면에서 확인해 주세요.',
    };
  } catch (error) {
    return { ...base, text: `${where}에서 ${who}의 ${picks} 옮기지 못했습니다. ${userMessage(error)}` };
  }
}

/**
 * **창에서 골라 두고 단추를 미룬 일**의 마지막 걸음 — 예금관리의 입금처리. 저장과 같은 Auto 게이트다.
 *
 * 준비가 그 줄의 처리 창을 열고 미수를 골라 두었다. Auto 가 꺼져 있으면 창을 연 채로 **무엇을
 * 누르면 되는지** 말하고 멈춘다 — 사람은 그 자리에서 `확인` 하나만 누르면 된다. 켜져 있으면
 * 확정을 한 번만 부른다. 못 했어도 되짜는 길(`settleBlocked`)로 보내지 않는다 — 그 길은 저장을
 * 다시 누르는데, 창 단추를 두 번 누르면 같은 입금이 두 번 맞춰질 수 있다.
 */
async function finishWindow(
  deferred: { row: string; window: { action: string; button: string; picked: string } },
  route: string,
  where: string,
  deps: Deps,
  task: TaskSpec | undefined,
): Promise<Answer> {
  const { action, button, picked } = deferred.window;
  const base = { code: '', sources: [where], task };
  if (!deps.context?.auto || !deps.session.commit) {
    return {
      ...base,
      text: `${where}에서 ${deferred.row} 줄의 ${action} 창을 열어 ${objectParticle(picked)} 골라 두었습니다.`
        + ` 창의 「${button}」을 누르면 ${action}됩니다.`
        + ' 되돌리기 어려운 변경이라 Auto 가 꺼져 있을 때는 누르지 않았습니다.',
    };
  }
  try {
    const committed = await deps.session.commit({ routePath: route, auto: true, action: 'save' });
    const said = readableNotices(committed.notices) || committed.notices.join(' / ').slice(0, NOTICE_LIMIT);
    if (committed.saved) {
      return { ...base, text: `${where}에서 ${deferred.row} 줄을 ${action}했습니다. 맞춘 것: ${picked}.` };
    }
    return {
      ...base,
      text: `${where}에서 ${deferred.row} 줄을 ${action}하지 못했습니다.${said ? ` 화면 안내: ${said}` : ''}`
        + ' 화면에서 확인해 주세요.',
    };
  } catch (error) {
    return { ...base, text: `${where}에서 ${deferred.row} 줄을 ${action}하지 못했습니다. ${userMessage(error)}` };
  }
}

/** 되묻되 **하던 작업을 놓지 않는다.** 다음 턴이 그것을 이어받아야 한다. */
/**
 * 걸음 도중에 멈췄을 때 하는 말.
 *
 * **어디까지 저장됐는지가 핵심이다.** 되돌리지 않으므로, 사람이 이어서 하려면 무엇이
 * 이미 들어갔는지 알아야 한다. 아무것도 못 했으면 그 사실을 그대로 말한다.
 */
/**
 * 화면이 이름을 댄 모자란 칸들.
 *
 * `필수입력확인: 부서를 선택하세요` 처럼 화면은 **하나씩만** 알려 준다. 그때마다 멈추면
 * 사람이 대여섯 번 답해야 한다 — 인사등록에서 주민번호·근속기준일·부서가 차례로 나왔다.
 */
function demanded(notices: string[]): string[] {
  // **이름 뽑기는 `blocked.ts` 것 하나만 쓴다.** 여기 따로 두었던 정규식은 조사를 필수로
  // 보고 `기재` 를 몰라서, `필수입력확인: 호기 확인하십시요` 를 「이름을 못 댄 거절」로
  // 읽고 모델에게 되돌렸다 — 정작 분류기는 그 이름을 읽을 줄 알았다.
  return namedLabels(notices);
}

/**
 * 초안 시도가 죽기 전에 **화면에 실제로 들어간** 칸들.
 *
 * 계획이 낸 칸을 읊으면 비어 있는 칸을 넣었다고 보고하게 된다. 드라이버가 찍어 보낸
 * 스탬프(`applied`)만 믿는다 — 스탬프가 없으면 아직 아무것도 안 채운 것이다.
 */
function filledSoFar(
  error: unknown,
  labels: Map<string, string>,
  skip: string | undefined,
): string {
  const landed = (error as { applied?: string[] }).applied ?? [];
  // 같은 이름의 칸이 둘인 화면이 있다(휴가신청서의 신청기간·시간) — 이름은 한 번만 말한다.
  const done = Array.from(new Set(landed.filter((key) => key !== skip).map((key) => labels.get(key) ?? key)));
  return done.length ? `${done.join('·')}까지 넣었습니다. ` : '';
}

function stoppedAt(where: string, finished: string[], failed: string, why: string): string {
  const done = finished.length
    ? `${finished.join('·')}까지 저장했습니다. `
    : '아직 아무것도 저장하지 못했습니다. ';
  return `${where} 에 ${done}${failed} 에서 멈췄습니다.${why ? ` ${why}` : ''}`;
}

/** 칸이 화면 목록에서 고르는 것인가 — 팝업·콤보·코드표(blocked.ts `kindOf` 의 목록 갈래와 같은 조건). */
function isListField(field: AgentField, codeLists: CodeLists | undefined): boolean {
  if (field.searchPopup) return true;
  if (field.options?.length || field.optionCodes?.length) return true;
  if (/combo|select|radio/i.test(field.widget)) return true;
  if (codeListFor(field, codeLists)?.items?.length) return true;
  return false;
}

/**
 * 계획의 되묻기 `question` 이 **현재 화면의 목록 칸**을 가리키는가(스펙 §2.2).
 *
 * 그런 칸은 값을 몰라도 되묻지 말고 비운 채 prepare 로 내야 한다 — 화면이 고르게 한다.
 * 통지형 질문이면 v2 `namedLabels` 로 칸 이름을 뽑고, 아니면 질문 글자에 칸 이름이
 * 그대로 들어 있는지 본다(`출장거리를 알려주세요` 의 `출장거리`).
 */
function questionAsksListField(
  question: string,
  screen: AgentScreen | undefined,
  codeLists: CodeLists | undefined,
): boolean {
  const listFields = (screen?.draftFields ?? []).filter((field) => isListField(field, codeLists));
  if (!listFields.length || !question.trim()) return false;
  const named = namedLabels([question]);
  return listFields.some((field) => {
    const label = plainLabel(field.label ?? field.key);
    if (!label) return false;
    if (named.some((one) => labelKey(one) === labelKey(label))) return true;
    return question.includes(label);
  });
}

/**
 * 되물은 선택지가 **그 화면 조회 조건 콤보**의 선택지인가. 그렇다면 그 칸을 돌려준다.
 *
 * 선택지가 둘 이상이고 모두 한 콤보의 선택지 안에 있을 때만 그렇다고 본다. 한 개라도
 * 벗어나면 화면이나 뜻을 묻는 것이다.
 */
function askedSearchCombo(
  options: string[] | undefined,
  screen: AgentScreen | undefined,
): AgentField | undefined {
  const asked = (options ?? []).map((one) => one.trim()).filter(Boolean);
  if (asked.length < 2 || !screen) return undefined;
  return screen.fields.find((field) => (
    (field.options?.length ?? 0) >= 2
    && asked.every((one) => field.options?.includes(one))
  ));
}

function asking(question: string, options: string[] | undefined, task?: TaskSpec): AskUser {
  // 같은 줄을 두 번 내밀지 않는다. 화면 목록이 가상 스크롤이라 훑는 동안 같은 행을
  // 두 번 담는 일이 있다 — `덕봉빌딩(두암정철) … 003` 이 나란히 두 번 나왔다.
  // 사람은 둘 중 무엇이 다른지 찾느라 멈춘다.
  const unique = options
    ? Array.from(new Set(options.map((option) => option.trim()).filter(Boolean)))
    : options;
  const error = new AskUser(question, unique);
  error.task = task;
  return error;
}

/**
 * 같은 화면을 **조건 값만 바꿔** 늘어놓은 계획을 한 번으로 접는다.
 *
 * 인사 보고서가 사업장 열둘을 각각 조회하는 계획을 세워 397초를 쓰고 조회 예산을
 * 다 태웠다(37/24). 화면에 `전체` 선택지가 있고, 사업장 순회가 정말 필요하면
 * 런타임이 따로 한다(`expandBusinessSites`). 프롬프트로도 막지만 계획은 흔들린다 —
 * 여기서 접는 편이 확실하다.
 *
 * 기간은 접지 않는다. 월 조건 하나뿐인 화면을 열두 달 도는 것은 옳은 계획이고,
 * 날짜 칸에는 `전체` 가 없으므로 이 함수에 걸리지도 않는다.
 */
function collapseFanned(
  retrievals: Extract<QueryPlan, { kind: 'retrieve' }>['retrievals'],
  screens: Deps['screens'],
): Extract<QueryPlan, { kind: 'retrieve' }>['retrievals'] {
  const groups = new Map<string, typeof retrievals>();
  for (const retrieval of retrievals) {
    // 펼쳐 읽는 범위가 다르면 다른 조회다 — 같은 달을 범위만 나눠 낸 것을 접으면 첫 범위만 읽는다.
    const key = `${retrieval.route}|${retrieval.rowsStateKey}|${JSON.stringify(retrieval.sweep ?? null)}`;
    groups.set(key, [...(groups.get(key) ?? []), retrieval]);
  }
  const collapsed: typeof retrievals = [];
  for (const group of Array.from(groups.values())) {
    const folded = group.length >= 3 ? foldGroup(group, screens) : undefined;
    if (folded) collapsed.push(folded);
    else collapsed.push(...group);
  }
  return collapsed;
}

/** 한 조건만 다르고 그 칸에 `전체` 가 있으면 그 하나로 합친다. 아니면 그대로 둔다. */
function foldGroup(
  group: Extract<QueryPlan, { kind: 'retrieve' }>['retrievals'],
  screens: Deps['screens'],
): Extract<QueryPlan, { kind: 'retrieve' }>['retrievals'][number] | undefined {
  const first = group[0];
  if (!first) return undefined;
  const keys = new Set(group.flatMap((one) => Object.keys(one.conditions)));
  const varying = Array.from(keys).filter((key) => new Set(
    group.map((one) => one.conditions[key] ?? ''),
  ).size > 1);
  // **똑같은 조회를 여러 번 낸 계획도 있다.** 조건이 하나도 안 다르면 그것은 한 번이면
  // 된다 — 실측으로 매출현황을 열한 번 똑같이 조회하고 답변 끝에도 열한 번 적어 냈다.
  if (varying.length === 0 && group.length > 1) {
    // eslint-disable-next-line no-console
    console.debug('[agent] 조회 접기', `${first.route} 같은 조회 ${group.length}개 → 한 번`);
    return first;
  }
  if (varying.length !== 1) return undefined;
  const key = varying[0] as string;
  const field = screens.find((screen) => screen.route === first.route)
    ?.fields.find((one) => one.key === key);
  const all = field?.optionCodes?.find((choice) => choice.label === '전체')?.label
    ?? field?.options?.find((option) => option === '전체');
  if (!all) return undefined;
  // eslint-disable-next-line no-console
  console.debug('[agent] 조회 접기', `${first.route} ${key} ${group.length}개 → 전체 한 번`);
  return {
    ...first,
    conditions: { ...first.conditions, [key]: all },
    requiredColumns: Array.from(new Set(group.flatMap((one) => one.requiredColumns ?? []))),
  };
}

/**
 * **계산이 따라야 할 업무 규칙.** 이번 계획의 화면을 권장 화면으로 가진 스킬의 지침이다 —
 * 「거리 0 은 ERP 홈 화면 출근」 같은 셈법은 화면에도 표 칸에도 없고 스킬에만 있다.
 */
export function skillRules(
  skills: AgentSkill[],
  plan: Extract<QueryPlan, { kind: 'retrieve' }>,
): string {
  const routes = plan.retrievals.map((retrieval) => retrieval.route);
  return skills
    // 셈법 스킬만 — 쓰기 절차·보고서 꾸밈 지침까지 「반드시 따른다」로 넣으면 계산이 헷갈린다(검토, 2026-10-20).
    .filter((skill) => skill.kind === 'metric' && skill.instructions?.trim()
      && (skill.preferredRoutes ?? []).some((route) => routes.includes(route)))
    .map((skill) => `[${skill.name}]
${skill.instructions.trim()}`)
    .join('\n\n');
}

/**
 * 그 조건들을 **모두** 가진 화면. 같은 업무 영역부터 센다.
 *
 * `기간이 없는 화면이다` 라고만 말하면 모델은 카탈로그에서 이름이 그럴듯한 화면을
 * 또 고른다 — 그것도 기간이 없다. 고를 수 있는 것을 손에 쥐여 주는 편이 빠르다.
 */
function screensWith(
  screens: { route: string; name: string; module: string; fields: { key: string }[] }[],
  conditions: string[],
  besides: string,
  limit = 6,
): string[] {
  const source = screens.find((screen) => screen.route === besides);
  const able = screens.filter((screen) => screen.route !== besides
    && conditions.every((key) => screen.fields.some((field) => field.key === key)));
  // 같은 모듈을 앞에 둔다. 공사 이야기에 회계 화면을 먼저 보여 줄 이유가 없다.
  const ordered = [
    ...able.filter((screen) => screen.module === source?.module),
    ...able.filter((screen) => screen.module !== source?.module),
  ];
  return ordered.slice(0, limit).map((screen) => `${screen.route}(${screen.name})`);
}

/**
 * 코드 열에 **그 코드가 무엇인지**를 적어 준다.
 *
 * 조회 결과의 `rtclafi` 에는 `001`·`002` 가 들어온다. 열 이름이 `입퇴사` 라는 것만
 * 아는 계산은 `입사` 로 걸러 **입사 0명, 퇴사 0명**을 답했다 — 오류도 경고도 없이.
 * 뜻은 같은 화면의 검색조건 콤보가 들고 있다(`001=입사`). 그것을 열 이름에 붙인다.
 *
 * 행 값을 넘기는 것이 아니다. 화면이 소스에 적어 둔 선택지일 뿐이라 사람의 자료는
 * 여전히 드라이버와 워커 사이에만 오간다.
 */
function withCodeLegend(
  columns: { id: string; text?: string }[],
  fields?: { key: string; optionCodes?: { value: string; label: string }[] }[],
): { id: string; text?: string }[] {
  if (!fields?.length) return columns;
  return columns.map((column) => {
    const codes = fields.find((field) => field.key === column.id)?.optionCodes;
    if (!codes?.length) return column;
    const legend = codes.map((code) => `${code.value}=${code.label}`).join(', ');
    return { ...column, text: `${column.text ?? column.id}(${legend})` };
  });
}

/**
 * 조회가 전부 비었을 때 **사람이 말하지 않은 상태 조건**을 「전체」로 넓혀 한 번 더 본다.
 *
 * 화면에는 사람이 고르지 않아도 걸려 있는 조건이 있다. 전자세금계산서발행은 응답상태가
 * 처음부터 「진행중」이라, 「7월 세금계산서」가 0건으로 나왔다 — 발행한 계산서는 있었다.
 * 사람은 0건을 보면 조건 칸부터 훑어 「전체」로 바꿔 다시 누른다. 그것을 한다.
 *
 * 넓히는 것은 「전체(`%`)」 선택지가 있는 목록 칸 가운데 계획이 값을 정하지 않은 것뿐이다.
 * 사람이 말한 조건(「미처리 입금」의 처리구분)은 계획에 들어 있으므로 건드리지 않는다.
 * 넓혀서 나온 행은 원래 물음의 답이 아닐 수 있다 — 그래서 답이 그 사실을 밝히게 한다.
 */
async function widenUnsaid(
  plan: Extract<QueryPlan, { kind: 'retrieve' }>,
  deps: Deps,
): Promise<{
  found: boolean;
  /** 넓힌 조건 칸의 사람 이름. `응답상태` 처럼. */
  names: string;
  plan: Extract<QueryPlan, { kind: 'retrieve' }>;
  retrieved: Record<string, Retrieved>;
  fact: string;
} | undefined> {
  const widenedLabels: string[] = [];
  const retrievals = plan.retrievals.map((retrieval) => {
    const screen = deps.screens.find((one) => one.route === retrieval.route);
    const extra: Record<string, string> = {};
    for (const field of screen?.fields ?? []) {
      if (field.key in retrieval.conditions) continue;
      const all = field.optionCodes?.find((code) => code.value === '%' || code.label === '전체');
      if (!all) continue;
      extra[field.key] = all.value;
      widenedLabels.push(plainLabel(field.label ?? field.key));
    }
    // 드라이버는 같은 이름의 조회를 두 번 받지 않는다 — 꼬리를 붙여 새 조회로 낸다.
    return {
      ...retrieval,
      label: `${retrieval.label.replace(/__(?:retry|settled|wide)\d*$/, '')}__wide`,
      conditions: { ...retrieval.conditions, ...extra },
    };
  });
  if (!widenedLabels.length) return undefined;
  const names = Array.from(new Set(widenedLabels)).join('·');
  const retrieved: Record<string, Retrieved> = {};
  try {
    for (const retrieval of retrievals) {
      const screen = deps.screens.find((one) => one.route === retrieval.route);
      // eslint-disable-next-line no-await-in-loop
      retrieved[retrieval.label] = await deps.session.retrieve({
        label: retrieval.label,
        routePath: retrieval.route,
        rowsStateKey: retrieval.rowsStateKey,
        conditions: retrieval.conditions,
        scalarKeys: screen?.scalars?.map((scalar) => scalar.key),
        force: true,
      });
    }
  } catch (error) {
    // 넓혀 보는 것은 덤이다. 못 봤다고 빈 답까지 잃지 않는다.
    // eslint-disable-next-line no-console
    console.debug('[agent] 조건 넓혀 보기 실패', String(error).slice(0, 200));
    return undefined;
  }
  const found = Object.values(retrieved).some((one) => one.rows.length > 0);
  // eslint-disable-next-line no-console
  console.debug('[agent] 빈 조회 — 말하지 않은 조건을 전체로', names, found ? '행 있음' : '그래도 없음');
  return {
    found,
    names,
    plan: { ...plan, retrievals },
    retrieved,
    fact: found
      ? `화면에 처음부터 걸려 있던 ${names} 조건으로는 한 건도 없었다. 사용자가 말하지 않은 조건이라`
        + ` ${names}을(를) 전체로 넓혀 다시 조회한 결과다. 답 첫머리에 그 사실을 밝혀라`
        + ' (예: "기본 조건으로는 없어서 전체로 넓혀 봤습니다").'
      : `${names}을(를) 전체로 넓혀 다시 조회해도 한 건도 없었다. 그 조건 때문이 아니라는 뜻이다.`,
  };
}

/**
 * 방금 저장한 문서를 목록에서 가리킬 값. 초안에 넣은 값 중 그 화면 목록 표의 열인 것(업무일지의
 * 업무일자). 날짜는 목록에 `20261016` 으로 있다. 고를 것이 없으면 `undefined` — 그대로 누른다.
 */
function savedRow(
  route: string,
  fields: Record<string, string>,
  deps: Deps,
): { stateKey: string; match: Record<string, string> } | undefined {
  const grid = deps.screens.find((screen) => screen.route === route)?.grids[0];
  if (!grid) return undefined;
  const match: Record<string, string> = {};
  for (const [key, value] of Object.entries(fields)) {
    if (!grid.columns.some((column) => column.id === key) || !String(value ?? '').trim()) continue;
    match[key] = /^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? String(value).replace(/-/g, '') : String(value);
  }
  return Object.keys(match).length ? { stateKey: grid.key, match } : undefined;
}

/** 사람이 결재에 올리라고 한 말. 「결재 올려」·「상신해」·「결재까지」. 결재 「받아」는 남의 일이라 아니다. */
const SUBMIT_SAID = /상신|결재\s*(?:도|까지)?\s*(?:올려|올리|넣어|상신)|결재\s*까지/;

/** 묶음 확인을 기다리는 되묻기 표시. */
const BATCH_PENDING = '__batch';
/** 한 건 답에서 「못 했다」를 가리는 말. 하나라도 있으면 그 건은 실패로 센다. */
const BATCH_FAILED = /되지 않았|하지 못했|못했습니다|확인하지 못했|않았습니다|막혔|없습니다/;

/** 묶음의 한 건을 사람 말로 — 넣을 값. */
function batchLine(item: Extract<QueryPlan, { kind: 'prepare' }>, deps: Deps): string {
  const screen = deps.screens.find((one) => one.route === item.route);
  const label = (key: string) => plainLabel(
    screen?.draftFields?.find((field) => field.key === key)?.label
      ?? screen?.fields.find((field) => field.key === key)?.label ?? key,
  );
  const values = Object.entries(item.fields).filter(([, value]) => String(value ?? '').trim())
    .map(([key, value]) => `${label(key)} ${/^\d{4}-?\d{2}-?\d{2}$/.test(String(value)) ? spokenDate(String(value)) : value}`);
  // 사람이 확인하는 목록이다 — 「줄 1개」로는 무엇을 하는지 모른다(당직 묶음이 그렇게 나왔다, 2026-10-13).
  // 날(`calendarDay`)과 줄에 넣을 값을 그대로 보인다.
  const day = item.calendarDay ? spokenDate(item.calendarDay) : '';
  const rowValues = (item.rows?.items ?? []).map((row) => Object.values(row).filter((value) => String(value ?? '').trim())
    .join(' ')).filter(Boolean);
  const rows = rowValues.length ? rowValues.join(', ') : '';
  return [day, values.join(', '), rows].filter(Boolean).join(' · ') || (screen?.name ?? item.route);
}

/**
 * **여러 건을 보여 주고 한 번 묻는다.** 사람이 본 목록이 곧 할 일이다 — 확인 뒤에는 그대로 한다.
 * 저장은 Auto 일 때만 하므로 꺼져 있으면 목록만 보인다.
 */
async function offerBatch(
  made: Extract<QueryPlan, { kind: 'batch' }>,
  deps: Deps,
  task?: TaskSpec,
): Promise<Answer> {
  const where = deps.screens.find((one) => one.route === made.items[0]?.route)?.name ?? '';
  const shown = made.items.slice(0, 15).map((item) => `- ${batchLine(item, deps)}`).join('\n');
  const more = made.items.length > 15 ? `\n- 그 밖에 ${made.items.length - 15}건` : '';
  const sources = where ? [where] : [];
  if (!deps.context?.auto) {
    return {
      text: `${where} ${made.items.length}건을 하려면 Auto 를 켜 주세요.\n${shown}${more}`, code: '', sources, task,
    };
  }
  const options = ['실행', '취소'];
  const base: TaskSpec = task ?? {
    intent: 'prepare', capability: '묶음', goal: `${where} ${made.items.length}건`,
    dimensions: [], operations: [], filters: {}, unresolved: [],
  };
  throw asking(
    `${where}에서 다음 ${made.items.length}건을 할까요?\n${shown}${more}`,
    options,
    { ...base, pending: { field: BATCH_PENDING, wanted: JSON.stringify(made.items), options } },
  );
}

/**
 * 확인받은 묶음을 차례로 한다. 건마다 정해 둔 계획으로 한 턴을 돈다(`presetPlan`) — 저장 판정·되묻기·
 * 막힘 처리가 홑 요청과 같다. 한 건이 막혀도 나머지는 하고, 막힌 것을 모아 말한다.
 */
async function runBatch(answer: string, wanted: string, deps: Deps): Promise<Answer> {
  if (answer === '취소') return { text: '하지 않았습니다.', code: '', sources: [] };
  const items = JSON.parse(wanted) as Extract<QueryPlan, { kind: 'prepare' }>[];
  const done: string[] = [];
  const failed: string[] = [];
  for (let index = 0; index < items.length; index += 1) {
    const item = items[index]!;
    const line = batchLine(item, deps);
    try {
      // eslint-disable-next-line no-await-in-loop
      const one = await askOnce(`(${index + 1}/${items.length}) ${line}`, {
        ...deps,
        taskChat: undefined,
        context: { ...deps.context, presetPlan: item, task: undefined, history: [] },
      });
      // 됐다는 말은 일마다 다르다(저장했습니다·옮겼습니다·입금처리했습니다). 못 했다는 말로 가린다.
      if (!BATCH_FAILED.test(one.text)) done.push(line);
      else failed.push(`${line} — ${one.text.replace(/\s+/g, ' ').slice(0, 120)}`);
    } catch (error) {
      const said = error instanceof AskUser ? error.question : userMessage(error);
      failed.push(`${line} — ${String(said).replace(/\s+/g, ' ').slice(0, 120)}`);
    }
  }
  const sources = Array.from(new Set(items.map((item) => deps.screens.find((one) => one.route === item.route)?.name ?? '')))
    .filter(Boolean);
  const why = failed.length ? `\n하지 못한 ${failed.length}건:\n${failed.map((one) => `- ${one}`).join('\n')}` : '';
  return { text: `${items.length}건 중 ${done.length}건을 저장했습니다.${why}`, code: '', sources };
}

/**
 * **결과가 나중에 오는 단추.** 대기열(waitQueue)에 안 올라가고 서버 알림으로만 끝을 알린다 — 「눌렀습니다」로
 * 끝내면 사람은 끝난 줄 안다. 어디서 결과를 보는지 붙인다.
 */
const LATER_RESULT: Record<string, string> = {
  '/w_tb_ia090 보내기': ' 신고 결과는 잠시 뒤 서버 알림으로 옵니다 — 화면에 뜨는 알림이나 응답상태(전체로 다시 조회)로 확인해 주세요.',
};

/** 결재 확인을 기다리는 되묻기 표시. 답 턴이 이것을 보고 계획 없이 결재한다. */
const APPROVE_PENDING = '__approve';
const APPROVAL_ROUTE = '/w_tb_e080_list';
/**
 * 문서 종류 코드. 미결함의 문서구분 콤보는 서버에서 받아 와 카탈로그에 선택지가 없다 — 「업무일지 승인해줘」가
 * 0건이었다(개발 테넌트, 2026-10-13). 앱 상수 `PAPERCD`(constants/global.ts)라 테넌트마다 같다.
 */
const PAPER_NAMES: Record<string, string> = {
  101: '업무일지', 201: '휴가신청서', 202: '제증명발행', 203: '휴무근무신청서', 301: '견적서', 302: '설치견적서',
  304: '사전검사일지', 311: '공사결과보고서', 401: '지출결의서', 402: '일일회계마감', 501: '발주서', 512: '당직근무보고서',
  521: '안전교육일지', 601: '해지보고서', 602: '전자계약서', 603: '민원처리보고서', 604: '영업보고서',
  605: '다발고장처리보고서', 606: '중대고장처리보고서', 701: '출장보고서', 702: '교육보고서', 704: '시간외근무보고서',
  801: '통합문서', 802: '회의록', 901: '자재청구서',
};

/**
 * **결재할 문서를 보여 주고 한 번 묻는다.** 미결함의 「승인」은 묻지 않고 바로 서버로 간다 — 그래서
 * 우리가 묻는다. 사람은 미결함을 줄줄이 승인한다(904건이 묶음, 사용 기록 2026-09-29~10-13). 사용자
 * 결정(2026-10-13): 목록을 보여 주고 한 번 확인받은 뒤 결재한다.
 */
async function offerApproval(
  made: Extract<QueryPlan, { kind: 'approve' }>,
  deps: Deps,
  task?: TaskSpec,
): Promise<Answer> {
  const listed = await deps.session.retrieve({
    label: `미결함-${Date.now()}`, routePath: APPROVAL_ROUTE, rowsStateKey: 'incompleteBoxList', conditions: {}, force: true,
  });
  const papers = deps.screens.find((screen) => screen.route === APPROVAL_ROUTE)?.fields
    .find((field) => field.key === 'papercd')?.optionCodes ?? [];
  const paperName = (code: unknown) => papers.find((one) => one.value === String(code ?? ''))?.label
    ?? PAPER_NAMES[String(code ?? '')] ?? '';
  const has = (value: unknown, part?: string) => !part
    || String(value ?? '').replace(/\s+/g, '').includes(part.replace(/\s+/g, ''));
  const rows = listed.rows.filter((row) => has(row.title, made.match?.title) && has(row.repopernm, made.match?.writer)
    && (has(paperName(row.papercd), made.match?.paper) || has(row.papercd, made.match?.paper)));
  const sources = [deps.screens.find((screen) => screen.route === APPROVAL_ROUTE)?.name ?? '미결함'];
  if (!rows.length) {
    return { text: `미결함에 ${made.match ? '말씀하신 ' : ''}결재할 문서가 없습니다.`, code: '', sources, task };
  }
  const line = (row: Record<string, unknown>) => [
    row.repodate ? spokenDate(String(row.repodate)) : '', row.repopernm, paperName(row.papercd), row.title,
  ].map((part) => String(part ?? '').trim()).filter(Boolean).join(' · ');
  const shown = rows.slice(0, 10).map((row) => `- ${line(row)}`).join('\n');
  const more = rows.length > 10 ? `\n- 그 밖에 ${rows.length - 10}건` : '';
  if (!deps.context?.auto) {
    return {
      text: `미결함에 ${rows.length}건이 있습니다.\n${shown}${more}\n결재하려면 Auto 를 켜 주세요.`,
      code: '',
      sources,
      task,
    };
  }
  const options = [made.action, '취소'];
  const wanted = JSON.stringify({
    action: made.action, remark: made.remark ?? '', appnums: rows.map((row) => String(row.appnum ?? '')),
  });
  const base: TaskSpec = task ?? {
    intent: 'prepare', capability: '결재', goal: `미결함 ${made.action}`,
    dimensions: [], operations: [], filters: {}, unresolved: [],
  };
  throw asking(
    `다음 ${rows.length}건을 ${made.action}할까요?${made.remark ? ` (지시사항: ${made.remark})` : ''}\n${shown}${more}`,
    options,
    { ...base, pending: { field: APPROVE_PENDING, wanted, options } },
  );
}

/** 확인받은 문서를 결재한다. 하나가 막혀도 나머지는 하고, 결과를 건마다 모아 말한다. */
async function runApproval(answer: string, wanted: string, deps: Deps): Promise<Answer> {
  const sources = ['미결함'];
  if (answer === '취소') return { text: '결재하지 않았습니다.', code: '', sources };
  if (!deps.session.approve) return { text: '이 앱에서는 결재를 할 수 없습니다.', code: '', sources };
  const spec = JSON.parse(wanted) as { action: '승인' | '반려' | '전결'; remark: string; appnums: string[] };
  try {
    const { results } = await deps.session.approve({
      appnums: spec.appnums, action: spec.action, remark: spec.remark, auto: Boolean(deps.context?.auto),
    });
    const done = results.filter((one) => one.done).length;
    const failed = results.filter((one) => !one.done);
    const why = failed.length
      ? ` ${failed.length}건은 하지 못했습니다 — ${Array.from(new Set(failed.map((one) => one.notice))).join(' / ')}.`
      : '';
    return { text: `${results.length}건 중 ${done}건을 ${spec.action}했습니다.${why}`, code: '', sources };
  } catch (error) {
    return { text: `결재하지 못했습니다. ${userMessage(error)}`, code: '', sources };
  }
}

/**
 * **저장한 문서를 결재에 올린다.** 사람이 올리라고 말한 초안에만(`submit`) 저장 성공 뒤에 부른다.
 *
 * 상신은 화면마다 일반 단추 「결재상신」 → 「결재를 상신하시겠습니까?」(확인) → 결재선 창(「닫기」가
 * 확인이다)으로 간다. 드라이버가 그 길을 알고, 올라갔는지는 단추가 「상신취소」로 바뀌었는지로 본다.
 * 성공 알림이 없는 화면이라 알림으로는 못 가른다. 저장은 이미 됐으니 상신이 막혀도 저장을 무르지 않는다.
 */
async function submitForApproval(route: string, deps: Deps, fields: Record<string, string> = {}): Promise<string> {
  if (!deps.session.operate) return ' 결재상신은 이 앱에서 할 수 없습니다.';
  try {
    const focus = savedRow(route, fields, deps);
    const { notices } = await deps.session.operate({
      routePath: route, button: '결재상신', auto: true, confirm: '확인', ...(focus ? { focus } : {}),
    });
    if (notices.some((notice) => /상신취소 단추로 바뀌었다/.test(notice))) return ' 결재도 올렸습니다.';
    // 결재선이 비면 창이 막는다 — 사람이 정할 일이라 그렇게 말한다(개발 테넌트에서 실제로 났다, 2026-10-13).
    if (notices.some((notice) => /결재라인에 결재자가/.test(notice))) {
      return ' 결재는 올리지 못했습니다. 결재선에 결재자가 없습니다 — 화면의 「결재 라인 설정」에서'
        + ' 결재자를 정해 주시면 다음부터 올릴 수 있습니다.';
    }
    const told = readableNotices(notices.filter((notice) => !/^결재상신: (?:선택|확인):/.test(notice)));
    return ` 결재상신을 눌렀지만 올라갔는지 확인하지 못했습니다.${told ? ` 화면 안내: ${told}` : ''}`;
  } catch (error) {
    return ` 결재는 올리지 못했습니다. ${userMessage(error)}`;
  }
}

/** 그 표의 자유 기재 칸 — 다 비어도 다시 계획할 일이 아니다(`classifyRetrieved`). */
function freeTextColumns(retrieval: { route: string; rowsStateKey: string }, screens: AgentScreen[]): string[] {
  const grid = screens.find((screen) => screen.route === retrieval.route)
    ?.grids.find((one) => one.key === retrieval.rowsStateKey);
  return (grid?.columns ?? []).filter((column) => column.concept === 'note').map((column) => column.id);
}

/** 연도·연월·날짜처럼 생긴 값. */
const PERIOD_VALUE = /^\d{4}(?:-?\d{2}){0,2}$/;

/**
 * 비어 있던 조회 가운데 아직 오지 않은 기간을 건 것. 2027년 연차생성이 비어 「확인할 수 없다」로 끝났다 —
 * 아직 생성 전인 해다(2026-10-22). 그렇다고 알려야 규칙으로 내다본 값을 예상이라 밝혀 말한다.
 */
function futurePeriodFacts(
  retrievals: { label: string; route: string; conditions: Record<string, string> }[],
  retrieved: Record<string, { rows: unknown[] } | undefined>,
  screens: AgentScreen[],
  today: string,
): string[] {
  const now = today.replace(/-/g, '');
  const ahead = (value: string) => {
    const digits = value.replace(/-/g, '');
    return PERIOD_VALUE.test(value) && digits > now.slice(0, digits.length);
  };
  return retrievals
    .filter((retrieval) => !retrieved[retrieval.label]?.rows.length
      && Object.keys(retrieval.conditions).some((key) => ahead(String(retrieval.conditions[key] ?? '').trim())))
    .map((retrieval) => `${readableSources([retrieval as never], screens).join(', ')} 은 아직 오지 않은 기간이라 자료가 생기기 전이다.`
      + ' 「자료가 없어 확인할 수 없다」로 끝내지 말고, 업무 규칙으로 내다볼 수 있으면 예상이라 밝히고 말하라.');
}

/** 물음이 때를 말했나. 말했으면 기간은 사람이 정한 것이라 넓히지 않는다. */
const SPOKEN_TIME = /오늘|어제|그제|내일|이번|지난|저번|다음|올해|작년|재작년|금년|전년|\d+\s*(?:년|월|일|주|분기)|분기|상반기|하반기|최근|요즘|부터|까지|기간|동안/;

/**
 * **찾는 물음이 비었으면 기간을 넓혀 한 번 더 본다.**
 *
 * 사람은 이름으로 찾다가 없으면 끝 날짜는 오늘에 두고 시작을 뒤로 당긴다 — 검색어로 찾은
 * 방문의 58%가 기간을 넓혔고, 많이 쓴 시작은 지난달 1일·올해 1월 1일·1년 넘게 전이었다
 * (사용 기록 2026-09-29~10-13, 471방문·84명·38테넌트). 화면을 열면 기간은 이번 달이나 오늘
 * 하루라서, 지난봄에 쓴 견적을 이름으로 찾으면 0건이 나온다. 에이전트는 콤보만 넓히고(`widenUnsaid`)
 * 기간은 그대로 둬서 「없습니다」로 답했다.
 *
 * 넓히는 것은 **물음이 때를 말하지 않았고**, 조회가 이름·검색어 같은 찾는 조건을 걸었을 때뿐이다.
 * 목록·건수 물음(「이번 달 견적 몇 건」)은 기간이 답의 일부라 건드리지 않는다.
 */
async function widenPeriod(
  plan: Extract<QueryPlan, { kind: 'retrieve' }>,
  question: string,
  deps: Deps,
  today = new Date(),
): Promise<{ plan: Extract<QueryPlan, { kind: 'retrieve' }>; retrieved: Record<string, Retrieved>; from: string } | undefined> {
  if (SPOKEN_TIME.test(question)) return undefined;
  const pad = (value: number) => String(value).padStart(2, '0');
  const end = `${today.getFullYear()}${pad(today.getMonth() + 1)}${pad(today.getDate())}`;
  const start = `${today.getFullYear() - 1}0101`;
  const retrievals = plan.retrievals.map((retrieval) => {
    const fields = deps.screens.find((one) => one.route === retrieval.route)?.fields ?? [];
    const period = fields.filter((field) => field.concept === 'period');
    const finds = Object.entries(retrieval.conditions).some(([key, value]) => {
      const concept = fields.find((field) => field.key === key)?.concept ?? '';
      return String(value ?? '').trim() !== '' && String(value) !== '%'
        && (concept === 'text' || concept.endsWith('.name'));
    });
    // 기간 칸이 둘(시작·끝)이고 8자리 날짜일 때만 — 월 칸(stmon)은 다른 이야기다.
    const [from, to] = period;
    if (!finds || !from || !to || period.length !== 2) return undefined;
    if (!/^\d{8}$/.test(String(retrieval.conditions[from.key] ?? end))) return undefined;
    return {
      ...retrieval,
      label: `${retrieval.label.replace(/__(?:retry|settled|wide)\d*$/, '')}__longer`,
      conditions: { ...retrieval.conditions, [from.key]: start, [to.key]: end },
    };
  });
  if (!retrievals.some(Boolean)) return undefined;
  const widened = plan.retrievals.map((retrieval, index) => retrievals[index] ?? retrieval);
  const retrieved: Record<string, Retrieved> = {};
  try {
    for (const retrieval of widened) {
      const screen = deps.screens.find((one) => one.route === retrieval.route);
      // eslint-disable-next-line no-await-in-loop
      retrieved[retrieval.label] = await deps.session.retrieve({
        label: retrieval.label,
        routePath: retrieval.route,
        rowsStateKey: retrieval.rowsStateKey,
        conditions: retrieval.conditions,
        scalarKeys: screen?.scalars?.map((scalar) => scalar.key),
        force: true,
      });
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.debug('[agent] 기간 넓혀 보기 실패', String(error).slice(0, 200));
    return undefined;
  }
  const found = Object.values(retrieved).some((one) => one.rows.length > 0);
  // eslint-disable-next-line no-console
  console.debug('[agent] 빈 찾기 — 기간을', `${start}~${end}`, found ? '행 있음' : '그래도 없음');
  return found ? { plan: { ...plan, retrievals: widened }, retrieved, from: start } : undefined;
}

/**
 * 빈 조회의 답에 붙이는 사실 — **어떤 조건으로 봤는지.**
 *
 * 「자료가 없습니다」만 들으면 사람은 기간이 틀렸는지, 이름이 틀렸는지, 정말 없는지 모른다.
 * 근거 줄이 답 끝에 붙지만 본문은 그것을 보지 못해 「다른 조건으로 볼까요」를 말할 수 없었다.
 */
function emptyConditionsFact(
  plan: Extract<QueryPlan, { kind: 'retrieve' }>,
  screens: AgentScreen[],
  /**
   * 넓혀 보자고 제안할지. 아직 오지 않은 기간이 빈 것이면 하지 않는다 — 「2027년 자료는 아직 생성 전」 답에
   * 「2026년 자료까지 넓혀 볼까요?」가 붙었다(2026-10-22).
   */
  suggest = true,
): string {
  return `조회는 정상으로 끝났고 행이 없었다. 본 화면과 조건: ${readableSources(plan.retrievals, screens).join(' / ')}.`
    + ' 답 첫 줄에 어떤 조건(기간 등)으로 봤는지 사람 말로 밝혀라.'
    + (suggest ? ' 넓혀 볼 만한 조건 하나를 물음으로 제안하라 (예: "지난달까지 넓혀 볼까요?").' : '');
}

/**
 * 질문 하나를 끝까지 처리한다.
 *
 * 계획은 LLM 이, 조회는 드라이버가, 계산은 워커가, 문장은 다시 LLM 이 만든다.
 * **행 값은 드라이버와 워커 사이에만 오간다** — LLM 은 컬럼 이름과 계산 결과만 본다.
 */
/**
 * 그 화면에서 목록으로 고르는 칸의 허용값. 코드와 이름을 둘 다 담는다.
 *
 * 드라이버는 무엇이 옳은지 모르게 둔다 — 판단은 여기서 하고 그쪽은 시키는 것만 지킨다.
 * 열거할 수 없는 목록(거래처 11,270개)과 못 받은 목록은 담지 않는다. 모르는 것을
 * 거절하면 맞는 값도 막힌다.
 */
/**
 * 그 화면에서 코드 칸과 짝이 되는 이름 칸. `{ gregicd: 'greginm' }` 꼴이다.
 *
 * 드라이버는 접미사(`cd`→`nm`)로 짐작하고 있었다. 그 짐작은 규약을 어기는 화면에서
 * 조용히 틀리는데, 초안 판정이 그 값을 보고 「우리가 넣은 것이 남았나」를 가른다.
 * 화면에서 관측한 짝이 있으면 그것이 맞다.
 */
function pairsOf(route: string, deps: Deps): Record<string, string> | undefined {
  const screen = deps.screens.find((candidate) => candidate.route === route);
  if (!screen) return undefined;
  const out: Record<string, string> = {};
  for (const field of [...(screen.draftFields ?? []), ...screen.fields]) {
    if (field.pairs && !out[field.key]) out[field.key] = field.pairs;
  }
  return Object.keys(out).length ? out : undefined;
}

function allowedValues(route: string, deps: Deps): Record<string, string[]> | undefined {
  if (!deps.codeLists) return undefined;
  const screen = deps.screens.find((candidate) => candidate.route === route);
  if (!screen) return undefined;
  const out: Record<string, string[]> = {};
  for (const field of [...(screen.draftFields ?? []), ...screen.fields]) {
    if (out[field.key]) continue;
    const list = codeListFor(field, deps.codeLists);
    const choices = list?.items?.length
      ? list.items.flatMap((item) => [item.code, item.name])
      : field.options ?? [];
    if (choices.length) out[field.key] = choices.filter(Boolean);
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * 코드 칸에 든 값을 **사람이 읽는 이름**으로 바꾼다.
 *
 * 사무직은 `직위=13` 을 읽지 못한다. 목록이 있으면 그 이름을 쓰고(`직위=사원`),
 * 없으면 아무 말도 아닌 숫자이므로 답에서 뺀다. 금액(`samt=200000`)은 코드 칸이
 * 아니므로 그대로 남는다.
 *
 * 돌려주는 값이 `undefined` 면 「이 칸은 답에 적지 마라」는 뜻이다.
 */
export function readableValue(
  field: AgentField | undefined,
  key: string,
  value: string,
  lists?: CodeLists,
): string | undefined {
  const text = String(value ?? '').trim();
  // **날짜 칸은 사람 말로.** 답이 `20261005`·`2026-10-05` 를 섞어 말했다(2026-10-05). 날짜 칸일
  // 때만 바꾼다 — 여덟 자리 숫자는 금액·번호일 수도 있다.
  const dateField = /date$/i.test(key) || /Date/.test(String(field?.widget ?? ''));
  if (dateField && /^\d{4}-?\d{2}-?\d{2}$/.test(text)) return spokenDate(text);
  if (!/(cd|id|code)$/i.test(key) || !/^\d+$/.test(text)) return text;
  const named = field && codeListFor(field, lists)?.items
    ?.find((item) => String(item.code) === text)?.name;
  return named ? String(named) : undefined;
}

/**
 * 넣은 칸 `[이름, 값]` 을 한 줄로. **같은 이름은 한 번만** — 휴가신청서는 신청기간·시간 칸이 둘씩이라
 * `신청기간=…, 신청기간=…` 으로 두 번 말했다(2026-10-05). 값이 같으면 하나, 다르면 `처음~끝` 으로 묶는다.
 */
export function joinFilled(pairs: Array<[string, string]>): string {
  const order: string[] = [];
  const values = new Map<string, string[]>();
  for (const [label, value] of pairs) {
    if (!values.has(label)) { values.set(label, []); order.push(label); }
    const seen = values.get(label)!;
    if (!seen.includes(value)) seen.push(value);
  }
  return order.map((label) => `${label}=${values.get(label)!.join('~')}`).join(', ');
}

/**
 * 흐리게 읽은 값을 답 끝에 적는다.
 *
 * **넣지는 않는다.** 그렇다고 말없이 버리면 사람은 그 칸이 왜 비었는지 모른 채 저장한다.
 * 읽은 값과 근거를 함께 내밀어, 사람이 한 줄로 확정할 수 있게 한다.
 *
 * 되묻기로 턴을 멈추지 않는 이유: 문서는 **올린 턴에만** 있다. 멈추면 다음 턴에는
 * 문서가 없어 또렷하게 읽은 값까지 통째로 잃는다. 확실한 것은 넣고, 흐린 것만 말한다.
 */
export function unsureNote(unsure: Fact[]): string {
  if (!unsure.length) return '';
  // 조사는 앞말에 맞춘다. `자격증번호은(는) 총무팀로` 는 사람이 읽는 글이 아니다.
  const lines = unsure.map((one) => `${topicParticle(one.label)} ${withParticle(one.value, '으로', '로')} 보입니다`
    + `${one.evidence ? ` (문서: ${one.evidence})` : ''}`).join(', ');
  return `

문서에서 또렷하게 읽지 못해 넣지 않은 것이 있습니다 — ${lines}.`
    + ' 맞으면 그대로 넣어 달라고 말해 주세요.';
}

/**
 * 올린 문서를 먼저 읽고, 읽은 값을 **글로 바꿔** 본 흐름에 넘긴다.
 *
 * 그림을 계획에 그대로 실으면 계획이 검증에 걸려 다시 돌 때마다 같은 그림이 또 나가고,
 * 판마다 다르게 읽는다. 한 번 읽어 글로 굳히면 그 뒤로는 흔들리지 않는다.
 *
 * 추출이 **아무것도 못 읽으면** 그림을 계획에 그대로 넘긴다 — 그 길이 살아 있어야
 * 문서를 못 읽었다고 턴이 헛돌지 않는다.
 */
/**
 * 목록에 없는 값을 말했을 때 하는 말.
 *
 * **계획이 대신 넣으려던 값을 밝힌다.** 실측(2026-09-01, 민원접수): `민원구분은 소음`
 * 이라 했더니 계획이 `001`(고객불만)을 냈다. 목록 안의 값이라 어느 검증도 안 걸리고
 * 화면에는 사용자가 말한 적 없는 민원구분이 남는다. 말하지 않으면 사람은 자기가 댄
 * 말이 조용히 바뀐 것을 모른다.
 */
export function unlistedQuestion(where: string, unlisted: Unlisted): string {
  const instead = unlisted.put
    ? ` 대신 ${withParticle(unlisted.put, '으로', '로')} 넣으려던 것을 멈췄습니다.`
    : '';
  return `${where} 에 ${topicParticle(unlisted.said)} 화면 목록에 없습니다.${instead}`
    + ` ${unlisted.label}만 골라 주세요.`;
}

export async function ask(question: string, deps: Deps): Promise<Answer> {
  // 서버에 맡긴 일(대기열)은 턴 어디서 생기든 여기서 거둬 답에 싣는다. 패널이 그 끝을 지켜본다.
  const queued: QueuedJob[] = [];
  // 전문가의 코드 뜻을 화면 칸에 채운다 — 계산이 「코드값 B=주의관찰」을 본다.
  const screens = deps.experts?.length ? applyExpertCodes(deps.screens, deps.experts) : deps.screens;
  // 같은 대화에서 이미 읽은 화면·조건은 다시 읽지 않는다(`recall.ts`).
  const followUp = (deps.context?.history?.length ?? 0) > 0;
  const session = rememberRetrievals(collectQueued(deps.session, queued), { followUp });
  const answer = await askWithFiles(question, { ...deps, screens, session });
  return queued.length
    ? { ...answer, queued, text: `${answer.text}

${queuedNote(queued)}` }
    : answer;
}

async function askWithFiles(question: string, deps: Deps): Promise<Answer> {
  const uploaded = deps.context?.files ?? [];
  // **읽지 못할 파일은 모델을 부르기 전에 돌려보낸다.** 게이트웨이도 같은 검사를 하지만,
  // 8MB 를 올려 놓고 왕복을 기다린 뒤에 거절당하면 그 시간이 통째로 버려진다.
  const trouble = uploadTrouble(uploaded);
  if (trouble) return { text: trouble, code: '', sources: [] };
  if (!uploaded.length) return askOnce(question, deps);

  const facts = await extractFacts(deps.chat, uploaded);
  const { sure, unsure } = splitFacts(facts);
  // eslint-disable-next-line no-console
  console.debug('[agent] 문서에서 읽음', facts.length, '또렷', sure.length, '흐림', unsure.length,
    facts.map((one) => `${one.label}=${one.value}(${one.confidence})`).join(' | ').slice(0, 400));
  if (!facts.length) return askOnce(question, deps);

  // 주민번호꼴 값은 **계획에 글로 넘기지 않는다.** 모델은 그 문장을 통째로 못 본 것처럼
  // 군다(`sensitive.ts`). 질문에 붙여 넘기면 거기서 가려지고, 화면에 넣기 직전에 되돌아온다.
  const RRN = /\d{6}[-\s]?[1-8]\d{6}/;
  const answer = await askOnce(question + describeFacts(uploaded, sure), {
    ...deps,
    context: {
      ...deps.context,
      // 글로 넘겼으니 그림은 더 보내지 않는다.
      files: undefined,
      facts: sure.filter((one) => !RRN.test(one.value))
        .map((one) => ({ label: one.label, value: one.value })),
    },
  });
  const note = unsureNote(unsure);
  return note ? { ...answer, text: `${answer.text}${note}` } : answer;
}

/** 사람이 화면을 새로 받으라고 했나 — 「다시 조회해줘」「새로고침」「최신으로」. 그때만 떠 있는 목록을 다시 쓰지 않는다. */
export function wantsFreshData(question: string): boolean {
  return /(다시|새로)\s*(조회|불러|받아|읽어)|새로\s*고침|최신/.test(question);
}

/** 계획이 고를 것 같은 화면 — 업무 도구의 첫 화면, 없으면 첫 스킬의 권장 화면. 목록에 있고 막히지 않은 것만. */
export function likelyRoute(
  capability: { targets: Array<{ route: string }> } | undefined,
  skills: Array<{ preferredRoutes?: string[] }>,
  screens: Array<{ route: string }>,
  offLimits: Set<string>,
): string | undefined {
  const guess = capability?.targets[0]?.route ?? skills[0]?.preferredRoutes?.[0];
  return guess && !offLimits.has(guess) && screens.some((one) => one.route === guess) ? guess : undefined;
}

/** 미리 시작한 계획과, 그것을 정할 때 쓴 스킬. */
type Speculation = { skills: string[]; plan: Promise<QueryPlan | undefined> };

/** 미리 계획해도 되는 턴인가 — 앞 대화·앞 작업·붙인 파일·되묻기 답·이어 쓰기가 하나도 없는 첫 질문. */
function speculable(question: string, deps: Deps): boolean {
  const context = deps.context;
  if (!deps.speculate) return false;
  return !context?.task && !(context?.history?.length) && !context?.presetPlan
    && !(context?.files?.length) && !answeredOurQuestion(context) && !refusalNote(context)
    && Boolean(question.trim());
}

/**
 * 과제 없이 계획을 시작한다. 스킬은 말에서 고른다(트리거 채점) — 과제가 고를 것과 같으면 쓰인다.
 * 실패해도 던지지 않는다. 버려진 계획은 그냥 끝나게 둔다.
 */
function speculatePlan(question: string, deps: Deps): Speculation {
  const routeNames = new Map(deps.screens.map((screen) => [screen.route, screen.name]));
  const skills = skillsBesideNamedScreen(question, selectSkillsForTurn(
    question, effectiveQuestion(question, deps.context), deps.skills ?? [], routeNames,
  ), deps.screens);
  // 과제를 기다리지 않고 화면도 연다. 과제 뒤 계획 앞에서 다시 부르면 이미 연 화면이라 아무것도 안 한다.
  const likely = likelyRoute(undefined, skills, deps.screens, new Set());
  if (likely && deps.session.warm) deps.session.warm(likely);
  // 정상 경로처럼 용어 뜻과 용어 화면을 싣는다 — 안 실으면 「시제 체크해줘」의 첫 후보가 엉뚱한 열둘이다(2026-10-23).
  const glossary = glossaryFor(question, deps.experts ?? []);
  const plan = makePlan(question + (glossary ? `\n\n${glossary}` : ''), deps.screens, deps.chat, {
    spoken: question,
    shortlist: deps.shortlist,
    codeLists: deps.codeLists,
    context: {
      ...deps.context,
      skills,
      allSkills: deps.skills ?? [],
      continuing: false,
      capability: undefined,
      termRoutes: termRoutes(question, deps.experts ?? [], deps.screens),
    },
  }).catch(() => undefined);
  return { skills: skills.map((skill) => skill.name), plan };
}

/**
 * 미리 한 계획을 쓸까. 정상 경로가 지금 막 고른 스킬·업무 도구가 그때와 같고 덧붙일 쪽지가 없을 때만이다.
 * 다르면 undefined — 부르는 쪽이 예전처럼 계획한다.
 */
async function adoptSpeculation(
  speculation: Speculation,
  now: {
    skills: Array<{ name: string; preferredRoutes?: string[] }>;
    capability: { targets: Array<{ route: string }> } | undefined;
    extra: string; offLimits: number; intent?: string;
    /** 과제를 못 읽었다 — 정상 경로도 원래 말과 말로 고른 스킬로 계획하니 미리 한 계획과 입력이 같다. */
    taskless?: boolean;
    /** 과제가 다른 말로 부른 용어를 골라 계획 후보 화면이 미리 계획 때와 달라졌다. */
    termsMoved?: boolean;
  },
): Promise<QueryPlan | undefined> {
  const drop = (why: string): undefined => {
    // eslint-disable-next-line no-console
    console.debug('[agent] 미리 계획 버림', why);
    return undefined;
  };
  if (now.extra || now.offLimits) return drop('덧붙일 쪽지');
  // 미리 한 계획은 그 용어의 뜻과 원본 화면을 못 봤다(「중대 이슈」가 점검결과 화면으로 갔다, 실제 테넌트 2026-10-27).
  if (now.termsMoved) return drop('과제가 고른 용어');
  // **조회만 미리 한 계획을 쓴다.** 쓰기는 과제가 정리한 문장(목표·조건)을 보고 짜야 초안을 쓴다 — 원래 말만 본
  // 계획은 「어느 호기인가요?」로 되물어 고장접수 두 건이 저장을 못 했다(쓰기 벤치, 2026-10-14). 조회 벤치는
  // 미리 계획을 써도 91.0% → 95.0%(차이 유의 아님)로 안 떨어졌다.
  if (!now.taskless && !['query', 'aggregate', 'rank', 'list', 'export'].includes(String(now.intent ?? ''))) {
    return drop(`조회 아님(${now.intent ?? '모름'})`);
  }
  const was = speculation.skills.join(',');
  const is = now.skills.map((skill) => skill.name).join(',');
  // **과제가 미리 본 스킬 중 일부만 골랐으면** 그 스킬들의 화면 안에서 짠 계획만 쓴다. 미리 계획이 스킬 둘을 보고
  // 짰는데 과제가 하나만 골라 버려졌다(덕봉빌딩 미수금, 2026-10-14). 새로 뽑힌 스킬이 있으면 버린다.
  const subset = was !== is && now.skills.length > 0
    && now.skills.every((skill) => speculation.skills.includes(skill.name));
  if (was !== is && !subset) return drop(`스킬 ${was || '없음'} → ${is || '없음'}`);
  const plan = await speculation.plan;
  if (!plan) return drop('계획 실패');
  if (!now.taskless && plan.kind !== 'retrieve') return drop(`조회 계획 아님(${plan.kind})`);
  if (subset) {
    const kept = new Set(now.skills.flatMap((skill) => skill.preferredRoutes ?? []));
    const routes = planRoutes(plan);
    if (!routes.length || routes.some((route) => !kept.has(route))) {
      return drop(`스킬 ${was} → ${is}, 남은 스킬 밖 화면 ${routes.join(',') || '없음'}`);
    }
  }
  // **업무 도구는 계획을 그 화면들에 묶는 계약이다.** 미리 한 계획은 도구를 못 봤으니, 고른 화면이 모두 그
  // 도구의 화면 안일 때만 쓴다 — 도구를 보고 짠 계획도 그 밖이면 검증에서 걷어차인다. 세 번 중 두 번이 같은
  // 스킬에 도구만 붙어 버려졌다(2026-10-14 실측).
  if (now.capability) {
    const allowed = new Set(now.capability.targets.map((target) => target.route));
    const routes = planRoutes(plan);
    if (!routes.length || routes.some((route) => !allowed.has(route))) {
      return drop(`업무 도구 밖 화면 ${routes.join(',') || '없음'}`);
    }
  }
  // eslint-disable-next-line no-console
  console.debug('[agent] 미리 계획 씀');
  return plan;
}

/** 계획이 여는 화면들. */
function planRoutes(plan: QueryPlan): string[] {
  if (plan.kind === 'retrieve') return plan.retrievals.map((one) => one.route);
  const route = (plan as { route?: unknown }).route;
  return typeof route === 'string' ? [route] : [];
}

/** 조회 이름의 꼬리(`__retry1`·`__settled1`)를 뗀다 — 사람에게 가는 말에 내부 이름이 섞이지 않게. */
function bareLabel(label: string): string {
  return label.replace(/__(?:(?:retry|settled)\d+|wide|조건뺌)$/, '');
}

/**
 * **위 표 행마다 상세를 펼쳐 읽는다**(드라이버 `sweepDetail`). 본 조회·조건 뺀 조회·같은 조건 다시 읽기가 같이 쓴다.
 *
 * 상세가 탭에만 있으면 그 탭을 먼저 연다(인사등록 경력). 앞선 조회로 좁히면(`matchFrom`) 그 조회의 칸 값을
 * 모아 넘기고, 값이 없으면 위 표를 다 고르지 않는다 — 좁히지 않고 펼치면 전원을 읽는다.
 */
async function runSweep(
  deps: Deps,
  retrieval: Retrieval,
  driverLabel: string,
  conditions: Record<string, string>,
  earlier: Record<string, Retrieved>,
): Promise<Retrieved> {
  const detailGrid = deps.screens.find((screen) => screen.route === retrieval.route)
    ?.grids.find((grid) => grid.key === retrieval.rowsStateKey);
  const master = detailGrid?.master;
  if (!master || !deps.session.sweepDetail) throw new Error(`${retrieval.rowsStateKey} 는 펼쳐 읽을 수 없다`);
  const { matchFrom, ...range } = retrieval.sweep ?? {};
  // 앱은 downlevelIteration 없이 굽는다 — Set 을 펼치지 않고 Array.from 으로 바꾼다.
  const values = matchFrom
    ? Array.from(new Set((earlier[matchFrom.label]?.rows ?? [])
      .map((row) => String(row[matchFrom.column ?? matchFrom.key] ?? '').trim())
      .filter(Boolean)))
    : [];
  const result: Retrieved = matchFrom && !values.length
    ? {
      label: retrieval.label, rows: [], masterRows: [], answered: true,
      swept: { masterRows: 0, read: 0, reason: `앞 조회 「${bareLabel(matchFrom.label)}」에 대상이 없어 펼쳐 읽지 않았다` },
    }
    : await deps.session.sweepDetail({
      label: driverLabel,
      routePath: retrieval.route,
      masterStateKey: master.stateKey,
      detailStateKey: retrieval.rowsStateKey,
      conditions,
      ...(master.rangeKey ? { range: { key: master.rangeKey, ...range } } : {}),
      limit: sweepLimit(master.msPerRow),
      ...(detailGrid?.tab ? { tab: detailGrid.tab } : {}),
      ...(matchFrom ? { only: { key: matchFrom.key, values } } : {}),
    });
  // 펼쳐 읽기가 무엇을 받았는지 남긴다 — 0행이 「상세가 없다」인지 「안 펼쳤다」인지 이것 없이는 못 가른다.
  // eslint-disable-next-line no-console
  console.debug('[agent] 펼쳐 읽기', retrieval.route, retrieval.rowsStateKey, {
    범위: JSON.stringify(range), 탭: detailGrid?.tab ?? '',
    좁힘: matchFrom ? `${matchFrom.label}.${matchFrom.column ?? matchFrom.key}→${matchFrom.key} ${values.length}개` : '',
    결과: result.rows.length, 펼침: JSON.stringify(result.swept ?? {}),
  });
  return result;
}

async function askOnce(question: string, deps: Deps): Promise<Answer> {
  const report = deps.onProgress ?? (() => {});
  // 직전 턴이 실패했는데 `확인해줘` 라고 하면 그 실패한 질문을 다시 하는 것이다.
  const retried = retryOfFailedTurn(question, deps.context);
  if (retried) question = retried;
  // **민감값은 모델에 보내지 않는다.** 주민번호가 든 문장은 모델이 통째로 못 본 것처럼
  // 굴어 계획을 아예 안 만든다(실측 3회). 자리표로 바꿔 보내고, 화면에 넣기 직전에만
  // 되돌린다 — 값은 브라우저 밖으로 나가지 않는다.
  const hidden = maskSensitive(question);
  question = hidden.text;
  // 자율 모드가 실제로 켜진 채로 왔는지 남긴다. 답만 보고는 체크가 안 먹은 것인지
  // 저장이 막힌 것인지 못 가린다.
  // eslint-disable-next-line no-console
  console.debug('[agent] 자율', Boolean(deps.context?.auto));
  const capabilityTools = buildCapabilityTools(deps.skills ?? [], deps.screens);
  // 질문에 든 업무 용어 — 「중대결함」을 중대고장으로 읽어 다른 화면에 갔다(실제 테넌트, 2026-10-22).
  // 용어는 직전 질문까지 보고 찾는다. 「시제 체크해줘」 다음의 「아니 회계」에는 「시제」가 없다(실제 테넌트, 2026-10-23).
  const recentSaid = [...(deps.context?.history ?? []).slice(-1).map((turn) => turn.question), question].join('\n');
  const glossary = glossaryFor(recentSaid, deps.experts ?? []);
  const capabilityHints = capabilityTools.map(({ id, description }) => ({ id, description }));
  // 작업 구조화는 계획을 돕는 장치다. 모델이 스키마를 어겼다고 사용자의 질문을
  // 못 받은 것처럼 굴지 않는다 — 직전 작업으로 이어 가거나 발화 그대로 처리한다.
  // 작업 갱신이 없어도 **앞 턴이 남긴 작업은 이어받는다.** 그러지 않으면 되묻기 다음
  // 턴이 화면에 열어 둔 초안의 값을 통째로 잊는다.
  // 결재 확인에 답한 턴이면 계획 없이 바로 한다 — 무엇을 결재할지는 앞 턴에 사람이 본 그 목록이다.
  const approving = deps.context?.task?.pending;
  if (approving?.field === APPROVE_PENDING && approving.options?.includes(question.trim())) {
    return runApproval(question.trim(), approving.wanted, deps);
  }
  if (approving?.field === BATCH_PENDING && approving.options?.includes(question.trim())) {
    return runBatch(question.trim(), approving.wanted, deps);
  }
  // 앞 턴 과제의 이번 발화 값(duplicates, exclusions)은 떼고 시작한다. 과제 단계가 없거나 실패하면 앞 턴의 구절로 판정했다(독립 검토).
  let task: TaskSpec | undefined = deps.context?.task && carriedTask(deps.context.task);
  // 사용자에게 붙어 온 별칭을 이번 작업의 밑에 깔아 둔다. 이번 턴에 배운 것은 그 위에 쌓인다.
  if (deps.context?.aliases && Object.keys(deps.context.aliases).length) {
    const carried = deps.context.aliases;
    task = task
      ? { ...task, aliases: { ...carried, ...(task.aliases ?? {}) } }
      : task;
  }
  // **계획을 과제와 함께 미리 시작한다**(2026-10-14, 과제 3~6초 + 계획 8~10초가 차례로 돌았다). 과제가 돌아와
  // 정상 경로가 고를 스킬·업무 도구가 미리 계획 때와 같을 때만 그 계획을 쓴다 — 다르면 버리고 예전처럼 계획한다.
  // 이어지는 대화는 과제가 앞 맥락을 문장에 붙이므로 미리 하지 않는다. 첫 질문만이다.
  const speculation = deps.taskChat && speculable(question, deps)
    ? speculatePlan(question, deps)
    : undefined;
  // 뺀 계정을 묻거나 넣어 달라는 질문(`duplicatesTalk`): 계산 전에 빼지 않고, 앞 답의 뺀 계정 문장을 과제에도 준다.
  const duplicateTalk = duplicatesTalk(question);
  const aboutDuplicates = duplicateTalk.asks;
  // 직전 답에서 뺀 계정 이름. 과제 단계에 따로 주고(「그 사람들」이 누구인지), 판정에서 가리키는 말을 단서로 받는다(독립 검토).
  const dropped = droppedNames(deps.context?.history);
  if (deps.taskChat) {
    report({ kind: 'understanding' });
    try {
      // 직전 답까지 넘긴다. 요지는 사용자가 물은 것만이 아니라 **우리가 이미 답한
      // 것**까지 담아야 다음 턴이 같은 것을 다시 하지 않는다.
      // 우리가 넣은 뺀 계정 문장은 거른다. 과제가 그것을 조건으로 적으면 2턴이 같은 일로 보지 않고 다시 조회한다(독립 검토).
      // 그 계정을 묻는 질문이면 남긴다. 「그 뺀 두 계정」이 누구인지 과제가 알아야 한다(독립 재검토).
      const lastAnswer = [...(deps.context?.history ?? [])].reverse()
        .map((turn) => (aboutDuplicates ? turn.answer ?? '' : withoutOwnLines(turn.answer ?? ''))).find((text) => Boolean(text.trim()));
      task = await updateTaskSpec(
        question, deps.context?.task, deps.taskChat, capabilityHints, lastAnswer, undefined, glossary, dropped,
        termIndex(deps.experts ?? []),
      );
    } catch (error) {
      // 왜 못 읽었는지 남긴다 — 과제 없이 가면 다듬지 않은 말로 계획한다(2026-10-15, 세 번 중 두 번).
      // eslint-disable-next-line no-console
      console.debug('[agent] 과제 못 읽음', String((error as Error)?.message ?? error).slice(0, 200));
      task = deps.context?.task && carriedTask(deps.context.task);
    }
  }
  // 개념을 어떻게 판정할지(「경력직 구분 기준」)는 사람에게 묻기 전에 우리가 세운다(`reasoning.ts`) —
  // 아래 계획 앞에서 한다. 세우지 못하면 그때 묻는다.
  let toDefine: string[] = [];
  // 모자란 값이 여러 개면 한 번에 묻는다. 한 턴에 하나씩 물으면 초안 하나를 놓고
  // 되묻기만 반복하다 끝난다 — 실제 시나리오 실행에서 본 것이다.
  if (task?.unresolved.length) {
    // 기간은 앞 대화에 이미 있다. 작업 상태(`filters`)가 아니라 **직전 조회 조건**에
    // 들어 있어서, 작업 단계에서 물려주는 것만으로는 못 막았다 — `올해 매출 성장세`
    // 다음의 `수금 상태는?` 이 두 번 다 기간을 되물었다. 계획은 그 조건을 읽을 수
    // 있으니(답변 끝의 `조회한 화면:` 줄) 여기서 묻지 않고 넘긴다.
    // 기간은 되묻지 않는다. 앞 대화에 있으면 그것을 쓰고, 없으면 계획이 오늘을 기준
    // 삼는다 — `미수금이 많은 현장 알려줘` 는 지금 기준을 묻는 말이지, 연도를 되물어야
    // 하는 말이 아니다. 사무직 직원에게 `조회 기간을 알려 주세요` 는 답할 말이 없다.
    const askableItems = task.unresolved.filter((item) => !periodish(item, '') && askable(item));
    toDefine = task.intent === 'prepare' ? [] : askableItems.filter((item) => DEFINITION.test(item));
    const asked = askableItems.filter((item) => !toDefine.includes(item));
    // 항목 이름만 던지면 `조회 기간` 이라는 한 마디가 질문으로 나간다. 실제로 그랬다 —
    // 무엇을 답해야 하는지 알 수 없는 말이다. 문장으로 묻는다.
    // 모델이 항목 이름 대신 질문을 적어 보내기도 한다 — 그것은 그대로 묻는다. 뒤에 조사를 붙여
    // 「어떤 회계 업무를 원하시나요?을(를) 알려 주세요.」가 나갔다(실제 테넌트, 2026-10-23).
    const questions = asked.filter((item) => /\?\s*$/.test(item));
    const named = asked.filter((item) => !questions.includes(item));
    if (asked.length) {
      throw asking([
        ...questions.map((item) => item.trim()),
        ...(named.length ? [`${objectParticle(named.join(', '))} 알려 주세요.`] : []),
      ].join(' '), undefined, task);
    }
    // 묻지 않기로 한 것은 작업 상태에서도 지운다. 남겨 두면 답변 끝에 "알려 주시면
    // 더 정확합니다" 로 다시 나온다 — 이미 아는 것을 두 번 조르는 셈이다.
    task = { ...task, unresolved: asked };
  }
  const structuredQuestion = task ? taskQuestion(task) : question;
  const skillQuestion = task ? structuredQuestion : effectiveQuestion(question, deps.context);
  const semanticSkills = task ? selectSkillsForTask(task, deps.skills ?? [], question) : [];
  const pickedTool = task ? findCapabilityTool(task.capability, capabilityTools) : undefined;
  // **사람이 화면 이름을 통째로 말했으면 업무 도구도 그 화면을 가리켜야 한다.** 스킬은 아래에서
  // 내려놓는데(`skillsBesideNamedScreen`) 도구는 남아 계약이 그 화면을 거절했다 — 「이번 달 지출결의서
  // 목록」이 한 판에서 통합문서 도구를 받아 계획 세 번 끝에 통합문서의 문서구분에 「지출결의서」를
  // 넣다 실패했고, 다른 판에서는 도구 없이 지출결의서로 갔다(실제 테넌트, 2026-10-06·07). 업무 단계가
  // 어느 도구를 고르느냐가 판마다 갈려도 말한 화면이 이기게 한다.
  // **같은 업무의 짝 화면을 가리키는 도구는 남긴다.** 「이번 달 매출 등록된 거」는 매출등록을 말했고 매출
  // 도구는 매출현황을 가리킨다 — 핵심 구절(`매출`)이 같은 등록↔현황 짝이라 같은 자료다. 도구를 내려놓자
  // 모델은 말한 화면으로 다시 계획하라는 쪽지까지 받고도 월매출매입현황으로 갔다(실제 테넌트, 2026-10-07).
  const spokenWhole = pickedTool ? namedScreen(question, deps.screens) : undefined;
  const nameAt = (route: string) => deps.screens.find((one) => one.route === route)?.name ?? '';
  const capability = pickedTool && spokenWhole && pickedTool.targets.length
    && !pickedTool.targets.some((target) => target.route === spokenWhole.route
      || screenTopic(nameAt(target.route)) === screenTopic(spokenWhole.name))
    ? undefined : pickedTool;
  if (pickedTool && !capability) {
    // eslint-disable-next-line no-console
    console.debug('[agent] 말한 화면이 이긴다 — 업무 도구 내려놓음', pickedTool.id, spokenWhole?.route);
  }
  const routeNames = new Map(deps.screens.map((screen) => [screen.route, screen.name]));
  const chosenSkills = semanticSkills.length
    ? semanticSkills
    : selectSkillsForTurn(question, skillQuestion, deps.skills ?? [], routeNames);
  // 사람이 화면 이름을 통째로 말했으면 그 화면을 안 가리키는 스킬은 내려놓는다.
  const selectedSkills = skillsBesideNamedScreen(question, chosenSkills, deps.screens);
  if (selectedSkills.length !== chosenSkills.length) {
    // eslint-disable-next-line no-console
    console.debug('[agent] 말한 화면이 이긴다 — 스킬 내려놓음',
      chosenSkills.filter((skill) => !selectedSkills.includes(skill)).map((skill) => skill.name).join(','));
  }
  const clarification = skillClarification(skillQuestion, selectedSkills, routeNames);
  if (clarification) throw asking(clarification.question, clarification.options, task);
  const artifactSkill = selectedSkills.find((skill) => skill.kind === 'artifact');
  // 이름에 숫자가 붙은 계정을 이번 턴에 어떻게 다룰지(`duplicateDecision`). 넣기 상태는 같은 업무 안에서 잇는다.
  const decision = duplicateDecision(question, task, dropped);
  const previousKeep = !!deps.context?.task?.keepNumbered && (deps.context?.task?.capability ?? '') === (task?.capability ?? '');
  const keepState = decision.keep ? true : decision.action === 'exclude' ? false : previousKeep;
  const reused = await reuseForArtifact(question, deps.context, deps.chat, deps.screens, artifactSkill, task, decision, deps.experts ?? []);
  if (reused) return reused;
  // 예산은 한 지갑에서 센다. 계획 라운드, 계획 안의 재시도, 계산 재시도가 서로를
  // 모른 채 각자 세면 한 질문에 계획을 아홉 번 돈다 — 사용자는 그동안 보고 있다.
  const budget = deps.budget ?? new Budget();
  // **차단을 세는 지갑.** 계획 라운드(`MAX_ROUNDS`)와 다른 것이다 — 그것이 없어서 같은
  // 저장을 다섯 번 눌렀다. 턴에 하나이고 단일 초안 경로와 걸음 경로가 같이 쓴다.
  const ledger = new BlockLedger();
  const MAX_ROUNDS = 3;
  let retrieved: Record<string, Retrieved> = {};
  // 루프를 빠져나온 뒤에는 조회 계획만 남는다 — ask 는 던지고 howto 는 곧장 돌려준다.
  let plan!: Extract<QueryPlan, { kind: 'retrieve' }>;
  // 직전 턴에서 화면이 신규를 거부했으면 그 사실을 계획에 물려준다. 안 물려주면 다음
  // 턴이 같은 화면으로 또 간다 — 자재청구서가 네 턴을 같은 안내로 썼고, 그 안내는
  // 처음부터 어디로 가야 하는지 말하고 있었다(`견적서에서 자재청구서 버튼`).
  // 이 사용자의 메뉴에 없는 화면. **라운드를 넘어 기억한다** — 문구로만 알려 주면
  // 모델이 또 고른다.
  const offLimits = new Set<string>();
  let note = refusalNote(deps.context);
  // 초안 시도가 화면의 실제 선택지를 알려 주며 실패하면 한 번 더 계획한다. 그때는
  // 이미 만들어 둔 미저장 행을 갱신해야 한다 — 다시 `new` 를 누르면 초안이 둘이 된다.
  let retryDraftInPlace = false;
  // 화면이 잠가 둔 칸. 한 번 걸리면 다음 판에서 아예 빼고 낸다 — 모델에게 다시
  // 짜라고만 하면 같은 칸을 또 내고, 초안이 그것 하나로 통째로 죽는다.
  const lockedFields = new Set<string>();
  // **계획이 목록 칸을 글로 되물었나**(스펙 §2.2). 한 번은 버리고 다시 계획한다 —
  // 그 칸은 화면이 고르게 하는 것이지 사람이 목록을 모르는 채 답할 것이 아니다.
  // 두 번째면 그대로 글 되묻기로 내보낸다 — 무한히 다시 짜지 않는다.
  let replannedListAsk = false;
  let replannedDataGap = false;
  // **되묻기 전에 판정 기준을 세웠나**(`reasoning.ts`). 한 턴에 한 번 — 세운 기준으로 다시 짠 계획이 또
  // 되물으면 그 말을 그대로 낸다. 세운 기준은 계산과 답에 간다.
  let reasoned = false;
  let derived: Derivation | undefined;
  // 계획이 마지막으로 본 화면 목록. 추론이 같은 것을 본다.
  let shown: AgentScreen[] = [];
  if (toDefine.length) {
    reasoned = true;
    budget.spend('plans');
    const wanted = objectParticle(toDefine.join(', '));
    const found = await deriveCriterion({
      question: structuredQuestion,
      asked: { question: `${wanted} 알려 주세요.`, reason: '작업 정리 단계가 판정 기준을 정하지 못했다' },
      // 첫 계획 전이라 계획이 볼 목록을 따로 고른다 — 같은 목록이어야 같은 길을 짠다.
      screens: (await planScreens(structuredQuestion, deps.screens, {
        shortlist: deps.shortlist,
        context: {
          ...deps.context,
          skills: selectedSkills,
          termRoutes: termRoutes(`${recentSaid} ${structuredQuestion}`, deps.experts ?? [], deps.screens, task?.terms),
        },
      })).map((screen) => describeScreen(screen)).join('\n\n'),
      chat: deps.chat,
    });
    // eslint-disable-next-line no-console
    console.debug('[agent] 되묻기 전 추론(작업)', found ? `${found.criterion} | ${found.path}` : '기준 못 세움');
    if (!found) throw asking(`${wanted} 알려 주세요.`, undefined, task);
    derived = found;
  }
  // 이름을 말한 화면으로 다시 계획한 것은 한 번뿐이다.
  let replannedNamedScreen = false;
  let replannedComboAsk = false;
  // **목록 밖 값을 우리가 대신 고른 칸.** 계획은 다음 판에도 사용자가 말한 옛 이름을
  // 그대로 낸다 — 고른 값을 그 위에 덮어 준다.
  const chosen: Record<string, string> = {};
  // 같은 칸에 같은 값을 두 번 고르지 않는다. 화면이 그래도 거절하면 사람에게 묻는다.
  const autoPicked = new Set<string>();
  // **팝업이 안 열려 다시 연 칸.** 화면이 바쁘면 더블클릭이 씹힌다 — 한 번은 다시
  // 열어 보고, 두 번째는 열리지 않았다고 말한다(스펙 §5.8·§6 3번).
  const popupRetried = new Set<string>();
  // 화면이 저장할 때 요구한 칸. 한 번은 모델에게 되돌려 채우게 하고, 그래도 비면 묻는다.
  const demandedFields = new Set<string>();
  // 걸음 계획의 **머리 문서를 이미 만들었나.** 다시 계획하는 판에서 첫 걸음이 신규를 또
  // 누르면 사람이 치워야 할 빈 문서가 하나 더 생긴다 — 인사등록이면 빈 사원이다.
  let headMade = false;
  // **이미 저장한 걸음은 다시 하지 않는다.** 다시 계획하는 판에서 그 걸음을 또 밟으면
  // 같은 문서를 두 번 저장하게 된다. 어디까지 됐는지는 답변에도 그대로 쓴다.
  const finished: string[] = [];
  // **우리가 고른 값은 답에 적는다**(스펙 §5.3). 사람이 승인하지 않은 값을 조용히 넣지
  // 않는다 — 틀렸으면 「틀려요」로 되돌릴 수 있어야 한다.
  const picked: string[] = [];
  // **앞 판이 낸 칸을 기억한다.** 다시 내는 판에서 모델이 칸을 흘린다 — 부서·직위를
  // 사용자가 말했는데도 두 번째 판에서 이름만 남기고 다 지운 판을 봤다. 모델에게
  // 「줄이지 마라」고 이르는 것만으로는 안 되므로 우리가 이어 붙인다.
  const carriedSteps = new Map<string, Record<string, string>>();
  // **걸음 목록도 이어 붙인다.** 다시 내는 판이 걸음을 통째로 흘린다 — 가족·자격증을
  // 시켰는데 두 번째 판이 기본정보만 남긴 판을 봤다. 「줄이지 마라」고 이르는 것만으로는
  // 안 되므로 앞 판의 걸음을 순서 그대로 되살린다.
  let carriedPlan: SequenceStep[] = [];
  // **화면이 한 말을 판을 넘어 붙잡아 둔다.** 다시 밟는 판에서 같은 거절이 문구 없이
  // 오는 일이 있어, 사람에게 이유 없이 `멈췄습니다` 만 나갔다 — 화면은 분명히
  // `필수입력확인: 교육기관을 입력하십시오` 라고 말했다. 판 안에 두면 매번 지워진다.
  let lastSaid = '';
  // **다른 화면으로 옮기기 전, 처음 고른 화면의 빈 조회.** 옮긴 화면도 비면 이것으로 답한다(아래).
  let firstEmpty: { plan: Extract<QueryPlan, { kind: 'retrieve' }>; retrieved: Record<string, Retrieved> } | undefined;

  for (let round = 0; round < MAX_ROUNDS; round += 1) {
    // **왜 또 도는지 남긴다.** 라운드 하나에 계획 프롬프트(18k 자)가 통째로 다시 간다 —
    // 되물음을 0으로 만든 뒤에도 단순한 물음에 세 번 돌았다(2026-09-21).
    // eslint-disable-next-line no-console
    if (round > 0) console.debug('[agent] 계획 라운드', `${round + 1}번째`, (note || '(까닭 없음)').replace(/\s+/g, ' ').slice(0, 200));
    // 횟수가 남아도 시간이 다 됐으면 멈춘다. 여기서 멈추면 지금까지 얻은 것으로 답한다.
    if (round > 0 && !budget.allows('plans')) break;
    // 되묻기에 버튼으로 답한 턴이면 모델에게 다시 묻지 않는다. 첫 라운드에서만 —
    // 뒤 라운드는 무언가 틀려 다시 짜는 중이라 모델이 계획해야 한다.
    // **직전 턴이 우리가 물은 턴일 때만이다.** 답이 안 온 `pending` 은 오래 남는다 —
    // 조회로 끝난 턴은 그것을 안 지운다. 며칠 뒤의 발화가 그때의 선택지와 우연히 같은
    // 글자면, 모델을 거치지 않고 낡은 초안의 화면으로 턴 전체가 끌려간다.
    const continued = round === 0
      ? (deps.context?.presetPlan ?? (answeredOurQuestion(deps.context) ? continuationPlan(question, task) : undefined))
      : undefined;
    // 모델을 안 불렀으면 계획 예산도 쓰지 않는다.
    if (!continued) budget.spend('plans');
    // **계획을 기다리는 동안 화면을 미리 연다.** 업무 도구나 스킬이 가리키는 화면이 계획이 고를 화면인 일이
    // 대부분이다. 틀리면 계획이 고른 화면으로 가면 그만이다 — 화면을 여는 것은 아무것도 바꾸지 않는다.
    // 초안을 이어 쓰는 턴은 그 화면을 떠나지 않는다.
    const likely = round === 0 && !continued && !answeredOurQuestion(deps.context)
      ? likelyRoute(capability, selectedSkills, deps.screens, offLimits)
      : undefined;
    if (likely && deps.session.warm) {
      // eslint-disable-next-line no-console
      console.debug('[agent] 미리 엶', likely);
      deps.session.warm(likely);
    }
    report({ kind: 'planning', screen: likely ? deps.screens.find((one) => one.route === likely)?.name : undefined });
    // 되묻기에 답한 턴이 조회부터 시작하면 화면이 비워지고 방금 만든 초안이 사라진다.
    // 그 뒤 신규는 화면 규칙에 막힌다. 무엇이 화면에 있는지 계획에 먼저 알린다.
    const resuming = answeredOurQuestion(deps.context)
      ? '\n\n직전 턴에서 초안을 만들고 값 하나를 되물었다. 지금 화면에 그 미저장 초안이 그대로 있다.'
        + ' 조회하지 말고 같은 화면의 prepare 로 그 값만 채워라. 새 문서를 만들지 마라.'
      : '';
    // `let` 으로 두면 앱 쪽 TS 가 콜백 안에서 좁히기를 잃는다(`made.route` 를 못 읽는다).
    // 받아서 곧바로 `const` 로 고정한다.
    let planned: QueryPlan | undefined;
    try {
      // 권한 밖 화면은 목록에서 아예 뺀다. `덕봉빌딩 미수금 얼마야?` 가 못 여는 화면을
      // 세 라운드 내리 골라 턴이 죽었다.
      const left = offLimits.size
        ? deps.screens.filter((screen) => !offLimits.has(screen.route))
        : deps.screens;
      // 다 빼 버리면 고를 것이 없어져 `화면 목록에 없다` 라는 엉뚱한 말로 끝난다.
      // 그때는 원래 목록으로 두고, 못 여는 화면이라는 사실은 문구가 말한다.
      const pickable = left.length ? left : deps.screens;
      const early = round === 0 && !continued && speculation
        ? await adoptSpeculation(speculation, {
          skills: selectedSkills, capability, extra: note + resuming, offLimits: offLimits.size,
          intent: task?.intent, taskless: !task,
          // 고른 용어가 글자로 걸린 용어 밖의 화면을 더했을 때만(차례는 보지 않는다).
          termsMoved: termRoutes(question, deps.experts ?? [], deps.screens, task?.terms)
            .some((route) => !termRoutes(question, deps.experts ?? [], deps.screens).includes(route)),
        })
        : undefined;
      // 세운 판정 기준은 라운드마다 붙인다 — 다시 계획하는 쪽지(retryNote 등)가 note 를 새로 써도 빠지지 않게.
      const derivedNote = derived ? derivationNote(derived) : '';
      // 과제가 앞 대화를 이어 다듬은 질문으로도 용어를 찾는다 — 「그럼 내년에 생겨?」에는 「연차」가 없다.
      const planGlossary = glossaryFor(`${recentSaid} ${structuredQuestion}`, deps.experts ?? [], task?.terms);
      planned = continued ?? early ?? await makePlan(structuredQuestion + note + derivedNote + (planGlossary ? `\n\n${planGlossary}` : '') + resuming, pickable, deps.chat, {
        // 계획은 다시 쓴 문장을 받지만, 사람이 화면을 이름으로 댔는지는 원문으로 본다.
        spoken: question,
        shortlist: deps.shortlist,
        onShortlist: (list) => { shown = list; },
        codeLists: deps.codeLists,
        context: {
          ...deps.context,
          skills: selectedSkills,
          allSkills: deps.skills ?? [],
          continuing: answeredOurQuestion(deps.context),
          capability,
          termRoutes: termRoutes(`${recentSaid} ${structuredQuestion}`, deps.experts ?? [], deps.screens, task?.terms),
        },
      });
      // **계획이 무엇을 골랐는지 그 자리에서 남긴다.**
      //
      // 지금까지 계획 품질을 재려면 잣대 한 판(50분 + 하루 AI 한도)을 돌려야 했다.
      // 그런데 재고 싶은 것은 대개 **화면·조건을 맞게 골랐는가**뿐이고, 그것은 실행
      // 전에 이미 정해져 있다. 이 한 줄이 있으면 탐침이 여기서 턴을 끊고 다음 건으로
      // 갈 수 있다 — 한 건이 LLM 두 번(task·plan)이고 실행이 통째로 빠진다.
      //
      // 값은 안 찍는다. 화면과 조건 **이름**만 있으면 채점이 된다.
      // eslint-disable-next-line no-console
      console.debug('[agent] 계획', {
        갈래: planned?.kind ?? '없음',
        화면: planned?.kind === 'retrieve'
          ? planned.retrievals.map((one) => one.route).join(',')
          : (planned as { route?: string } | undefined)?.route ?? '',
        조건: planned?.kind === 'retrieve'
          ? planned.retrievals.map((one) => Object.keys(one.conditions ?? {}).join('+')).join(' | ')
          : '',
        // 쓰기는 칸 **이름**만 — 발주처를 계획이 냈는지 못 냈는지 이것 없이는 안 보였다(2026-09-25).
        칸: planned?.kind === 'prepare' ? Object.keys(planned.fields ?? {}).join('+') : '',
        스킬: selectedSkills.map((skill) => skill.name).join(',') || '없음',
        업무: task?.capability ?? '',
      });
    } catch (error) {
      // 계획이 **목록에 없는 화면**을 골랐다. 모델이 이름을 지어낸 것이고, 그것은
      // 다시 세우면 고쳐지는 실수다 — 여기서 끝내면 사용자는 25초 만에
      // `화면을 찾지 못했습니다` 만 받는다(공사 실적 R9).
      const message = error instanceof Error ? error.message : String(error);
      if (!/화면 목록에 없다/.test(message) || round === MAX_ROUNDS - 1 || !budget.allows('plans')) {
        throw error;
      }
      budget.spend('plans');
      // eslint-disable-next-line no-console
      console.debug('[agent] 계획 다시', message);
      note = `

직전 계획이 없는 화면을 골랐다: ${message}`
        + ' 아래 화면 목록에 있는 경로만 쓴다. 딱 맞는 화면이 없으면 가장 가까운 화면을'
        + ' 고르거나, 무엇으로 대신할지 되물어라.';
      // eslint-disable-next-line no-continue
      continue;
    }
    const made = planned;

    // **이름을 말한 화면이 조회에 없으면 한 번 다시 계획한다.** 스킬을 내려놓아도 모델이
    // 「이번 달 공사 결과 보고서」에 공사현황을 골랐다(실제 테넌트, 2026-09-25).
    const spokenScreen = namedScreen(question, deps.screens);
    if (made.kind === 'retrieve' && spokenScreen && spokenScreen.grids.length
      && !made.retrievals.some((one) => one.route === spokenScreen.route)
      // 권한이 없어 이미 뺀 화면이면 다시 들이밀지 않는다.
      && !note.includes(spokenScreen.route)
      && !replannedNamedScreen && round < MAX_ROUNDS - 1 && budget.allows('plans')) {
      replannedNamedScreen = true;
      budget.spend('plans');
      // eslint-disable-next-line no-console
      console.debug('[agent] 계획 다시(말한 화면)', spokenScreen.route);
      note = `

사용자가 화면 이름 「${spokenScreen.name}」(${spokenScreen.route})을 그대로 말했다. 그 화면으로 조회하라.`;
      // eslint-disable-next-line no-continue
      continue;
    }

    if (made.kind === 'ask' && asksForDataGap(made) && !replannedDataGap
      && round < MAX_ROUNDS - 1 && budget.allows('plans')) {
      // 「이 화면엔 그 자료가 없다 — 다른 화면을 알려 달라」는 사람에게 낼 말이 아니다(`dataGapAsk.ts`).
      replannedDataGap = true;
      budget.spend('plans');
      // eslint-disable-next-line no-console
      console.debug('[agent] 계획 다시(자료 공백 되묻기)', made.question.slice(0, 80));
      note = `

${DATA_GAP_NOTE}`;
      // eslint-disable-next-line no-continue
      continue;
    }

    if (made.kind === 'ask') {
      // **모델이 쓴 말에서 우리끼리 쓰는 말을 걷어낸다.** `화면 계약에는 staffList 만
      // 있어…` 가 사람에게 그대로 나갔다(실측 2026-09-02).
      const here = deps.screens.find((one) => one.route === made.route)
        ?? deps.screens.find((one) => one.route === deps.context?.currentRoute);
      // 스펙 §2.2: 목록에서 고르는 칸(팝업·콤보·코드표)을 값 없이 글로 되묻는 계획은
      // 버리고 한 번 다시 계획한다 — 그 칸은 화면이 고르게 한다(§2.1 을 note 로 실어).
      // ask 는 화면이 정해지지 않았으므로 **현재 화면**의 draftFields 로 잇는다.
      // 두 번째이거나 목록 칸으로 못 이으면 그대로 글 되묻기로 내보낸다.
      const emptyOptions = !made.options || made.options.length === 0;
      if (emptyOptions && !replannedListAsk
        && questionAsksListField(made.question ?? '', here, deps.codeLists)
        && round < MAX_ROUNDS - 1 && budget.allows('plans')) {
        replannedListAsk = true;
        note = `

직전 계획이 화면에서 고르는 목록 칸을 값 없이 글로 되물었다: "${made.question ?? ''}".`
          + ' 목록에서 고르는 칸(콤보·검색 칸)은 값을 모르면 되묻지 말고 비운 채 prepare 로'
          + ' 내라 — 화면이 고르게 한다. 글자칸(자유 입력)만 되물어라.';
        // eslint-disable-next-line no-continue
        continue;
      }
      // **조회 조건 콤보는 되묻지 않는다.** 사람은 화면을 열고 기본값 그대로 조회한다 —
      // 「올해 연차 생성 현황 보여줘」에 「연차 생성 기준을 입사일로 할까요, 회계년도로
      // 할까요?」를 되물었다(2026-09-23 실측). 프롬프트가 이미 말리는데도 그랬다.
      // 되물은 선택지가 **그 화면 조회 조건 콤보의 선택지 안에 다 있을 때만** 한 번 다시
      // 계획한다. 화면이 애매해서 묻는 것(「결과냐 계획이냐」)은 그대로 되묻는다.
      // 계획이 화면을 비운 되묻기면 계획에 보인 화면과 고른 스킬의 화면에서 그 콤보를 찾는다. 실측 2026-10-26 15:30: 화면 경로
      // 없이 「입사일 / 회계년도」를 되물어 지금 화면에서 콤보를 못 찾았고, 1턴과 2턴(공지 요청)이 되물음으로 끝났다.
      const comboScreen = [here, ...(shown.length ? shown : candidates(structuredQuestion, deps.screens)),
        ...selectedSkills.flatMap((skill) => (skill.preferredRoutes ?? []).map((route) => deps.screens.find((one) => one.route === route)))]
        .find((screen) => askedSearchCombo(made.options, screen));
      const combo = askedSearchCombo(made.options, comboScreen);
      if (combo && !replannedComboAsk && round < MAX_ROUNDS - 1 && budget.allows('plans')) {
        replannedComboAsk = true;
        // eslint-disable-next-line no-console
        console.debug('[agent] 계획 다시(조건 되묻기)', comboScreen?.route, combo.key);
        note = `

직전 계획이 조회 조건 칸을 되물었다: "${made.question ?? ''}".`
          + ` ${combo.key}(${combo.label ?? combo.key}) 는 조회 조건이다. 값을 모르면 conditions 에서 빼라 —`
          + ' 화면 기본값으로 조회된다. 되묻지 말고 조회 계획을 내라.';
        // eslint-disable-next-line no-continue
        continue;
      }
      // **사람에게 묻기 전에 사람처럼 생각해 본다.** 질문의 개념이 칸에 그대로 없을 뿐 판정할 재료는
      // 화면에 있는 일이 많다 — 「경력직」은 경력 탭의 앞 회사 기록으로 센다(2026-10-21).
      // 쓰기 일과 선택지를 내민 되묻기는 사람이 고를 일이다 — 추론이 값을 지어내 초안에 넣는다(검토, 2026-10-21).
      if (!reasoned && task?.intent !== 'prepare' && !made.options?.length
        && round < MAX_ROUNDS - 1 && budget.allows('plans')) {
        reasoned = true;
        budget.spend('plans');
        const seenScreens = shown.length ? shown : candidates(structuredQuestion, deps.screens);
        // eslint-disable-next-line no-await-in-loop
        const found = await deriveCriterion({
          question: structuredQuestion,
          asked: { question: made.question ?? '', reason: made.reason ?? '' },
          screens: seenScreens.map((screen) => describeScreen(screen)).join('\n\n'),
          chat: deps.chat,
        });
        // eslint-disable-next-line no-console
        console.debug('[agent] 되묻기 전 추론', found ? `${found.criterion} | ${found.path}` : '기준 못 세움');
        if (found) {
          derived = found;
          // 기준은 아래 계획 호출이 붙인다 — 다시 계획하는 라운드에도 따라가게.
          note = refusalNote(deps.context);
          // eslint-disable-next-line no-continue
          continue;
        }
      }
      throw asking(plainSpeech(made.question ?? '', here), made.options, task);
    }
    if (made.kind === 'prepare') {
      // **올리라고 말했으면 올린다 — 모델에 맡기지 않는다.** 같은 「다 쓰면 결재도 올려줘」에 계획이 한 판은
      // submit 을 냈고 다음 판은 빠뜨렸다(개발 테넌트, 2026-10-13). 사람이 한 말이 근거다.
      if (!made.submit && SUBMIT_SAID.test(question)) made.submit = true;
      const screen = deps.screens.find((candidate) => candidate.route === made.route);
      // **사용자가 말한 값을 계획이 조용히 빼지 못하게 한다.** 목록에 없는 값이면
      // 모델은 그 칸을 통째로 뺀다 — 판단은 옳지만 말하지 않는 것이 틀렸다.
      // 사람은 직위가 들어간 줄 알고 저장한다(실측 2026-09-01, `직위는 통제관`).
      const unlisted = unlistedSpoken(
        made.fields ?? {}, screen?.draftFields ?? [], question, deps.codeLists,
      );
      if (unlisted) throw asking(unlistedQuestion(screen?.name ?? made.route, unlisted), unlisted.options, task);
      if (!deps.session.prepare) {
        return { text: '이 환경에서는 신규 초안 작성을 지원하지 않습니다.', code: '', sources: [] };
      }
      report({ kind: 'preparing', screen: screen?.name ?? made.route });
      let continuedDraft = false;
      // 확정으로 미룬 칸. 준비가 그 행까지 짚었고, 칸은 아래 Auto 게이트에서 바꾼다.
      let deferred: Parameters<typeof finishDeferred>[0] | undefined;
      try {
        const continueDraft = Boolean(
            deps.context?.task
            && task?.capability === deps.context.task.capability
            && /(해야지|바꿔|수정|보완|아까|방금|그대로|이어서|기존에\s*(?:올린|작성한|만든|신청한)|확인해\s*봐)/.test(question)
        ) || answeredOurQuestion(deps.context);
        continuedDraft = continueDraft;
        // 현재 초안 보정과 과거 문서 복사를 모델 계획만으로 혼동하지 않게 분리한다.
        // 사용자가 과거 문서를 명시했을 때만 재사용 탐색을 유지한다.
        const asksHistoricalReuse = /(기존|과거|이전).*(신청서|보고서|문서|양식|제목|사유|내용).*(조회|확인|가져|재사용|그대로)|(?:조회|확인|가져|재사용).*(기존|과거|이전)/.test(question);
        // **사용자가 말했을 때만 과거 문서를 뒤진다.**
        //
        // 주석은 처음부터 그렇게 적혀 있었는데 코드는 `!continueDraft` 면 무조건
        // 따랐다 — 새 요청은 언제나 그 조건이라, 사실상 늘 열려 있었다.
        //
        // 실측(2026-09-02): `내일 오후 반차 신청해줘` 에 스킬이 시킨 재사용이 돌았고,
        // 끝나고 보니 **남의 기존 신청서(2026-08-17 연차)가 내 값으로 덮여 있었다.**
        // 줄 수가 안 늘어 잣대도 못 잡았다. 운영이었으면 되돌릴 값을 아무도 안 갖고 있다.
        //
        // 과거 제목·사유를 베끼는 것은 **편의**다. 남의 기록이 조용히 바뀌는 것과
        // 바꿀 수 없다. 사람이 「기존 것 그대로」라고 말하면 그때 한다.
        const plannedReuse = made.reuse && asksHistoricalReuse ? made.reuse : undefined;
        // 되묻기에 답한 턴의 계획은 방금 고른 값 하나만 낸다. 앞 턴이 같은 화면에
        // 넣어 둔 값을 밑에 깔아 준다 — 화면에는 있는데 계획만 모르는 값이다.
        const carried = task?.draft?.route === made.route ? task.draft : undefined;
        if (carried) made.fields = { ...carried.fields, ...made.fields };
        // **새 문서를 쓰던 중이면 되묻기 답 턴이 남의 문서를 고치러 가면 안 된다.** 고장접수에서
        // 호기를 답하자 계획이 같은 날·같은 현장의 기존 접수를 `edit` 으로 짚었다(2026-09-25) —
        // 운영에서는 흔한 일이고, 그대로 가면 남의 접수를 덮어쓴다. 이어 쓰던 초안을 잇는다.
        // 행 고르기 되묻기(`pending.field` 가 빈 것)는 원래 고치기에서 나온다 — 그것은 그대로 둔다.
        if (carried && !carried.editing && made.edit && task?.pending?.field) {
          // eslint-disable-next-line no-console
          console.debug('[agent] 되묻기 답 턴의 고치기를 버림 — 새 초안을 잇는다', made.route);
          delete made.edit;
        }
        // 우리가 대신 고른 값은 계획보다 세다. 안 그러면 같은 이름을 또 내고 또 막힌다.
        if (Object.keys(chosen).length) made.fields = { ...made.fields, ...chosen };
        // 앞서 배운 별칭이 있으면 말한 이름 대신 화면이 아는 이름을 넣는다.
        // 작업이 없는 턴에도 사용자에게 붙어 온 별칭은 쓴다 — 기억은 작업이 아니라
        // 사람에게 붙은 것이다.
        const aliases = usableAliases({ ...(deps.context?.aliases ?? {}), ...(task?.aliases ?? {}) });
        made.fields = Object.fromEntries(Object.entries(made.fields).map(([key, value]) => (
          [key, aliases[value] ?? value]
        )));
        // 행도 같이 잃는다. 품목을 고르고 나면 계획은 상세행을 다시 내지 않는다.
        // **되묻기에 답하는 턴에는 이어받은 행이 진짜다.** 모델이 상세행을 다시 내도
        // 수량 같은 칸을 빠뜨린 채 낸다 — `{pcode:"WIRE ROPE"}` 로 qty:"2" 를 잃고
        // 초안이 미완으로 끝났다(실측 2026-09-06). 이어받은 행(완전한 초안)을 쓰고
        // 아래에서 별칭으로 품목만 갈아 끼운다.
        if (((task?.pending && carried?.rows) || !made.rows) && carried?.rows) made.rows = carried.rows;
        // 되묻기에 답한 턴이다. 못 골랐던 이름과 지금 넣는 값을 짝지어 기억한다.
        // **표의 칸도 같이 본다.** 자재는 언제나 행에 실리는데 헤더 칸만 보면 방금
        // 고른 규격을 배우지 못하고, 이어받은 옛 행이 거절당한 이름을 도로 넣는다 —
        // `WIRE ROPE` 를 고르라는 같은 질문이 영원히 되돌아왔다.
        const pending = task?.pending;
        // 행 고르기 되묻기에 답한 턴이다. 사용자가 누른 글자가 곧 그 행이다.
        if (made.edit && pending && !pending.field) {
          const picked = (pending.options ?? []).find((option) => option.trim() === question.trim());
          if (picked) made.edit = { ...made.edit, pick: picked };
        }
        // 이어받은 행에는 **물리친 이름이 그대로 남아 있다.** 그것을 답으로 세면 아무것도
        // 배우지 못하고 같은 질문이 되돌아온다. 그 이름과 다른 첫 값이 답이다.
        const answered = pending
          ? [made.fields[pending.field], rowValue(made.rows, pending.field), chosenOption(question, pending)]
            .find((value) => value && value !== pending.wanted)
          : undefined;
        // 고른 라벨을 코드로 바꾼다. 표 셀 품목은 라벨을 넣으면 팝업이 못 되맞춰
        // 같은 질문이 되돌아왔다(0행). 코드를 넣으면 화면이 이름·단가를 채우고 저장된다.
        // 코드가 없으면(예전 되묻기) 라벨을 그대로 쓴다.
        const answeredCode = answered
          ? pending?.optionCodes?.find((one) => one.label === answered)?.code
          : undefined;
        const rowFill = answeredCode || answered;   // 빈 코드(널 아님)는 라벨로 폴백
        // **사용자가 고른 값을 계획이 안 실어 오는 일이 있다.** 저장이 막혀 되물은
        // 칸은 계획이 아예 모르던 칸이라(화면 안내로만 드러난다) 다음 판에도 안 낸다 —
        // 고장상세부위를 골라 줘도 같은 질문이 되돌아왔다. 답을 그 칸에 직접 꽂는다.
        // 다만 **이 화면의 헤더 칸일 때만** 꽂는다. 표 셀(pcode)이면 헤더에 넣으면 안 된다 —
        // 아래 rowAliases 로 행에 코드가 이미 들어간다.
        const screenNow = deps.screens.find((one) => one.route === made.route);
        const isHeaderField = (screenNow?.draftFields ?? []).some((one) => one.key === pending?.field);
        // **물리친 값이 남아 있는 칸도 빈 칸과 같다.** 이어받은 초안의 그 칸에는 화면이
        // 되돌려보낸 그 값이 그대로 있다 — 그것이 prepare 를 막은 값인데 「채워진 칸」으로
        // 읽혀 누른 값이 영영 안 들어갔다. 후보끼리 서로를 담고 있으면(`덕봉빌딩` ⊂
        // `덕봉빌딩(두암정철)`) §5.3 이 대신 고르지도 못해 **같은 질문이 그대로 되돌아온다.**
        const rejected = String(made.fields[pending?.field ?? ''] ?? '').trim();
        const stale = !rejected || rejected === String(pending?.wanted ?? '').trim();
        if (pending?.field && answered && isHeaderField && stale) {
          made.fields = { ...made.fields, [pending.field]: answeredCode || answered };
        }
        if (pending && answered) {
          task = {
            ...task!,
            // **모든 고름이 이름은 아니다.** `덕봉빌딩 → 덕봉빌딩(두암정철)` 은 같은 것을
            // 부르는 두 이름이지만, `도어센서 → WIRE ROPE 8mm X 2R` 은 그때 한 번 다른
            // 물건을 고른 것이다. 그것을 이름으로 배우면 다음부터 `도어센서` 라 말할 때마다
            // 조용히 다른 자재가 들어간다 — 실제로 그렇게 저장됐다.
            aliases: namesTheSame(pending.wanted, answered)
              ? { ...aliases, [pending.wanted]: answered }
              : aliases,
            pending: undefined,
          };
        }
        // 이어받은 행에는 사용자가 방금 물리친 이름이 그대로 남아 있다. 별칭을 행에도
        // 입혀야 다음 시도가 고쳐진 이름으로 간다.
        const rowAliases = pending && answered
          ? { ...aliases, [pending.wanted]: rowFill ?? answered }
          : aliases;
        if (made.rows) made.rows = { ...made.rows, items: applyAliases(made.rows.items, rowAliases) };
        // 앞판에서 화면이 잠가 둔 것으로 드러난 칸은 뺀다. 잠긴 칸은 어차피 안 들어간다.
        if (lockedFields.size) {
          made.fields = Object.fromEntries(Object.entries(made.fields)
            .filter(([key]) => !lockedFields.has(key)));
        }
        const prepared = await deps.session.prepare({
          routePath: made.route,
          // 자리표를 진짜 값으로 되돌린다. **여기서만 되돌린다** — 이 값은 화면으로 갈 뿐
          // 모델로 되돌아가지 않는다.
          fields: fillMasked(unmaskFields(made.fields, hidden.values), screen?.draftFields, hidden),
          ...(allowedValues(made.route, deps) ? { allowed: allowedValues(made.route, deps)! } : {}),
          ...(pairsOf(made.route, deps) ? { pairs: pairsOf(made.route, deps)! } : {}),
          ...(plannedReuse ? { reuse: plannedReuse } : {}),
          ...(continueDraft || retryDraftInPlace ? { continueDraft: true } : {}),
          ...(made.rows
            ? { rows: { ...made.rows, items: unmaskRows(made.rows.items, hidden.values) } }
            : {}),
          ...(made.edit ? { edit: made.edit } : {}),
          ...(made.calendarDay ? { calendarDay: made.calendarDay } : {}),
        });
        made.fields = prepared.fields;
        deferred = prepared.deferred;
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        // 고를 수 없는 값 하나로 대화를 끝내지 않는다. 화면이 들고 온 실제 선택지를
        // 그대로 사용자에게 버튼으로 내민다 — 이미 채운 값은 미저장 행에 남아 있다.
        const notFound = optionNotFound(error);
        if (notFound) {
          const labels = new Map((screen?.draftFields ?? [])
            .map((field) => [field.key, plainLabel(field.label ?? field.key)]));
          // 되묻기는 표의 칸에서도 나온다. 그 이름은 초안 칸 목록에 없어 `pcode만 골라
          // 주세요` 가 그대로 나갔다 — 사무직 직원에게 그것은 아무 말도 아니다.
          for (const grid of screen?.grids ?? []) {
            for (const column of grid.columns ?? []) {
              if (!labels.has(column.id)) labels.set(column.id, column.text ?? column.id);
            }
          }
          const missing = labels.get(notFound.field ?? '') ?? '선택값';
          // **팝업이 안 열린 것은 「대안 없음」이다**(스펙 §5.8 마지막 줄). 후보가 빈
          // 되묻기를 던지면 사람이 누를 것이 없다 — 그건 §5.7 이 실패로 세는 모양이다.
          // 한 번 그대로 다시 열고, 그래도 안 열리면 그 사실을 말한다.
          // 이유가 무엇이든 **후보가 비면 되묻지 않는다**(§5.7). 팝업이 안 열린 것만
          // 한 번 다시 열어 보고, 그 밖의 이유는 못 읽었다는 사실을 그대로 말한다.
          if (!notFound.options.length) {
            if (notFound.reason === '팝업이 안 열렸다') {
              const shut = signatureOf([`popup:${notFound.field ?? ''}`]);
              if (!popupRetried.has(shut) && ledger.allows(shut)
                && round < MAX_ROUNDS - 1 && budget.allows('plans')) {
                popupRetried.add(shut);
                retryDraftInPlace = true;
                // eslint-disable-next-line no-continue
                continue;
              }
              return {
                text: `${screen?.name ?? made.route} 초안을 열어 ${filledSoFar(error, labels, notFound.field)}`
                  + `저장은 되지 않았습니다. ${topicParticle(missing)} 고르는 창이 열리지 않았습니다.`
                  + ' 잠시 뒤 다시 말씀해 주세요.',
                code: '',
                sources: [screen?.name ?? made.route],
                task,
              };
            }
            // **읽었는데 없는 것**은 못 읽은 것과 다르다. 목록을 다 훑고도 사용자가 댄
            // 이름과 닮은 것이 하나도 없었다 — 그때 낯선 이름 다섯을 후보라고 내밀면
            // 말한 적 없는 값이 칸에 앉는다. 아무것도 세팅하지 않고 이름을 다시 청한다.
            if (notFound.reason === '비슷한 이름을 찾지 못했다') {
              return {
                text: `${screen?.name ?? made.route} 초안을 열어 ${filledSoFar(error, labels, notFound.field)}`
                  + `저장은 되지 않았습니다. ${topicParticle(notFound.wanted ?? '')} ${missing} 목록에서 찾지 못했습니다.`
                  + ' 화면에 있는 정확한 이름을 알려 주시면 다시 해보겠습니다.',
                code: '',
                sources: [screen?.name ?? made.route],
                task,
              };
            }
            return {
              text: `${screen?.name ?? made.route} 초안을 열어 ${filledSoFar(error, labels, notFound.field)}`
                + `저장은 되지 않았습니다. ${topicParticle(missing)} 목록을 화면에서 읽지 못했습니다.`,
              code: '',
              sources: [screen?.name ?? made.route],
              task,
            };
          }
          // **화면에 들어간 칸만 말한다.** 계획이 낸 칸을 그대로 읊으면 비어 있는
          // 칸을 넣었다고 보고하게 된다 — 고장접수에서 `고장내용까지 넣었습니다` 라
          // 해 놓고 그 칸이 비어 있었고, 사용자는 호기만 답했다가 저장이
          // `고장내용을 선택하세요` 로 막히는 것을 봤다.
          // 스탬프가 없으면 **아직 아무것도 안 채운 것**이다. 초안이 열리기도 전에
          // 죽는 실패가 있다(고장처리결과는 고칠 행부터 고른다) — 그때 계획의 칸을
          // 읊으면 화면이 통째로 비어 있는데 `처리결과·고장요인까지 넣었습니다` 가
          // 나간다. 덜 말하는 쪽이 안전하다.
          const filled = filledSoFar(error, labels, notFound.field);
          // 말한 값이 목록에 없다는 사실을 밝힌다. 그러지 않으면 사용자는 자기가 댄
          // 이름(`본사`)이 왜 안 먹었는지 모른 채 낯선 후보만 스무 개 보게 된다.
          const missed = notFound.wanted && !notFound.options.some((option) => option === notFound.wanted)
            ? `${topicParticle(notFound.wanted)} 화면 목록에 없습니다. `
            : '';
          // 무엇을 고르려다 못 골랐는지 남긴다. 다음 턴에 사용자가 고른 값과 짝지어
          // 별칭으로 저장한다 — 같은 현장을 매일 쓰는 사람에게 두 번 묻지 않기 위해서다.
          // 칸이 아니라 **행**을 고르는 되묻기도 있다(어느 미수를 갚는가). 그때는
          // 다음 턴이 그 답을 조건으로 다시 짜 맞추려다 엉뚱한 칸에 넣는다 —
          // 우리가 내민 글자를 그대로 되받아 `pick` 으로 넘긴다.
          const pending = notFound.field && notFound.wanted
            ? {
              field: notFound.field,
              wanted: notFound.wanted,
              options: notFound.options,
              // 코드로 확정해야 하는 표 셀 품목은 라벨→코드도 들고 있다가, 답 턴에
              // 고른 라벨을 코드로 바꿔 행에 넣는다. 라벨은 팝업이 못 되맞춘다.
              ...(notFound.optionCodes ? { optionCodes: notFound.optionCodes } : {}),
            }
            : (made.edit && notFound.options.length
              ? { field: '', wanted: notFound.wanted ?? '', options: notFound.options }
              : undefined);
          // 되묻는 턴에도 **이 화면에 무엇을 넣으려 했는지** 남긴다. 남기지 않으면
          // 다음 턴이 답한 칸 하나만 들고 오고, 아직 안 들어간 칸은 영영 안 채워진다 —
          // 고장접수에서 호기를 고른 뒤에도 `고장내용을 선택하세요` 로 저장이 막혔다.
          // 다음 턴이 **모델 없이** 이 초안을 이어도 되는지도 여기서 표시한다. 무엇이
          // 이 초안을 만들었는지는 이 자리에서만 안다 — 초안 자체는 `{route, fields,
          // rows}` 뿐이라 고치기에서 나온 것과 구별되지 않는다. 홑 prepare 로 이으면
          // 고치기는 **새 문서를 하나 더 만들고**, 달력은 날짜를 안 열어 쓸 자리가 없다.
          const unfinished = {
            route: made.route,
            fields: made.fields,
            ...(made.rows ? { rows: made.rows } : {}),
            ...(!made.edit && !made.calendarDay ? { continuable: true } : {}),
          };
          // **목록 밖 값도 §5.3 을 거친다.** 두 벌을 두면 한쪽만 고쳐진다 — 여기가
          // 후보를 답 글에 늘어놓던 자리다. 사람은 그것을 다시 타이핑해야 했다.
          const decided = choose(question, {
            notice: '',
            label: missing,
            field: notFound.field,
            kind: 'popup',
            observed: notFound.wanted ?? '',
            alternatives: notFound.options,
            source: 'popup',
            signature: signatureOf([notFound.wanted ?? '']),
          });
          const mark = `${notFound.field ?? ''}=${decided.kind === 'pick' ? decided.value : ''}`;
          if (decided.kind === 'pick' && notFound.field && !autoPicked.has(mark)
            && round < MAX_ROUNDS - 1 && budget.allows('plans')) {
            autoPicked.add(mark);
            chosen[notFound.field] = decided.value;
            made.fields = { ...made.fields, [notFound.field]: decided.value };
            // 사람이 승인하지 않은 값을 조용히 넣지 않는다. 걸음 경로와 같은 장부다.
            picked.push(`${topicParticle(missing)} ${decided.value} 로 넣었습니다`);
            retryDraftInPlace = true;
            // eslint-disable-next-line no-continue
            continue;
          }
          throw asking(
            `${screen?.name ?? made.route} 초안을 열어 ${filled}${missed}`
            + `${decided.kind === 'ask' ? decided.question : `${topicParticle(missing)} 어느 것입니까?`}`,
            decided.kind === 'ask' ? decided.options : notFound.options,
            task
              ? { ...task, ...(pending ? { pending } : {}), draft: unfinished }
              : task,
          );
        }
        // 목록이 **비어 있는 채로** 다시 계획하면 모델이 값을 지어낸다. 실제로
        // `도어센서` 가 `WIRE ROPE 8mm X 2R` 로 바뀌어 견적서에 저장됐다 — 사람은
        // 자기가 말하지 않은 품목이 든 문서를 받았다. 내밀 후보가 없으면 사람에게 묻는다.
        const blind = blindOption(error);
        if (blind) {
          throw asking(
            `다음 값을 알려 주세요: 화면 목록에서 ${objectParticle(blind.wanted)} 찾지 못했습니다.`
            + ' 화면에 적힌 정확한 이름이나 코드를 알려 주세요.',
            undefined,
            task,
          );
        }
        // 화면이 잠가 둔 칸 하나로 초안 전체가 죽는다. 어느 칸인지 화면이 이름으로
        // 말해 주므로 모델을 다시 부르기 전에 **그 칸을 빼고** 같은 초안 위에서
        // 다시 채운다 — 고장접수의 `사람갇힘일자` 가 그렇게 두 번 죽었다.
        // **쓰려는 화면이 이 사용자의 메뉴에 없다.** 조회 경로는 그 화면을 빼고 다시
        // 계획하는데 쓰기 경로에는 그 갈래가 없어 「초안을 다 채우지 못했습니다」로 끝났다.
        // 업무일지는 사람마다 개별업무일지작성과 개별업무일지[총무팀] 중 한쪽만 메뉴에 있다
        // (2026-10-05). 화면을 열지 못한 것이라 만든 초안은 없다 — 이어쓰기로 두지 않는다.
        if (/메뉴에 \S+ 가 없다/.test(message) && !offLimits.has(made.route)
          && round < MAX_ROUNDS - 1 && budget.allows('plans')) {
          offLimits.add(made.route);
          note = `\n\n이 사용자의 메뉴에 ${made.route} 화면이 없다(권한 없음).`
            + ' 그 화면은 쓰지 말고 같은 업무를 쓸 수 있는 다른 화면으로 계획하라.';
          // eslint-disable-next-line no-console
          console.debug('[agent] 계획 다시(쓰기 화면 권한 없음)', made.route);
          // eslint-disable-next-line no-continue
          continue;
        }
        const lockedField = /^(\S+) 은 지금 화면이 잠가 둔 칸이다/.exec(message)?.[1];
        if (lockedField && !lockedFields.has(lockedField)
          && round < MAX_ROUNDS - 1 && budget.allows('plans')) {
          lockedFields.add(lockedField);
          note = `

직전 초안 시도가 실패했다: ${message}`
            + ' 그 칸은 화면이 잠가 두었다. 다시 낼 때 그 칸을 아예 빼라.';
          retryDraftInPlace = true;
          // eslint-disable-next-line no-continue
          continue;
        }
        // 화면이 "있는 것: ..." 로 실제 선택지를 알려 준 실패는 다시 계획할 수 있다.
        // 그 목록 없이 같은 값을 또 넣는 것이 실측된 실패였다.
        //
        // 다시 계획하더라도 **이미 만든 초안 위에** 채운다(`retryDraftInPlace`).
        // 신규를 또 누르면 미저장 행이 둘이 되고 그것을 사용자가 치워야 한다 —
        // 이것이 `repeatable(draft-partial) === false` 가 뜻하는 바다.
        if (/있는 것:/.test(message) && round < MAX_ROUNDS - 1 && budget.allows('plans')) {
          note = `

직전 초안 시도가 실패했다: ${message}`
            + ' 화면이 알려 준 그 목록 안에서 고르고, 없는 문구를 만들지 마라.';
          retryDraftInPlace = true;
          // eslint-disable-next-line no-continue
          continue;
        }
        // 초안 실패는 사용자에게 사람 말로 나가지만, 그 말로는 무엇이 걸렸는지 못 고친다.
        // 원문과 어느 화면·어느 칸이었는지를 여기 남긴다 — S1 에서 견적서가 세 번
        // `조건에 맞는 자료가 없습니다` 로 끝났고 이유를 볼 방법이 없었다.
        // eslint-disable-next-line no-console
        console.debug('[agent] 초안 실패', made.route, JSON.stringify(made.fields).slice(0, 200),
          // **행 값을 적는다.** 개수만 적으면 「overperid 이 반영되지 않았다」를 보고도
          // 무엇을 넣으려 했는지 모른다 — 코드였는지 이름이었는지, 아예 빈 값이었는지.
          // 드라이버는 코드와 이름을 다 받아 주므로(실측 2026-09-22) 값을 봐야 갈린다.
          made.rows
            ? `행 ${made.rows.stateKey} ${made.rows.items.length}개 ${
              JSON.stringify(made.rows.items).slice(0, 200)}`
            : '행 없음',
          // 고칠 문서를 못 찾았을 때 **무엇으로 찾았는지**가 없으면 고칠 수가 없다.
          made.edit ? `고칠 행 ${made.edit.rowsStateKey} ${JSON.stringify(made.edit.match)}${made.edit.then ? ` → ${made.edit.then.stateKey} ${JSON.stringify(made.edit.then.match)}` : ''}` : '',
          message.slice(0, 300));
        // 여기서 나가던 문장이 `actcd 칸의 선택지 …` 였다. 쓰는 사람은 그 코드를
        // 본 적이 없다 — 화면에 적힌 이름으로 바꾸고, 나머지는 사람 말로 옮긴다.
        const named = (screen?.draftFields ?? []).reduce(
          (text, field) => (field.label ? text.split(field.key).join(field.label) : text),
          message,
        );
        return {
          // 바로 반영되는 칸을 고치던 자리에는 초안이 없다 — 준비는 짚기만 한다.
          text: made.edit?.deferCells || made.edit?.window
            ? `${screen?.name ?? made.route}에서 바꾸지 않았습니다. ${userMessage(new Error(named))}`
            : `${screen?.name ?? made.route} 초안을 다 채우지 못했습니다. ${userMessage(new Error(named))}`,
          code: '',
          sources: [screen?.name ?? made.route],
          task,
        };
      }
      // 이 화면에 무엇을 넣었는지 작업에 남긴다. 다음 턴이 되묻기 답만 들고 와도
      // 여기서 합쳐 준다.
      if (task) {
        task = {
          ...task,
          draft: {
            route: made.route,
            fields: made.fields,
            ...(made.rows ? { rows: made.rows } : {}),
            ...(made.edit ? { editing: true } : {}),
          },
        };
      }
      // 성공한 초안도 남긴다. 되묻기 뒤 앞서 넣은 값이 사라지는 일이 있는데, 실패
      // 추적만으로는 **무엇이 들어갔다가 사라졌는지**를 못 본다.
      // eslint-disable-next-line no-console
      console.debug('[agent] 초안', made.route, continuedDraft ? '이어서' : '신규',
        // **왜 이어쓰기가 됐는지까지 적는다.** 「이어서」만 남으면 계획이 고치기(`edit`)를
        // 낸 것인지, 되묻기에 답한 턴이라 이어받은 것인지 구분할 수 없다 — 출장보고서가
        // 남의 문서(202603050001)를 덮었는데 그 둘을 못 갈라 원인을 좁히지 못했다
        // (실측 2026-09-15). 덮어쓰기는 되돌릴 값이 없으므로 추적이 특히 중요하다.
        continuedDraft
          ? `(까닭: ${answeredOurQuestion(deps.context) ? '되묻기에 답한 턴' : '말에 이어쓰기 낱말'})`
          : '',
        made.edit ? `고칠 행 ${made.edit.rowsStateKey} ${JSON.stringify(made.edit.match)}${made.edit.then ? ` → ${made.edit.then.stateKey} ${JSON.stringify(made.edit.then.match)}` : ''}` : '',
        JSON.stringify(made.fields).slice(0, 300),
        made.rows?.items?.length
          ? `행 ${JSON.stringify(made.rows.items).slice(0, 200)}`
          : '행 없음');
      const labels = new Map((screen?.draftFields ?? []).map((field) => [field.key, plainLabel(field.label ?? field.key)]));
      const byKey = new Map((screen?.draftFields ?? []).map((field) => [field.key, field]));
      const filled = joinFilled(Object.entries(made.fields)
        // 빈 칸은 넣은 것이 아니다. 계획이 모르는 칸을 `""` 로 채워 보내면
        // `고장부위=, 고장요인=, 고장원인=` 같은 꼬리가 답에 그대로 나갔다.
        .filter(([, value]) => String(value ?? '').trim())
        // **사무직은 코드를 본 적이 없다.** `직위=13` 도 `청구자명=00456` 도 아무 말이
        // 아니다. 목록이 있으면 이름으로 바꾸고, 없으면 뺀다.
        .map(([key, value]): [string, string] | null => {
          const said = readableValue(byKey.get(key), key, String(value), deps.codeLists);
          return said === undefined ? null : [labels.get(key) ?? key, said];
        })
        .filter((pair): pair is [string, string] => pair !== null));
      // 이미 올린 문서를 고치는 자리에서 `신규 초안을 열어` 라고 말하면 사람은 문서가
      // 하나 더 생긴 줄 안다. 실제로는 그 문서를 열어 고쳤다.
      const draftAction = made.edit
        ? '올려 둔 문서를 열어'
        : (continuedDraft ? '현재 초안을 이어서' : '신규 초안을 열어');
      // 상세행은 그 문서의 알맹이다(견적서의 자재, 업무일지의 한 일). 답이 그것을
      // 말하지 않으면 사용자는 무엇이 들어갔는지 모르고, 읽는 쪽도 헛짚는다 —
      // `자재 없이 저장됐다` 고 두 번 잘못 읽었다.
      // 그 행이 무엇인지 말해 주는 칸을 먼저 본다 — 업무일지는 한 일(`remark`),
      // 자재는 품목이다. 현장 이름만 되풀이하면 `덕봉빌딩, 덕봉빌딩 2건` 이 된다.
      const rowVoice = ['productnm', 'remark', 'pname', 'pcode', 'contents', 'wkcd'];
      const rowNames = (made.rows?.items ?? []).map((item) => {
        const said = rowVoice.map((key) => item[key]).find((value) => String(value ?? '').trim());
        if (said) return String(said);
        return Object.entries(item)
          // 날짜는 그 행이 무엇인지 말해 주지 않는다 — `20260814 1건을 넣었습니다` 는
          // 통행료를 넣고도 아무 말도 안 한 것과 같다.
          .filter(([key, value]) => !/(qty|amt|date)$/i.test(key)
            && !/^\d{8}$/.test(String(value ?? '').trim())
            && String(value ?? '').trim())
          .map(([, value]) => String(value))[0] ?? '';
      }).filter(Boolean);
      const rowsSaid = rowNames.length
        ? `${rowNames.join(', ')} ${rowNames.length}건을 함께 넣었습니다. `
        : '';
      // 머리 칸 없이 줄의 칸만 고쳤으면 무엇을 바꿨는지가 답이다.
      const cellsSaid = made.edit && !filled
        ? editedCellsSaid(made.edit, screen, (deps.skills ?? [])
          .find((one) => one.editRows === made.edit!.rowsStateKey && one.preferredRoutes?.includes(made.route)))
        : '';
      const wrote = cellsSaid
        ? `${screen?.name ?? made.route}에서 ${cellsSaid} ${rowsSaid}`
        : `${screen?.name ?? made.route}에서 ${draftAction}${filled ? ` ${objectParticle(filled)}` : ''} 입력했습니다. ${rowsSaid}`;
      if (deferred) {
        return finishDeferred(deferred, made.route, screen?.name ?? made.route, deps, task);
      }
      // Auto 를 켰으면 초안에서 멈추지 않는다. 켜지 않았으면 예전과 한 글자도 다르지 않다.
      if (deps.context?.auto && deps.session.commit) {
        try {
          if (!ledger.allowsSave()) {
            return {
              text: `${wrote}저장은 되지 않았습니다. 저장을 ${SAVE_ATTEMPTS}번 시도했습니다.`,
              code: '',
              sources: [screen?.name ?? made.route],
              task,
            };
          }
          const committed = await deps.session.commit({
            routePath: made.route, auto: true, action: 'save',
          });
          // 앱이 저장하며 띄운 문구를 그대로 싣는다. 사용자는 그 팝업을 못 봤고,
          // 무엇을 승인했는지는 그 문구에만 남아 있다.
          // 화면이 틀린 이름으로 요구하는 자리를 먼저 바로잡는다. 그래야 사람에게 나가는
          // 말과 우리가 채우려는 칸이 같은 이름을 쓴다 — 걸음 경로는 이미 그렇게 한다.
          // 안 바로잡으면 `교육기관` 을 그대로 분류해 **없는 칸**을 찾는다.
          let notices = correctNotices(committed.notices, made.route, made.rows?.tab);
          let said = readableNotices(notices);
          // 앱이 거절했는데 성공이라고 말하지 않는다. 판정은 화면 상태가 한다.
          if (!committed.saved) {
            // **고른 값으로 되저장했는데 또 막히면 그 자리에서 다시 판정한다.** 판을 새로
            // 돌면 계획을 한 번 더 세우고 저장을 한 번 더 누르게 된다 — 같은 통지로 저장을
            // 세 번 누른 것이 실측 사고다(§5.4·§6.4). 도는 횟수는 저장 지갑이 막는다.
            for (;;) {
              // **화면이 칸 이름을 대지 못한 거절도 정보다.** `경고: 청구할 품목이
              // 없습니다` 는 무엇이 모자란지 말하고 있는데, 칸 이름이 아니라서 되묻지도
              // 다시 짜지도 못하고 대화가 끝났다 — 자재청구서가 품목 없이 그렇게 끝났다.
              if (!demanded(notices).length && said && !demandedFields.has(said)
                && round < MAX_ROUNDS - 1 && budget.allows('plans')) {
                demandedFields.add(said);
                note = `

직전 초안은 저장이 막혔다: ${said}`
                  + ' 화면이 말한 것을 채워 다시 내라.'
                  + ' 표에 넣을 행이 빠졌으면 그 행을 넣어라. 사용자 말에 없는 값은 지어내지 마라.';
                retryDraftInPlace = true;
                break;
              }
              // 차단 하나를 어떻게 넘길지는 `blocked.ts` 가 정한다(스펙 §6). 통지의 동사가
              // 아니라 칸의 계약으로 종류를 가르고, 읽을 수 있는 대안은 읽어 고른다.
              // eslint-disable-next-line no-await-in-loop
              const settled = await settleBlocked({
                notices,
                spoken: question,
                draft: made.fields,
                deps: {
                  route: made.route,
                  screen,
                  codeLists: deps.codeLists,
                  options: deps.session.options?.bind(deps.session),
                },
                ledger,
                demanded: demandedFields,
              });
              if (settled.kind === 'filled') {
                // 고른 값만 채워 다시 저장한다. 모델을 부르지 않는다.
                made.fields = { ...made.fields, ...settled.fields };
                // **고른 것은 답에 적는다**(§5.3). 이 저장이 막혀 다음 판에서 저장돼도
                // 마찬가지다 — 그래서 돌려주기 전이 아니라 여기서 장부에 적는다.
                picked.push(...settled.said);
                if (!ledger.allowsSave()) {
                  return {
                    text: `${wrote}저장은 되지 않았습니다. 저장을 ${SAVE_ATTEMPTS}번 시도했습니다.`,
                    code: '',
                    sources: [screen?.name ?? made.route],
                    task,
                  };
                }
                // 짝 열과 허용 목록은 **다시 넣을 때도** 넘긴다. 다른 `prepare` 는 전부
                // 넘기는데 이 자리만 빠져 있었다 — 짝 열이 없으면 이름 칸(`equpnm`)이
                // 안 따라 채워지고, 허용 목록이 없으면 고른 값이 검증 없이 들어간다.
                const allowed = { ...(allowedValues(made.route, deps) ?? {}), ...settled.allowed };
                // eslint-disable-next-line no-await-in-loop
                await deps.session.prepare({
                  routePath: made.route,
                  fields: settled.fields,
                  continueDraft: true,
                  ...(Object.keys(allowed).length ? { allowed } : {}),
                  ...(pairsOf(made.route, deps) ? { pairs: pairsOf(made.route, deps)! } : {}),
                });
                // eslint-disable-next-line no-await-in-loop
                const retried = await deps.session.commit({
                  routePath: made.route, auto: true, action: 'save',
                });
                if (retried.saved) {
                  const told = noticesAfterSave(retried.notices);
                  const submitted = made.submit ? await submitForApproval(made.route, deps, made.fields) : '';
                  return {
                    text: `${wrote}${picked.join(', ')}. ${savedWord(retried)}${told ? ` 화면 안내: ${told}` : ''}${submitted}`,
                    code: '',
                    sources: [screen?.name ?? made.route],
                    task,
                  };
                }
                // 같은 자리에서 다시 판정한다. 장부가 같은 서명을 세고, 두 번째면
                // 고르지 않고 단추로 묻는다 — 저장을 또 누르지 않는다.
                notices = correctNotices(retried.notices, made.route, made.rows?.tab);
                said = readableNotices(notices) || said;
                retryDraftInPlace = true;
                note = '';
                // eslint-disable-next-line no-continue
                continue;
              }
              if (settled.kind === 'ask') {
                throw asking(
                  `${wrote}저장은 되지 않았습니다. ${settled.question}`,
                  settled.options,
                  task
                    ? {
                      ...task,
                      pending: { field: settled.field, wanted: '', options: settled.options },
                      // 이미 채운 칸까지 잃지 않는다. 다음 판은 고른 값 하나만 낸다.
                      draft: { route: made.route, fields: made.fields },
                    }
                    : task,
                );
              }
              if (settled.kind === 'replan' && round < MAX_ROUNDS - 1 && budget.allows('plans')) {
                note = settled.note;
                retryDraftInPlace = true;
                break;
              }
              return {
                text: `${wrote}저장은 되지 않았습니다.${said ? ` 화면 안내: ${said}` : ''}`
                  + `${settled.kind === 'stop' && settled.reason ? ` ${settled.reason}` : ''}`,
                code: '',
                sources: [screen?.name ?? made.route],
                task,
              };
            }
            // 다시 짜라는 판정만 여기로 온다. 바깥 판이 계획을 새로 세운다.
            // eslint-disable-next-line no-continue
            continue;
          }
          const submitted = made.submit ? await submitForApproval(made.route, deps, made.fields) : '';
          return {
            text: `${wrote}${picked.length ? `${picked.join(', ')}. ` : ''}${savedWord(committed)}`
              + `${noticesAfterSave(notices) ? ` 화면 안내: ${noticesAfterSave(notices)}` : ''}${submitted}`,
            code: '',
            sources: [screen?.name ?? made.route],
            task,
          };
        } catch (error) {
          // **되묻기는 실패가 아니다.** 저장이 막힌 자리에서 사람에게 고르게 하려고
          // 던진 것을 여기서 삼키면 `저장은 하지 못했습니다` 로 뭉개지고 후보도 사라진다.
          if (error instanceof AskUser) throw error;
          // 저장이 막혔다고 채운 것까지 잃지 않는다. 무엇이 막았는지 말하고 넘긴다.
          return {
            text: `${wrote}저장은 하지 못했습니다. ${userMessage(error)}`,
            code: '',
            sources: [screen?.name ?? made.route],
            task,
          };
        }
      }
      return {
        text: `${wrote}저장·삭제·결재상신은 하지 않았습니다. 화면에서 내용을 확인한 뒤 직접 저장해 주세요.`,
        code: '',
        sources: [screen?.name ?? made.route],
        task,
      };
    }
    if (made.kind === 'send') {
      const screen = deps.screens.find((candidate) => candidate.route === made.route);
      const where = screen?.name ?? made.route;
      const channelName = {
        email: '메일', sms: '문자', kakao: '카카오톡', fax: '팩스',
      }[made.channel];
      if (made.channel === 'fax') {
        return {
          text: '팩스는 보낼 문서를 첨부해야 나가는데, 지금은 파일을 붙일 수 없습니다.'
            + ' 화면에서 첨부한 뒤 직접 보내 주세요.',
          code: '',
          sources: [where],
          task,
        };
      }
      if (!deps.context?.auto || !deps.session.send) {
        return {
          text: `${channelName} 전송은 패널 위쪽 Auto 를 켜 주셔야 대신 보낼 수 있습니다.`,
          code: '',
          sources: [where],
          task,
        };
      }
      report({ kind: 'preparing', screen: where });
      // 번호는 ERP 가 이미 들고 있다. `덕봉빌딩 담당자한테 문자 보내줘` 에 번호를
      // 되묻는 것은 아는 것을 모른 척하는 것이다 — 계획이 어디서 찾을지 적어 온다.
      let recipient = made.to;
      if (made.toLookup) {
        try {
          const found = await deps.session.retrieve({
            routePath: made.toLookup.route,
            conditions: made.toLookup.conditions,
            rowsStateKey: made.toLookup.rowsStateKey,
            label: '받는 사람',
          });
          const column = made.toLookup.column;
          const value = found.rows
            .map((row) => String((row as Record<string, unknown>)[column] ?? '').trim())
            .find(Boolean);
          if (value) recipient = value;
        } catch {
          // 못 찾으면 계획이 적어 둔 값으로 간다. 아래에서 비었으면 되묻는다.
        }
      }
      if (!recipient) {
        return {
          text: `${objectParticle(channelName)} 보낼 곳을 찾지 못했습니다. 받는 번호나 주소를 알려 주세요.`,
          code: '',
          sources: [where],
          task,
        };
      }
      // 보낼 화면에 자료가 올라와 있어야 보낼 것이 생긴다. 조회 없이 누르면 고장접수는
      // `문자전송할 고장내역이 없습니다` 로 되돌려보낸다.
      if (made.conditions && Object.keys(made.conditions).length) {
        try {
          await deps.session.retrieve({
            routePath: made.route,
            conditions: made.conditions,
            // 무엇을 읽을지는 상관없다. 화면에 자료를 올리는 것이 목적이다.
            rowsStateKey: screen?.grids?.[0]?.key ?? '',
            label: '보낼 자료',
          });
        } catch {
          // 조회가 막혀도 보내는 것까지 포기하지 않는다. 화면이 이미 들고 있을 수 있다.
        }
      }
      try {
        const done = await deps.session.send({
          routePath: made.route,
          channel: made.channel,
          auto: true,
          to: recipient,
          subject: made.subject,
          body: made.body,
          ...(made.fields ? { fields: made.fields } : {}),
        });
        // 전송 창은 제 버튼 이름까지 알림으로 흘린다 — `문자전송: 문자유형 음성문자 문자
        // 확인 닫기` 는 사람에게 아무 말도 아니다.
        const said = readableNotices(done.notices);
        return {
          text: `${where}에서 ${channelName} (${recipient}) 로 보냈습니다.`
            + `${said ? ` 화면 안내: ${said}` : ''}`,
          code: '',
          sources: [where],
          task,
        };
      } catch (error) {
        return {
          text: `${channelName} 전송을 하지 못했습니다. ${userMessage(error)}`,
          code: '',
          sources: [where],
          task,
        };
      }
    }
    if (made.kind === 'operate') {
      const screen = deps.screens.find((candidate) => candidate.route === made.route);
      const where = screen?.name ?? made.route;
      // 문서를 잇는 버튼은 새 문서를 만들고 결재까지 올릴 수 있다. Auto 를 켠 사람만
      // 그 결정을 한 것이다.
      if (!deps.context?.auto || !deps.session.operate) {
        return {
          text: `${where} 화면의 ${made.button} 버튼을 눌러야 하는 일입니다.`
            + ' 대신 눌러 드리려면 패널 위쪽 Auto 를 켜 주세요.',
          code: '',
          sources: [where],
          task,
        };
      }
      report({ kind: 'preparing', screen: where });
      try {
        // eslint-disable-next-line no-console
        console.debug('[agent] 누름', JSON.stringify({ button: made.button, confirm: made.confirm, fields: made.fields, selectKey: made.selectKey, list: made.list, focus: made.focus }).slice(0, 400));
        const done = await deps.session.operate({
          routePath: made.route,
          button: made.button,
          auto: true,
          confirm: made.confirm,
          fields: made.fields,
          selectKey: made.selectKey,
          row: made.row,
          ...(made.list ? { list: made.list } : {}),
          ...(made.focus ? { focus: made.focus } : {}),
        });
        const said = operateSaid(done.notices, made.button, made.confirm);
        // 무엇에 대고 눌렀는지 — `덕봉빌딩 1-1` . 목록에서 고른 줄과 그 안쪽 줄의 값이다.
        const target = [
          ...Object.values(made.focus?.match ?? {}),
          ...Object.values(made.focus?.then?.match ?? {}),
        ].map((value) => String(value ?? '').trim()).filter(Boolean).join(' ');
        return {
          text: `${where}에서 ${target ? `${target}의 ` : ''}${objectParticle(made.button)} 눌렀습니다.${said ? ` ${said}` : ''}`
            + (LATER_RESULT[`${made.route} ${made.button}`] ?? ''),
          code: '',
          sources: [where],
          task,
        };
      } catch (error) {
        // **이유를 남긴다.** 사용자에게 나가는 말은 「요청을 처리하지 못했습니다」 한 줄이고,
        // 그러면 버튼을 못 찾은 것인지·행을 못 찾은 것인지·화면이 막은 것인지 알 길이
        // 없다. 실제로 그 상태로 헤맸다 — 계획이 `row` 를 냈는지조차 못 봤다.
        // eslint-disable-next-line no-console
        console.debug('[agent] 버튼 실패', {
          화면: made.route,
          버튼: made.button,
          행: made.row ? JSON.stringify(made.row) : '안 냈다',
          목록: made.list ? JSON.stringify(made.list) : '안 냈다',
          고를문서: made.focus ? JSON.stringify(made.focus) : '안 냈다',
          이유: String((error as Error)?.message ?? error).slice(0, 200),
        });
        // 목록이 비어 누르지 않았으면 그렇게 말한다 — 어떤 조건으로 봤는지가 사람에게 필요한 전부다.
        // **누를 문서가 여럿이면 되묻는다.** 아무거나 누르지 않았다는 것과, 무엇으로 가리면 되는지를 말한다 —
        // 「덕봉빌딩 견적서 복사」에 35건이 걸렸는데 답은 「요청을 처리하지 못했습니다」였다(2026-10-13).
        const many = /을 누를 문서가 (\d+)개다 — 어느 것인지 말해 달라: (.*)$/.exec(String((error as Error)?.message ?? error));
        if (many) {
          return {
            text: `${where}에서 ${objectParticle(made.button)} 누르지 않았습니다. 해당하는 문서가 ${many[1]}건이라 어느 것인지`
              + ` 알려 주세요 — 날짜나 문서 이름으로 말씀해 주시면 됩니다. 예: ${many[2]}`,
            code: '',
            sources: [where],
            task,
          };
        }
        if (made.list && /목록이 비었다/.test(String((error as Error)?.message ?? error))) {
          const looked = readableSources([{ route: made.route, conditions: made.list.conditions ?? {} }], deps.screens)[0];
          return {
            text: `${where}에서 ${objectParticle(made.button)} 누르지 않았습니다. 조건에 맞는 목록이 없습니다.`
              + (looked ? ` 조회한 조건: ${looked}` : ''),
            code: '',
            sources: [where],
            task,
          };
        }
        return {
          text: `${where}에서 ${objectParticle(made.button)} 누르지 못했습니다. ${userMessage(error)}`,
          code: '',
          sources: [where],
          task,
        };
      }
    }
    if (made.kind === 'approve') return offerApproval(made, deps, task);
    if (made.kind === 'batch') return offerBatch(made, deps, task);
    if (made.kind === 'howto') {
      const procedure = deps.procedures?.[made.route ?? ''];
      if (!procedure) {
        const screen = deps.screens.find((candidate) => candidate.route === made.route);
        return {
          text: `${screen?.name ?? made.route} 화면은 찾았지만, 검증된 조작 절차가 아직 없어 `
            + '대신 입력하거나 저장할 수 없습니다.',
          code: '',
          sources: screen ? [screen.name] : [],
        };
      }
      report({ kind: 'answering', text: '' });
      return explain(question, procedure, deps.chat, {
        stream: deps.chatStream,
        onDelta: deps.onProgress && ((text) => report({ kind: 'answering', text })),
      });
    }

    if (made.kind === 'attach') {
      const screen = deps.screens.find((candidate) => candidate.route === made.route);
      if (!deps.session.attach) {
        return {
          text: '이 앱에서는 파일을 붙일 수 없습니다. 화면에서 직접 첨부해 주세요.',
          code: '',
          sources: screen ? [screen.name] : [],
          task,
        };
      }
      const done = await deps.session.attach({ routePath: made.route });
      const said = readableNotices(done.notices);
      return {
        text: done.attached
          ? `${screen?.name ?? made.route} 에 파일 ${done.attached}건을 붙였습니다.${said ? ` 화면 안내: ${said}` : ''}`
          : `파일을 붙이지 못했습니다.${said ? ` ${said}` : ''}`,
        code: '',
        sources: screen ? [screen.name] : [],
        task,
      };
    }

    // **여러 번 저장하는 일.** 탭마다 저장이 갈리는 화면이 있다.
    if (made.kind === 'sequence') {
      const screen = deps.screens.find((candidate) => candidate.route === made.route);
      // **사용자가 말한 값을 계획이 조용히 빼지 못하게 한다.** 목록에 없는 값이면
      // 모델은 그 칸을 통째로 뺀다 — 판단은 옳지만 말하지 않는 것이 틀렸다.
      // 사람은 직위가 들어간 줄 알고 저장한다(실측 2026-09-01, `직위는 통제관`).
      const unlisted = unlistedSpoken(
        Object.assign({}, ...made.steps.map((step) => step.fields ?? {})), screen?.draftFields ?? [], question, deps.codeLists,
      );
      if (unlisted) throw asking(unlistedQuestion(screen?.name ?? made.route, unlisted), unlisted.options, task);

      // 되묻기에 답한 턴이다. **고른 값을 머리 걸음에 꽂는다** — 계획이 그것을 안 실어
      // 오면 같은 질문이 되돌아온다. 단일 초안에는 있던 길이 걸음 쪽에 없어서, 부서를
      // 골라 줘도 다음 판이 또 물었다.
      const answeredStep = task?.pending;
      const chosen = answeredStep ? chosenOption(question, answeredStep) : undefined;
      if (answeredStep?.field && chosen && made.steps.length) {
        const head = made.steps[0]!;
        if (!String(head.fields?.[answeredStep.field] ?? '').trim()) {
          head.fields = { ...(head.fields ?? {}), [answeredStep.field]: chosen };
        }
        task = { ...task!, pending: undefined };
      }
      if (!deps.session.prepare || !deps.session.commit) {
        return {
          text: '이 화면은 여러 걸음으로 나눠 써야 하는데, 지금은 대신 쓸 수 없습니다.',
          code: '',
          sources: screen ? [screen.name] : [],
          task,
        };
      }
      // 화면이 이름을 댄 칸을 채워 다시 내게 할 때 넘길 말. 비어 있으면 다시 안 낸다.
      let replan = '';
      // 앱 쪽 tsconfig 는 반복자를 못 편다(downlevelIteration 없음). 색인으로 돈다.
      // 앞 판에 있던 걸음이 이번 판에 없으면 되살린다. 순서는 앞 판을 따른다.
      if (carriedPlan.length) {
        const now = new Set(made.steps.map((one) => one.label));
        const revived = carriedPlan.filter((one) => !now.has(one.label));
        if (revived.length) {
          const byLabel = new Map(made.steps.map((one) => [one.label, one]));
          made.steps = carriedPlan.map((one) => byLabel.get(one.label) ?? one)
            .concat(made.steps.filter((one) => !carriedPlan
              .some((before) => before.label === one.label)));
        }
      }
      carriedPlan = made.steps;
      // 앞 판이 낸 칸을 밑에 깔고 이번 판을 그 위에 얹는다. **잠긴 칸은 빼고** 깐다 —
      // 화면이 못 받는다고 한 값을 되살리면 같은 자리에서 또 죽는다.
      for (const step of made.steps) {
        const before = carriedSteps.get(step.label);
        const merged = { ...(before ?? {}), ...(step.fields ?? {}) };
        // 앱 쪽 tsconfig 는 Set 순회를 못 편다(downlevelIteration 없음).
        for (const key of Array.from(lockedFields)) delete merged[key];
        if (Object.keys(merged).length) step.fields = merged;
        carriedSteps.set(step.label, merged);
      }
      for (let index = 0; index < made.steps.length; index += 1) {
        const step = made.steps[index]!;
        // 앞 판에서 저장까지 끝난 걸음이다. 또 밟으면 같은 문서를 두 번 저장한다.
        if (finished.includes(step.label)) continue;
        report({ kind: 'preparing', screen: `${screen?.name ?? made.route} — ${step.label}` });
        // **무엇을 냈는지 남긴다.** 걸음이 막혔을 때 그 말만으로는 어느 칸이 어떤 값으로
        // 갔는지 알 수 없다 — 단일 초안에는 있던 자취가 걸음 쪽에 없어, 부서가 왜 안
        // 들어갔는지 보려다 볼 것이 없었다.
        // eslint-disable-next-line no-console
        console.debug('[agent] 걸음', made.route, step.label,
          step.tab ? `탭 ${step.tab}` : '',
          JSON.stringify(step.fields ?? {}).slice(0, 200),
          step.rows ? `행 ${step.rows.stateKey} ${JSON.stringify(step.rows.items).slice(0, 200)}` : '');
        try {
          // 탭을 열고 **그 탭 자료가 도착할 때까지 기다린다.** 열자마자 채우면 뒤늦게
          // 온 자료가 방금 넣은 값을 덮는다. 첫 걸음은 머리 문서라 탭을 열지 않는다.
          if (step.tab && deps.session.openTab) {
            // eslint-disable-next-line no-await-in-loop
            await deps.session.openTab({ routePath: made.route, tab: step.tab });
          }
          // eslint-disable-next-line no-await-in-loop
          await deps.session.prepare({
            routePath: made.route,
            // 가려 둔 값은 **머리 걸음에만** 얹는다. 주민번호는 기본정보의 칸이라
            // 가족 탭에는 없고, 거기 얹으면 `초안 칸 rnum 이 화면에 없다` 로
            // 그 걸음이 통째로 죽는다 — 실측했다.
            fields: index === 0
              ? fillMasked(
                unmaskFields(step.fields ?? {}, hidden.values), screen?.draftFields, hidden,
              )
              : unmaskFields(step.fields ?? {}, hidden.values),
            // 첫 걸음만 신규를 누른다. 그 뒤 걸음은 방금 만든 문서에 이어 붙인다 —
            // 또 누르면 사람이 치워야 할 빈 문서가 생긴다. **다시 계획한 판에서도 그렇다.**
            //
            // 앞 판이 단일 초안이었어도 마찬가지다. 실측: 1라운드가 `prepare` 로 신규를
            // 누르고 저장에서 막혔는데, 2라운드가 걸음 계획으로 바뀌며 또 눌러 화면이
            // `한번에 한 행만 추가하실 수 있습니다` 로 막았다.
            ...(index === 0 && !headMade && !retryDraftInPlace ? {} : { continueDraft: true }),
            ...(allowedValues(made.route, deps) ? { allowed: allowedValues(made.route, deps)! } : {}),
            ...(pairsOf(made.route, deps) ? { pairs: pairsOf(made.route, deps)! } : {}),
            ...(step.rows
              ? {
                rows: {
                  stateKey: step.rows.stateKey,
                  items: unmaskRows(step.rows.items, hidden.values),
                  // **탭 걸음의 행은 표의 `추가` 가 아니라 화면의 `신규` 가 더한다.**
                  // 계획이 그것을 빠뜨리면 `추가 단추를 찾지 못했다` 로 걸음이 죽는다 —
                  // 인사등록 가족 탭에서 실제로 그랬다. 계획이 적었으면 그것을 쓴다.
                  addLabel: step.rows.addLabel ?? (step.tab ? '신규' : '추가'),
                },
              }
              : {}),
          });
          // 머리 문서가 화면에 생겼다. 다시 계획해도 신규를 또 누르지 않는다.
          if (index === 0) headMade = true;
          if (step.save) {
            if (!ledger.allowsSaveFor(step.label)) {
              return {
                text: stoppedAt(screen?.name ?? made.route, finished, step.label,
                  `저장을 ${SAVE_ATTEMPTS}번 시도했습니다.`),
                code: '',
                sources: screen ? [screen.name] : [],
                task,
              };
            }
            // eslint-disable-next-line no-await-in-loop
            const committed = await deps.session.commit({
              routePath: made.route, auto: Boolean(deps.context?.auto),
            });
            if (!committed.saved) {
              // 화면이 틀린 이름으로 요구하는 자리를 먼저 바로잡는다. 그래야 사람에게
              // 나가는 말과 우리가 채우려는 칸이 같은 이름을 쓴다.
              const notices = correctNotices(committed.notices, made.route, step.tab);
              const said = readableNotices(notices) || lastSaid;
              if (said) lastSaid = said;
              // **화면은 모자란 칸을 하나씩만 알려 준다.** 그때마다 멈추면 사람이
              // 대여섯 번 답해야 한다 — 인사등록에서 주민번호·근속기준일·부서가
              // 차례로 나왔다. 화면이 이름을 댄 칸은 한 번 되돌려 채우게 한다.
              const names = demanded(notices);
              // **화면이 이름을 댄 칸을 사용자 말에서 곧바로 채운다.** 모델을 한 번 더
              // 부르는 것으로는 안정되지 않는다 — 같은 문장으로 돌린 판들이 어떤 때는
              // `부서` 를 내고 어떤 때는 통째로 빠뜨렸다. 추측이 아니다. 화면이 이름을
              // 대고 그 이름이 발화에 있을 때만 쓴다.
              const spoken = fillSpoken(step.fields ?? {}, names, screen?.draftFields, question);
              if (spoken.filled.length) {
                step.fields = spoken.fields;
                carriedSteps.set(step.label, spoken.fields);
                // eslint-disable-next-line no-console
                console.debug('[agent] 말에서 채움', made.route, step.label, spoken.filled.join(', '));
                index -= 1;
                // eslint-disable-next-line no-continue
                continue;
              }
              // **표 안의 칸도 사용자 말에서 채운다.** 인사등록에서 저장을 막은 것은
              // 언제나 행 안의 칸이었다 — `자격증번호를 입력하십시오`. 사용자는 그것을
              // 분명히 말했는데 계획이 그 열을 안 냈다(실측 여덟 판 중 두 판).
              if (step.rows?.items?.length) {
                const tab = (screen?.tabs ?? [])
                  .find((one) => one.stateKey === step.rows?.stateKey);
                const inRows = fillSpokenRows(step.rows.items, names, tab?.labels, question);
                if (inRows.filled.length) {
                  step.rows = { ...step.rows, items: inRows.items };
                  // eslint-disable-next-line no-console
                  console.debug('[agent] 행 칸을 말에서 채움', made.route, step.label,
                    inRows.filled.join(', '));
                  index -= 1;
                  // eslint-disable-next-line no-continue
                  continue;
                }
              }
              // 차단 처리는 단일 초안 경로와 **같은 것**을 쓴다. 두 벌을 두면 한쪽만
              // 고쳐지고, 실제로 그래서 걸음 쪽만 후보 없이 끝났다.
              // eslint-disable-next-line no-await-in-loop
              const settled = await settleBlocked({
                notices,
                spoken: question,
                draft: step.fields ?? {},
                deps: {
                  route: made.route,
                  screen,
                  codeLists: deps.codeLists,
                  options: deps.session.options?.bind(deps.session),
                },
                ledger,
                demanded: demandedFields,
              });
              if (settled.kind === 'filled') {
                // 고른 값만 채워 그 걸음을 다시 저장한다. 모델을 부르지 않는다.
                step.fields = { ...(step.fields ?? {}), ...settled.fields };
                carriedSteps.set(step.label, step.fields);
                const refill = { ...(allowedValues(made.route, deps) ?? {}), ...settled.allowed };
                // eslint-disable-next-line no-await-in-loop
                await deps.session.prepare({
                  routePath: made.route,
                  fields: settled.fields,
                  continueDraft: true,
                  ...(Object.keys(refill).length ? { allowed: refill } : {}),
                  ...(pairsOf(made.route, deps) ? { pairs: pairsOf(made.route, deps)! } : {}),
                });
                if (!ledger.allowsSaveFor(step.label)) {
                  return {
                    text: stoppedAt(screen?.name ?? made.route, finished, step.label,
                      `저장을 ${SAVE_ATTEMPTS}번 시도했습니다.`),
                    code: '',
                    sources: screen ? [screen.name] : [],
                    task,
                  };
                }
                // eslint-disable-next-line no-await-in-loop
                const retried = await deps.session.commit({
                  routePath: made.route, auto: Boolean(deps.context?.auto),
                });
                if (retried.saved) {
                  picked.push(...settled.said);
                  finished.push(step.label);
                  // eslint-disable-next-line no-continue
                  continue;
                }
                return {
                  text: stoppedAt(screen?.name ?? made.route, finished, step.label,
                    readableNotices(retried.notices) || said),
                  code: '',
                  sources: screen ? [screen.name] : [],
                  task,
                };
              }
              if (settled.kind === 'ask') {
                const done = finished.length ? `${finished.join('·')}까지 저장했습니다. ` : '';
                throw asking(
                  `${screen?.name ?? made.route} 에 ${done}${settled.question}`,
                  settled.options,
                  task
                    ? {
                      ...task,
                      pending: { field: settled.field, wanted: '', options: settled.options },
                      // 이미 채운 칸을 잃지 않는다. 다음 판은 고른 값 하나만 낸다.
                      draft: { route: made.route, fields: step.fields ?? {} },
                    }
                    : task,
                );
              }
              if (settled.kind === 'replan' && round < MAX_ROUNDS - 1 && budget.allows('plans')) {
                // **걸음 목록을 그대로 유지하게 이른다.** 안 그러면 모델이 막힌 걸음
                // 하나만 남기고 나머지를 지운다 — 가족·자격증을 시켰는데 기본정보만
                // 저장하고 끝났다.
                const plan = made.steps.map((one) => one.label).join(', ');
                replan = `

직전 걸음이 저장에서 막혔다: ${said}`
                  + ` 걸음은 그대로 ${plan} 이다. **그 목록을 줄이지 마라.**`
                  + ' 화면이 말한 칸만 채워 같은 걸음 계획을 다시 내라.'
                  + ' 사용자 말에 없으면 그 칸을 비워 둬라 — 지어내지 마라.';
                break;
              }
              return {
                text: stoppedAt(screen?.name ?? made.route, finished, step.label,
                  `${said}${settled.kind === 'stop' && settled.reason ? ` ${settled.reason}` : ''}`),
                code: '',
                sources: screen ? [screen.name] : [],
                task,
              };
            }
          }
          finished.push(step.label);
        } catch (error) {
          // **고를 수 없는 값 하나로 걸음을 끝내지 않는다.** 화면이 들고 온 실제 후보를
          // 버튼으로 내민다 — 단일 초안 경로에는 있던 길이 걸음 쪽에 없어서, 목록을
          // 39개 가진 부서에서도 후보 없이 「정확한 이름을 알려 주세요」로 끝났다.
          const notFound = optionNotFound(error);
          if (notFound) {
            const labels = new Map((screen?.draftFields ?? [])
              .map((field) => [field.key, plainLabel(field.label ?? field.key)]));
            const missing = labels.get(notFound.field ?? '') ?? '선택값';
            // 단일 초안과 같은 규칙이다. 두 벌을 두지 않는다 — 이유가 무엇이든
            // 후보가 비면 되묻지 않는다(§5.7).
            if (!notFound.options.length) {
              if (notFound.reason === '팝업이 안 열렸다') {
                const shut = signatureOf([`popup:${step.label}:${notFound.field ?? ''}`]);
                if (!popupRetried.has(shut) && ledger.allows(shut)) {
                  popupRetried.add(shut);
                  index -= 1;
                  // eslint-disable-next-line no-continue
                  continue;
                }
                return {
                  text: stoppedAt(screen?.name ?? made.route, finished, step.label,
                    `${topicParticle(missing)} 고르는 창이 열리지 않았습니다. 잠시 뒤 다시 말씀해 주세요.`),
                  code: '',
                  sources: screen ? [screen.name] : [],
                  task,
                };
              }
              // **읽었는데 없는 것**은 못 읽은 것과 다르다. 단일 초안 경로가 쓰는 문장을
              // 그대로 쓴다 — 「두 벌을 두지 않는다」는 이 자리의 규칙이라 한쪽만 고치면
              // 같은 실패가 걸음 경로로만 남는다.
              if (notFound.reason === '비슷한 이름을 찾지 못했다') {
                return {
                  text: stoppedAt(screen?.name ?? made.route, finished, step.label,
                    `${topicParticle(notFound.wanted ?? '')} ${missing} 목록에서 찾지 못했습니다.`
                    + ' 화면에 있는 정확한 이름을 알려 주시면 다시 해보겠습니다.'),
                  code: '',
                  sources: screen ? [screen.name] : [],
                  task,
                };
              }
              return {
                text: stoppedAt(screen?.name ?? made.route, finished, step.label,
                  `${topicParticle(missing)} 목록을 화면에서 읽지 못했습니다.`),
                code: '',
                sources: screen ? [screen.name] : [],
                task,
              };
            }
            const missed = notFound.wanted
              && !notFound.options.some((option) => option === notFound.wanted)
              ? `${topicParticle(notFound.wanted)} 화면 목록에 없습니다. `
              : '';
            const done = finished.length ? `${finished.join('·')}까지 저장했습니다. ` : '';
            const decided = choose(question, {
              notice: '',
              label: missing,
              field: notFound.field,
              kind: 'popup',
              observed: notFound.wanted ?? '',
              alternatives: notFound.options,
              source: 'popup',
              signature: signatureOf([notFound.wanted ?? '']),
            });
            const mark = `${step.label}:${notFound.field ?? ''}=${decided.kind === 'pick' ? decided.value : ''}`;
            if (decided.kind === 'pick' && notFound.field && !autoPicked.has(mark)) {
              autoPicked.add(mark);
              step.fields = { ...(step.fields ?? {}), [notFound.field]: decided.value };
              carriedSteps.set(step.label, step.fields);
              picked.push(`${topicParticle(missing)} ${decided.value} 로 넣었습니다`);
              index -= 1;
              // eslint-disable-next-line no-continue
              continue;
            }
            throw asking(
              `${screen?.name ?? made.route} 에 ${done}${missed}`
              + `${decided.kind === 'ask' ? decided.question : `${topicParticle(missing)} 어느 것입니까?`}`,
              decided.kind === 'ask' ? decided.options : notFound.options,
              task
                ? {
                  ...task,
                  ...(notFound.field
                    ? {
                      pending: {
                        field: notFound.field,
                        wanted: notFound.wanted ?? '',
                        options: notFound.options,
                      },
                    }
                    : {}),
                  // 이미 채운 칸을 잃지 않는다. 다음 판은 고른 값 하나만 낸다.
                  draft: { route: made.route, fields: step.fields ?? {} },
                }
                : task,
            );
          }
          // **사용자가 말하지 않은 칸 하나가 걸음을 죽이지 않는다.**
          //
          // 계획이 시키지도 않은 칸을 얹는 판이 있다 — 인사등록에서 결혼여부(`mrgyn`)를
          // 넣었고 그 체크칸이 안 바뀌자 문서 전체가 죽었다. 사람이 말한 값이면 그것은
          // 진짜 실패지만, 말하지 않은 값이면 우리가 얹은 군더더기다. 빼고 다시 한다.
          const raw = error instanceof Error ? error.message : String(error);
          const blamed = (screen?.draftFields ?? []).find((field) => (
            raw.includes(field.key)
            && step.fields?.[field.key] !== undefined
            && !lockedFields.has(field.key)
            && !spokenValue(question, plainLabel(field.label ?? ''))
          ));
          if (blamed && step.fields) {
            lockedFields.add(blamed.key);
            const trimmed = { ...step.fields };
            delete trimmed[blamed.key];
            step.fields = trimmed;
            carriedSteps.set(step.label, trimmed);
            // eslint-disable-next-line no-console
            console.debug('[agent] 군더더기 뺌', made.route, step.label, blamed.key);
            index -= 1;
            // eslint-disable-next-line no-continue
            continue;
          }
          // 사람에게 나가는 말은 다듬지만, **원문은 남긴다.** 다듬은 말로는 무엇이
          // 막았는지 못 고친다 — 가족 걸음이 왜 죽는지 보려는데 볼 것이 없었다.
          // eslint-disable-next-line no-console
          console.debug('[agent] 걸음 실패', made.route, step.label,
            error instanceof Error ? error.message.slice(0, 300) : String(error).slice(0, 300));
          // **내부 이름을 사람에게 보이지 않는다.** `divicd 을 검색 팝업에서 고르지
          // 못했습니다` 는 사무직에게 아무 말도 아니다. 화면에 적힌 이름으로 바꾼다.
          const named = (screen?.draftFields ?? []).reduce(
            (text, field) => (field.label ? text.split(field.key).join(plainLabel(field.label)) : text),
            userMessage(error),
          );
          return {
            text: stoppedAt(screen?.name ?? made.route, finished, step.label, named),
            code: '',
            sources: screen ? [screen.name] : [],
            task,
          };
        }
      }
      if (replan) {
        note = replan;
        retryDraftInPlace = true;
        // eslint-disable-next-line no-continue
        continue;
      }
      return {
        text: `${screen?.name ?? made.route} 에 ${finished.join('·')}까지 넣고 저장했습니다.`
          + `${picked.length ? ` ${picked.join(', ')}.` : ''}`,
        code: '',
        sources: screen ? [screen.name] : [],
        task,
      };
    }

    // Session은 같은 label을 두 번 받지 않으므로 재시도 라운드에는 접미사를 붙인다.
    const relabel = (label: string) => (round === 0 ? label : `${label}__retry${round}`);
    // 「지난달 것까지」만 말했는데 그보다 이른 달까지 조회하려 하면 그 조회는 뺀다(실측 2026-10-26 06:37·07:17·07:33).
    // 앞 질문(이어받은 기간)과 과제의 기간도 본다(독립 검토).
    const asked = withinAskedMonths(made.retrievals, question, localToday(), {
      history: (deps.context?.history ?? []).slice(-2).map((turn) => turn.question ?? ''),
      taskMonths: task ? periodMonths(task.filters, localToday()).months : [],
    });
    if (asked.length !== made.retrievals.length) {
      // 값은 안 찍는다. 화면과 달만.
      // eslint-disable-next-line no-console
      console.debug('[agent] 묻지 않은 달 조회 뺌', made.retrievals.filter((one) => asked.indexOf(one) < 0)
        .map((one) => `${one.route} ${Object.values(one.conditions).map((value) => monthKey(String(value ?? ''))).filter(Boolean).join(',')}`).join(' | '));
    }
    const retrievals = collapseFanned(asked, deps.screens).map((retrieval) => ({
      ...retrieval,
      label: relabel(retrieval.label),
      // 앞 조회를 가리키는 이름도 같이 바꾼다 — 안 바꾸면 대상이 없다고 보고 펼치지 않는다.
      ...(retrieval.sweep?.matchFrom
        ? { sweep: { ...retrieval.sweep, matchFrom: { ...retrieval.sweep.matchFrom, label: relabel(retrieval.sweep.matchFrom.label) } } }
        : {}),
    }));
    plan = { ...made, kind: 'retrieve', retrievals };
    retrieved = {};
    let unavailableRoute: string | undefined;
    // 조건을 하나 빼고 다시 조회해도 또 없는 조건이 나온 화면. 계속 빼면 기간까지
    // 사라져 전체 기간을 조회한다 — 그 답은 틀린 줄도 모르고 나간다.
    let lacking: { route: string; conditions: string[] } | undefined;
    for (const retrieval of retrievals) {
      report({
        kind: 'retrieving',
        label: retrieval.label,
        done: Object.keys(retrieved).length + 1,
        total: retrievals.length,
      });
      const master = deps.screens.find((screen) => screen.route === retrieval.route)
        ?.grids.find((grid) => grid.key === retrieval.rowsStateKey)?.master;
      try {
        // 순서대로 한다. 같은 화면을 조건만 바꿔 다시 조회하는 것이 흔하고,
        // 그것은 동시에 할 수 없다.
        // eslint-disable-next-line no-await-in-loop
        budget.spend('retrievals');
        if (retrieval.sweep && master && deps.session.sweepDetail) {
          // eslint-disable-next-line no-await-in-loop
          retrieved[retrieval.label] = await runSweep(deps, retrieval, retrieval.label, retrieval.conditions, retrieved);
          // eslint-disable-next-line no-continue
          continue;
        }
        // 펼쳐 읽지 못하면(구버전 드라이버) 상세 한 건만 온다 — sweep 을 지워야 답이 「선택된 한 건」이라고 말한다.
        delete retrieval.sweep;
        retrieved[retrieval.label] = await deps.session.retrieve({
          label: retrieval.label,
          routePath: retrieval.route,
          rowsStateKey: retrieval.rowsStateKey,
          conditions: retrieval.conditions,
          scalarKeys: deps.screens.find((screen) => screen.route === retrieval.route)?.scalars
            ?.map((scalar) => scalar.key),
          selectionStateKeys: deps.screens.find((screen) => screen.route === retrieval.route)?.grids
            .map((grid) => grid.key)
            .filter((key) => /^openChk\d*$/i.test(key)),
          // 같은 화면·같은 조건이 이미 떠 있으면 드라이버가 그 목록을 다시 쓴다. 사람이 새로 받으라고 했을 때만 다시 조회한다.
          ...(wantsFreshData(question) ? { force: true } : {}),
        });
      } catch (error) {
        // 카탈로그는 화면을 다 알지만 사용자의 메뉴는 권한만큼만 있다. 못 여는 화면을
        // 골랐으면 그 화면을 빼고 다시 계획한다 — 여기서 끝내면 답할 길이 남아 있는데도
        // 사용자는 아무것도 못 받는다.
        const message = error instanceof Error ? error.message : String(error);
        // 계획이 화면에 없는 조건을 넣었다. 그것은 다시 세우면 고칠 수 있는 실수다 —
        // 탭마다 조건이 다른 화면이 있어서 카탈로그에 있어도 지금 열린 탭에는 없다.
        // 여기서 끝내면 고칠 수 있는 것을 놓고 사용자가 빈손이 된다.
        // 계획이 화면에 없는 조건을 넣었다. 탭마다 조건이 다른 화면이 있어 카탈로그에
        // 있어도 지금 열린 탭에는 없다 — 모델에게 다시 세우라고 해도 카탈로그를 보고
        // 같은 것을 또 넣는다. 그 조건만 빼고 여기서 다시 조회한다.
        const missing = /검색조건 (\S+) 이 화면에 없다/.exec(message)?.[1];
        if (missing && missing in retrieval.conditions && budget.allows('retrievals')) {
          const { [missing]: _dropped, ...rest } = retrieval.conditions;
          retrieval.conditions = rest;
          budget.spend('retrievals');
          try {
            // 펼쳐 읽던 것은 조건만 빼고 그대로 펼쳐 읽는다 — 한 건 조회로 바꾸면 상세 한 건만 온다.
            if (retrieval.sweep && master && deps.session.sweepDetail) {
              // eslint-disable-next-line no-await-in-loop
              retrieved[retrieval.label] = await runSweep(deps, retrieval, `${retrieval.label}__조건뺌`, rest, retrieved);
              // eslint-disable-next-line no-continue
              continue;
            }
            // eslint-disable-next-line no-await-in-loop
            retrieved[retrieval.label] = await deps.session.retrieve({
              // 세션은 같은 이름을 두 번 쓰지 못하게 한다. 결과는 원래 자리에 넣는다.
              label: `${retrieval.label}__조건뺌`,
              routePath: retrieval.route,
              rowsStateKey: retrieval.rowsStateKey,
              conditions: rest,
              scalarKeys: deps.screens.find((screen) => screen.route === retrieval.route)?.scalars
                ?.map((scalar) => scalar.key),
              selectionStateKeys: deps.screens.find((screen) => screen.route === retrieval.route)
                ?.grids.map((grid) => grid.key).filter((key) => /^openChk\d*$/i.test(key)),
            });
            // eslint-disable-next-line no-continue
            continue;
          } catch (retryError) {
            // 뺀 조회가 또 없는 조건에 걸렸다. 이 `await` 는 catch 안이라 받아 주는
            // 사람이 없었고, 그대로 밖으로 나가 턴 전체가 죽었다 — 보고서 R9 가
            // 세 턴을 그렇게 잃었다(`stdate` 를 뺐더니 `enddate` 로 걸렸다).
            // 남은 조건까지 빼면 기간 없는 전체 조회가 되므로, 이 화면에 없는 조건을
            // 적어 다시 계획한다. 실제로 그때 모델은 조건이 맞는 다른 화면을 고른다.
            const retryMessage = retryError instanceof Error
              ? retryError.message : String(retryError);
            const alsoMissing = /검색조건 (\S+) 이 화면에 없다/.exec(retryMessage)?.[1];
            if (!alsoMissing || round === MAX_ROUNDS - 1) throw retryError;
            lacking = { route: retrieval.route, conditions: [missing, alsoMissing] };
            break;
          }
        }
        if (!/메뉴에 \S+ 가 없다/.test(message) || round === MAX_ROUNDS - 1) throw error;
        unavailableRoute = retrieval.route;
        offLimits.add(retrieval.route);
        break;
      }
    }
    if (unavailableRoute) {
      note = `\n\n이 사용자의 메뉴에 ${unavailableRoute} 화면이 없다(권한 없음).`
        + ' 그 화면은 쓰지 말고 같은 업무를 볼 수 있는 다른 화면으로 계획하라.';
      // eslint-disable-next-line no-console
      console.debug('[agent] 계획 다시(권한 없음)', unavailableRoute);
      // eslint-disable-next-line no-continue
      continue;
    }
    if (lacking) {
      // 조건이 없다고만 말하면 모델은 **또 그런 화면을 고른다.** 카탈로그를 훑어
      // 이름만 보고 고르기 때문이다 — 공사 실적(R9)이 세 라운드를 그렇게 태웠다.
      // 그 조건을 실제로 가진 화면을 세어 알려 준다. 고르는 일이 훨씬 쉬워진다.
      const able = screensWith(deps.screens, lacking.conditions, lacking.route);
      note = `\n\n${lacking.route} 화면에는 검색조건 ${lacking.conditions.join(', ')} 이(가) 없다.`
        + ' 그 조건 없이 조회하면 기간이 빠져 엉뚱한 범위가 나온다.'
        + ' 그 조건을 실제로 가진 다른 화면으로 계획하라.'
        + (able.length ? ` 그 조건을 모두 가진 화면: ${able.join(', ')}` : '');
      // eslint-disable-next-line no-console
      console.debug('[agent] 계획 다시(조건 없음)', lacking.route, lacking.conditions.join(','));
      // eslint-disable-next-line no-continue
      continue;
    }

    // 행이 하나도 안 왔으면 같은 화면의 다른 표를 본다. 월별입금현황은 현장별과
    // 거래처별이 열 구성까지 같아서 어느 쪽인지 고를 수가 없다 — 계획이 빈 쪽을 집으면
    // 화면 합계에는 백팔십억이 있는데 답은 `0원` 이 된다. 화면은 이미 조회된 상태라
    // 다른 상태 키를 읽어 보는 것은 싸다.
    // eslint-disable-next-line no-await-in-loop
    await tryOtherGrids(plan, retrieved, deps, budget);

    // eslint-disable-next-line no-await-in-loop
    await expandBusinessSites(plan, retrieved, deps, budget);

    let outcomes = plan.retrievals.map((retrieval) => (
      classifyRetrieved(retrieval, retrieved[retrieval.label], freeTextColumns(retrieval, deps.screens))
    ));
    if (outcomes.every((outcome) => outcome.kind === 'success') || round === MAX_ROUNDS - 1) break;
    let recovery = decideRecovery(outcomes, round);
    if (recovery.kind === 'stop') throw new RecoveryStoppedError(recovery.reason);
    if (recovery.kind === 'retry-same') {
      // eslint-disable-next-line no-console
      console.debug('[agent] 같은 조회 다시', recovery.reason,
        outcomes.map((outcome, at) => `${plan.retrievals[at]?.label ?? at}:${outcome.kind}`).join(' '));
      const settledLabel =(label: string) => `${label.replace(/__(?:retry|settled)\d+$/, '')}__settled${round + 1}`;
      const settledRetrievals = plan.retrievals.map((retrieval) => ({
        ...retrieval,
        label: settledLabel(retrieval.label),
        // 앞 조회를 가리키는 이름도 같이 바꾼다 — 다시 읽은 앞 조회의 값으로 좁혀야 한다.
        ...(retrieval.sweep?.matchFrom
          ? { sweep: { ...retrieval.sweep, matchFrom: { ...retrieval.sweep.matchFrom, label: settledLabel(retrieval.sweep.matchFrom.label) } } }
          : {}),
      }));
      const settledPlan = { ...plan, retrievals: settledRetrievals };
      const settled: Record<string, Retrieved> = {};
      // 앱은 반복자를 for-of 로 못 돈다(downlevelIteration 없음) — 번호로 돈다.
      for (let at = 0; at < settledRetrievals.length; at += 1) {
        const retrieval = settledRetrievals[at]!;
        report({
          kind: 'retrieving', label: retrieval.label,
          done: Object.keys(settled).length + 1, total: settledRetrievals.length,
        });
        // **펼쳐 읽은 것은 다시 읽지 않는다.** 한 건 조회로 다시 읽으면 앱이 저절로 고른 한 행의 상세만
        // 와서, 그것이 전 기간 자료로 답에 나간다(검토, 2026-10-21). 펼쳐 읽기는 이미 위 표를 새로 받았다.
        const before = retrieved[plan.retrievals[at]?.label ?? ''];
        // 앞 조회가 비어 건너뛴 펼쳐 읽기는 다시 읽은 앞 조회로 새로 한다 — 복사하면 「대상 없음」이 남는다.
        if (retrieval.sweep?.matchFrom && before?.swept && before.swept.read === 0 && deps.session.sweepDetail) {
          // eslint-disable-next-line no-await-in-loop
          settled[retrieval.label] = await runSweep(deps, retrieval, retrieval.label, retrieval.conditions, settled);
          // eslint-disable-next-line no-continue
          continue;
        }
        if (retrieval.sweep && before?.swept) {
          settled[retrieval.label] = { ...before, label: retrieval.label };
          // eslint-disable-next-line no-continue
          continue;
        }
        // 렌더링 지연 확인은 계획을 다시 세우지 않고 같은 화면·조건을 강제로 새로 읽는다.
        // eslint-disable-next-line no-await-in-loop
        settled[retrieval.label] = await deps.session.retrieve({
          label: retrieval.label,
          routePath: retrieval.route,
          rowsStateKey: retrieval.rowsStateKey,
          conditions: retrieval.conditions,
          scalarKeys: deps.screens.find((screen) => screen.route === retrieval.route)?.scalars
            ?.map((scalar) => scalar.key),
          selectionStateKeys: deps.screens.find((screen) => screen.route === retrieval.route)?.grids
            .map((grid) => grid.key)
            .filter((key) => /^openChk\d*$/i.test(key)),
          force: true,
        });
      }
      plan = settledPlan;
      retrieved = settled;
      outcomes = plan.retrievals.map((retrieval) => (
        classifyRetrieved(retrieval, retrieved[retrieval.label], freeTextColumns(retrieval, deps.screens))
      ));
      if (outcomes.every((outcome) => outcome.kind === 'success')) break;
      recovery = decideRecovery(outcomes, round + 1);
      if (recovery.kind === 'stop') throw new RecoveryStoppedError(recovery.reason);
    }
    if (emptyOnPinnedTable(outcomes, plan, selectedSkills, question, deps.screens)) {
      // eslint-disable-next-line no-console
      console.debug('[agent] 빈 것은 자료 사정 — 다시 계획하지 않는다',
        outcomes.map((outcome) => outcome.message).filter(Boolean).join(' / '));
      break;
    }
    // **왜 다시 계획하는지 남긴다.** 이 갈래는 아무 말도 안 하고 계획 프롬프트(20k 자)를
    // 통째로 다시 보낸다 — 「이번 달 점검 계획」 한 턴이 `plan×3`(56k 자)이었는데
    // 되묻기 기록이 없어 출처를 한참 찾았다(2026-09-22).
    // eslint-disable-next-line no-console
    console.debug('[agent] 계획 다시(조회)', `${round + 1}라운드`, recovery.kind,
      outcomes.map((outcome, at) => `${plan.retrievals[at]?.label ?? at}:${outcome.kind}`).join(' '));
    if (recovery.kind === 'replan-screen' && !firstEmpty
      && outcomes.every((outcome) => outcome.kind === 'legitimate-empty')) {
      firstEmpty = { plan, retrieved: { ...retrieved } };
    }
    note = retryNote(outcomes, plan, deps.screens) + recoveryNote(recovery);
  }

  // **옮긴 화면도 비었으면 처음 고른 화면으로 답한다.** 빈 조회 뒤 「다른 화면을 골라라」는 자료가
  // 있는 화면을 찾으려는 것이지, 질문에 덜 맞는 화면으로 답하려는 것이 아니다. 둘 다 비면 처음 것이
  // 질문에 가장 맞게 고른 화면이다 — 「오늘 총무팀 업무일지」가 [총무팀] → 작성 → [총무팀]으로 돌아
  // 한 판은 [총무팀]으로, 다른 판은 작성으로 「자료 없음」을 답했다(실제 테넌트, 2026-10-06·07).
  // 마지막에 어느 화면이 걸리느냐가 판마다 갈렸다. 사람이 보는 화면도 그 화면으로 되돌린다.
  if (firstEmpty
    && plan.retrievals.every((retrieval) => (retrieved[retrieval.label]?.rows.length ?? 0) === 0)
    && !plan.retrievals.every((one) => firstEmpty?.plan.retrievals.some((first) => first.route === one.route))) {
    // eslint-disable-next-line no-console
    console.debug('[agent] 옮긴 화면도 비었다 — 처음 화면으로 답한다',
      firstEmpty.plan.retrievals.map((one) => one.route).join(','));
    plan = firstEmpty.plan;
    retrieved = firstEmpty.retrieved;
    for (const retrieval of plan.retrievals) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await deps.session.retrieve({
          label: `${retrieval.label.replace(/__(?:retry|settled)\d+$/, '')}__back`,
          routePath: retrieval.route,
          rowsStateKey: retrieval.rowsStateKey,
          conditions: retrieval.conditions,
          force: true,
        });
      } catch {
        // 화면을 되돌리지 못해도 답은 처음 조회로 한다 — 그 결과는 이미 손에 있다.
      }
    }
  }

  // 빈 조회를 답으로 내기 전에 사람이 안 말한 상태 조건부터 넓혀 본다(`widenUnsaid`).
  const emptyFacts: string[] = [];
  let widenedLead = '';
  if (plan.retrievals.length
    // 펼쳐 읽은 조회는 넓혀 다시 읽지 않는다 — 넓히는 조회는 한 건 조회라 상세 한 건만 읽는다.
    && !plan.retrievals.some((retrieval) => retrieval.sweep)
    && plan.retrievals.every((retrieval) => (retrieved[retrieval.label]?.rows.length ?? 0) === 0)) {
    const widened = await widenUnsaid(plan, deps);
    if (widened?.found) {
      plan = widened.plan;
      retrieved = widened.retrieved;
      widenedLead = `화면에 기본으로 걸려 있던 ${widened.names} 조건으로는 자료가 없어서,`
        + ` ${objectParticle(widened.names)} 전체로 넓혀 다시 봤습니다.`;
    }
    if (widened) emptyFacts.push(widened.fact);
    // 그래도 비었고 찾는 물음이면 기간을 넓힌다(`widenPeriod`) — 사람이 0건 뒤에 하는 일이다.
    if (!widened?.found) {
      const longer = await widenPeriod(plan, question, deps);
      if (longer) {
        plan = longer.plan;
        retrieved = longer.retrieved;
        widenedLead = `처음 기간에는 없어서 ${longer.from.slice(0, 4)}년 1월 1일부터 오늘까지로 넓혀 찾았습니다.`;
        emptyFacts.length = 0;
      }
    }
  }

  // 카탈로그가 아는 컬럼을 쓴다. 행에서 키를 긁으면 화면이 쓰지 않는 칸까지
  // 딸려 오고, 모델이 그 빈 칸을 골라 숫자를 지어낸다.
  const declared: Record<string, { id: string; text?: string; concept?: string }[]> = {};
  // **펼쳐 읽은 위 표 행은 따로 준다.** 상세 행에는 상세가 있는 위 표 행만 남는다 — 「최근 입사자 중
  // 경력직 비율」에서 경력이 없는 사람이 분모에서 사라진다(2026-10-21).
  for (const retrieval of plan.retrievals) {
    const result = retrieved[retrieval.label];
    // eslint-disable-next-line no-continue
    if (!retrieval.sweep || !result?.masterRows?.length) continue;
    const screen = deps.screens.find((candidate) => candidate.route === retrieval.route);
    const masterKey = screen?.grids.find((candidate) => candidate.key === retrieval.rowsStateKey)?.master?.stateKey;
    const label = `${retrieval.label} 위표`;
    retrieved[label] = { label, rows: result.masterRows, answered: true };
    const columns = screen?.grids.find((candidate) => candidate.key === masterKey)?.columns;
    // 행마다 상세가 몇 줄이었는지가 이 표의 요점이다 — 안 알려 주면 계산이 이 표를 상세로 읽고
    // 「4명 모두 경력직」이라 답했다(개발 테넌트, 2026-10-21).
    const counted = result.masterRows.some((row) => 'detailRows' in row)
      ? [{ id: 'detailRows', text: `이 행의 「${retrieval.label}」 상세 행 수 — 0 이면 읽었는데 상세가 없다, 칸이 비면 못 읽었다` }]
      : [];
    if (columns?.length || counted.length) {
      declared[label] = [
        ...(columns ?? []).map((column) => ({
          ...column, text: `${column.text ?? column.id} — 펼쳐 읽은 위 표 전체(대상마다 한 행), 상세는 「${retrieval.label}」 표에 있다`,
        })),
        ...counted,
      ];
    }
  }
  const declaredScalars: Record<string, { key: string; label: string }[]> = {};
  for (const retrieval of plan.retrievals) {
    const screen = deps.screens.find((candidate) => candidate.route === retrieval.route);
    const grid = screen?.grids.find((candidate) => candidate.key === retrieval.rowsStateKey);
    // 펼쳐 읽었으면 위 표 칸(일자 등)이 행마다 붙어 있다 — 안 알려 주면 계산은 「날짜가 없다」고 한다
    // (일근태등록 1,762행, 2026-10-21). 상세 표에 같은 칸이 있으면 그쪽이 이긴다(드라이버가 안 덮는다).
    // 위 표 칸은 위 표 한 행의 상세마다 같은 값이 붙는다 — 그렇다고 적지 않으면 문서 합계 같은 위 표 금액을
    // 항목 수만큼 더한다(검토, 2026-10-21).
    const masterColumns = retrieval.sweep && grid?.master
      ? (screen?.grids.find((candidate) => candidate.key === grid.master!.stateKey)?.columns ?? [])
        .filter((column) => !grid.columns.some((own) => own.id === column.id))
        .map((column) => ({ ...column, text: `${column.text ?? column.id} — 위 표 칸, 상세 행마다 같은 값이 반복된다` }))
      : [];
    if (grid) declared[retrieval.label] = withCodeLegend([...grid.columns, ...masterColumns], screen?.fields);
    if (screen?.scalars?.length) declaredScalars[retrieval.label] = screen.scalars;
  }
  // 코드 뜻을 아는 칸(결과 1=계획, 2=완료)은 계산 전에 우리가 글자로 바꾼다 — 모델에게 맡기면 판마다 달랐다
  // (「완료 0·계획 0·기타 7,017」, 실제 테넌트 2026-10-23). 계산·답·보고서가 모두 같은 글자를 본다.
  Object.keys(declared).forEach((label) => {
    const result = retrieved[label];
    if (!result?.rows.length) return;
    const decoded = decodeRows(result.rows as Record<string, unknown>[], declared[label] as Column[]);
    if (decoded.rows === result.rows) return;
    retrieved[label] = { ...result, rows: decoded.rows as typeof result.rows };
    declared[label] = decoded.columns;
  });
  // 연차 자료에서 이름에 숫자가 붙은 계정(같은 사람을 관리상 두 번 등록한 계정)은 계산 전에 뺀다(`duplicateAccounts`). 계산, 답,
  // 엑셀, 다음 턴 문서가 모두 같은 대상을 센다. 연차·휴가를 물은 질문이고 화면이 연차·휴가 화면일 때만이다. 급여현황의 「연차환급금」,
  // 근태 화면의 「연차」 횟수만 보고 급여 합계와 근태 인원에서 사람을 뺐다(독립 재검토). 펼쳐 읽는 조회는 위 표와 상세가 어긋나니 둔다.
  // 이번 턴 판정(`duplicateDecision`)이 묻기거나 넣기 상태면 빼지 않는다. 그 계정의 이름을 말한 질문에도 빼지 않는다. 판정이 어긋난
  // 채 남에게 나갈 문서를 만들려 하면 계산 전에 넣을지 뺄지 묻는다.
  const duplicates: { label: string; line: string; answer: string }[] = [];
  const numbered = new Set<string>();
  const keepNumbered = keepState;
  // 넣으라고 했거나 넣기 상태를 이어 남긴 계정. 답에 「넣어서 셌습니다」를 코드가 쓴다. 묻기는 답이 그 계정 얘기라 쓰지 않는다.
  const included: { label: string; names: string[] }[] = [];
  const including = decision.action === 'include' || (keepState && decision.action !== 'ask');
  // 계산 표에 남긴 계정(결과 기록의 `numbered`). 다음 턴 문서가 이 표를 그대로 쓸 때 넣고 빼기를 맞춘다.
  const leftIn = new Set<string>();
  if (/연차|휴가/.test(`${question} ${task?.goal ?? ''}`)) {
    plan.retrievals.forEach((retrieval) => {
      const screenName = deps.screens.find((one) => one.route === retrieval.route)?.name ?? '';
      const result = retrieved[retrieval.label];
      if (retrieval.sweep || !result?.rows.length || !/연차|휴가/.test(screenName) || /급여|근태|수당|정산|임금/.test(screenName)) return;
      const found = duplicateAccounts(result.rows as Record<string, unknown>[], declared[retrieval.label] ?? []);
      if (!found) return;
      // 계정 이름을 말한 질문은 그 계정을 묻는 말이라 빼지 않는다. 빼라고 했으면 뺀다(독립 재검토: 「차직원2는 빼고」에 13행이 갔다).
      const named = decision.action !== 'exclude' && found.names.some((one) => question.includes(one));
      if (named || decision.action === 'ask' || decision.action === 'include' || keepState) {
        found.names.forEach((one) => leftIn.add(one));
        if (named) return;
        found.names.forEach((one) => numbered.add(one));
        if (including) included.push({ label: bareLabel(retrieval.label), names: found.names });
        return;
      }
      found.names.forEach((one) => numbered.add(one));
      retrieved[retrieval.label] = { ...result, rows: found.rows as typeof result.rows };
      duplicates.push({ label: bareLabel(retrieval.label), line: found.line, answer: found.answer });
    });
  }
  if (decision.doubted && numbered.size && documentTurn(question, deps.screens)) {
    throw askDuplicates(numbered.size, task, documentKind(question, deps.screens));
  }
  // 연차 조회가 둘이면(작년과 올해) 문장마다 조회 이름을 밝힌다(독립 재검토: 해 표시 없이 같은 문장이 이어졌다).
  const excludedLines = duplicates.length > 1
    ? duplicates.map((one) => ({ line: `(${one.label}) ${one.line}`, answer: `${one.label} 자료: ${one.answer}` }))
    : duplicates;
  const validation = validateRetrieved(plan, retrieved);
  // 행은 왔는데 열 하나가 비었을 뿐이면 답을 버릴 이유가 없다. `수금은 잘 되고 있어?` 가
  // 미수금 열이 비었다는 이유로 통째로 죽었다 — 조회는 됐고 다른 열에는 값이 있었다.
  // 표가 아예 비었거나 요청한 열이 없는 것은 다시 계획할 일이라 그대로 둔다.
  const onlyEmptyColumns = !validation.valid
    && validation.issues.every((issue) => issue.code === 'empty-column');
  // **한 조회가 비었다고 다른 조회까지 버리지 않는다.** 민원을 물었을 때 현장 이름으로
  // 건진 다섯 행은 멀쩡했는데, 같이 던진 `소음` 검색이 0행이라 답이 통째로 죽었다 —
  // 사용자는 화면에 있는 것을 두고 `조건에 맞는 자료가 없습니다` 를 봤다.
  const someRows = plan.retrievals.some((retrieval) => (retrieved[retrieval.label]?.rows.length ?? 0) > 0);
  const onlySomeEmpty = !validation.valid && someRows
    && validation.issues.every((issue) => issue.code === 'empty-result' || issue.code === 'empty-column');
  // **빈 것이 곧 답인 질문이 있다.** `이번 달 자체점검 아직 안 한 현장 있어?` 에 계획이
  // 0건이면 그것은 "안 한 곳이 없다" 가 아니라 "이번 달 계획이 아직 없다" 는 뜻이다.
  // 조회는 제대로 됐으므로(권한·화면 오류가 아니다) 그 사실을 답으로 만들게 둔다 —
  // 여기서 죽이면 사용자는 `조건에 맞는 자료가 없습니다` 한 줄만 받는다.
  const allEmpty = !validation.valid
    && validation.issues.every((issue) => issue.code === 'empty-result');
  if (!validation.valid && !onlyEmptyColumns && !onlySomeEmpty && !allEmpty) {
    // 여기서 죽으면 답변 끝의 `[agent]` 추적 줄을 못 찍는다. 사용자는 `조건에 맞는
    // 자료가 없습니다` 만 보고, 로그에는 라벨 하나뿐이라 **어느 화면을 어떤 조건으로**
    // 조회해 비었는지가 남지 않는다 — 인사 보고서(R6)를 그 상태로 두 번 헤맸다.
    // eslint-disable-next-line no-console
    console.debug('[agent] 조회 실패', {
      질문: question.replace(/\s+/g, ' ').slice(0, 60),
      조회: plan.retrievals.map((retrieval) => `${retrieval.label}: ${retrieval.route}`
        + `/${retrieval.rowsStateKey}(${retrieved[retrieval.label]?.rows.length ?? 0}행)`
        + ` ${JSON.stringify(retrieval.conditions)}`).join(' | '),
      막힌것: validation.issues.map((issue) => `${issue.code}: ${issue.message}`).join(' | '),
      예산: budget.describe(),
    });
    throw new ExecutionValidationError(validation);
  }
  const emptyColumns = onlyEmptyColumns || onlySomeEmpty
    ? validation.issues.map((issue) => issue.message)
    : [];
  report({ kind: 'computing' });
  const resolvedQuestion = task ? structuredQuestion : effectiveQuestion(question, deps.context);
  // 자료 읽기(표가 무엇을 말하는지): 조회 턴에는 분석 틀에 적어 둔 화면만이다(모델 호출 없음). 모델 읽기는 문서를 만들 때 한다.
  const reading = readingFor({
    retrievals: plan.retrievals,
    rows: Object.fromEntries(Object.entries(retrieved).map(([label, result]) => [label, result.rows])),
    declared, experts: deps.experts ?? [],
  });
  const wantsFile = requestedFormats(resolvedQuestion).length > 0;
  const metricSkill = selectedSkills.find((skill) => skill.kind === 'metric'
    && skill.calculationCode
    && plan.retrievals.some((retrieval) => skill.preferredRoutes?.includes(retrieval.route)));
  const shapes = shapesOf(retrieved, declared, declaredScalars).map((shape) => {
    const retrieval = plan.retrievals.find((one) => one.label === shape.label);
    const conditions = retrieval ? conditionText(retrieval, deps.screens, retrieved[retrieval.label]?.applied) : '';
    return conditions ? { ...shape, conditions } : shape;
  });
  // 스킬의 업무 규칙은 계획만 받았다 — 계산이 못 받아 「거리 값이 있으면 모바일」로 셌다(운영, 2026-10-20).
  const rules = [
    skillRules(selectedSkills, plan),
    // 우리가 세운 판정 기준도 업무 규칙이다 — 계산이 그대로 세고, 검토가 그것으로 본다.
    derived ? `[판정 기준]\n${derived.criterion}${derived.assumptions ? `\n정한 범위: ${derived.assumptions}` : ''}` : '',
  ].filter(Boolean).join('\n\n');
  // 계산이 오류로 멈추면 아래에서 다시 짠다. 검증도 **실제로 돈 코드**를 봐야 한다.
  let code = metricSkill?.calculationCode ?? await makeCode(
    resolvedQuestion,
    shapes,
    deps.chat,
    wantsFile,
    plan.requirements,
    rules,
  );
  const rows = Object.fromEntries(
    Object.entries(retrieved).map(([label, result]) => [label, result.rows]),
  );
  const totals = Object.fromEntries(
    Object.entries(retrieved).map(([label, result]) => [label, plainTotals(result.totals ?? {})]),
  );
  // 부팅은 몇 초, 계산은 그렇지 않다. 한 예산에 묶으면 첫 계산이 늘 시간 초과다.
  if (deps.sandbox.warmup) await deps.sandbox.warmup();
  let value: unknown;
  const rowsRetrieved = Object.values(rows).reduce((count, list) => count + list.length, 0);
  const verifyValue = (candidate: unknown, candidateCode: string) => verifyResult({
    value: candidate,
    code: candidateCode,
    shapes,
    requirements: plan.requirements,
    question: resolvedQuestion,
    rowsRetrieved,
    totals,
    concepts: deps.concepts,
    retrievals: plan.retrievals.map((retrieval) => ({
      conditions: retrieval.conditions,
      rows: retrieved[retrieval.label]?.rows ?? [],
    })),
  });
  // **답이 될 때까지 다시 계산한다 — 시도는 3번, 규칙 검토는 시도마다.** 예전에는 오류·검증 거절마다 한 번씩만
  // 다시 짰고, 두 번째 오류는 턴을 통째로 버렸다. 같은 근태 질문이 한 판은 맞고, 한 판은 규칙과 다르게 세고,
  // 한 판은 계산 오류 두 번으로 「요청을 처리하지 못했습니다」였다(2026-10-20). 매 시도는 직전 시도가 왜
  // 안 됐는지를 들고 간다 — `다시 해봐` 보다 `거리 0 도 모바일로 셌다` 가 훨씬 잘 듣는다.
  // 계획 예산은 쓰지 않는다. 조회는 끝났고 남은 것은 파이썬 한 벌이다 — 상한은 시도 수와 시간이다.
  const MAX_COMPUTE = 3;
  // 검토는 시도마다 한다. 두 번까지였을 때는 지적대로 고친 세 번째 값이 검토를 못 받아 늘
  // 「맞지 않을 수 있습니다」를 달고 나갔다(인사회의 입퇴사 문서, 실제 테넌트 2026-10-23).
  const MAX_REVIEWS = MAX_COMPUTE;
  let reviews = 0;
  let verdict: ReturnType<typeof verifyResult> = { ok: false, cause: 'useless-result', reason: '계산을 끝내지 못했다' };
  let settled = false;
  let computeFailed = false;
  /** 마지막 값이 왜 못 미더운가 — 경고 문구를 원인에 맞게 고른다. */
  let doubt: 'empty' | 'rule' | undefined;
  /** 검토가 한 번이라도 거절했는가. 그 뒤의 값은 검토를 받기 전에는 믿지 않는다. */
  let ruleRejected = false;
  /** 실제로 돈 값 가운데 고를 것: 다 통과한 것이 없으면 검증을 넘은 마지막 것, 그것도 없으면 처음 것. */
  let firstRan: { value: unknown; code: string; verdict: ReturnType<typeof verifyResult> } | undefined;
  let lastVerified: typeof firstRan;
  for (let attempt = 1; ; attempt += 1) {
    let reason = '';
    let shown = '';
    let ran = false;
    try {
      value = await deps.sandbox.run(code, { rows, totals });
      ran = true;
    } catch (error) {
      // 파이썬 오류는 앞이 늘 같은 pyodide 호출이고 원인(`ValueError: …`)은 끝에 있다 — 끝을 남긴다.
      // eslint-disable-next-line no-console
      console.debug('[agent] 계산 실패', errorTail(error), budget.describe());
      // **조회가 전부 비었으면 계산할 것이 없다.** 빈 표에 대해 짠 코드가 화면 요약 칸을 찾다 `KeyError` 로
      // 죽고, 다시 짠 코드도 같은 칸을 찾아 죽었다(실제 테넌트, 2026-10-07). 빈 결과는 답이 정해져 있다.
      if (rowsRetrieved === 0) {
        // eslint-disable-next-line no-console
        console.debug('[agent] 빈 조회의 계산 실패 — 다시 짜지 않고 0건으로 답한다');
        value = { 건수: 0 };
        verdict = verifyValue(value, code);
        settled = verdict.ok;
        break;
      }
      // 모델은 오류 문장을 보고 표준 파이썬만으로 다시 짠다. 사람에게는 「계산 오류」로만 보인다.
      reason = `직전 계산은 오류로 멈췄다: ${errorTail(error, 300)}
그 모듈이나 문법을 쓰지 말고, 파이썬 표준 자료형만으로 다시 짜라.`;
      shown = '계산 오류';
    }
    // 0행이면 셈할 것이 없다 — 검토도 다시 짜기도 하지 않는다(답 단계가 「자료 없음」을 쓴다).
    const nothing = rowsRetrieved === 0;
    if (ran) {
      verdict = verifyValue(value, code);
      settled = verdict.ok;
      const tried = { value, code, verdict };
      firstRan ??= tried;
      // 원인마다 대응이 다르다. 조회가 빈 것을 코드로 고칠 수는 없다 — 다시 짜도 같은 0 이 나온다.
      if (!nothing && !verdict.ok && (verdict.cause === 'empty-column' || verdict.cause === 'useless-result')) {
        reason = `직전 계산은 답이 되지 못했다: ${verdict.reason ?? ''}`;
        // 검증 사유에는 칸 id 와 모델에게 내리는 지시가 섞여 있다 — 사람에게는 정해 둔 말만 보인다.
        shown = '답이 되지 않는 값';
        doubt = 'empty';
      } else if (verdict.ok) {
        lastVerified = tried;
        doubt = undefined;
        // 업무 규칙이 있을 때만 검토한다 — 기댈 바깥 기준 없이 스스로 고치게 하면 나빠지기 쉽다.
        if (!nothing && rules && reviews < MAX_REVIEWS && !budget.expired()) {
          reviews += 1;
          report({ kind: 'checking' });
          const review = await reviewResult({
            question: resolvedQuestion, rules, code, value, chat: deps.chat,
          });
          if (!review.ok) {
            // eslint-disable-next-line no-console
            console.debug('[agent] 검토 거절', `${reviews}번`, (review.reason ?? '').slice(0, 300));
            settled = false;
            ruleRejected = true;
            doubt = 'rule';
            reason = `직전 계산이 업무 규칙과 맞지 않는다: ${review.reason ?? ''}
규칙대로 다시 세라.`;
            // 검토 사유는 모델이 쓴 글이라 코드가 들어 있다(`r['distance'] != ''`) — 사람에게는 정해 둔 말만.
            shown = '업무 규칙과 다르게 셌다';
          } else {
            ruleRejected = false;
          }
        } else if (ruleRejected) {
          // 검토가 거절한 뒤 다시 짠 값인데 검토 상한에 걸려 확인하지 못했다 — 통과로 치지 않는다.
          settled = false;
          doubt = 'rule';
        }
      }
    }
    if (!reason) break;
    if (attempt >= MAX_COMPUTE || budget.expired()) {
      // eslint-disable-next-line no-console
      console.debug('[agent] 계산 시도 끝', `${attempt}번`, reason.split('\n')[0]?.slice(0, 160));
      // 마지막 시도가 던졌으면 앞서 돈 값으로 답한다. 한 번도 못 돌았으면 계산을 못 끝낸 것이다.
      const keep = lastVerified ?? firstRan;
      if (ran && !verdict.ok && lastVerified) {
        // 마지막 값이 답이 못 됐다(숫자가 전부 0 등) — 앞서 검증을 넘은 값이 낫다.
        ({ value, code } = lastVerified);
        verdict = lastVerified.verdict;
      } else if (!ran && keep) {
        ({ value, code } = keep);
        verdict = keep.verdict;
      } else if (!ran) {
        computeFailed = true;
        value = { 계산: '끝내지 못함' };
      }
      settled = false;
      break;
    }
    report({ kind: 'retrying', attempt: attempt + 1, reason: shown });
    // eslint-disable-next-line no-await-in-loop
    code = await makeCode(
      `${resolvedQuestion}

${reason}`,
      shapes,
      deps.chat,
      wantsFile,
      plan.requirements,
      rules,
    );
  }

  // **요약이 넘치면 한 번 다시 쓰게 한다.** 목록을 summary 에 통째로 넣으면 답변은 앞
  // 2,000자만 보고 「제공된 목록이 중간에서 끊겨 전체 현장 목록은 확인할 수 없습니다」라고
  // 썼다(점검결과등록 2,035건, 실제 테넌트 2026-10-07). 몇 자였는지 들려주면 모델이 목록을
  // 표로 옮긴다 — 실행 피드백으로 코드를 고치는 자리다. 계산 재시도처럼 계획 예산은 쓰지
  // 않고 시간만 본다. 그래도 넘치면 런타임이 가장 큰 목록을 표로 옮긴다(`tidyOversized`).
  const overflow = summaryOverflow(value, plan.requirements);
  if (overflow) {
    // eslint-disable-next-line no-console
    console.debug('[agent] 요약이 넘친다', `${overflow}자`, budget.describe());
    if (!budget.expired()) {
      report({ kind: 'computing' });
      let retried: unknown;
      let again = '';
      try {
        again = await makeCode(
          `${resolvedQuestion}

${oversizedNote(overflow)}`,
          shapes,
          deps.chat,
          wantsFile,
          plan.requirements,
          rules,
        );
        retried = await deps.sandbox.run(again, { rows, totals });
      } catch (error) {
        // 더 나은 모양을 노리는 시도다. 실패해도 손에 쥔 값은 버리지 않는다.
        // eslint-disable-next-line no-console
        console.debug('[agent] 요약 줄이기 실패', String(error).slice(0, 200));
        retried = undefined;
      }
      // 다시 쓴 값이 검증을 통과해야 바꾼다. 처음 값이 이미 검증에서 떨어졌으면 더 짧은 쪽이 낫다.
      const accepted = retried !== undefined && (verifyResult({
        value: retried,
        code: again,
        shapes,
        requirements: plan.requirements,
        question: resolvedQuestion,
        rowsRetrieved,
        totals,
      }).ok || !settled);
      if (accepted) {
        value = retried;
        code = again;
      }
    }
    if (summaryOverflow(value, plan.requirements)) value = tidyOversized(value, plan.requirements);
  }

  // **선택된 한 건의 상세로 답하는가.** 그 표는 목록 전체가 아니다 — 비용항목등록의
  // 상세 8행을 읽고 「등록된 비용 항목은 모두 8건」이라 답했는데 47개 그룹 중 첫 그룹의
  // 것뿐이었다(2026-09-23 실측). 사람은 그 숫자를 전체로 믿는다.
  const fromDetail = plan.retrievals.some((retrieval) => (
    // 펼쳐 읽은 상세는 한 건 것이 아니다 — 위 표 행마다 읽었다.
    !retrieval.sweep
    && (rows[retrieval.label]?.length ?? 0) > 0
    && deps.screens.find((screen) => screen.route === retrieval.route)
      ?.grids.find((grid) => grid.key === retrieval.rowsStateKey)?.detail
  ));
  // 아직 오지 않은 기간을 건 빈 조회. 넓혀 보자는 제안·「자료가 없습니다」 덧말이 맞지 않는다.
  const future = futurePeriodFacts(plan.retrievals, retrieved, deps.screens, localToday());
  // 보고서와 갈래 답이 셀 모집단: 계산 표가 행을 덜 담았거나 달 칸이 없으면 같은 칸으로 조회한 행 전부(10:11 판의
  // 계산 표는 「비고가 있거나 미실시인」 1,828행이라 보고서가 그것을 합계로 실었다).
  // 질문이 말한 값으로 좁힌 계산 표(「B 담당만」)는 그대로 둔다. 다듬은 질문은 첫 줄만(뒤는 지난 대화다).
  // 질문과 과제가 말한 달은 계산 표가 담지 않았어도 담는다(실측 2026-10-26 10:00).
  // 자료 읽기가 열린 문제(결함, 고장)면 그 칸을 담고 읽기를 붙인다. 답 근거도 그 숫자로 준다.
  const populationQuestion = `${question}\n${resolvedQuestion.split('\n')[0] ?? ''}`;
  const population = withReading(populationTable(splitResult(value, plan.requirements).table, plan.retrievals ?? [], rows, declared,
    populationQuestion, askedMonths(populationQuestion, task?.filters, localToday()), reading), reading);
  const issues = population && isIssues(population) ? issueReport(population, new Date(`${localToday()}T00:00:00`)) : undefined;
  // eslint-disable-next-line no-console
  console.debug('[agent] 자료 읽기', reading ? `${reading.source} ${reading.story}` : '없음', issues ? `열린 문제 ${issues.total}건` : '');
  const answerFacts = [
    // 열린 문제(결함, 고장): 할 일을 안 한 것으로 쓰지 않게 하고 코드가 센 숫자를 준다. 2턴 답이 「예정일이 지난 건 102건」을
    // 썼고 그 말이 3턴 문서로 넘어갔다(실제 테넌트, 2026-10-27). 「대표 사례」가 표 앞 여덟 줄이었다.
    ...(issues ? [ISSUE_ANSWER, ...issues.facts] : []),
    ...(fromDetail
      ? ['이 자료는 화면 위쪽 목록에서 선택된 한 건의 상세다. 목록 전체가 아니다.'
        + ' "모두"·"총"·"전체"라고 말하지 말고, 선택된 한 건의 항목이라고 말하라.']
      : []),
    ...emptyFacts,
    // 전후 비교면 답에도 기간·한계를 말하게 한다 — 계산 지침과 같은 말이다.
    ...(periodRule(plan.requirements) ? [periodRule(plan.requirements).replace(/^- /, '')] : []),
    // 세 번 다 계산이 멎었으면 그렇다고 말한다 — 조회한 것까지 버리고 「처리하지 못했습니다」로 끝내지 않는다.
    ...(computeFailed ? ['조회는 됐지만 계산을 끝내지 못했다. 무엇을 조회했는지 말하고, 계산이 안 됐다고 사람 말로 밝혀라. 숫자를 지어내지 마라.'] : []),
    // 위 목록이 너무 길어 펼치지 못했으면 그 사실을 답이 말한다 — 0행을 「자료가 없다」로 읽지 않게.
    ...Object.values(retrieved).flatMap((one) => (one.swept?.reason ? [one.swept.reason] : [])),
    // 놓친 줄을 말한다 — 0행으로 친 날이 있으면 근무일·건수가 그만큼 틀어진다.
    ...Object.values(retrieved).flatMap((one) => {
      const { masterRows = 0, missed = 0, unchanged = 0 } = one.swept ?? {};
      if (!missed && !unchanged) return [];
      return [`위 목록 ${masterRows}건 중`
        + `${missed ? ` ${missed}건은 행을 고르지 못했다` : ''}${missed && unchanged ? ',' : ''}`
        + `${unchanged ? ` ${unchanged}건은 상세가 비어 0행으로 쳤다` : ''}. 그만큼 덜 셌을 수 있다고 말하라.`];
    }),
    ...(rowsRetrieved === 0 ? [emptyConditionsFact(plan, deps.screens, !future.length)] : []),
    // 조회 여럿 중 일부만 비었으면 어느 것이 비었는지 말한다 — 2027년 연차생성이 0행이었는데 답이 2026년 행을
    // 보고 「2027년에 19일 생성」이라 했다(실제 테넌트, 2026-10-22).
    ...(rowsRetrieved > 0
      ? plan.retrievals.filter((retrieval) => !retrieved[retrieval.label]?.rows.length)
        .map((retrieval) => `${readableSources([retrieval], deps.screens).join(', ')} 조회는 자료가 없었다.`
          + ' 그 조건(기간)의 값은 없다고 말하고, 다른 조회의 값을 그 조건의 것이라 말하지 마라.')
      : []),
    // 좁힌 대상 중 위 목록에 없던 사람 — 사원명부의 퇴사자는 인사등록 재직 목록에 없다. 분모를 밝히게 한다.
    // 이 수는 앞 조회 전체에서 센 것이다 — 답이 그중 일부(최근 입사자 9명)만 다루면 그대로 옮기면 틀린 말이 된다.
    ...Object.values(retrieved).flatMap((one) => (one.swept?.unmatched
      ? [`앞 조회 대상 중 ${one.swept.unmatched}건은 위 목록에서 찾지 못했다(퇴사자 등). 이 수는 앞 조회 전체에서 센 것이니`
        + ' 이 수를 그대로 옮기지 마라 — 답이 다루는 사람 가운데 상세를 못 찾은 사람이 있을 때만 그 수와 분모를 밝혀라.']
      : [])),
    ...(derived ? [derivationFact(derived)] : []),
    // 「요 근래」를 언제로 셌는지 — 답 단계는 계획이 정한 날짜를 모른다(실제 테넌트, 2026-10-22).
    ...[vaguePeriodFact(question, localToday())].filter((fact): fact is string => !!fact),
    ...future,
    // 업무 용어의 뜻 — 규칙(연차 가산 등)은 답이 알아야 「언제 느는지」까지 말한다(실제 테넌트, 2026-10-22).
    ...[glossaryFor(`${question} ${resolvedQuestion}`, deps.experts ?? [], task?.terms)].filter(Boolean),
  ];
  // **분야 전문가의 분석 틀이 맞으면** 갈래마다 따로 계산하고 작성 담당이 문서로 답한다. 한 번의 계산은
  // 「특징을 정리해줘」를 유형·현장 건수 세기로 끝냈다(실제 테넌트, 2026-10-22).
  const frame = rowsRetrieved > 0 && !budget.expired()
    ? frameFor(`${question} ${resolvedQuestion}`, deps.experts ?? [], plan.retrievals.map((retrieval) => ({
      route: retrieval.route, rowsStateKey: retrieval.rowsStateKey, rows: rows[retrieval.label]?.length ?? 0,
    })), task?.analysis ? termRoutes('', deps.experts ?? [], deps.screens, task.terms) : [])
    : undefined;
  // 갈래 결과는 결과 묶음에 남긴다 — 다음 턴 문서가 그 숫자를 근거로 쓰고 센 칸을 밝힌다(7차 검토).
  let axisResults: ResultAxis[] = [];
  const expertDoc = frame ? await (async () => {
    report({ kind: 'computing' });
    const dueTable = population ?? splitResult(value, plan.requirements).table;
    // 갈래의 달별 전체는 〈표 1〉과 대조한다. 기간을 날짜로 잘라 10월을 2,002건으로 센 갈래가 있었다(실측 13:06).
    const months = (dueTable ? statusTable(dueTable)?.table.rows ?? [] : [])
      .filter((row) => /^\d{6}$/.test(String(row[0]))).map((row) => [String(row[0]), Number(row[1])] as [string, number]);
    const results = await analyzeAxes({
      question: resolvedQuestion, frame, shapes, rows, totals, chat: deps.chat, sandbox: deps.sandbox, rules,
      ...(months.length > 1 ? { months } : {}),
    });
    // eslint-disable-next-line no-console
    console.debug('[agent] 갈래 분석', frame.name, results.map((one) => `${one.axis}:${one.failed ? '실패' : '됨'}`).join(' '));
    if (!results.some((one) => !one.failed)) return '';
    // 갈래 칸 이름에는 계산에 알린 「그 밖의 값」 꼬리를 남기지 않는다. 문서 각주에 실렸다(11:01).
    const labels = new Map(shapes.flatMap((shape) => shape.columns.map((column) => [
      column.id, (column.text ?? column.id).replace(/,\s*그 밖의 값:.*$/, ''),
    ] as const)));
    axisResults = results.filter((one) => !one.failed).map((one) => ({
      axis: one.axis,
      columns: Array.from(new Set((frame.axes.find((axis) => axis.name === one.axis)?.columns ?? [])
        .map((id) => labels.get(id) ?? id))),
      value: one.value,
    }));
    const expert = (deps.experts ?? []).find((one) => (one.frames ?? []).indexOf(frame) >= 0);
    // 갈래 답도 기준일 대비 경과를 안다 — 첫 답은 미전송·서명을 앞세우고 보고서는 경과 52건을 앞세워 「문제없어?」에
    // 두 결론이 나왔다(9차 검토). 표가 없거나 날짜·상태 칸이 없으면 아무것도 더하지 않는다.
    const dueDay = new Date(`${localToday()}T00:00:00`);
    const dueFacts = overdueFacts(dueTable, dueDay);
    // 여러 달 판은 묶음 문장을 코드가 쓴 절로 싣는다. 1턴 답이 묶음을 다시 풀어 쓰다 날짜와 담당을 섞었다(13차 검토).
    const grouped = dueTable ? carryLines(dueTable, dueDay) : [];
    return writeReport({
      question: resolvedQuestion, frame, expertName: expert?.name, results, facts: [...answerFacts, ...dueFacts], chat: deps.chat,
      ...(grouped.length ? { fixed: { title: '미실시 호기 구분', lines: grouped } } : {}),
      // 「현장 N건」을 대조할 달별 현장 수(실측 15:20), 메모 예시를 대조할 현장별 메모, 경과 전체 범위(17차 검토).
      ...(dueTable ? {
        sites: siteCounts(dueTable, dueDay),
        memos: memoFindings(dueTable).map((one) => ({ site: one.site, memo: one.memo })),
        overdue: overdueSpan(dueTable, dueDay),
      } : {}),
    }).catch((error) => {
      // 작성 담당이 멈추면 기본 답으로 간다. 까닭을 남긴다(실측 2026-10-26 05:35: 기록 없이 짧은 답이 나갔다).
      // eslint-disable-next-line no-console
      console.debug('[agent] 작성 실패', String((error as Error)?.message ?? error).slice(0, 200));
      return '';
    });
  })() : '';
  // 연차 촉진 명단은 답이 보는 표 앞부분을 잔여 많은 순으로 놓는다(`rankedView`). 갈래 문서는 작성 담당이 따로 쓴다.
  const ranked = expertDoc || rowsRetrieved === 0 ? undefined : rankedView(splitResult(value, plan.requirements),
    [question, resolvedQuestion.split('\n')[0] ?? '', task?.goal ?? ''].join('\n'), question);
  const composed = expertDoc
    ? answerFrom(resolvedQuestion, expertDoc, value, code, plan)
    : await compose(resolvedQuestion, value, code, plan, deps.chat, {
      stream: deps.chatStream,
      onDelta: deps.onProgress && ((text) => report({ kind: 'answering', text })),
    }, rowsRetrieved === 0, [...answerFacts, ...(ranked?.facts ?? [])], ranked?.view);
  // 계산 전에 뺀 계정 줄은 답의 첫 수치 문장 뒤에 코드가 넣는다. 문서 작성에는 모델이 쓴 답을 준다(문서는 같은 줄을 따로 싣는다).
  // 넣어서 센 턴도 같은 자리에 그 사실을 쓴다.
  const excludedNote = excludedLines.map((one) => one.answer).join(' ') || includedNote(included);
  const replied = excludedNote ? { ...composed, text: afterLead(composed.text, excludedNote) } : composed;
  // 계획이 비워 둔 선택 칸의 화면 값(「연차생성기준 입사일」)도 결과 기록에 남긴다. 다음 턴 문서가 연차 생성 기준을 계산 요약의 글에만
  // 기대지 않는다. 요약이 「조회 조건: 2026년, 재직자, 입사일 기준」으로 쓰인 판에 공지의 권장 기한과 보고서의 제61조 날짜가
  // 빠졌다(실측 2026-10-27 09:24).
  const screenValues = plan.retrievals.flatMap((retrieval) => screenChoices(retrieval, deps.screens, retrieved[retrieval.label]?.applied)
    .map((one) => `${one.label} ${one.meaning}`));
  const result = composed.result
    && (task || axisResults.length || population || excludedLines.length || keepNumbered || leftIn.size || screenValues.length || reading)
    ? {
      ...composed.result, ...(task ? { task } : {}), ...(axisResults.length ? { axes: axisResults } : {}),
      ...(reading ? { reading } : {}),
      ...(population ? { population } : {}), ...(excludedLines.length ? { excluded: excludedLines.map((one) => one.line) } : {}),
      ...(keepNumbered ? { keepNumbered } : {}), ...(leftIn.size ? { numbered: Array.from(leftIn) } : {}),
      ...(screenValues.length ? { screenValues } : {}),
    }
    : composed.result;
  // **파일은 청했을 때만 만든다.** 보고서 스킬이 뽑혔다는 이유로 문서를 만들면 답변
  // 본문이 통째로 `…를 준비했습니다. 아래 파일 버튼으로 받아 주세요` 로 바뀐다 —
  // `이번 달 지출결의서 얼마나 올렸어?` 에 그렇게 나갔다. 사람은 숫자를 물었다.
  // 화면 이름 속 `보고서` 는 파일을 청한 말이 아니다 — 「이번 달 당직근무 보고서 보여줘」에
  // 「ERP 현황 보고서를 준비했습니다 … 파일 버튼」이 나갔다(실제 테넌트, 2026-10-05).
  const namedForFile = namedScreen(question, deps.screens);
  const askedForFile = namedForFile
    ? question.replace(/\s+/g, '').split(namedForFile.name.replace(/\s+/g, '')).join(' ')
    : question;
  // 첫 턴의 공지 요청(「연차 촉진 공지 만들어줘」)도 문서를 청한 말이다. 문서 없이 채팅 글로 끝났다(독립 검토).
  const noticeAsked = noticeRequested(question, deps.screens);
  const wantsDocument = threadFormats(question, deps.context).length > 0
    || /보고서|문서로|파일로|받아\s*줘|출력/.test(askedForFile) || noticeAsked;
  // 답변도 모델이 쓴 말이다. 조회한 화면의 이름을 알면 그것으로 바꿔 준다.
  const answered = deps.screens.find((one) => one.route === plan.retrievals?.[0]?.route)
    ?? deps.screens.find((one) => one.route === deps.context?.currentRoute);
  const documentKeep = keepState || decision.action === 'include';
  // 보고서에 싣는 말도 사람 말로 — 「`distance`가 0보다 큰」이 요약에 그대로 실릴 뻔했다(검토, 2026-10-23).
  const madeReport = result && (artifactSkill || layoutAsked(question, task, deps.context) || noticeAsked) && wantsDocument
    ? makeReport(result, artifactSkill, await reportExtra(question, task, {
      summary: result.summary, table: result.table, answer: plainSpeech(composed.text, answered), axes: result.axes,
      // 같은 턴의 문서는 넣으라고 했으면 넣는다(넣기 상태를 잇지 않는 넣기도). 묻기만 했으면 규칙대로 뺀다. 묻기에도 넣으면 그
      // 사실을 어디에도 밝히지 않은 채 공지가 13명이 됐다(독립 재검토).
      population: result.population, excluded: result.excluded, screenValues: result.screenValues, keepNumbered: documentKeep,
      reading: result.reading, sources: result.sources, screens: deps.screens, experts: deps.experts,
      asked: (deps.context?.history ?? []).map((turn) => turn.question).join('\n'),
      applied: appliedConditions(result.sources, deps.screens), unit: deps.context?.me?.divinm,
      questions: (deps.context?.history ?? []).map((turn) => turn.question).join('\n'),
    }, deps.chat))
    : undefined;
  const reportDocument = madeReport && {
    ...madeReport,
    summary: plainSpeech(madeReport.summary, answered),
    ...(madeReport.headline ? { headline: plainSpeech(madeReport.headline, answered) } : {}),
    ...(madeReport.points ? {
      points: {
        summary: madeReport.points.summary.map((one) => plainSpeech(one, answered)),
        checks: madeReport.points.checks.map((one) => plainSpeech(one, answered)),
        actions: madeReport.points.actions.map((one) => plainSpeech(one, answered)),
      },
    } : {}),
    ...(madeReport.charts ? {
      charts: madeReport.charts.map((chart) => (chart.message ? { ...chart, message: plainSpeech(chart.message, answered) } : chart)),
    } : {}),
    sources: readableSources(usedRetrievals(plan.retrievals, population), deps.screens),
    // 이 문서가 계정을 남겼는가. 다음 턴이 넣고 빼기를 바꾸면 이 문서를 꺼내지 않는다(독립 재검토).
    keepNumbered: documentKeep,
  };
  // 보고서로 만든 답에만 형식을 붙인다. 대화에 한 번 나온 `엑셀` 이 뒤따르는 모든
  // 답에 버튼을 달면, 파일로 줄 것이 없는 답에까지 버튼이 붙는다.
  // **앞쪽 몇 건으로 다 못 적는 표에는 엑셀 단추를 붙인다.** 패널은 표를 답 아래에 그리지
  // 않는다 — 단추가 없으면 2,035건 가운데 답에 적힌 열 건 말고는 받을 길이 없다. 답변도
  // 「전체는 엑셀로 받기로」라고 안내한다(`compute.ts` 의 `bigTableNote`).
  const bigTable = !reportDocument && (replied.table?.rows.length ?? 0) > LISTED_ROWS;
  const answerFormats: FileFormat[] = reportDocument
    ? withFullTable(threadFormats(question, deps.context).length
      ? threadFormats(question, deps.context)
      : uniqueFormats([...(task?.artifact ? [task.artifact.format] : []), ...(artifactSkill?.formats ?? ['pdf', 'docx'])]),
    reportDocument)
    : task?.artifact && wantsDocument ? [task.artifact.format] : (bigTable ? ['xlsx'] : []);
  const answer: Answer = {
    ...replied,
    // 넓혀서 찾은 답은 그 사실을 **우리가** 앞에 적는다. 모델에게 사실로 알렸는데도
    // 「7월 전자세금계산서는 총 1,117건」으로만 시작했다(실제 테넌트, 2026-10-11) —
    // 사람은 자기가 말한 조건의 답으로 읽는다.
    // 모델이 제 말로 적었으면 겹쳐 적지 않는다 — 「제한하지 않고 다시 조회했습니다」.
    text: widenedLead && !/넓혀|전체로|제한하지 않|다시 (?:조회|봤|확인)/.test(replied.text)
      ? `${widenedLead}\n\n${plainSpeech(replied.text, answered)}`
      : plainSpeech(replied.text, answered),
    // 공지 문서에는 뺀 계정 줄이 없다(게시판에 붙인다). 채팅 답에는 알린다(독립 재검토: 첫 질문이 공지면 어디에도 없었다).
    ...(reportDocument ? { text: reportText(reportDocument, reportDocument.genre === 'notice' ? excludedNote : '') } : {}),
    // 공지 답의 표는 공지 문서의 표(부서별)뿐이다. 엑셀 받기가 답의 표(개인 명단)로 떨어지지 않게(국가직 팀장 검토).
    ...(reportDocument?.genre === 'notice' ? { table: reportDocument.table } : {}),
    ...(answerFormats.length ? { formats: answerFormats } : {}),
    ...(reportDocument && !emptyNotice(reportDocument) ? { report: reportDocument } : {}),
    // 신호가 어긋났으면 반대 선택을 단추로 붙인다: 뺐으면 「넣어서 다시」, 넣었으면 「빼고 다시」(`duplicateDecision`). 이번 말 없이
    // 넣기 상태를 이어 넣은 턴에도 「빼고 다시」를 붙인다.
    ...(!reportDocument && decision.doubted === 'include' && duplicates.length ? { options: [INCLUDE_AGAIN] } : {}),
    ...(!reportDocument && numbered.size && !duplicates.length
      && (decision.doubted === 'exclude' || (including && !decision.keep && decision.action !== 'include')) ? { options: [EXCLUDE_AGAIN] } : {}),
    // 넣기 상태는 과제에 실어 다음 턴이 잇는다.
    task: task ? { ...task, keepNumbered: keepState || undefined } : task,
    result,
  };
  if (task) {
    // 부족한 점이 있어도 만든 답은 준다. 아무것도 주지 않으면 사용자는 다음에 무엇을
    // 물어야 할지조차 모른 채 끝난다 — 무엇이 빠졌는지 적어 주면 적어도 그것은 안다.
    // 공지 답의 표는 공지 문서의 부서별 표라, 완료 검사는 계산 표로 한다. 「사원명 항목은 이 자료에 없습니다」가 붙었다(독립 재검토).
    const verification = verifyCompletion(task, reportDocument?.genre === 'notice' ? { ...answer, table: composed.table } : answer);
    if (!verification.complete) {
      answer.text = `${answer.text}\n\n(${verification.issues.join(' / ')})`;
    }
  }
  // 근거는 화면 이름과 사람이 읽는 조건으로 적는다. `/w_tb_e050w_05 (year=2026)` 은
  // 우리에게만 뜻이 있는 문자열이다 — 쓰는 사람은 그 화면을 메뉴 이름으로 안다.
  // 같은 줄 지우기·기간 묶기·코드 감추기는 `readableSources` 가 한다(§sources.ts).
  answer.sources = readableSources(usedRetrievals(plan.retrievals, population), deps.screens);

  // 차선으로 답했다는 사실은 알린다. 다만 무엇을 못 지켰는지는 우리 사정이다 —
  // 쓰는 사람은 사무직이고 화면 경로도 스킬 이름도 알 이유가 없다.
  // 다시 계산할 수 없는데 검증이 거절했으면 적어도 그 사실은 밝힌다. 스킬의 고정
  // 스크립트가 낸 값이 그렇다 — 고쳐 쓸 수는 없지만 답인 척 내보내서는 안 된다.
  // 행이 하나도 없을 때는 이 경고가 겹말이다 — 바로 아래에서 자료가 없다고 밝힌다.
  // 까닭은 원인마다 다르다 — 계산을 끝내지 못한 것은 답 본문이 이미 말한다.
  // 갈래 문서로 답했으면 붙이지 않는다. 그 숫자는 코드가 센 근거와 검산을 거친 갈래 값뿐인데, 일괄 문장이 원자료와 맞는
  // 숫자까지 의심하게 했다(13차 검토).
  if (!settled && rowsRetrieved > 0 && !computeFailed && !expertDoc) {
    answer.text = `${answer.text}

이 값은 화면 자료와 맞지 않을 수 있습니다.`
      + (doubt === 'rule'
        ? ' 정해 둔 계산 방식대로 셌는지 끝내 확인하지 못했습니다.'
        : ' 일부 항목에 값이 없어 계산에서 빠졌습니다.');
  }
  // **행이 하나도 없었으면 그 사실을 답이 반드시 말한다.** 계산이 0을 내든 요약이
  // 무슨 말을 지어내든, 조회가 비었다는 것은 우리가 아는 사실이다.
  // 답변이 이미 그 말을 했으면 다시 적지 않는다 — `자료가 없습니다` 가 두 번 이어 나왔다.
  // 답이 그 사실을 이미 말했으면 다시 적지 않는다. 안 말했으면 우리가 붙인다 —
  // 모델에게 알려도 「모두 8건」이라 쓴 판이 있었다.
  if (fromDetail && !/선택(된|한)/.test(answer.text)) {
    answer.text = `${answer.text}

이 목록은 화면 위쪽 목록에서 선택된 한 건의 상세입니다. 전체가 아니며, 다른 건은 그 행을 골라야 보입니다.`;
  }
  if (rowsRetrieved === 0) {
    // **이름을 잘못 들은 것과 그 기간에 없는 것은 다르다.** 사람은 오타를 낸다 —
    // `왕십리제일빌딩 고장 몇 건이야?` 에 `자료가 없습니다` 라고만 답하면, 그런 현장이
    // 없다는 것인지 고장이 없다는 것인지 알 수 없다. 이름으로 걸렀는데 한 건도 없으면
    // 그 이름이 현장 목록에 있기는 한지 한 번만 더 본다.
    const named = plan.retrievals
      .map((retrieval) => retrieval.conditions.searchQuery ?? retrieval.conditions.actnm)
      .map((value) => String(value ?? '').trim())
      // 검색어 칸이 「년도」인 화면이 있다 — 「2027은 등록된 현장에서 찾지 못했습니다」라고 했다(연차생성, 2026-10-22).
      .find((value) => value && !PERIOD_VALUE.test(value));
    let unknown = false;
    if (named) {
      try {
        const found = await deps.session.retrieve({
          label: '이름 확인',
          routePath: SITE_LIST.route,
          rowsStateKey: SITE_LIST.rowsStateKey,
          conditions: { searchQuery: named },
        });
        unknown = found.rows.length === 0;
      } catch {
        // 확인하지 못하면 하던 대로 말한다. 없는 말을 지어내지 않는다.
      }
      // **다녀온 화면에 사람을 두고 오지 않는다.** 이 조회는 우리 사정이지
      // 사람이 요청한 화면이 아니다 — 「이번 달 점검 계획」 다음 「덕봉빌딩 것만」
      // 을 물었더니 답은 나왔는데 화면은 보수현장현황이었다(2026-09-22 실측).
      const back = plan.retrievals[plan.retrievals.length - 1]?.route;
      if (back && deps.session.show) {
        try {
          await deps.session.show(back);
        } catch {
          // 돌아가지 못해도 답은 그대로 낸다. 화면 위치는 답보다 덜 중요하다.
        }
      }
    }
    // 이름을 못 찾았다는 것은 **새 정보다.** 답변이 이미 `자료가 없습니다` 라고 했어도
    // 따로 적어 준다. 그 말만 겹칠 때는 다시 적지 않는다.
    if (unknown) {
      answer.text = `${answer.text}

${topicParticle(named!)} 등록된 현장에서 찾지 못했습니다. 이름이 맞는지 확인해 주세요.`;
    // 아직 오지 않은 기간이면 답이 「생성 전」이라고 이미 말했다 — 겹말을 붙이지 않는다.
    } else if (!future.length && !/자료가 없습니다|자료는 없습니다/.test(answer.text)) {
      answer.text = `${answer.text}

그 조건으로는 자료가 없습니다.`;
    }
  }
  // 자료가 없는 것은 답변이 밝힌다. 숫자를 냈다고 답이 된 것이 아니다.
  if (rowsRetrieved > 0 && !verdict.ok && verdict.cause === 'empty-result') {
    answer.text = `${answer.text}

그 기간에는 자료가 없습니다.`;
  }
  // 비어 있던 항목은 답변에 밝힌다. 없는 값을 조용히 0 으로 읽게 두면 안 된다.
  if (emptyColumns.length) {
    answer.text = `${answer.text}

일부 항목은 이 자료에 값이 없어 계산에서 빠졌습니다.`;
  }
  // 계획 검증을 푼 것은 **우리 사정**이다. 검증까지 통과한 답에 이 말을 붙이면
  // 맞는 답을 스스로 의심하게 만든다 — `덕봉빌딩 견적 낸 거 있어?` 가 33건을
  // 정확히 찾아 놓고 "비슷한 자료로 답했습니다" 를 달고 나갔다.
  if (plan.relaxed && !settled) {
    answer.text = `${answer.text}\n\n요청하신 기준에 딱 맞는 화면이 없어 비슷한 자료로 답했습니다.`;
  }
  // 무엇을 어디서 몇 행 읽었고 예산을 얼마나 썼는지 남긴다. 사용자에게는 안 보인다.
  //
  // `수금 0원` 을 세 번 고쳤는데 세 번 다 추측이었다 — 한 번은 12억을 지울 뻔했다.
  // 답이 이상할 때 물어볼 데가 없으면 계속 그렇게 된다.
  // eslint-disable-next-line no-console
  console.debug('[agent]', {
    질문: resolvedQuestion.replace(/\s+/g, ' ').slice(0, 60),
    // 배열로 두면 콘솔이 `Array(1)` 로 접어 정작 필요한 것이 안 보인다.
    조회: plan.retrievals.map((retrieval) => `${retrieval.route}/${retrieval.rowsStateKey}`
      + `(${retrieved[retrieval.label]?.rows.length ?? 0}행)`).join(' | '),
    // 첫 판정이 아니라 **끝난 뒤 상태**를 적는다. 다시 계산해 고쳐졌는데도 거절로
    // 남아 있으면, 로그를 보는 쪽이 멀쩡한 답을 실패로 읽는다.
    // 원인 코드만으로는 어느 규칙이 걸렸는지 못 가린다. `useless-result` 하나에 규칙이
    // 넷이라, 고정 스크립트가 거절당했을 때 무엇을 고쳐야 하는지 알 수 없었다.
    // 값 검증은 넘었는데 업무 규칙 검토가 거절한 것이면 그렇게 적는다 — 「알 수 없음」으로 남았다(2026-10-23).
    검증: settled ? '통과' : verdict.ok && doubt === 'rule' ? '거절(업무 규칙 검토)'
      : `거절(${verdict.cause ?? '알 수 없음'}: ${(verdict.reason ?? '').slice(0, 120)})`,
    표: answer.table ? `${answer.table.columns.length}열 ${answer.table.rows.length}행` : '없음',
    // 카탈로그가 있다고 한 열을 서버가 안 주는 일이 있다. `iamt(년합계)` 가 그랬고,
    // 계산이 그것을 더해 수금을 0원이라고 답했다 — 오류는 하나도 나지 않았다.
    // 진짜 조건으로 받은 행이 여기 있으니, 지나가는 김에 대조해 남긴다.
    어긋난열: plan.retrievals.flatMap((retrieval) => {
      const rows = retrieved[retrieval.label]?.rows ?? [];
      if (!rows.length) return [];
      const screen = deps.screens.find((one) => one.route === retrieval.route);
      const grid = screen?.grids.find((one) => one.key === retrieval.rowsStateKey);
      if (!grid) return [];
      const fields = new Set(rows.slice(0, 5).flatMap((row) => Object.keys(row)));
      return grid.columns.map((column) => column.id).filter((id) => !fields.has(id));
    }).join(',') || '없음',
    // 답이 틀렸을 때 마지막 물음은 "무엇을 더했나" 다. 코드 전문보다 집은 열이 답이다.
    계산열: Array.from(new Set(Array.from(code.matchAll(/['"]([a-z][a-z0-9_]{2,})['"]/gi))
      .map((hit) => hit[1]))).join(','),
    // 표를 만들었는데 답에 안 실리는 일이 있다. 결과 모양을 봐야 어디서 빠졌는지 안다.
    결과: (() => { try { return JSON.stringify(value).slice(0, 120); } catch { return '직렬화 실패'; } })(),
    예산: budget.describe(),
  });
  // 위 객체는 콘솔이 길면 뒤를 접는다 — `계산열` 이 늘 잘려 무엇을 더했는지 못 봤다.
  // 답이 틀렸을 때 가장 먼저 묻는 것이므로 한 줄로 따로 남긴다.
  // eslint-disable-next-line no-console
  console.debug('[agent] 계산열', Array.from(new Set(Array.from(code.matchAll(/['"]([a-z][a-z0-9_]{2,})['"]/gi))
    .map((hit) => hit[1]))).join(','), '결과', (() => {
    try { return JSON.stringify(value).slice(0, 200); } catch { return '직렬화 실패'; }
  })(), budget.describe());
  // 경험에는 **사람이 한 말만** 싣는다. `resolvedQuestion` 은 작업이 붙으면
  // `지금까지:`·`조건:`·`처리:` 가 줄줄이 달린 내부 블롭이라, 그대로 저장하면
  // 다음 계획 프롬프트에 그 덩어리가 통째로 되돌아온다(실측: 8월 12일 재사용 확인 실행).
  // 첫 줄이 `TaskSpec.goal` 이고 작업이 없으면 사용자 질문 그대로다.
  const experienceQuestion = (resolvedQuestion.split('\n')[0] ?? '').trim() || resolvedQuestion;
  const experience = makeExperience(experienceQuestion, plan, retrieved, validation, answer.table);

  // 조회 중에 뜬 알림은 사용자가 못 봤다. 닫아 준 것을 말하지 않으면 못 봤다는 사실도
  // 모른다. LLM 에게 다시 쓰게 하지 않고 앱이 낸 문구를 그대로 붙인다.
  const notices = Object.values(retrieved).flatMap((result) => result.notices ?? []);
  return notices.length ? { ...answer, notices, experience } : { ...answer, experience };
}

/** 테스트에서만 쓴다. 사람이 읽는 문구를 만드는 작은 조각들. */
export const __test = {
  plainLabel, documentGenre, deadlineFacts, noticeRequested, chartsRefused, withoutCounts, lawDuplicate, noticeLeaveLines, byDate,
  ownedAction, markExcluded, afterLead, duplicatesTalk, duplicateDecision, duplicateSignals, withoutOwnLines, droppedNames,
  documentOnlyQuestion,
};

/**
 * 오류 글의 끝. 파이썬 traceback 은 원인(`ValueError: …`)이 맨 끝 줄이고 앞은 늘 같은 pyodide 호출이다.
 * 다시 짜게 할 때도 앞 300자를 줬다 — 모델은 원인을 못 보고 같은 int() 를 또 썼다(고장처리현황, 2026-10-12).
 */
function errorTail(error: unknown, length = 200): string {
  const text = String(error).replace(/\s+/g, ' ');
  return text.length > length ? `…${text.slice(-length)}` : text;
}

/**
 * 계산에 보이는 조회 조건 — `gijun=0(귀속년월), stmon=202610`. 값은 화면에 넣은 그대로, 뜻은 선택지
 * 글자나 칸 이름으로 붙인다. 빈 조건은 걸지 않은 것이라 뺀다.
 * 계획이 비워 둔 선택 칸은 화면에 걸린 값으로 조회됐다. 그 값(applied)을 「조회 때 화면 값」으로 붙인다(사람이 전에 손으로 바꾼
 * 값일 수도 있어 기본값이라 부르지 않는다). 연차생성기준을 몰라 계산이
 * 「조회 조건에 기준값 없음」이라 썼고 공지가 「…판단 불가」로 끝났다(실측 2026-10-26). 선택지가 있는 칸만: 글자 칸 값(사람 이름)은
 * 계획이 넣지 않았으면 싣지 않는다.
 */
function conditionText(
  retrieval: { route: string; conditions: Record<string, string> },
  screens: AgentScreen[],
  applied?: Record<string, string>,
): string {
  const fields = screens.find((screen) => screen.route === retrieval.route)?.fields ?? [];
  const given = Object.entries(retrieval.conditions)
    .filter(([, value]) => String(value ?? '').trim())
    .map(([key, value]) => {
      const field = fields.find((one) => one.key === key);
      const meaning = field?.optionCodes?.find((code) => code.value === String(value))?.label ?? field?.label;
      return meaning ? `${key}=${value}(${meaning})` : `${key}=${value}`;
    });
  const defaults = screenChoices(retrieval, screens, applied).map((one) => `${one.key}=${one.value}(${one.meaning}, 조회 때 화면 값)`);
  return [...given, ...defaults].join(', ');
}

/** 계획이 비워 둔 선택 칸(선택지가 있는 칸)에 조회 때 걸려 있던 화면 값과 그 뜻. */
function screenChoices(
  retrieval: { route: string; conditions: Record<string, string> },
  screens: AgentScreen[],
  applied?: Record<string, string>,
): { key: string; value: string; label: string; meaning: string }[] {
  const fields = screens.find((screen) => screen.route === retrieval.route)?.fields ?? [];
  return fields.filter((field) => field.optionCodes?.length && !String(retrieval.conditions[field.key] ?? '').trim())
    .flatMap((field) => {
      const value = applied?.[field.key];
      const meaning = field.optionCodes?.find((code) => code.value === String(value ?? ''))?.label;
      return value !== undefined && meaning ? [{ key: field.key, value: String(value), label: field.label ?? field.key, meaning }] : [];
    });
}
